import { Perf } from 'r3f-perf'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { Canvas, useFrame, useLoader, useThree,createPortal  } from "@react-three/fiber"
import { PerspectiveCamera, ContactShadows, Environment, useGLTF, Text, Html, useTexture, useAnimations, Text3D, Center, Clone, Box   } from "@react-three/drei"
import * as THREE from "three"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader"
import { SkeletonUtils } from 'three-stdlib'
import React, { useRef, useState, useEffect,  useMemo, useCallback, useImperativeHandle, useLayoutEffect, Suspense  } from "react"
import { GameContext,useGame } from "./GameContext"
import HighscoreModal from "./HighscoreModal"
import Leaderboard from "./Leaderboard"
import OptionsModal from "./OptionsModal"
import AideModal from "./AideModal"
import VeloIntro from "./VeloIntro"
import VeloShareScore from "./VeloShareScore"
import { trackEvent } from "./analytics"
import {
  REV_BERRI_ROUTE,
  calculateRouteProgressKmFromZ,
  formatKm,
  formatPercent
} from "./revBerriRoute"
import "./Velo.css"
import { TruckElectric } from 'lucide-react'
import { stubFalse } from 'lodash'


// DÉBUT

const DEBUG = false
const BOOST_MAX = (DEBUG) ? 20 : 1
const DEBUG_ALL_CARS = false
const MAX_VIES = 3
const MAX_PLAYER_SPEED =  0.1
const ARRIVE = -22600
const ROAD_WIDTH = 46
const CREMAZIE_Z = -3525
const COMMUNE_Z = -12865
const COMMUNE_TRAFFIC_OFFSET = 13
const COMMUNE_TRAFFIC_DENSITY_FACTOR = 0.5
const TRAFFIC_CAR_FORCE_RIGHT_TURNS = new Set(["Commune", "Tournant"])
const TRAFFIC_CAR_BLOCK_RIGHT_TURNS = new Set(["Cherrier", "Mill"])
const TRAFFIC_DISABLED_INTERSECTIONS = new Set(["Passerelle Côte-Saint-Paul"])
const TRAFFIC_LIGHT_DISABLED_INTERSECTIONS = new Set([
  "Cherrier",
  ...TRAFFIC_DISABLED_INTERSECTIONS
])
const STRAIGHT_CAR_ACTOR_AVOID_SIDE_EXTRA = 0.9
const STRAIGHT_CAR_ACTOR_DETECT_SIDE_EXTRA = 1.0
const COMMUNE_OFFSET_X = -28
const COMMUNE_ACTOR_LIMIT_SHIFT_X = 1
const MCGILL_OFFSET_X = -20
const MILL_OFFSET_X = -28
const MILL_ACTOR_CENTER_SHIFT_X = 1
const TOURNANT_OFFSET_X = -16
const MCGILL_OFFSET_BLEND_DISTANCE = 200
const CANAL_Z = -15950
const CANAL_CYCLISTS_SHIFT_START_Z = CANAL_Z
const CANAL2_CYCLISTS_SHIFT_START_Z = -17600
const CANAL3_CYCLISTS_SHIFT_START_Z = -20500
const CANAL_CYCLISTS_SHIFT_X =4
const CANAL2_CYCLISTS_SHIFT_X =5
const CANAL3_CYCLISTS_SHIFT_X =2
const CANAL_CYCLISTS_SHIFT_BLEND_DISTANCE = 100
const CANAL_CYCLISTS_LANE_CLOSER_X = 1
const LATE_CYCLISTS_SHIFT_START_Z = -17000
const LATE_CYCLISTS_SHIFT_X = -5
const FINAL_CYCLISTS_SHIFT_START_Z = -20500
const FINAL_CYCLISTS_SHIFT_X = -7
const WALL_CLEARANCE_LEFT = 1.5
const WALL_CLEARANCE_RIGHT = 2
const CYCLIST_WALL_PADDING = 1.2
const WALL_TILT = 0.08
const OUEST_WALL_EXTRA_TILT_START_LEVEL = 21
const OUEST_WALL_EXTRA_TILT_MULTIPLIER = 2
const BASE_GAME_SPEED = 12
const LEVEL_SPEED_GAIN = 1.2
const SEGMENT_LENGTH = 60
const BASE_Y = 1.6 // Hauteur du joueur

// Les collisions utilisent le repère du groupe qui affiche chaque élément.
// Les matrices sont actualisées ici, avant le rendu de la frame courante.
function getPositionInFrame(object, frame, target) {
  const node = object?.pivot || object
  if (!node?.getWorldPosition || !frame?.worldToLocal) return null

  node.getWorldPosition(target)
  frame.updateWorldMatrix(true, false)
  return frame.worldToLocal(target)
}

function getRouteOffsetX(playerZ) {
  let offsetX = playerZ <= commune.z ? COMMUNE_OFFSET_X : 0

  if (playerZ <= mcGill.z + MCGILL_OFFSET_BLEND_DISTANCE) {
    const t = THREE.MathUtils.clamp(
      (mcGill.z + MCGILL_OFFSET_BLEND_DISTANCE - playerZ) /
        MCGILL_OFFSET_BLEND_DISTANCE,
      0,
      1
    )
    offsetX = THREE.MathUtils.lerp(COMMUNE_OFFSET_X, MCGILL_OFFSET_X, t)
  }

  if (playerZ <= mill.z) offsetX = MILL_OFFSET_X
  if (playerZ <= tournant.z) offsetX = TOURNANT_OFFSET_X
  return offsetX
}

function shouldRenderRoadObstacles(playerZ) {
  return playerZ >= tournant.z
}

function followCyclistCameraX(camPivot, playerX, dt) {
  // Même axe pour la caméra et sa visée, sans saut lors d'un changement de voie.
  camPivot.position.x = THREE.MathUtils.damp(
    camPivot.position.x,
    playerX,
    -Math.log(1 - 0.12) * 60,
    dt
  )
}

function getLevelMaxPlayerSpeed(level) {
  const numericLevel = Number(level)
  const safeLevel = Number.isFinite(numericLevel)
    ? Math.max(1, numericLevel)
    : 1

  return MAX_PLAYER_SPEED * (1 + Math.log(safeLevel + 1) * 0.3)
}


const dracoLoader = new DRACOLoader()

dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')

const gltfLoader = new GLTFLoader()

gltfLoader.setDRACOLoader(dracoLoader)


// ===== LEVEL METRICS  =====

const LEVELS = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36]
const LEVEL_TEXTURES = {
  1:  { count: 1,  low: false, offsetLeft: 0, offsetRight: 0 },
  2:  { count: 23, low: false, offsetLeft: 0, offsetRight: 0 },
  3:  { count: 15, low: false, offsetLeft: 0, offsetRight: 0 },
  4:  { count: 14, low: false, offsetLeft: 1, offsetRight: 0 },
  5:  { count: 16, low: false, offsetLeft: 0, offsetRight: 0 },
  6:  { count: 12, low: false, offsetLeft: 1, offsetRight: 0 },
  7:  { count: 9,  low: false, offsetLeft: 0, offsetRight: 0 },
  8:  { count: 13, low: false, offsetLeft: 1, offsetRight: 1.5 },
  9:  { count: 10, low: false, offsetLeft: 0, offsetRight: 0 },
  10: { count: 21, low: true,  offsetLeft: 0, offsetRight: 0 },
  11: { count: 5,  low: true,  offsetLeft: 0.8, offsetRight: 0.5 },
  12: { count: 14, low: true,  offsetLeft: 1.5, offsetRight: 2 },
  13: { count: 16, low: true,  offsetLeft: 1, offsetRight: 2 },
  14: { count: 1,  low: false, offsetLeft: 0, offsetRight: 0 },
  15: { count: 1,  low: false, offsetLeft: 0, offsetRight: 0 },
  16: { count: 1,  low: false, offsetLeft: 0, offsetRight: 0.5 },
  17: { count: 1,  low: false, offsetLeft: 0, offsetRight: -1 },
  18: { count: 1,  low: false, offsetLeft: 4, offsetRight: -10 },
  19: { count: 1,  low: false, offsetLeft: 8.5, offsetRight: -9 },
  20: { count: 1,  low: false, offsetLeft: 8, offsetRight: -8 },
  21: { count: 1,  low: false, offsetLeft: 4.5, offsetRight: -5 },
  22: { count: 1,  low: false, offsetLeft: 4.5, offsetRight: -5 },
  23: { count: 1,  low: false, offsetLeft: 4.5, offsetRight: -5 },
  24: { count: 1,  low: false, offsetLeft: 4.5, offsetRight: -5 },
  25: { count: 1,  low: false, offsetLeft: 4.5, offsetRight: -5 },
  26: { count: 1,  low: false, offsetLeft: 8, offsetRight: -8 },
  27: { count: 1,  low: false, offsetLeft: 4, offsetRight: -8 },
  28: { count: 1,  low: false, offsetLeft: 4, offsetRight: -8 },
  29: { count: 1,  low: false, offsetLeft: 3, offsetRight: -7},
  30: { count: 1,  low: false, offsetLeft: 5, offsetRight: -7 },
  31: { count: 1,  low: false, offsetLeft: 3, offsetRight: -6 },
  32: { count: 1,  low: false, offsetLeft: 3.5, offsetRight: -6 },
  33: { count: 1,  low: false, offsetLeft: 3.5, offsetRight: -5 },
  34: { count: 1,  low: false, offsetLeft: 0, offsetRight: -5 },
  35: { count: 1,  low: false, offsetLeft: 0, offsetRight: -5 },
  36: { count: 1,  low: false, offsetLeft: 0, offsetRight: 0 },
}

const LEVEL_NAMES = [

  { name: "Gouin", metro: false },
  { name: "Henri-Bourassa", metro: true },
  { name: "Sauvé", metro: true },
  { name: "Crémazie", metro: true },
  { name: "Jarry", metro: true },
  { name: "Jean-Talon", metro: true },
  { name: "Beaubien", metro: true },
  { name: "Rosemont", metro: true },
  { name: "Laurier", metro: true },
  { name: "Mont-Royal", metro: true },
  { name: "Cherrier", metro: false },
  { name: "Sherbrooke", metro: true },
  { name: "Berri", metro: true },
  { name: "De la Commune", metro: false },
  { name: "St-Gabriel", metro: false },
  { name: "Youville", metro: false },
  { name: "McGill", metro: false },
  { name: "Mill", metro: false },

]

const PX_TO_UNIT = (13 * 1.5) / 256 
const LEVEL_EFFECTIVE_LENGTH = {
  1: 6144 * PX_TO_UNIT,
  2: 22932 * PX_TO_UNIT,
  3: 15348 * PX_TO_UNIT,
  4: 13360 * PX_TO_UNIT,
  5: 16380 * PX_TO_UNIT,
  6: 11564 * PX_TO_UNIT,
  7: 8364 * PX_TO_UNIT,
  8: 12920 * PX_TO_UNIT,
  9: 9380 * PX_TO_UNIT,
  10: 20484 * PX_TO_UNIT,
  11: 4570  * PX_TO_UNIT,
  12: 13400 * PX_TO_UNIT,
  13: 13218 * PX_TO_UNIT,
  14: 7616 * PX_TO_UNIT,
  15: 8192 * PX_TO_UNIT,
  16: 9000 * PX_TO_UNIT,
  17: 3060 * PX_TO_UNIT,
  18: 1760 * PX_TO_UNIT,
  19: 670 * PX_TO_UNIT,
  20: 5800 * PX_TO_UNIT,
  21: 5700 * PX_TO_UNIT,
  22: 5384 * PX_TO_UNIT,
  23: 8192 * PX_TO_UNIT,
  24: 15000 * PX_TO_UNIT,
  25: 4100 * PX_TO_UNIT,
  26: 1800 * PX_TO_UNIT,
  27: 16380 * PX_TO_UNIT,
  28: 2800 * PX_TO_UNIT,
  29: 2048 * PX_TO_UNIT,
  30: 1320 * PX_TO_UNIT,
  31: 2840 * PX_TO_UNIT,
  32: 15700 * PX_TO_UNIT,
  33: 4420 * PX_TO_UNIT,
  34: 1936 * PX_TO_UNIT,
  35: 5320 * PX_TO_UNIT,
  36: 640 * PX_TO_UNIT,
}


const intersections = [
  { offsetX: 0, id: "Henri-Bourassa sud", z: -309, width: 17, direction: -1 },
  { offsetX: 0, id: "Henri-Bourassa nord", z: -343, width: 25, direction: 1 },
  { offsetX: 0, id: "Prieur", z: -710, width: 16, direction: 1 },
  { offsetX: 0, id: "Fleury", z: -1180, width: 16, direction: 0 },
  { offsetX: 0, id: "Sauriol", z: -1678, width: 15, direction: 1 },
  { offsetX: 0, id: "Sauvé nord", z: -2110, width: 16, direction: 1 },
  { offsetX: 0, id: "Sauvé sud", z: -2135, width: 16, direction: -1 },
  { offsetX: 0, id: "Louvain", z: -2435, width: 14, direction: 0 },
  { offsetX: 0, id: "Chabanel", z: -2660, width: 8, direction: 0 },
  { offsetX: 0, id: "Legendre", z: -2940, width: 12, direction: 0 },
  { offsetX: 0, id: "Émile Journeault", z: -3165, width: 12, direction: 0 },
  { offsetX: 0, id: "Crémazie Nord", z: -3450, width: 40, direction: 1 },
  { offsetX: 0, id: "Crémazie Sud", z: -3530, width: 30, direction: -1 },
  { offsetX: 0, id: "Liège", z: -3760, width: 10, direction: 0 },
  { offsetX: 0, id: "Guizot", z: -4065, width: 14, direction: 0 },
  { offsetX: 0, id: "Jarry", z: -4360, width: 16, direction: 0 },
  { offsetX: 0, id: "Gounod", z: -4595, width: 16, direction: -1 },
  { offsetX: 0, id: "Villeray", z: -4960, width: 16, direction: 0 },
  { offsetX: 0, id: "Faillon", z: -5177, width: 16, direction: 0 },
  { offsetX: 0, id: "Castelnau", z: -5402, width: 16, direction: 0 },
  { offsetX: 0, id: "Jean-Talon", z: -5630, width: 20, direction: 0 },
  { offsetX: 0, id: "St-Zotique", z: -5850, width: 12, direction: 0 },
  { offsetX: 0, id: "Bélanger", z: -6175, width: 12, direction: 0 },
  { offsetX: 0, id: "Beaubien", z: -6475, width: 16, direction: 0 },
  { offsetX: 0, id: "Bellechasse", z: -6855, width: 18, direction: 1 },
  { offsetX: 0, id: "Rosemont", z: -7145, width: 20, direction: 0 },
  { offsetX: 0, id: "Carmel", z: -7572, width: 14, direction: 0 },
  { offsetX: 0, id: "Boucher", z: -7828, width: 12, direction: 1 },
  { offsetX: 0, id: "Laurier", z: -8115, width: 16, direction: 1 },
  { offsetX: 0, id: "St-Joseph", z: -8235, width: 24, direction: 0 },
  { offsetX: 0, id: "Villeneuve", z: -8470, width: 10, direction: 1 },
  { offsetX: 0, id: "Mont-Royal", z: -8802, width: 16, direction: 0 },
  { offsetX: 0, id: "Marie-Anne", z: -9085, width: 12, direction: -1 },
  { offsetX: 0, id: "Rachel", z: -9375, width: 20, direction: 1 },
  { offsetX: 0, id: "Roy", z: -10090, width: 16, direction: 0 },
  { offsetX: 0, id: "Cherrier", z: -10400, width: 46, direction: 1 },
  { offsetX: 0, id: "Berri", z: -10707, width: 46, direction: 0 },
{ offsetX: 0, id: "Ontario", z: -11260, width: 16, direction: 0 },
{ offsetX: 0, id: "Maisonneuve", z: -11583, width: 12, direction: 0 },
{ offsetX: 0, id: "Sainte-Catherine", z: -11738, width: 16, direction: 0 },
{ offsetX: 0, id: "René-Lévesque", z: -12083, width: 16, direction: 0 },
{ offsetX: 0, id: "de La Gauchetière", z: -12193, width: 16, direction: 0 },
{ offsetX: 0, id: "Viger", z: -12325, width: 23, direction: 0 },
{ offsetX: 0, id: "Saint-Antoine", z: -12394, width: 22, direction: 0 },
{ offsetX: 0, id: "Saint-Louis", z: -12488, width: 10, direction: -1 },
{ offsetX: 0, id: "Commune", z: -12758, width:44, direction: 1 },
{ offsetX: 62, id: "Bonneau", z: -12870, width: 10, direction: 1 },
{ offsetX: 62, id: "Bonsecours", z: -12991, width: 5, direction: 1 },
{ offsetX: 62, id: "Marché Bonsecours", z: -13153, width: 9, direction: 1 },
{ offsetX: 62, id: "Place Jacques-Cartier", z: -13212, width: 6, direction: 1 },
{ offsetX: 62, id: "Saint-Gabriel", z: -13362, width: 14, direction: 1 },
{ offsetX: 62, id: "Saint-Jean-Baptiste", z: -13407, width: 6, direction: 1 },
{ offsetX: 62, id: "Saint-Laurent", z: -13430, width: 14, direction: 1 },
{ offsetX: 62, id: "Saint-Sulpice", z: -13550, width: 12, direction: 1 },
{ offsetX: 62, id: "Place Royale", z: -13640, width: 15, direction: 1 },
{ offsetX: 62, id: "de Callière", z: -13732, width: 8, direction: 1 },
{ offsetX: 62, id: "du Port", z: -13817, width: 12, direction: 1 },
{ offsetX: 62, id: "Marguerite d'Youville", z: -14018, width: 10, direction: 1 },
{ offsetX: 62, id: "McGill", z: -14234, width: 14, direction: 1 },
{ offsetX: 62, id: "Soeurs-Grises", z: -14325, width: 12, direction: 1 },
{ offsetX: 62, id: "King", z: -14445, width: 8, direction: 1 },
{ offsetX: 62, id: "Queen", z: -14578, width: 8, direction: 1 },
{ offsetX: 0, id: "Mill", z: -14636, width: 44, direction: -1 },
{ offsetX: 0, id: "Tournant", z: -14900, width: 24, direction: 1 },
{ offsetX: 0, id: "SousBois", z: -15002, width: 18, direction: 1 },
{ offsetX: 0, id: "Bord", z: -15085, width: 20, direction: 1 },
{ offsetX: 0, id: "Canal1", z: -15555, width: 30, direction: 1 },
{ offsetX: 0, id: "Des Seigneurs", z: -16410, width: 26, direction: 0 },
{ offsetX: 0, id: "Passerelle Côte-Saint-Paul", z: -18452, width: 20, direction: 0 },
{ offsetX: 0, id: "Canal Lachine", z: -18608, width: 30, direction: 0 },
{ offsetX: 0, id: "Saint-Pierre", z: -20052, width: 25, direction: 0 },
{ offsetX: 0, id: "Passerelle Saint-Pierre", z: -20180, width: 14, direction: 0 },
{ offsetX: 0, id: "Passerelle Saint-Pierre2", z: -20312, width: 14, direction: 0 },
{ offsetX: 0, id: "Parc René-Lévesque", z: -22057, width: 14, direction: 0 },
{ offsetX: 0, id: "Parc René-Lévesque2", z: -22188, width:30, direction: 0 },
]

const DES_SEIGNEURS_INTERSECTION_Z =
  intersections.find(i => i.id === "Des Seigneurs")?.z ?? -Infinity

const cherrier = intersections.find(i => i.id === "Cherrier")
const berri = intersections.find(i => i.id === "Berri")
const commune = intersections.find(i => i.id === "Commune")
const stGabriel = intersections.find(i => i.id === "Saint-Gabriel")
const youville = intersections.find(i => i.id === "Marguerite d'Youville")
const mcGill = intersections.find(i => i.id === "McGill")
const mill = intersections.find(i => i.id === "Mill")
const tournant = intersections.find(i => i.id === "Tournant")
const sousBois = intersections.find(i => i.id === "SousBois")
const bord = intersections.find(i => i.id === "Bord")
const canal1 = intersections.find(i => i.id === "Canal1")
const passerelle = intersections.find(i => i.id === "Passerelle Côte-Saint-Paul")
const canalLachine = intersections.find(i => i.id === "Canal Lachine")
const stPierre = intersections.find(i => i.id === "Saint-Pierre")
const passerelleStPierre = intersections.find(i => i.id === "Passerelle Saint-Pierre")
const passerelleStPierre2 = intersections.find(i => i.id === "Passerelle Saint-Pierre2")
const parcReneLevesque = intersections.find(i => i.id === "Parc René-Lévesque")
const parcReneLevesque2 = intersections.find(i => i.id === "Parc René-Lévesque2")

function isVehicleTrafficIntersectionEnabled(intersection) {
  if (!intersection) {
    return false
  }

  if (TRAFFIC_DISABLED_INTERSECTIONS.has(intersection.id)) {
    return false
  }

  const afterMill =
    intersection.z < mill.z

  return !afterMill || intersection.id === "Des Seigneurs"
}

//================ TOURNANTS ===================================
const TOURNANTS = [
  {
    id: "Cherrier",
    level: 11,
    angle: Math.PI / 2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/Cherrier.jpg",
    yStart: 0.02,
    yEnd: 0.035,
    intersectionLength: 500,
    centerZ: cherrier.z,
    width: cherrier.width,
    offsetX: 14,
    offsetZ: 2,
    triggerOffsetZ: 0,
  },

  {
    id: "Berri",
    level: 12,
    angle: -Math.PI / 2,
    direction: "right",
    entrySide: "left",
    texture: "/textures/Berri.jpg",
    yStart: 0.025,
    yEnd: 0.04,
    intersectionLength: 1000,
    centerZ: berri.z,
    width: berri.width,
    offsetX: 27,
    offsetZ: -91.5,
    triggerOffsetZ: 32,
  },

  {
    id: "Commune",
    level: 14,
    angle: -Math.PI / 2,
    direction: "right",
    entrySide: "left",
    texture: "/textures/Commune.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 1000,
    centerZ: commune.z,
    width: commune.width,
    offsetX: 20,
    offsetZ: -66,
    triggerOffsetZ: 5,
  },
  {
    id: "Mill",
    level: 17,
    angle: Math.PI / 2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/Mill.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 1000,
    centerZ: mill.z,
    width: mill.width,
    offsetX: -2,
    offsetZ: 34,
    triggerOffsetZ: 25,
  },
  {
    id: "Tournant",
    level: 18,
    angle: Math.PI / 2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/Tournant.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 400,
    centerZ: tournant.z,
    width: tournant.width,
    previewOffsetX: -13,
    offsetX: -3,
    offsetZ: 12,
    triggerOffsetZ: 10,
  },
  {
    id: "SousBois",
    level: 19,
    angle: Math.PI / 2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/SousBois.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 500,
    centerZ: sousBois.z,
    width: sousBois.width,
    offsetX: -14,
    offsetZ: 48,
    triggerOffsetZ: 10,
  },
  {
    id: "Bord",
    level: 20,
    angle: Math.PI / 2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/SousBois.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 1000,
    centerZ: bord.z,
    width: bord.width,
    offsetX: -15,
    offsetZ: 15,
    triggerOffsetZ: 10,
  },
  {
    id: "Canal1",
    level: 21,
    angle: -Math.PI / 2,
    direction: "right",
    entrySide: "left",
    texture: "/textures/Canal1.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 1000,
    centerZ: canal1.z,
    width: canal1.width,
    offsetX: 14,
    offsetZ: -12,
    triggerOffsetZ: 15,
  },
  {
    id: "Passerelle Côte-Saint-Paul",
    level: 26,
    angle: -Math.PI / 2,
    direction: "right",
    entrySide: "left",
    texture: "/textures/SousBois.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 400,
    centerZ: passerelle.z,
    width: passerelle.width,
    offsetX:19,
    offsetZ: -33,
    triggerOffsetZ: 16,
  },
  {
    id: "Canal Lachine",
    level: 27,
    angle: Math.PI / 2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/Canal4.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 1000,
    centerZ: canalLachine.z,
    width: canalLachine.width,
    offsetX:-11,
    offsetZ: -12,
    triggerOffsetZ: 15,
  },
  {
    id: "Saint-Pierre",
    level: 29,
    angle: Math.PI / 2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/SousBois.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 400,
    centerZ: stPierre.z,
    width: stPierre.width,
    offsetX:-10,
    offsetZ: 8,
    triggerOffsetZ: 15,
  },
  {
    id: "Passerelle Saint-Pierre",
    level: 30,
    angle: Math.PI / 2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/SousBois.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 300,
    centerZ: passerelleStPierre.z,
    width: passerelleStPierre.width,
    offsetX:-12,
    offsetZ: 42,
    triggerOffsetZ: 15,
  },
  {
    id: "Passerelle Saint-Pierre2",
    level: 31,
    angle: -Math.PI/2,
    direction: "right",
    entrySide: "left",
    texture: "/textures/SousBois.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 500,
    centerZ: passerelleStPierre2.z,
    width: passerelleStPierre2.width,
    offsetX: 15,
    offsetZ: -25,
    triggerOffsetZ: 10,
  },
  {
    id: "Parc René-Lévesque",
    level: 34,
    angle: Math.PI/2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/SousBois.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 200,
    centerZ: parcReneLevesque.z,
    width: parcReneLevesque.width,
    offsetX: -7,
    offsetZ: 14,
    triggerOffsetZ: 15,
  },
   {
    id: "Parc René-Lévesque2",
    level: 35,
    angle: Math.PI/2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/Canal5.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 1000,
    centerZ: parcReneLevesque2.z,
    width: parcReneLevesque2.width,
    offsetX: -7,
    offsetZ: 22,
    triggerOffsetZ: 15,
  },
   {
    id: "Fin",
    level: 36,
    angle: Math.PI/2,
    direction: "left",
    entrySide: "right",
    texture: "/textures/Canal5.jpg",
    yStart: 0.01,
    yEnd: 0.04,
    intersectionLength: 200,
    centerZ: ARRIVE-20,
    width: parcReneLevesque2.width,
    offsetX: 0,
    offsetZ: 15,
    triggerOffsetZ: 0,
  },
  
]


const TURN_TREE_Z_RANGES = TOURNANTS.map(turn => {
  const length =
    turn.intersectionLength ??
    LEVEL_EFFECTIVE_LENGTH[turn.level] ??
    500

  return {
    turn,
    start: turn.centerZ,
    end: turn.centerZ - length
  }
})

function getTurnTreeRange(turn) {
  return TURN_TREE_Z_RANGES.find(r => r.turn.id === turn.id)
}



const roadSections = [
  {
    name: "Debut",
    level:1,
    start: Infinity,
    texture: "/textures/Debut.jpg",
    width: 44
  },
  {
    name: "St-Denis",
    level:4,
    start: CREMAZIE_Z,
    texture: "/textures/St-Denis.jpg",
    width: 46
  },
     {
    name: "Cherrier",
    level:11,
    start: cherrier.z,
    texture: "/textures/Cherrier.jpg",
    width: cherrier.width
  },
  {
    name: "Berri",
    level:12,
    start: berri.z,
    texture: "/textures/Berri.jpg",
    width: berri.width
  },
  {
    name: "Commune",
    level:14,
    start: commune.z,
    texture: "/textures/Commune.jpg",
    width: commune.width
  },
  {
    name: "St-Gabriel",
    level:14,
    start: stGabriel.z,
    texture: "/textures/St-Gabriel.jpg",
    width: 44
  },
  {
    name: "Youville",
    level:15,
    start: youville.z,
    texture: "/textures/Youville.jpg",
    width: 44
  },
  {
    name: "McGill",
    level:16,
    start: mcGill.z,
    texture: "/textures/McGill.jpg",
    width: 44
  },
  {
    name: "Mill",
    level:17,
    start: mill.z,
    texture: "/textures/Mill.jpg",
    width: 44
  },
  {
    name: "Tournant",
    level:18,
    texture: "/textures/Tournant.jpg",
    start: tournant.z,
    width: tournant.width
  },
  {
    name: "SousBois",
    level:19,
    texture: "/textures/SousBois.jpg",
    start : sousBois.z,
    width: sousBois.width,
  },
  {
    name: "Bord",
    level:20,
    texture: "/textures/SousBois.jpg",
    start : bord.z,
    width: bord.width,
  },
  {
    name: "Canal1",
    level:21,
    texture: "/textures/Canal1.jpg",
    start : canal1.z,
    width: canal1.width,
  },
  {
    name: "Canal2",
    level:22,
    texture: "/textures/Canal2.jpg",
    start : -16000,
    width: canal1.width,
  },
   {
    name: "Canal3",
    level:23,
    texture: "/textures/Canal5.jpg",
    start : -17000,
    width: canal1.width,
  },
   {
    name: "Canal4",
    level:24,
    texture: "/textures/Canal4.jpg",
    start : -17600,
    width: canal1.width,
  },
    {
    name: "Passerelle",
    level:26,
    texture: "/textures/SousBois.jpg",
    start : passerelle.z,
    width: passerelle.width,
  },
   {
    name: "Canal Lachine",
    level:27,
    texture: "/textures/Canal4.jpg",
    start : canalLachine.z,
    width: canalLachine.width,
  },
   {
    name: "Saint-Pierre",
    level:29,
    texture: "/textures/SousBois.jpg",
    start : stPierre.z,
    width: stPierre.width,
  },
    {
    name: "Canal 5",
    level:32,
    texture: "/textures/Canal5.jpg",
    start : -20500,
    width: 30,
  },

]

function getRoadSectionAtZ(playerZ) {
  let currentSection = roadSections[0]

  for (const section of roadSections) {
    if (playerZ <= section.start) {
      currentSection = section
    }
  }

  return currentSection
}

function getRoadLayout(totalWidth) {
  const routeW = totalWidth * 0.67
  const pisteW = totalWidth * 0.1534
  const trottoirW = totalWidth * 0.12
  const poteauxW = pisteW / 5.5
  const pisteX = 0
  const poteauxX = pisteX - pisteW / 2 - poteauxW / 2
  const routeX = pisteX - pisteW / 2 - poteauxW - routeW / 2
  const trottoirX = pisteX + pisteW / 2 + trottoirW / 2 + 0.7
  const trottoirLeftX = routeX - routeW / 2 - trottoirW / 2

  return {
    routeW,
    pisteW,
    trottoirW,
    poteauxW,
    pisteX,
    poteauxX,
    routeX,
    trottoirX,
    trottoirLeftX
  }
}

function getLevelWallBounds(totalWidth, level) {
  const layout = getRoadLayout(totalWidth)
  const levelInfo = LEVEL_TEXTURES[level] || LEVEL_TEXTURES[1]
  const leftWallX = layout.trottoirLeftX - (levelInfo.offsetLeft ?? 0)
  const rightWallX =
    layout.trottoirX +
    layout.trottoirW / 2 +
    (levelInfo.offsetRight ?? 0)

  return {
    ...layout,
    leftWallX,
    rightWallX,
    leftLimit:
      leftWallX +
      layout.trottoirW / 2 +
      WALL_CLEARANCE_LEFT +
      CYCLIST_WALL_PADDING,
    rightLimit:
      rightWallX -
      WALL_CLEARANCE_RIGHT -
      CYCLIST_WALL_PADDING
  }
}

function getTurnWallLayout(turn) {
  if (!turn) {
    return null
  }

  const leftOffset = turn.leftWallOffset ?? 0
  const rightOffset = turn.rightWallOffset ?? 0
  const leftX = -turn.width - turn.offsetZ - leftOffset
  const rightX = -turn.offsetZ + rightOffset

  return {
    leftX,
    rightX,
    leftOffset,
    rightOffset,
    width: rightX - leftX,
    centerX: (leftX + rightX) / 2
  }
}

const arbres = [
{ offsetX: 0, level: 1, z: -5, cote: "droit" },
{ offsetX: 0, level: 1, z: -15, cote: "gauche" },
{ offsetX: 0, level: 1, z: -30, cote: "droit" },
{ offsetX: 0, level: 1, z: -37, cote: "gauche" },
{ offsetX: 0, level: 1, z: -50, cote: "gauche" },
{ offsetX: 0, level: 1, z: -60, cote: "droit" },
{ offsetX: 0, level: 1, z: -80, cote: "droit" },
{ offsetX: 0, level: 1, z: -80, cote: "gauche" },
{ offsetX: 0, level: 1, z: -90, cote: "gauche" },
{ offsetX: 0, level: 1, z: -100, cote: "droit" },
{ offsetX: 0, level: 1, z: -120, cote: "droit" },
{ offsetX: 0, level: 1, z: -130, cote: "gauche" },
{ offsetX: 0, level: 1, z: -140, cote: "droit" },
{ offsetX: 0, level: 1, z: -150, cote: "droit" },
{ offsetX: 0, level: 1, z: -160, cote: "gauche" },
{ offsetX: 0, level: 1, z: -170, cote: "gauche" },
{ offsetX: 0, level: 1, z: -180, cote: "droit" },
{ offsetX: 0, level: 1, z: -190, cote: "gauche" },
{ offsetX: 0, level: 1, z: -200, cote: "droit" },
{ offsetX: 0, level: 1, z: -210, cote: "droit" },
{ offsetX: 0, level: 1, z: -215, cote: "gauche" },
{ offsetX: 0, level: 1, z: -220, cote: "gauche" },
{ offsetX: 0, level: 1, z: -225, cote: "droit" },
{ offsetX: 0, level: 1, z: -230, cote: "droit" },
{ offsetX: 0, level: 1, z: -235, cote: "gauche" },
{ offsetX: 0, level: 1, z: -240, cote: "gauche" },
{ offsetX: 0, level: 1, z: -245, cote: "droit" },
{ offsetX: 0, level: 1, z: -402, cote: "droit" },
{ offsetX: 0, level: 1, z: -406, cote: "gauche" },
{ offsetX: 0, level: 1, z: -467, cote: "gauche" },
{ offsetX: 0, level: 1, z: -455, cote: "droit" },
{ offsetX: 0, level: 1, z: -524, cote: "droit" },
{ offsetX: 0, level: 1, z: -528, cote: "gauche" },
{ offsetX: 0, level: 1, z: -590, cote: "gauche" },
{ offsetX: 0, level: 1, z: -586, cote: "droit" },
{ offsetX: 0, level: 1, z: -647, cote: "droit" },
{ offsetX: 0, level: 1, z: -651, cote: "gauche" },
{ offsetX: 0, level: 2, z: -790, cote: "gauche" },
{ offsetX: 0, level: 2, z: -786, cote: "droit" },
{ offsetX: 0, level: 2, z: -865, cote: "droit" },
{ offsetX: 0, level: 2, z: -869, cote: "gauche" },
{ offsetX: 0, level: 2, z: -947, cote: "gauche" },
{ offsetX: 0, level: 2, z: -943, cote: "droit" },
{ offsetX: 0, level: 2, z: -1021, cote: "droit" },
{ offsetX: 0, level: 2, z: -1010, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1085, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1124, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1100, cote: "droit" },
{ offsetX: 0, level: 2, z: -1261, cote: "droit" },
{ offsetX: 0, level: 2, z: -1265, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1348, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1344, cote: "droit" },
{ offsetX: 0, level: 2, z: -1427, cote: "droit" },
{ offsetX: 0, level: 2, z: -1431, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1514, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1510, cote: "droit" },
{ offsetX: 0, level: 2, z: -1593, cote: "droit" },
{ offsetX: 0, level: 2, z: -1597, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1752, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1748, cote: "droit" },
{ offsetX: 0, level: 2, z: -1820, cote: "droit" },
{ offsetX: 0, level: 2, z: -1824, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1896, cote: "gauche" },
{ offsetX: 0, level: 2, z: -1892, cote: "droit" },
{ offsetX: 0, level: 2, z: -1964, cote: "droit" },
{ offsetX: 0, level: 2, z: -1968, cote: "gauche" },
{ offsetX: 0, level: 2, z: -2040, cote: "gauche" },
{ offsetX: 0, level: 2, z: -2036, cote: "droit" },
{ offsetX: 0, level: 2, z: -2187, cote: "gauche" },
{ offsetX: 0, level: 2, z: -2175, cote: "droit" },
{ offsetX: 0, level: 2, z: -2233, cote: "droit" },
{ offsetX: 0, level: 2, z: -2237, cote: "gauche" },
{ offsetX: 0, level: 2, z: -2287, cote: "gauche" },
{ offsetX: 0, level: 2, z: -2283, cote: "droit" },
{ offsetX: 0, level: 2, z: -2333, cote: "droit" },
{ offsetX: 0, level: 2, z: -2337, cote: "gauche" },
{ offsetX: 0, level: 2, z: -2387, cote: "gauche" },
{ offsetX: 0, level: 2, z: -2383, cote: "droit" },
{ offsetX: 0, level: 2, z: -2470, cote: "droit" },
{ offsetX: 0, level: 2, z: -2474, cote: "gauche" },
{ offsetX: 0, level: 2, z: -2512, cote: "gauche" },
{ offsetX: 0, level: 2, z: -2508, cote: "droit" },
{ offsetX: 0, level: 3, z: -2545, cote: "droit" },
{ offsetX: 0, level: 3, z: -2549, cote: "gauche" },
{ offsetX: 0, level: 3, z: -2587, cote: "gauche" },
{ offsetX: 0, level: 3, z: -2583, cote: "droit" },
{ offsetX: 0, level: 3, z: -2620, cote: "droit" },
{ offsetX: 0, level: 3, z: -2624, cote: "gauche" },
{ offsetX: 0, level: 3, z: -2709, cote: "gauche" },
{ offsetX: 0, level: 3, z: -2705, cote: "droit" },
{ offsetX: 0, level: 3, z: -2751, cote: "droit" },
{ offsetX: 0, level: 3, z: -2755, cote: "gauche" },
{ offsetX: 0, level: 3, z: -2802, cote: "gauche" },
{ offsetX: 0, level: 3, z: -2798, cote: "droit" },
{ offsetX: 0, level: 3, z: -2845, cote: "droit" },
{ offsetX: 0, level: 3, z: -2860, cote: "gauche" },
{ offsetX: 0, level: 3, z: -2895, cote: "gauche" },
{ offsetX: 0, level: 3, z: -2891, cote: "droit" },
{ offsetX: 0, level: 3, z: -2975, cote: "droit" },
{ offsetX: 0, level: 3, z: -2979, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3017, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3013, cote: "droit" },
{ offsetX: 0, level: 3, z: -3050, cote: "droit" },
{ offsetX: 0, level: 3, z: -3054, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3092, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3088, cote: "droit" },
{ offsetX: 0, level: 3, z: -3125, cote: "droit" },
{ offsetX: 0, level: 3, z: -3129, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3214, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3210, cote: "droit" },
{ offsetX: 0, level: 3, z: -3258, cote: "droit" },
{ offsetX: 0, level: 3, z: -3262, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3309, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3305, cote: "droit" },
{ offsetX: 0, level: 3, z: -3353, cote: "droit" },
{ offsetX: 0, level: 3, z: -3357, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3404, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3570, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3566, cote: "droit" },
{ offsetX: 0, level: 3, z: -3605, cote: "droit" },
{ offsetX: 0, level: 3, z: -3609, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3647, cote: "gauche" },
{ offsetX: 0, level: 3, z: -3643, cote: "droit" },
{ offsetX: 0, level: 3, z: -3681, cote: "droit" },
{ offsetX: 0, level: 3, z: -3685, cote: "gauche" },
{ offsetX: 0, level: 4, z: -3724, cote: "gauche" },
{ offsetX: 0, level: 4, z: -3720, cote: "droit" },
{ offsetX: 0, level: 4, z: -3809, cote: "droit" },
{ offsetX: 0, level: 4, z: -3813, cote: "gauche" },
{ offsetX: 0, level: 4, z: -3864, cote: "gauche" },
{ offsetX: 0, level: 4, z: -3860, cote: "droit" },
{ offsetX: 0, level: 4, z: -3910, cote: "droit" },
{ offsetX: 0, level: 4, z: -3914, cote: "gauche" },
{ offsetX: 0, level: 4, z: -3965, cote: "gauche" },
{ offsetX: 0, level: 4, z: -3961, cote: "droit" },
{ offsetX: 0, level: 4, z: -4012, cote: "droit" },
{ offsetX: 0, level: 4, z: -4016, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4116, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4112, cote: "droit" },
{ offsetX: 0, level: 4, z: -4161, cote: "droit" },
{ offsetX: 0, level: 4, z: -4165, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4214, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4210, cote: "droit" },
{ offsetX: 0, level: 4, z: -4260, cote: "droit" },
{ offsetX: 0, level: 4, z: -4264, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4313, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4309, cote: "droit" },
{ offsetX: 0, level: 4, z: -4397, cote: "droit" },
{ offsetX: 0, level: 4, z: -4401, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4440, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4436, cote: "droit" },
{ offsetX: 0, level: 4, z: -4475, cote: "droit" },
{ offsetX: 0, level: 4, z: -4479, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4519, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4515, cote: "droit" },
{ offsetX: 0, level: 4, z: -4554, cote: "droit" },
{ offsetX: 0, level: 4, z: -4558, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4658, cote: "gauche" },
{ offsetX: 0, level: 4, z: -4654, cote: "droit" },
{ offsetX: 0, level: 5, z: -4715, cote: "droit" },
{ offsetX: 0, level: 5, z: -4719, cote: "gauche" },
{ offsetX: 0, level: 5, z: -4779, cote: "gauche" },
{ offsetX: 0, level: 5, z: -4775, cote: "droit" },
{ offsetX: 0, level: 5, z: -4836, cote: "droit" },
{ offsetX: 0, level: 5, z: -4840, cote: "gauche" },
{ offsetX: 0, level: 5, z: -4901, cote: "gauche" },
{ offsetX: 0, level: 5, z: -4897, cote: "droit" },
{ offsetX: 0, level: 5, z: -4920, cote: "gauche" },
{ offsetX: 0, level: 5, z: -4994, cote: "droit" },
{ offsetX: 0, level: 5, z: -5034, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5030, cote: "droit" },
{ offsetX: 0, level: 5, z: -5066, cote: "droit" },
{ offsetX: 0, level: 5, z: -5070, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5107, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5103, cote: "droit" },
{ offsetX: 0, level: 5, z: -5139, cote: "droit" },
{ offsetX: 0, level: 5, z: -5143, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5216, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5212, cote: "droit" },
{ offsetX: 0, level: 5, z: -5250, cote: "droit" },
{ offsetX: 0, level: 5, z: -5254, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5291, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5287, cote: "droit" },
{ offsetX: 0, level: 5, z: -5325, cote: "droit" },
{ offsetX: 0, level: 5, z: -5329, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5366, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5362, cote: "droit" },
{ offsetX: 0, level: 5, z: -5438, cote: "droit" },
{ offsetX: 0, level: 5, z: -5442, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5480, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5476, cote: "droit" },
{ offsetX: 0, level: 5, z: -5514, cote: "droit" },
{ offsetX: 0, level: 5, z: -5518, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5556, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5552, cote: "droit" },
{ offsetX: 0, level: 5, z: -5590, cote: "droit" },
{ offsetX: 0, level: 5, z: -5594, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5669, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5665, cote: "droit" },
{ offsetX: 0, level: 5, z: -5701, cote: "droit" },
{ offsetX: 0, level: 5, z: -5705, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5742, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5738, cote: "droit" },
{ offsetX: 0, level: 5, z: -5775, cote: "droit" },
{ offsetX: 0, level: 5, z: -5779, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5815, cote: "gauche" },
{ offsetX: 0, level: 5, z: -5811, cote: "droit" },
{ offsetX: 0, level: 5, z: -5902, cote: "droit" },
{ offsetX: 0, level: 5, z: -5906, cote: "gauche" },
{ offsetX: 0, level: 6, z: -5960, cote: "gauche" },
{ offsetX: 0, level: 6, z: -5956, cote: "droit" },
{ offsetX: 0, level: 6, z: -6010, cote: "droit" },
{ offsetX: 0, level: 6, z: -6014, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6069, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6065, cote: "droit" },
{ offsetX: 0, level: 6, z: -6119, cote: "droit" },
{ offsetX: 0, level: 6, z: -6123, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6227, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6223, cote: "droit" },
{ offsetX: 0, level: 6, z: -6273, cote: "droit" },
{ offsetX: 0, level: 6, z: -6277, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6327, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6323, cote: "droit" },
{ offsetX: 0, level: 6, z: -6373, cote: "droit" },
{ offsetX: 0, level: 6, z: -6377, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6427, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6423, cote: "droit" },
{ offsetX: 0, level: 6, z: -6536, cote: "droit" },
{ offsetX: 0, level: 6, z: -6540, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6604, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6600, cote: "droit" },
{ offsetX: 0, level: 6, z: -6663, cote: "droit" },
{ offsetX: 0, level: 6, z: -6667, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6730, cote: "gauche" },
{ offsetX: 0, level: 6, z: -6726, cote: "droit" },
{ offsetX: 0, level: 6, z: -6790, cote: "droit" },
{ offsetX: 0, level: 6, z: -6794, cote: "gauche" },
{ offsetX: 0, level: 7, z: -6905, cote: "gauche" },
{ offsetX: 0, level: 7, z: -6901, cote: "droit" },
{ offsetX: 0, level: 7, z: -6950, cote: "droit" },
{ offsetX: 0, level: 7, z: -6954, cote: "gauche" },
{ offsetX: 0, level: 7, z: -7002, cote: "gauche" },
{ offsetX: 0, level: 7, z: -6998, cote: "droit" },
{ offsetX: 0, level: 7, z: -7046, cote: "droit" },
{ offsetX: 0, level: 7, z: -7050, cote: "gauche" },
{ offsetX: 0, level: 7, z: -7099, cote: "gauche" },
{ offsetX: 0, level: 7, z: -7095, cote: "droit" },
{ offsetX: 0, level: 7, z: -7214, cote: "droit" },
{ offsetX: 0, level: 7, z: -7218, cote: "gauche" },
{ offsetX: 0, level: 7, z: -7289, cote: "gauche" },
{ offsetX: 0, level: 7, z: -7285, cote: "droit" },
{ offsetX: 0, level: 7, z: -7356, cote: "droit" },
{ offsetX: 0, level: 7, z: -7360, cote: "gauche" },
{ offsetX: 0, level: 7, z: -7432, cote: "gauche" },
{ offsetX: 0, level: 7, z: -7428, cote: "droit" },
{ offsetX: 0, level: 8, z: -7499, cote: "droit" },
{ offsetX: 0, level: 8, z: -7503, cote: "gauche" },
{ offsetX: 0, level: 8, z: -7617, cote: "gauche" },
{ offsetX: 0, level: 8, z: -7613, cote: "droit" },
{ offsetX: 0, level: 8, z: -7655, cote: "droit" },
{ offsetX: 0, level: 8, z: -7659, cote: "gauche" },
{ offsetX: 0, level: 8, z: -7702, cote: "gauche" },
{ offsetX: 0, level: 8, z: -7698, cote: "droit" },
{ offsetX: 0, level: 8, z: -7741, cote: "droit" },
{ offsetX: 0, level: 8, z: -7745, cote: "gauche" },
{ offsetX: 0, level: 8, z: -7787, cote: "gauche" },
{ offsetX: 0, level: 8, z: -7783, cote: "droit" },
{ offsetX: 0, level: 8, z: -7874, cote: "droit" },
{ offsetX: 0, level: 8, z: -7878, cote: "gauche" },
{ offsetX: 0, level: 8, z: -7926, cote: "gauche" },
{ offsetX: 0, level: 8, z: -7922, cote: "droit" },
{ offsetX: 0, level: 8, z: -7969, cote: "droit" },
{ offsetX: 0, level: 8, z: -7973, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8021, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8017, cote: "droit" },
{ offsetX: 0, level: 8, z: -8065, cote: "droit" },
{ offsetX: 0, level: 8, z: -8069, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8137, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8133, cote: "droit" },
{ offsetX: 0, level: 8, z: -8153, cote: "droit" },
{ offsetX: 0, level: 8, z: -8157, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8177, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8173, cote: "droit" },
{ offsetX: 0, level: 8, z: -8193, cote: "droit" },
{ offsetX: 0, level: 8, z: -8197, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8217, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8213, cote: "droit" },
{ offsetX: 0, level: 8, z: -8272, cote: "droit" },
{ offsetX: 0, level: 8, z: -8276, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8315, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8311, cote: "droit" },
{ offsetX: 0, level: 8, z: -8350, cote: "droit" },
{ offsetX: 0, level: 8, z: -8354, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8394, cote: "gauche" },
{ offsetX: 0, level: 8, z: -8390, cote: "droit" },
{ offsetX: 0, level: 8, z: -8429, cote: "droit" },
{ offsetX: 0, level: 8, z: -8433, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8527, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8523, cote: "droit" },
{ offsetX: 0, level: 9, z: -8579, cote: "droit" },
{ offsetX: 0, level: 9, z: -8583, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8638, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8634, cote: "droit" },
{ offsetX: 0, level: 9, z: -8689, cote: "droit" },
{ offsetX: 0, level: 9, z: -8693, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8749, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8745, cote: "droit" },
{ offsetX: 0, level: 9, z: -8847, cote: "droit" },
{ offsetX: 0, level: 9, z: -8851, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8898, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8894, cote: "droit" },
{ offsetX: 0, level: 9, z: -8941, cote: "droit" },
{ offsetX: 0, level: 9, z: -8945, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8993, cote: "gauche" },
{ offsetX: 0, level: 9, z: -8989, cote: "droit" },
{ offsetX: 0, level: 9, z: -9036, cote: "droit" },
{ offsetX: 0, level: 9, z: -9040, cote: "gauche" },
{ offsetX: 0, level: 9, z: -9135, cote: "gauche" },
{ offsetX: 0, level: 9, z: -9131, cote: "droit" },
{ offsetX: 0, level: 10, z: -9180, cote: "droit" },
{ offsetX: 0, level: 10, z: -9184, cote: "gauche" },
{ offsetX: 0, level: 10, z: -9232, cote: "gauche" },
{ offsetX: 0, level: 10, z: -9228, cote: "droit" },
{ offsetX: 0, level: 10, z: -9276, cote: "droit" },
{ offsetX: 0, level: 10, z: -9280, cote: "gauche" },
{ offsetX: 0, level: 10, z: -9329, cote: "gauche" },
{ offsetX: 0, level: 10, z: -9325, cote: "droit" },
{ offsetX: 0, level: 10, z: -9492, cote: "droit" },
{ offsetX: 0, level: 10, z: -9496, cote: "gauche" },
{ offsetX: 0, level: 10, z: -9615, cote: "gauche" },
{ offsetX: 0, level: 10, z: -9611, cote: "droit" },
{ offsetX: 0, level: 10, z: -9730, cote: "droit" },
{ offsetX: 0, level: 10, z: -9734, cote: "gauche" },
{ offsetX: 0, level: 10, z: -9854, cote: "gauche" },
{ offsetX: 0, level: 10, z: -9850, cote: "droit" },
{ offsetX: 0, level: 10, z: -9969, cote: "droit" },
{ offsetX: 0, level: 10, z: -9973, cote: "gauche" },
{ offsetX: 0, level: 10, z: -10145, cote: "gauche" },
{ offsetX: 0, level: 10, z: -10141, cote: "droit" },
{ offsetX: 0, level: 10, z: -10195, cote: "droit" },
{ offsetX: 0, level: 10, z: -10199, cote: "gauche" },
{ offsetX: 0, level: 10, z: -10252, cote: "gauche" },
{ offsetX: 0, level: 10, z: -10248, cote: "droit" },
{ offsetX: 0, level: 10, z: -10301, cote: "droit" },
{ offsetX: 0, level: 10, z: -10305, cote: "gauche" },
{ offsetX: 0, level: 10, z: -10359, cote: "gauche" },
{ offsetX: 0, level: 10, z: -10355, cote: "droit" },
{ offsetX: 1.5, level: 10, z: -10496, cote: "droit" },
{ offsetX: 0, level: 10, z: -10500, cote: "gauche" },
{ offsetX: 1.5, level: 11, z: -10515, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10530, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10545, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10560, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10575, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10590, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10605, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10620, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10635, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10650, cote: "droit" },
{ offsetX: 1.5, level: 11, z: -10675, cote: "droit" },


{ offsetX: 17, level: 11, z: -10500, cote: "gauche" },
{ offsetX: 17, level: 11, z: -10520, cote: "gauche" },
{ offsetX: 17, level: 11, z: -10540, cote: "gauche" },
{ offsetX: 17, level: 11, z: -10560, cote: "gauche" },
{ offsetX: 17, level: 11, z: -10580, cote: "gauche" },
{ offsetX: 17, level: 11, z: -10600, cote: "gauche" },
{ offsetX: 17, level: 11, z: -10620, cote: "gauche" },
{ offsetX: 17, level: 11, z: -10640, cote: "gauche" },
{ offsetX: 17, level: 11, z: -10660, cote: "gauche" },
{ offsetX: 17, level: 11, z: -10680, cote: "gauche" },


{ offsetX: 0, level: 10, z: -10544, cote: "gauche" },
{ offsetX: 0, level: 10, z: -10589, cote: "gauche" },

{ offsetX: 0, level: 12, z: -11244, cote: "droit" },
{ offsetX: 0, level: 12, z: -11248, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11301, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11297, cote: "droit" },
{ offsetX: 0, level: 12, z: -11350, cote: "droit" },
{ offsetX: 0, level: 12, z: -11354, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11408, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11404, cote: "droit" },
{ offsetX: 0, level: 12, z: -11457, cote: "droit" },
{ offsetX: 0, level: 12, z: -11461, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11539, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11535, cote: "droit" },
{ offsetX: 0, level: 12, z: -11561, cote: "droit" },
{ offsetX: 0, level: 12, z: -11565, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11590, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11586, cote: "droit" },
{ offsetX: 0, level: 12, z: -11612, cote: "droit" },
{ offsetX: 0, level: 12, z: -11616, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11641, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11637, cote: "droit" },
{ offsetX: 0, level: 12, z: -11721, cote: "droit" },
{ offsetX: 0, level: 12, z: -11725, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11783, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11779, cote: "droit" },
{ offsetX: 0, level: 12, z: -11836, cote: "droit" },
{ offsetX: 0, level: 12, z: -11840, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11898, cote: "gauche" },
{ offsetX: 0, level: 12, z: -11894, cote: "droit" },
{ offsetX: 0, level: 12, z: -11952, cote: "droit" },
{ offsetX: 0, level: 12, z: -11956, cote: "gauche" },
{ offsetX: 0, level: 12, z: -12033, cote: "gauche" },
{ offsetX: 0, level: 12, z: -12029, cote: "droit" },
{ offsetX: 0, level: 12, z: -12048, cote: "droit" },
{ offsetX: 0, level: 12, z: -12052, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12108, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12104, cote: "droit" },
{ offsetX: 0, level: 13, z: -12145, cote: "droit" },
{ offsetX: 0, level: 13, z: -12149, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12171, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12167, cote: "droit" },
{ offsetX: 0, level: 13, z: -12215, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12211, cote: "droit" },
{ offsetX: 0, level: 13, z: -12233, cote: "droit" },
{ offsetX: 0, level: 13, z: -12237, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12270, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12278, cote: "droit" },
{ offsetX: 0, level: 13, z: -12301, cote: "droit" },
{ offsetX: 0, level: 13, z: -12305, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12312, cote: "droit" },
{ offsetX: 0, level: 13, z: -12340, cote: "droit" },
{ offsetX: 0, level: 13, z: -12355, cote: "droit" },
{ offsetX: 0, level: 13, z: -12371, cote: "droit" },
{ offsetX: 0, level: 13, z: -12496, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12644, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12660, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12680, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12700, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12720, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12793, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12810, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12820, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12840, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12860, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12885, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12904, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12900, cote: "droit" },
{ offsetX: 0, level: 13, z: -12918, cote: "droit" },
{ offsetX: 0, level: 13, z: -12922, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12940, cote: "gauche" },
{ offsetX: 0, level: 13, z: -12936, cote: "droit" },
{ offsetX: 0, level: 13, z: -12959, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -12997, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13017, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13037, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13058, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13100, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13121, cote: "droit" },
{ offsetX: 0, level: 13, z: -13125, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13152, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13148, cote: "droit" },
{ offsetX: 0, level: 13, z: -13175, cote: "droit" },
{ offsetX: 0, level: 13, z: -13179, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13206, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13202, cote: "droit" },
{ offsetX: 0, level: 13, z: -13229, cote: "droit" },

{ offsetX: 0, level: 13, z: -13270, cote: "gauche", plan: false },
{ offsetX: 0, level: 13, z: -13289, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13305, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13320, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13330, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13340, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13350, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13360, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13375, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13390, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13400, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13415, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13427, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13440, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13450, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13460, cote: "gauche", plan: false },
{ offsetX: 0, level: 14, z: -13470, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13485, cote: "gauche", plan: false },

// ========================== CENTRE =================/


{ offsetX: 13, level: 13, z: -12790, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12810, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12830, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12850, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12870, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12890, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12910, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12930, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12950, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12960, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12970, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12980, cote: "gauche" },
{ offsetX: 13, level: 13, z: -12990, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13000, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13010, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13020, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13030, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13058, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13090, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13100, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13110, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13120, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13133, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13145, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13160, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13175, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13190, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13205, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13215, cote: "gauche" },

{ offsetX: 13, level: 13, z: -13266, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13276, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13289, cote: "gauche" },
{ offsetX: 13, level: 13, z: -13300, cote: "gauche" },
{ offsetX: 13, level: 14, z: -13310, cote: "gauche" },
{ offsetX: 13, level: 14, z: -13320, cote: "gauche" },
{ offsetX: 13, level: 14, z: -13355, cote: "gauche" },

{ offsetX: 0, level: 15, z: -13524, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13535, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13548, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13560, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13571, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13584, cote: "gauche", plan: false },

{ offsetX: 0, level: 15, z: -13595, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13605, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13620, cote: "gauche", plan: false },


{ offsetX: 0, level: 15, z: -13633, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13644, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13655, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13669, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13682, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13692, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13704, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13718, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13731, cote: "gauche", plan: false },

{ offsetX: 0, level: 15, z: -13778, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13791, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13803, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13815, cote: "gauche", plan: false },

{ offsetX: 0, level: 15, z: -13828, cote: "gauche", plan: false },

{ offsetX: 0, level: 15, z: -13840, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13850, cote: "gauche", plan: false },

{ offsetX: 0, level: 15, z: -13860, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13870, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13889, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13903, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13916, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13927, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13939, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13952, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -13963, cote: "gauche", plan: false },

{ offsetX: 0, level: 15, z: -14014, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14025, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14039, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14052, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14063, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14101, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14115, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14127, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14153, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14165, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14179, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14192, cote: "gauche", plan: false },
{ offsetX: 0, level: 15, z: -14203, cote: "gauche", plan: false },

{ offsetX: 5, level: 15, z: -14269, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14282, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14294, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14306, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14318, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14345, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14358, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14371, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14383, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14395, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14408, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14447, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14456, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14467, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14477, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14485, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14495, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14505, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14515, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14525, cote: "gauche", plan: false },

{ offsetX: 5, level: 15, z: -14540, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14550, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14565, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14580, cote: "gauche", plan: false },
{ offsetX: 5, level: 15, z: -14595, cote: "gauche", plan: false },

{ offsetX: 16, level: 14, z: -13369, cote: "gauche" },
{ offsetX: 16, level: 14, z: -13380, cote: "gauche" },
{ offsetX: 16, level: 14, z: -13390, cote: "gauche" },
{ offsetX: 15, level: 14, z: -13400, cote: "gauche" },
{ offsetX: 16, level: 14, z: -13419, cote: "gauche" },
{ offsetX: 16, level: 14, z: -13430, cote: "gauche" },
{ offsetX: 16, level: 14, z: -13445, cote: "gauche" },
{ offsetX: 16, level: 14, z: -13455, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13470, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13488, cote: "gauche" },


{ offsetX: 15, level: 15, z: -13528, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13539, cote: "gauche" },
{ offsetX: 16, level: 15, z: -13552, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13563, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13574, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13613, cote: "gauche" },
{ offsetX: 16, level: 15, z: -13624, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13636, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13649, cote: "gauche" },
{ offsetX: 16, level: 15, z: -13662, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13675, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13687, cote: "gauche" },
{ offsetX: 16, level: 15, z: -13701, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13715, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13726, cote: "gauche" },
{ offsetX: 16, level: 15, z: -13777, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13789, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13802, cote: "gauche" },
{ offsetX: 16, level: 15, z: -13814, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13828, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13879, cote: "gauche" },
{ offsetX: 16, level: 15, z: -13892, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13905, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13919, cote: "gauche" },
{ offsetX: 16, level: 15, z: -13932, cote: "gauche" },
{ offsetX: 15, level: 15, z: -13944, cote: "gauche" },
{ offsetX: 17, level: 15, z: -13957, cote: "gauche" },
{ offsetX: 16, level: 15, z: -13970, cote: "gauche" },

{ offsetX: 16, level: 15, z: -14011, cote: "gauche" },
{ offsetX: 15, level: 15, z: -14023, cote: "gauche" },
{ offsetX: 17, level: 15, z: -14037, cote: "gauche" },
{ offsetX: 16, level: 15, z: -14049, cote: "gauche" },
{ offsetX: 15, level: 15, z: -14063, cote: "gauche" },
{ offsetX: 15, level: 15, z: -14102, cote: "gauche" },
{ offsetX: 16, level: 15, z: -14114, cote: "gauche" },
{ offsetX: 15.5, level: 15, z: -14150, cote: "gauche" },
{ offsetX: 15.5, level: 15, z: -14204, cote: "gauche" },




{ offsetX: 10, level: 21, z: -15590, cote: "gauche", plan: false },
{ offsetX: 9, level: 21, z: -15610, cote: "gauche", plan: false },
{ offsetX: 8, level: 21, z: -15630, cote: "gauche", plan: false },
{ offsetX: 9, level: 21, z: -15650, cote: "gauche", plan: false },
{ offsetX: 10, level: 21, z: -15670, cote: "gauche", plan: false },
{ offsetX: 7, level: 21, z: -15690, cote: "gauche", plan: false },
{ offsetX: 9, level: 21, z: -15710, cote: "gauche", plan: false },
{ offsetX: 10, level: 21, z: -15730, cote: "gauche", plan: false },
{ offsetX: 8, level: 21, z: -15750, cote: "gauche", plan: false },
{ offsetX: 9, level: 21, z: -15770, cote: "gauche", plan: false },
{ offsetX: 10, level: 21, z: -15790, cote: "gauche", plan: false },
{ offsetX: 9, level: 21, z: -15800, cote: "gauche", plan: false },
{ offsetX: 10, level: 21, z: -15815, cote: "gauche", plan: false },
{ offsetX: 9, level: 21, z: -15830, cote: "gauche", plan: false },
{ offsetX: 8, level: 21, z: -15845, cote: "gauche", plan: false },
{ offsetX: 9, level: 21, z: -16060, cote: "gauche", plan: false },
{ offsetX: 10, level: 21, z: -16075, cote: "gauche", plan: false },
{ offsetX: 7, level: 21, z: -16090, cote: "gauche", plan: false },
{ offsetX: 9, level: 21, z: -16115, cote: "gauche", plan: false },
{ offsetX: 10, level: 21, z: -16130, cote: "gauche" , plan: false },
{ offsetX: 8, level: 21, z: -16150, cote: "gauche" , plan: false },
{ offsetX: 9, level: 21, z: -16170, cote: "gauche" , plan: false },
{ offsetX: 10, level: 21, z: -16190, cote: "gauche", plan: false },
{ offsetX: 9, level: 21, z: -16200, cote: "gauche", plan: false },

{ offsetX: -10, level: 21, z: -15590, cote: "droite", plan: false },
{ offsetX: -12, level: 21, z: -15610, cote: "droite", plan: false },
{ offsetX: -13, level: 21, z: -15630, cote: "droite", plan: false },
{ offsetX: -12, level: 21, z: -15650, cote: "droite", plan: false },
{ offsetX: -14, level: 21, z: -15670, cote: "droite" , plan: false },
{ offsetX: -16, level: 21, z: -15690, cote: "droite" , plan: false },
{ offsetX: -15, level: 21, z: -15710, cote: "droite", plan: false },
{ offsetX: -13, level: 21, z: -15730, cote: "droite", plan: false },
{ offsetX: -15, level: 21, z: -15750, cote: "droite", plan: false },
{ offsetX: -12, level: 21, z: -15760, cote: "droite", plan: false },
{ offsetX: -11, level: 21, z: -15775, cote: "droite", plan: false },
{ offsetX: -10, level: 21, z: -15790, cote: "droite", plan: false },
{ offsetX: -13, level: 21, z: -15800, cote: "droite", plan: false },

{ offsetX: 8, level: 22, z: -16235, cote: "gauche", plan: false },
{ offsetX: -10, level: 22, z: -16270, cote: "droite", plan: false },
{ offsetX: 10, level: 22, z: -16340, cote: "gauche", plan: false },
{ offsetX: 13, level: 22, z: -16360, cote: "gauche", plan: false },
{ offsetX: 13, level: 22, z: -16370, cote: "gauche", plan: false },
{ offsetX: 10, level: 22, z: -16440, cote: "gauche", plan: false },
{ offsetX: -12, level: 22, z: -16450, cote: "droite", plan: false },
{ offsetX: 10, level: 22, z: -16470, cote: "gauche", plan: false },
{ offsetX: 10, level: 22, z: -16470, cote: "gauche", plan: false },
{ offsetX: -10, level: 22, z: -16475, cote: "droite", plan: false },
{ offsetX: 10, level: 22, z: -16480, cote: "gauche", plan: false },
{ offsetX: 10, level: 22, z: -16490, cote: "gauche", plan: false },
{ offsetX: 10, level: 22, z: -16500, cote: "gauche", plan: false },
{ offsetX: 10, level: 22, z: -16510, cote: "gauche", plan: false },
{ offsetX: -11, level: 22, z: -16515, cote: "droite", plan: false },
{ offsetX: -10, level: 22, z: -16525, cote: "droite" , plan: false },
{ offsetX: 10, level: 22, z: -16530, cote: "gauche", plan: false },
{ offsetX: -10, level: 22, z: -16535, cote: "droite", plan: false },
{ offsetX: 10, level: 22, z: -16550, cote: "gauche", plan: false },
{ offsetX: 10, level: 22, z: -16570, cote: "gauche", plan: false },
{ offsetX: 10, level: 22, z: -16590, cote: "gauche", plan: false },
{ offsetX: -11, level: 22, z: -16595, cote: "droite", plan: false },
{ offsetX: -10, level: 22, z: -16605, cote: "droite", plan: false },
{ offsetX: -10, level: 22, z: -16615, cote: "droite", plan: false },
{ offsetX: -11, level: 22, z: -16625, cote: "droite", plan: false },
{ offsetX: -11, level: 22, z: -16630, cote: "droite" , plan: false },
{ offsetX: -10, level: 22, z: -16635, cote: "droite", plan: false },
{ offsetX: -10, level: 22, z: -16640, cote: "droite" , plan: false },
{ offsetX: -10, level: 22, z: -16645, cote: "droite" , plan: false },
{ offsetX: 10, level: 22, z: -16660, cote: "gauche" , plan: false },
{ offsetX: -11, level: 22, z: -16685, cote: "droite" , plan: false },
{ offsetX: -11, level: 22, z: -16690, cote: "droite" , plan: false },
{ offsetX: -10, level: 22, z: -16720, cote: "droite" , plan: false },
{ offsetX: -10, level: 22, z: -16750, cote: "droite" , plan: false },
{ offsetX: -10, level: 22, z: -16780, cote: "droite" , plan: false },
{ offsetX: -11, level: 22, z: -16790, cote: "droite" , plan: false },
{ offsetX: -11, level: 22, z: -16800, cote: "droite" , plan: false },
{ offsetX: 10, level: 22, z: -16770, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -16775, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -16780, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -16785, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -16790, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -16800, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -16805, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -16810, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -16815, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -16820, cote: "gauche" , plan: false },
{ offsetX: -10, level: 22, z: -16810, cote: "droite" , plan: false },
{ offsetX: -10, level: 22, z: -16820, cote: "droite" , plan: false },
{ offsetX: -9, level: 22, z: -16835, cote: "droite" , plan: false },
{ offsetX: 12, level: 22, z: -16960, cote: "gauche" , plan: false },
{ offsetX: -10, level: 22, z: -16975, cote: "droite" , plan: false },


{ offsetX: -15, level: 22, z: -17010, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17020, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17030, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17040, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17050, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17060, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17070, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17080, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17090, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17100, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17110, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17120, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17130, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17145, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17155, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17170, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17190, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17210, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17230, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17240, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17250, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17260, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17270, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17280, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17290, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17200, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17210, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17220, cote: "droite" , plan: false },
{ offsetX: -16, level: 22, z: -17230, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17245, cote: "droite" , plan: false },
{ offsetX: -15, level: 22, z: -17255, cote: "droite" , plan: false },


{ offsetX: 10, level: 22, z: -17020, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17035, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17050, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17065, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17075, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17090, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17115, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17130, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17145, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17160, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17220, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17235, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17250, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17265, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17275, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17290, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17315, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17330, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17345, cote: "gauche" , plan: false },
{ offsetX: 10, level: 22, z: -17360, cote: "gauche" , plan: false },


{ offsetX: 8, level: 24, z: -17380, cote: "gauche" , plan: false },
{ offsetX: -16, level: 24, z: -17380, cote: "droite" , plan: false },
{ offsetX: 8, level: 24, z: -17398, cote: "gauche" , plan: false },
{ offsetX: -15, level: 24, z: -17398, cote: "droite" , plan: false },
{ offsetX: 13, level: 24, z: -17615, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -17615, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -17630, cote: "gauche" , plan: false },
{ offsetX: -9, level: 24, z: -17630, cote: "droite" , plan: false },
{ offsetX: 17, level: 24, z: -17643, cote: "gauche" , plan: false },
{ offsetX: -9, level: 24, z: -17643, cote: "droite" , plan: false },
{ offsetX: 13, level: 24, z: -17668, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -17668, cote: "droite" , plan: false },
{ offsetX: 15, level: 24, z: -17681, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -17681, cote: "droite" , plan: false },
{ offsetX: 16, level: 24, z: -17704, cote: "gauche" , plan: false },
{ offsetX: -8, level: 24, z: -17704, cote: "droite" , plan: false },
{ offsetX: 13, level: 24, z: -17718, cote: "gauche" , plan: false },
{ offsetX: -8, level: 24, z: -17718, cote: "droite" , plan: false },
{ offsetX: 17, level: 24, z: -17735, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -17735, cote: "droite" , plan: false },
{ offsetX: 13, level: 24, z: -17750, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -17750, cote: "droite" , plan: false },
{ offsetX: 15, level: 24, z: -17761, cote: "gauche" , plan: false },
{ offsetX: -9, level: 24, z: -17761, cote: "droite" , plan: false },
{ offsetX: 15, level: 24, z: -17778, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -17778, cote: "droite" , plan: false },
{ offsetX: 15, level: 24, z: -17788, cote: "gauche" , plan: false },
{ offsetX: -9, level: 24, z: -17788, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -17806, cote: "gauche" , plan: false },
{ offsetX: -9, level: 24, z: -17806, cote: "droite" , plan: false },
{ offsetX: 16, level: 24, z: -17823, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -17823, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -17842, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -17842, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -17862, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -17862, cote: "droite" , plan: false },
{ offsetX: 15, level: 24, z: -17875, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -17875, cote: "droite" , plan: false },
{ offsetX: 15, level: 24, z: -17894, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -17894, cote: "droite" , plan: false },
{ offsetX: 13, level: 24, z: -17906, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -17906, cote: "droite" , plan: false },
{ offsetX: 13, level: 24, z: -17922, cote: "gauche" , plan: false },
{ offsetX: -8, level: 24, z: -17922, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -17946, cote: "gauche" , plan: false },
{ offsetX: -9, level: 24, z: -17946, cote: "droite" , plan: false },
{ offsetX: 16, level: 24, z: -17965, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -17965, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -17975, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -17975, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -17998, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -17998, cote: "droite" , plan: false },
{ offsetX: 15, level: 24, z: -18012, cote: "gauche" , plan: false },
{ offsetX: -9, level: 24, z: -18012, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -18025, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -18025, cote: "droite" , plan: false },
{ offsetX: 15, level: 24, z: -18044, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -18044, cote: "droite" , plan: false },
{ offsetX: 16, level: 24, z: -18068, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -18068, cote: "droite" , plan: false },
{ offsetX: 13, level: 24, z: -18090, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -18090, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -18106, cote: "gauche" , plan: false },
{ offsetX: -11, level: 24, z: -18106, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -18131, cote: "gauche" , plan: false },
{ offsetX: -9, level: 24, z: -18131, cote: "droite" , plan: false },
{ offsetX: 14, level: 24, z: -18143, cote: "gauche" , plan: false },
{ offsetX: -10, level: 24, z: -18143, cote: "droite" , plan: false },
{ offsetX: 14, level: 25, z: -18165, cote: "gauche" , plan: false },
{ offsetX: -11, level: 25, z: -18165, cote: "droite" , plan: false },
{ offsetX: 16, level: 25, z: -18180, cote: "gauche" , plan: false },
{ offsetX: -10, level: 25, z: -18180, cote: "droite" , plan: false },
{ offsetX: 14, level: 25, z: -18197, cote: "gauche" , plan: false },
{ offsetX: -9, level: 25, z: -18197, cote: "droite" , plan: false },
{ offsetX: 16, level: 25, z: -18217, cote: "gauche" , plan: false },
{ offsetX: -8, level: 25, z: -18217, cote: "droite" , plan: false },
{ offsetX: 14, level: 25, z: -18241, cote: "gauche" , plan: false },
{ offsetX: -9, level: 25, z: -18241, cote: "droite" , plan: false },
{ offsetX: 17, level: 25, z: -18251, cote: "gauche" , plan: false },
{ offsetX: -11, level: 25, z: -18251, cote: "droite" , plan: false },
{ offsetX: 16, level: 25, z: -18270, cote: "gauche" , plan: false },
{ offsetX: -11, level: 25, z: -18270, cote: "droite" , plan: false },
{ offsetX: 17, level: 25, z: -18283, cote: "gauche" , plan: false },
{ offsetX: -10, level: 25, z: -18283, cote: "droite" , plan: false },
{ offsetX: 15, level: 25, z: -18295, cote: "gauche" , plan: false },
{ offsetX: -9, level: 25, z: -18295, cote: "droite" , plan: false },
{ offsetX: 15, level: 25, z: -18319, cote: "gauche" , plan: false },
{ offsetX: -9, level: 25, z: -18319, cote: "droite" , plan: false },
{ offsetX: 16, level: 25, z: -18343, cote: "gauche" , plan: false },
{ offsetX: -9, level: 25, z: -18343, cote: "droite" , plan: false },
{ offsetX: 16, level: 25, z: -18350, cote: "gauche" , plan: false },
{ offsetX: -8, level: 25, z: -18350, cote: "droite" , plan: false },

{ offsetX: 15, level: 27, z: -18800, cote: "gauche" , plan: false },

{ offsetX: 17, level: 27, z: -18824, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -18824, cote: "droite" , plan: false },
{ offsetX: 14, level: 27, z: -18847, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -18847, cote: "droite" , plan: false },
{ offsetX: 16, level: 27, z: -18873, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -18873, cote: "droite" , plan: false },
{ offsetX: 15, level: 27, z: -18898, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -18898, cote: "droite" , plan: false },
{ offsetX: 14, level: 27, z: -18921, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -18921, cote: "droite" , plan: false },
{ offsetX: 17, level: 27, z: -18949, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -18949, cote: "droite" , plan: false },
{ offsetX: 16, level: 27, z: -18970, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -18970, cote: "droite" , plan: false },
{ offsetX: 15, level: 27, z: -18996, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -18996, cote: "droite" , plan: false },
{ offsetX: 13, level: 27, z: -19018, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19018, cote: "droite" , plan: false },
{ offsetX: 16, level: 27, z: -19043, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -19043, cote: "droite" , plan: false },
{ offsetX: 14, level: 27, z: -19069, cote: "gauche" , plan: false },
{ offsetX: -8, level: 27, z: -19069, cote: "droite" , plan: false },
{ offsetX: 17, level: 27, z: -19092, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19092, cote: "droite" , plan: false },
{ offsetX: 15, level: 27, z: -19120, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19120, cote: "droite" , plan: false },
{ offsetX: 14, level: 27, z: -19145, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -19145, cote: "droite" , plan: false },
{ offsetX: 16, level: 27, z: -19173, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19173, cote: "droite" , plan: false },
{ offsetX: 13, level: 27, z: -19197, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19197, cote: "droite" , plan: false },
{ offsetX: 15, level: 27, z: -19225, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -19225, cote: "droite" , plan: false },
{ offsetX: 17, level: 27, z: -19250, cote: "gauche" , plan: false },
{ offsetX: -9, level: 27, z: -19250, cote: "droite" , plan: false },
{ offsetX: 14, level: 27, z: -19278, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19278, cote: "droite" , plan: false },
{ offsetX: 16, level: 27, z: -19302, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19302, cote: "droite" , plan: false },
{ offsetX: 15, level: 27, z: -19329, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -19329, cote: "droite" , plan: false },
{ offsetX: 13, level: 27, z: -19353, cote: "gauche" , plan: false },
{ offsetX: -9, level: 27, z: -19353, cote: "droite" , plan: false },
{ offsetX: 17, level: 27, z: -19380, cote: "gauche" , plan: false },
{ offsetX: -8, level: 27, z: -19380, cote: "droite" , plan: false },
{ offsetX: 14, level: 27, z: -19406, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19406, cote: "droite" , plan: false },
{ offsetX: 16, level: 27, z: -19431, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -19431, cote: "droite" , plan: false },
{ offsetX: 15, level: 27, z: -19459, cote: "gauche" , plan: false },
{ offsetX: -9, level: 27, z: -19459, cote: "droite" , plan: false },
{ offsetX: 13, level: 27, z: -19484, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19484, cote: "droite" , plan: false },
{ offsetX: 17, level: 27, z: -19511, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19511, cote: "droite" , plan: false },

{ offsetX: 16, level: 27, z: -19564, cote: "gauche" , plan: false },

{ offsetX: 15, level: 27, z: -19590, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19590, cote: "droite" , plan: false },
{ offsetX: 13, level: 27, z: -19615, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19615, cote: "droite" , plan: false },
{ offsetX: 17, level: 27, z: -19643, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -19643, cote: "droite" , plan: false },
{ offsetX: 14, level: 27, z: -19670, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19670, cote: "droite" , plan: false },
{ offsetX: 16, level: 27, z: -19696, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19696, cote: "droite" , plan: false },
{ offsetX: 15, level: 27, z: -19722, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -19722, cote: "droite" , plan: false },
{ offsetX: 13, level: 27, z: -19749, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19749, cote: "droite" , plan: false },
{ offsetX: 17, level: 27, z: -19775, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19775, cote: "droite" , plan: false },
{ offsetX: 14, level: 27, z: -19802, cote: "gauche" , plan: false },
{ offsetX: -9, level: 27, z: -19802, cote: "droite" , plan: false },
{ offsetX: 16, level: 27, z: -19829, cote: "gauche" , plan: false },
{ offsetX: -11, level: 27, z: -19829, cote: "droite" , plan: false },
{ offsetX: 15, level: 27, z: -19852, cote: "gauche" , plan: false },
{ offsetX: -10, level: 27, z: -19852, cote: "droite" , plan: false },
{ offsetX: 17, level: 28, z: -19878, cote: "gauche" , plan: false },
{ offsetX: -10, level: 28, z: -19878, cote: "droite" , plan: false },
{ offsetX: 14, level: 28, z: -19905, cote: "gauche" , plan: false },
{ offsetX: -10, level: 28, z: -19905, cote: "droite" , plan: false },
{ offsetX: 16, level: 28, z: -19931, cote: "gauche" , plan: false },
{ offsetX: -9, level: 28, z: -19931, cote: "droite" , plan: false },
{ offsetX: 13, level: 28, z: -19957, cote: "gauche" , plan: false },
{ offsetX: -11, level: 28, z: -19957, cote: "droite" , plan: false },
{ offsetX: 15, level: 28, z: -19982, cote: "gauche" , plan: false },
{ offsetX: -10, level: 28, z: -19982, cote: "droite" , plan: false },
{ offsetX: 16, level: 28, z: -20000, cote: "gauche" , plan: false },
{ offsetX: -10, level: 28, z: -20000, cote: "droite" , plan: false },


{ offsetX: 12, level: 31, z: -20528, cote: "gauche" , plan: false },
{ offsetX: -14, level: 31, z: -20528, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -20552, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -20552, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -20583, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -20583, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -20616, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -20616, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -20679, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -20679, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -20710, cote: "gauche" , plan: false },
{ offsetX: -14, level: 32, z: -20710, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -20742, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -20742, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -20775, cote: "gauche" , plan: false },
{ offsetX: -14, level: 32, z: -20775, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -20838, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -20838, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -20870, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -20870, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -20903, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -20903, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -20935, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -20935, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -20968, cote: "gauche" , plan: false },
{ offsetX: -14, level: 32, z: -20968, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21002, cote: "gauche" , plan: false },
{ offsetX: -9, level: 32, z: -21002, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21034, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -21034, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -21067, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21067, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -21098, cote: "gauche" , plan: false },
{ offsetX: -14, level: 32, z: -21098, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21132, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21132, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -21163, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -21163, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -21197, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21197, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21230, cote: "gauche" , plan: false },
{ offsetX: -14, level: 32, z: -21230, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -21262, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21262, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21296, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -21296, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21329, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21329, cote: "droite" , plan: false },
{ offsetX: 9, level: 32, z: -21362, cote: "gauche" , plan: false },
{ offsetX: -14, level: 32, z: -21362, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -21396, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21396, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21428, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -21428, cote: "droite" , plan: false },
{ offsetX: 9, level: 32, z: -21461, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21461, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -21495, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -21495, cote: "droite" , plan: false },
{ offsetX: 12, level: 32, z: -21527, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21527, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21561, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -21561, cote: "droite" , plan: false },
{ offsetX: 9, level: 32, z: -21593, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21593, cote: "droite" , plan: false },
{ offsetX: 11, level: 32, z: -21627, cote: "gauche" , plan: false },
{ offsetX: -15, level: 32, z: -21627, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21660, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21660, cote: "droite" , plan: false },
{ offsetX: 9, level: 32, z: -21694, cote: "gauche" , plan: false },
{ offsetX: -14, level: 32, z: -21694, cote: "droite" , plan: false },
{ offsetX: 10, level: 32, z: -21729, cote: "gauche" , plan: false },
{ offsetX: -8, level: 32, z: -21729, cote: "droite" , plan: false },
{ offsetX: 9, level: 33, z: -21758, cote: "gauche" , plan: false },
{ offsetX: -15, level: 33, z: -21758, cote: "droite" , plan: false },
{ offsetX: 10, level: 33, z: -21790, cote: "gauche" , plan: false },
{ offsetX: -9, level: 33, z: -21790, cote: "droite" , plan: false },
{ offsetX: 10, level: 33, z: -21824, cote: "gauche" , plan: false },
{ offsetX: -15, level: 33, z: -21824, cote: "droite" , plan: false },
{ offsetX: 11, level: 33, z: -21858, cote: "gauche" , plan: false },
{ offsetX: -8, level: 33, z: -21858, cote: "droite" , plan: false },
{ offsetX: 11, level: 33, z: -21892, cote: "gauche" , plan: false },
{ offsetX: -15, level: 33, z: -21892, cote: "droite" , plan: false },
{ offsetX: 10, level: 33, z: -21925, cote: "gauche" , plan: false },
{ offsetX: -9, level: 33, z: -21925, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -21959, cote: "gauche" , plan: false },
{ offsetX: -14, level: 33, z: -21959, cote: "droite" , plan: false },
{ offsetX: 11, level: 33, z: -21983, cote: "gauche" , plan: false },
{ offsetX: -8, level: 33, z: -21983, cote: "droite" , plan: false },
{ offsetX: 10, level: 33, z: -22000, cote: "gauche" , plan: false },
{ offsetX: -15, level: 33, z: -22000, cote: "droite" , plan: false },


{ offsetX: 12, level: 33, z: -22290, cote: "gauche" , plan: false },
{ offsetX: -8, level: 33, z: -22290, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22324, cote: "gauche" , plan: false },
{ offsetX: -15, level: 33, z: -22324, cote: "droite" , plan: false },
{ offsetX: 13, level: 33, z: -22358, cote: "gauche" , plan: false },
{ offsetX: -8, level: 33, z: -22358, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22392, cote: "gauche" , plan: false },
{ offsetX: -14, level: 33, z: -22392, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22425, cote: "gauche" , plan: false },
{ offsetX: -8, level: 33, z: -22425, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22459, cote: "gauche" , plan: false },
{ offsetX: -14, level: 33, z: -22459, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22483, cote: "gauche" , plan: false },
{ offsetX: -8, level: 33, z: -22483, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22500, cote: "gauche" , plan: false },
{ offsetX: -15, level: 33, z: -22500, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22525, cote: "gauche" , plan: false },
{ offsetX: -8, level: 33, z: -22535, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22559, cote: "gauche" , plan: false },
{ offsetX: -14, level: 33, z: -22559, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22583, cote: "gauche" , plan: false },
{ offsetX: -8, level: 33, z: -22583, cote: "droite" , plan: false },
{ offsetX: 12, level: 33, z: -22608, cote: "gauche" , plan: false },
{ offsetX: -8, level: 33, z: -22608, cote: "droite" , plan: false },
]

const arbresSortedByZ = [...arbres].sort((a, b) => b.z - a.z)

window.GAME_QUALITY = {
  low: false,
  texturesLow: false,
  forced: null,
  maxAnisotropy: 1
}

// ======================================================
// INITIAL QUALITY
// ======================================================

function getInitialQuality() {

  if (typeof window === "undefined") {
    return true
  }

  // déjà décidé
  if (window.GAME_QUALITY.initialized) {

    return window.GAME_QUALITY.low
  }

  const ua = navigator.userAgent

  // ======================================================
  // iOS MODERNE
  // ======================================================

  const isIOS =
    /iPad|iPhone|iPod/i.test(ua)
    ||
    (
      navigator.platform ===
        "MacIntel"
      &&
      navigator.maxTouchPoints > 1
    )

  const isMobile = isIOS || /Android/i.test(ua)
  const cores = navigator.hardwareConcurrency || 2
  const memory =
    typeof navigator.deviceMemory === "number"
      ? navigator.deviceMemory
      : null
  const dpr = window.devicePixelRatio || 1
  const hasLowMemory = memory !== null && memory <= 4
  const hasWeakCpu = cores <= 4
  const hasLimitedMobileProfile = cores <= 6 && dpr <= 2

  // ======================================================
  // MANUAL OVERRIDE
  // ======================================================

  let low = false

  if (window.GAME_QUALITY.forced === "low") {

    low = true

  } else if (window.GAME_QUALITY.forced === "high") {

    low = false

  } else {

    // ======================================================
    // DESKTOP
    // ======================================================

    if (!isMobile) {

      low = false

    } else {

      // ======================================================
      // iOS
      // ======================================================

      if (isIOS) {

        low = hasWeakCpu || hasLowMemory || hasLimitedMobileProfile

      } else {

        // ======================================================
        // ANDROID
        // ======================================================

        const weakAndroidBrand = /LG|K61|Moto G|Galaxy A/i.test(ua)

        low =
          weakAndroidBrand ||
          hasWeakCpu ||
          hasLowMemory ||
          hasLimitedMobileProfile
      }
    }
  }

  // ======================================================
  // FINAL APPLY
  // ======================================================

  window.GAME_QUALITY.low = low
  window.GAME_QUALITY.texturesLow = low
  window.GAME_QUALITY.initialized = true

  console.log({

    QUALITY: low ? "LOW" : "HIGH",
    userAgent: ua,
    isIOS,
    isMobile,
    dpr,
    cores,
    memory,
    GAME_QUALITY: window.GAME_QUALITY
  })

  return low
}
const IS_MOBILE = typeof window !== "undefined" && (navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches)

//=============================================================
const VEHICLE_CACHE = {}
const VEHICLE_LENGTH_CACHE = {}
const VEHICLE_BOUNDS = {}
const VEHICLE_MATERIAL_CACHE = {}
const WHEEL_AXIS_VECTORS = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1)
}
const WHEEL_WORLD_UP = new THREE.Vector3(0, 1, 0)
const WHEEL_MOVE_DIR = new THREE.Vector3()
const WHEEL_TARGET_AXIS = new THREE.Vector3()
const WHEEL_WORLD_AXIS = new THREE.Vector3()

const VEHICLE_NAMES = [

  // =========================
  // 🚗 VEHICULES STANDARDS
  // =========================
  "lancer_evo",
  "toyota_hiace",
  "volvo_xc90",
  "jeep_commander",
  "hummer",
  "lada_combi",
  "lada_coupe",
  "lada_kalina",
  "pickup_new",
  "ambulans_yeni",
  "isuzu",

  // =========================
  // 🚚 CAMIONS / BUS
  // =========================
  "gazel_bus",
  "scania_bus",
  "tir",
  "truck1",
  "truck2",
  "truck3",

  // =========================
  // 🤖 AI VEHICLES (VALIDÉS)
  // =========================
  "AI_Bmw4_High",
  "AI_Chevy_High",
  "AI_Clio_High",
  "AI_Cruze_High",
  "AI_Ford_E150_High",
  "AI_Ford_Excursion_High",
  "AI_Ford_F150_High",
  "AI_Ford_Transit_High",
  "AI_Freelander_High",
  "AI_Golf7_High",
  "AI_Kia_Ceed_High",
  "AI_Maxity_High",
  "AI_Mazda2_High",
  "AI_Mazda3_High",
  "AI_Mb_Sprinter_High",
  "AI_Mb_Unimog_High",
  "AI_Nissan_Atlas_High",
  "AI_Nissan_Atleon_High",
  "AI_Scania_High",
  "AI_Skoda_Octavia_High",
  "AI_Tir_High",
  "AI_Truck1_High",
  "AI_Vw_Amorak_High"

]

const PARKED_VEHICULES = [

  // =========================
  // 🚗 VEHICULES STANDARDS
  // =========================
  "lancer_evo",
  "toyota_hiace",
  "volvo_xc90",
  "jeep_commander",
  "hummer",
  "lada_combi",
  "lada_coupe",
  "lada_kalina",
  "pickup_new",
  "ambulans_yeni",
  "isuzu",

 
  // =========================
  // 🤖 AI VEHICLES (VALIDÉS)
  // =========================
  "AI_Bmw4_High",
  "AI_Chevy_High",
  "AI_Clio_High",
  "AI_Cruze_High",
  "AI_Ford_E150_High",
  "AI_Ford_Excursion_High",
  "AI_Ford_F150_High",
  "AI_Ford_Transit_High",
  "AI_Freelander_High",
  "AI_Golf7_High",
  "AI_Kia_Ceed_High",
  "AI_Mazda2_High",
  "AI_Mazda3_High",
  "AI_Mb_Sprinter_High",
  "AI_Mb_Unimog_High",
  "AI_Nissan_Atlas_High",
  "AI_Skoda_Octavia_High",
  "AI_Vw_Amorak_High"

]

const VEHICLE_FIX = {

  hummer:            { modelRot:[Math.PI/2,Math.PI,0], forward: 1,  posY:0.02, scale:1.05},
  jeep_commander:    { modelRot:[Math.PI/2,Math.PI,0], forward: 1,  posY:0.02, scale:1.1 },
  lada_kalina:       { modelRot:[Math.PI/2,Math.PI,0], forward: 1,  posY:0.02, scale:1.15 },
  lancer_evo:        { modelRot:[0,Math.PI/2,0], forward: 1,  posY:0.02, scale:1 },
  toyota_hiace:      { modelRot:[0,-Math.PI/2,0], forward: -1, posY:0.02, scale:1 },
  volvo_xc90:        { modelRot:[0,Math.PI/2,0], forward: 1,  posY:0.02, scale:1 },
  pickup_new:        { modelRot:[0,-Math.PI/2,0], forward: 1, posY:0.02, scale:1.25 },
  AI_Bmw4_High:      { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.6 },
  AI_Golf7_High:     { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.6 },
  AI_Ford_F150_High: { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.35 },
  AI_Chevy_High:     { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.6 },
  AI_Clio_High:      { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.55 },
  AI_Cruze_High:     { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.5 },
  AI_Ford_E150_High:      { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.5 },
  AI_Ford_Excursion_High: { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.4 },
  AI_Ford_Transit_High:   { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.5 },
  AI_Freelander_High: { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.4 },
  AI_Kia_Ceed_High:   { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.65 },
  AI_Maxity_High:     { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.5 },
  AI_Mazda2_High:     { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.6 },
  AI_Mazda3_High:     { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.6 },
  AI_Mb_Sprinter_High:{ modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.2 },
  AI_Mb_Unimog_High:  { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.1 },
  AI_Nissan_Atlas_High:  { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.4 },
  AI_Nissan_Atleon_High: { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.2},
  AI_Skoda_Octavia_High: { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.4 },
  AI_Truck1_High:        { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.5 },
  AI_Vw_Amorak_High:     { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.4 },
  AI_Scania_High:        { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.2 },
  ambulans_yeni: { modelRot:[Math.PI/2,Math.PI,0], forward:1, posY:0.02, scale:1 }, // TODO
  gazel_bus:     { modelRot:[Math.PI/2,Math.PI,0], forward:1, posY:0.02, scale:1.2 }, // TODO
  scania_bus:    { modelRot:[Math.PI/2,Math.PI,0], forward:1, posY:0.02, scale:0.9 }, // TODO
  tir:           { modelRot:[0,Math.PI/2,0], forward:1, posY:0.02, scale:0.8 }, // TODO
  truck1:        { modelRot:[-Math.PI/2,0,0], forward:1, posY:0.02, scale:0.9 }, // TODO
  truck2:        { modelRot:[0,-Math.PI/2,0], forward:1, posY:0.02, scale:0.9 }, // TODO
  truck3:        { modelRot:[-Math.PI/2,0,0], forward:1, posY:0.02, scale:0.9 }, // TODO
  AI_Tir_High:   { modelRot:[Math.PI,0,Math.PI], forward:1, posY:0.02, scale:1.2 }, // TODO
  lada_combi: { modelRot:[Math.PI/2,Math.PI,0], forward:1, posY:0.02, scale:1.3 }, // proche lada_kalina
  lada_coupe: { modelRot:[Math.PI/2,Math.PI,0], forward:1, posY:0.02, scale:1.2 }, // proche lada_kalina
  isuzu:      { modelRot:[0,Math.PI/2,0],       forward:1, posY:0.02, scale:1.2 },  // petit camion

}

THREE.ColorManagement.enabled = true


// ==================================================
// DRACO
// ==================================================

const DRACO_PATH =
  'https://www.gstatic.com/draco/versioned/decoders/1.5.6/'

// pour useGLTF / preload
useGLTF.setDecoderPath(DRACO_PATH)

// ==================================================
// PRELOAD
// ==================================================

useGLTF.preload("/modeles/vehicules.glb")
useGLTF.preload("/modeles/arbres.glb")
useGLTF.preload("/modeles/manA.glb")
useGLTF.preload("/modeles/manB.glb")
useGLTF.preload("/modeles/manC.glb")
useGLTF.preload("/modeles/womanA.glb")
useGLTF.preload("/modeles/womanB.glb")
useGLTF.preload("/modeles/womanC.glb")
useGLTF.preload("/modeles/boy.glb")
useGLTF.preload("/modeles/baby.glb")
useGLTF.preload("/modeles/manC_bike.glb")
useGLTF.preload("/modeles/womanA_bike.glb")
useGLTF.preload("/modeles/boy_bike.glb")
useGLTF.preload("/modeles/sportManA.glb")
useGLTF.preload("/modeles/sportManB.glb")
useGLTF.preload("/modeles/sportWomanA.glb")
useGLTF.preload("/modeles/sportWomanB.glb")
useGLTF.preload("/modeles/sportWomanC.glb")
useLoader.preload(
  THREE.TextureLoader,
  [
    "/decors/fond/arbres.png",
    "/decors/fond/ville.png"
  ]
)

const isNearIntersection = (z, intersections) => {

  for (const inter of intersections) {

    const CLEAR_ZONE = inter.width 

    if (Math.abs(z - inter.z) < CLEAR_ZONE)
      return true
  }

  return false
}

// --------------------------------------------------
// 🎮 TOUCH CONTROLS (sans casser le clavier)
// --------------------------------------------------
const GAME_UI_SELECTOR = "input, textarea, button, select, a, [contenteditable], [role='dialog'], [data-game-ui]"

function isGameUIEvent(event) {
  return Boolean(
    event.target?.isContentEditable ||
    event.target?.closest?.(GAME_UI_SELECTOR)
  )
}

function useTouchControls(accelerating, setDirection, onTap, enabled) {
  const onTapRef = useRef(onTap)
  onTapRef.current = onTap

  useEffect(() => {
    if (!enabled) {
      accelerating.current = false
      setDirection(0)
      return
    }

    const TAP_TIME = 160   // ms — seuil tap vs hold
    const DOUBLE_TAP_DELAY = 300
    let lastTapTime = 0
    let touchStartTime = 0
    let startX = 0
    let currentX = 0
    let touchActive = false

    const resetTouch = () => {
      touchActive = false
      accelerating.current = false
      setDirection(0)
      startX = 0
      currentX = 0
    }

    const handleTouchStart = (e) => {
      if (isGameUIEvent(e)) return
      const touch = e.touches[0]
      if (!touch) return

      touchStartTime = performance.now()
      touchActive = true
      accelerating.current = true

      startX = touch.clientX
      currentX = touch.clientX
    }

    const handleTouchMove = (e) => {
      if (!touchActive || isGameUIEvent(e)) return

      const touch = e.touches[0]
      if (!touch) return

      currentX = touch.clientX

      const deltaX = currentX - startX

      // 🎯 sensibilité (clé)
      const SENSITIVITY = 0.003

      let dir = deltaX * SENSITIVITY

      // dead zone douce
      if (Math.abs(dir) < 0.02) dir = 0

      dir = Math.max(-1, Math.min(1, dir))

      setDirection(dir)
    }

    const handleTouchEnd = (e) => {
      if (!touchActive) return
      if (isGameUIEvent(e)) {
        resetTouch()
        return
      }

      const duration = performance.now() - touchStartTime

      if (duration < TAP_TIME) {

        const now = performance.now()
        const delta = now - lastTapTime

        if (delta < DOUBLE_TAP_DELAY) {
          onTapRef.current?.()
        }

        lastTapTime = now
      }

      resetTouch()
    }

    window.addEventListener("touchstart", handleTouchStart, { passive: true })
    window.addEventListener("touchmove", handleTouchMove, { passive: true })
    window.addEventListener("touchend", handleTouchEnd)
    window.addEventListener("touchcancel", resetTouch)

    return () => {

      window.removeEventListener("touchstart", handleTouchStart)
      window.removeEventListener("touchmove", handleTouchMove)
      window.removeEventListener("touchend", handleTouchEnd)
      window.removeEventListener("touchcancel", resetTouch)
      resetTouch()

    }

  }, [enabled, accelerating, setDirection])

}

function useBikeKeyboardControls(enabled, accelerating, boostActive, setBraking, setDirection) {
  useEffect(() => {
    const resetControls = () => {
      accelerating.current = false
      boostActive.current = false
      setBraking(false)
      setDirection(0)
    }

    if (!enabled) {
      resetControls()
      return
    }

    const down = e => {
      if (isGameUIEvent(e)) return
      if (e.key === "ArrowUp") accelerating.current = true
      if (e.key === "ArrowDown") setBraking(true)
      if (e.key === "ArrowLeft") setDirection(-1)
      if (e.key === "ArrowRight") setDirection(1)
      if (e.shiftKey) boostActive.current = true
    }

    const up = e => {
      if (isGameUIEvent(e)) return
      if (e.key === "ArrowUp") accelerating.current = false
      if (e.key === "ArrowDown") setBraking(false)
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") setDirection(0)
      if (!e.shiftKey) boostActive.current = false
    }

    window.addEventListener("keydown", down)
    window.addEventListener("keyup", up)
    return () => {
      window.removeEventListener("keydown", down)
      window.removeEventListener("keyup", up)
      resetControls()
    }
  }, [enabled, accelerating, boostActive, setBraking, setDirection])
}

// --------------------------------------------------
// TEXTE FLOTTANT
// --------------------------------------------------

function spawnText(textGroup, texts, x, z, value, color = "white") {

  if (!textGroup?.current) return

  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")

  canvas.width = 256
  canvas.height = 128
  ctx.fillStyle = color
  ctx.font = "bold 72px Arial"
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(value, 128, 64)

  const tex = new THREE.CanvasTexture(canvas)
  const material = new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthTest: true,
    depthWrite: false
  })

  const sprite = new THREE.Sprite(material)

  sprite.scale.set(2.8, 1.4, 1)
  textGroup.current.add(sprite)
  texts.current.push({ x, z, life: 1, sprite })
}

// --------------------------------------------------
// SOL
// --------------------------------------------------
function GroundInstances({ sections = [], x }) {

  const { worldGroup, restartVersion } = useGame()

  const anisotropy =  window.GAME_QUALITY?.maxAnisotropy

  const COUNT = 30
  const BACK_MARGIN = 80
  const TAILLE_SEGMENT = SEGMENT_LENGTH / 4

  const lastWorldZ = useRef(0)

  // ==================================================
  // SORTED SECTIONS
  // ==================================================

  const sortedSections = useMemo(() => {
    return [...sections].sort((a, b) => b.start - a.start)
  }, [sections])

  // ==================================================
  // TEXTURES
  // ==================================================

  const textureList = useMemo(() => {
    return sortedSections.map(s => s.texture)
  }, [sortedSections])

  const textures = useLoader(THREE.TextureLoader, textureList)

  // ==================================================
  // GEOMETRY CACHE
  // ==================================================

  const geometryCache = useMemo(() => {

    const cache = new Map()

    for (const section of sortedSections) {

      if (!cache.has(section.width)) {

        cache.set(

          section.width,

          new THREE.PlaneGeometry(
            section.width,
            TAILLE_SEGMENT
          )
        )
      }
    }

    return cache

  }, [sortedSections])

  const temp = useMemo(() => new THREE.Object3D(), [])

  // ==================================================
  // TEXTURE PREP
  // ==================================================

  // ==================================================
// TEXTURE PREP
// ==================================================

function prepare(tex) {

  if (!tex) {
    return null
  }

  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.repeat.set(1, TAILLE_SEGMENT / 8)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = anisotropy || 8
  return tex
}

// ==================================================
// MATERIALS
// ==================================================

const materials = useMemo(() => {

  return textures.map((tex) => {

    const map = prepare(tex)

    return map ? new THREE.MeshBasicMaterial({ map }) : null
  })

}, [textures, anisotropy])

  useEffect(() => {

    return () => {

      materials.forEach(m => {

        m?.dispose()
      })
    }

  }, [materials])

  // ==================================================
  // MESH REFS
  // ==================================================

  const meshRefs = useRef([])

  useEffect(() => {

    meshRefs.current = meshRefs.current.slice(0, materials.length)

  }, [materials.length])

  // ==================================================
  // SEGMENTS
  // ==================================================

  const segmentZRef = useRef(Array.from({ length: COUNT }, (_, i) => BACK_MARGIN - i * TAILLE_SEGMENT))
  const segmentTypeRef = useRef(Array.from({ length: COUNT }, () => 0))

  // ==================================================
  // GET TYPE FROM Z
  // ==================================================

  const getTypeFromZ = (z) => {

    let type = 0

    for (let i = 0; i < sortedSections.length; i++) {

      if (z <= sortedSections[i].start) {
        type = i
      }
    }

    return type
  }

  // ==================================================
  // RESET
  // ==================================================

  const resetSegments = (worldZ) => {

    const zList = segmentZRef.current
    const typeList = segmentTypeRef.current

    for (let i = 0; i < COUNT; i++) {

      const z = -worldZ + BACK_MARGIN - i * TAILLE_SEGMENT

      zList[i] = z
      typeList[i] = getTypeFromZ(z)
    }
  }

  useEffect(() => {

    if (!worldGroup?.current)
      return

    resetSegments(worldGroup.current?.position?.z ?? 0)

  }, [restartVersion])


  // ==================================================
  // FRAME LOOP
  // ==================================================

  useFrame(() => {

    if (!worldGroup?.current)
      return

    const worldZ = worldGroup.current?.position?.z ?? 0
    const delta = Math.abs(worldZ - lastWorldZ.current)

    lastWorldZ.current = worldZ

    const zList = segmentZRef.current
    const typeList = segmentTypeRef.current

    // ==================================================
    // TELEPORT RESET
    // ==================================================

    if (delta > 200) {

      resetSegments(worldZ)

      for (let i = 0; i < COUNT; i++) {

        const z = zList[i]
        const currentType = typeList[i]

        for (let m = 0; m < meshRefs.current.length; m++) {

          const mesh = meshRefs.current[m]

          if (!mesh)
            continue

          if (m === currentType) {

            temp.position.set(x, -0.001, z)

          } else {

            temp.position.set(0, 0, 99999)
          }

          temp.rotation.set(-Math.PI / 2, 0, 0)
          temp.updateMatrix()
          mesh.setMatrixAt(i, temp.matrix)
        }
      }

      meshRefs.current.forEach(
        mesh => {

          if (mesh) {

            mesh.instanceMatrix.needsUpdate = true
          }
        }
      )

      return
    }

    // ==================================================
    // MODE NORMAL
    // ==================================================

    let frontZ = Math.min(...zList)

    for (let i = 0; i < COUNT; i++) {

      let z = zList[i]

      const visibleZ = z + worldZ

      // recycle segment

      if (visibleZ > BACK_MARGIN) {

        z = frontZ - TAILLE_SEGMENT
        zList[i] = z
        frontZ = z
        typeList[i] = getTypeFromZ(z)
      }

      const currentType = typeList[i]

      for (let m = 0; m < meshRefs.current.length; m++) {

        const mesh = meshRefs.current[m]

        if (!mesh)
          continue

        if (m === currentType) {

          temp.position.set(x, -0.001, z)

        } else {

          temp.position.set(0, 0, 99999)
        }

        temp.rotation.set(-Math.PI / 2, 0, 0)
        temp.updateMatrix()
        mesh.setMatrixAt(i, temp.matrix)
      }
    }

    meshRefs.current.forEach(
      mesh => {

        if (mesh) {

          mesh.instanceMatrix.needsUpdate = true
        }
      }
    )
  })

  // ==================================================
  // RENDER
  // ==================================================

  return (
    <>
      {
        materials.map(
          (material, index) => (

            <instancedMesh
              key={index}
              ref={el => {
                meshRefs.current[index] = el
              }}
              args={[
                geometryCache.get(
                  sortedSections[index].width
                ),
                material,
                COUNT
              ]}
              frustumCulled={false}
            />
          )
        )
      }
    </>
  )
}
const RandomBonuses = React.forwardRef(({
  cyclistRef,
  pisteX,
  pisteW,
  count = 6,
  onCollectPoints,
  onCollectEnergy,
}, ref) => {

  // ==================================================
  // CONFIG
  // ==================================================

  const ENERGY_SPAWN_RATIO = 0.5
  const POINT_VALUE = 100
  const ENERGY_VALUE = 10
  const ENERGY_TEXT = "+10%"
  const ENERGY_COLOR = "#39FF14"
  const ROT_SPEED = 2.8
  const PLAYER_HALF_X = 0.5
  const PLAYER_HALF_Z = 1.0
  const BONUS_HALF_X = 0.6
  const BONUS_HALF_Z = 0.6
  const FLOAT_Y = 1.45
  const FRAMES = 12
  const FPS = 16

  // ==================================================

  const { gameState, worldGroup } = useGame()

  const group = useRef()
  const energyGroup = useRef()
  const textGroup = useRef()
  const playerPosition = useMemo(() => new THREE.Vector3(), [])

  const texture = useLoader(
    THREE.TextureLoader,
    "/sprites/bouteille.png"
  )

  const energyTexture = useLoader(
    THREE.TextureLoader,
    "/sprites/barre.png"
  )

  const bonuses = useRef([])
  const energyBonuses = useRef([])
  const texts = useRef([])
  const low = window.GAME_QUALITY?.low

  // --------------------------------------------------
  // CONFIG TEXTURES
  // --------------------------------------------------

  useEffect(() => {

    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.repeat.set(1 / FRAMES, 1)
    texture.magFilter = THREE.NearestFilter
    texture.minFilter = THREE.NearestFilter
    texture.generateMipmaps = !low
    texture.colorSpace = THREE.SRGBColorSpace

    energyTexture.colorSpace = THREE.SRGBColorSpace

  }, [texture, energyTexture])

  // --------------------------------------------------
  // INIT BONUS
  // --------------------------------------------------

  if (!bonuses.current.length) {

    const energyCount = Math.max(1, Math.floor(count * ENERGY_SPAWN_RATIO))

    for (let i = 0; i < count; i++) {

      bonuses.current.push({
        x: pisteX + (Math.random() - 0.5) * pisteW,
        z: -(40 + Math.random() * 120)
      })
    }

    for (let i = 0; i < energyCount; i++) {

      energyBonuses.current.push({
        x: pisteX + (Math.random() - 0.5) * pisteW,
        z: -(60 + Math.random() * 160)
      })
    }
  }

  // --------------------------------------------------
  // UPDATE
  // --------------------------------------------------

  useFrame((state, dt) => {

  if (gameState === "waitingContinue") return
  if (!worldGroup?.current || !cyclistRef?.current) return

  const worldZ = worldGroup.current.position.z

  // ==================================================
  // BONUS POINTS
  // ==================================================

  if (group.current) {

    const playerPos = getPositionInFrame(cyclistRef.current, group.current, playerPosition)
    if (!playerPos) return

    const meshes = group.current.children

    for (let i = 0; i < bonuses.current.length; i++) {

      const b = bonuses.current[i]
      const mesh = meshes[i]

      if (!mesh) continue

      mesh.position.x = b.x
      mesh.position.z = b.z

      const bob =
        Math.sin(state.clock.elapsedTime * 2 + i) * 0.15

      mesh.position.y = FLOAT_Y + bob

      const frame =
        Math.floor(
          state.clock.elapsedTime * FPS + i
        ) % FRAMES

      mesh.material.map.offset.x = frame / FRAMES

      const dz = b.z - playerPos.z
      const dx = playerPos.x - b.x

      // =====================================
      // ♻ RESPAWN
      // =====================================

      if (dz > 60 || dz < -400) {

        b.z = -worldZ - (80 + Math.random() * 220)

        b.x =
          pisteX +
          (Math.random() - 0.5) * pisteW

        mesh.position.set(b.x, mesh.position.y, b.z)
        continue
      }

      // =====================================
      // 💥 COLLISION
      // =====================================

      const hit =
        Math.abs(dx) <
          PLAYER_HALF_X + BONUS_HALF_X &&
        Math.abs(dz) <
          PLAYER_HALF_Z + BONUS_HALF_Z + 7

      if (hit) {

        onCollectPoints?.(POINT_VALUE)

        spawnText(
          textGroup,
          texts,
          b.x,
          b.z,
          `+${POINT_VALUE}`,
          "white"
        )

        b.z =
          -worldZ -
          (200 + Math.random() * 300)
        mesh.position.z = b.z
      }
    }
  }

  // ==================================================
  // BONUS ÉNERGIE
  // ==================================================

  if (energyGroup.current) {

    const playerPos = getPositionInFrame(cyclistRef.current, energyGroup.current, playerPosition)
    if (!playerPos) return

    const meshes = energyGroup.current.children

    for (let i = 0; i < energyBonuses.current.length; i++) {

      const b = energyBonuses.current[i]
      const mesh = meshes[i]

      if (!mesh) continue

      mesh.position.x = b.x
      mesh.position.z = b.z

      const bob =
        Math.sin(
          state.clock.elapsedTime * 2 + i + 10
        ) * 0.15

      mesh.position.y = FLOAT_Y + bob
      mesh.rotation.y += ROT_SPEED * dt

      const dz = b.z - playerPos.z
      const dx = playerPos.x - b.x

      // =====================================
      // ♻ RESPAWN
      // =====================================

      if (dz > 60 || dz < -400) {

        b.z = -worldZ - (80 + Math.random() * 220)

        b.x =
          pisteX +
          (Math.random() - 0.5) * pisteW

        mesh.position.set(b.x, mesh.position.y, b.z)
        continue
      }

      // =====================================
      // 💥 COLLISION
      // =====================================

      const hit =
        Math.abs(dx) <
          PLAYER_HALF_X + BONUS_HALF_X &&
        Math.abs(dz) <
          PLAYER_HALF_Z + BONUS_HALF_Z + 7

      if (hit) {

        onCollectEnergy?.(ENERGY_VALUE)

        spawnText(
          textGroup,
          texts,
          b.x,
          b.z,
          ENERGY_TEXT,
          ENERGY_COLOR
        )

        b.z =
          -worldZ -
          (200 + Math.random() * 300)
        mesh.position.z = b.z
      }
    }
  }

  // ==================================================
  // TEXTES FLOTTANTS
  // ==================================================

  if (textGroup.current) {

    const meshes = textGroup.current.children

    for (
      let i = texts.current.length - 1;
      i >= 0;
      i--
    ) {

      const t = texts.current[i]
      const mesh = meshes[i]

      if (!mesh) continue

      t.life -= dt
      mesh.position.x = t.x
      mesh.position.z = t.z
      mesh.position.y = FLOAT_Y + 0.8 + (1 - t.life) * 1.2
      mesh.renderOrder = 9999
      mesh.material.depthTest = false
      mesh.material.depthWrite = false
      mesh.material.transparent = true
      mesh.material.opacity = t.life

      if (t.life <= 0) {
        texts.current.splice(i, 1)
        textGroup.current.remove(mesh)
      }
    }
  }

})

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <>
      {/* BONUS POINTS */}
      <group ref={group}>
        {bonuses.current.map((b, i) => (
          <mesh key={`points-${i}`} renderOrder={9999} frustumCulled={false} >
            <planeGeometry args={[1.4, 2.2]} />
            <meshBasicMaterial
              map={texture}
              transparent={false}
              alphaTest={0.5}
              side={THREE.DoubleSide}
              depthWrite={true}
              depthTest={true}
            />
          </mesh>
        ))}
      </group>

      {/* BONUS ÉNERGIE */}
      <group ref={energyGroup}>
        {energyBonuses.current.map((b, i) => (
          <group key={i} renderOrder={9999}>
            <mesh key={`energy-${i}`} renderOrder={9999} frustumCulled={false}>
              <planeGeometry args={[1.6, 0.9]} />
              <meshBasicMaterial
                map={energyTexture}
                transparent={false}
                alphaTest={0.5}
                side={THREE.FrontSide}
                depthWrite={true}
                depthTest={true}
              />
            </mesh>
            <mesh key={`energy-back-${i}`} renderOrder={9999} frustumCulled={false} rotation-y={Math.PI}>
              <planeGeometry args={[1.6, 0.9]} />
              <meshBasicMaterial
                map={energyTexture}
                transparent={false}
                alphaTest={0.5}
                side={THREE.FrontSide}
                depthWrite={true}
                depthTest={true}
              />
            </mesh>
          </group>
        ))}
      </group>
      <group ref={textGroup} />
    </>
  )
})

function Banderole({
  leftX = -10,
  rightX = 10,
  height = 6,
  bannerHeight = 2,
  z = 0,
  text = "Bienvenue",
  poleRadius = 0.25,
  poleColor = "#000000",
  triggerZ = 0,
  range = 1000
}) {


  const { worldGroup } = useGame()
  const visibleRef = useRef(false)
   // =========================
  // TEXTURE TEXTE
  // =========================
  const textTexture = useMemo(() => {

    const canvas = document.createElement("canvas")
    canvas.width = 2048
    canvas.height = 512
    const ctx = canvas.getContext("2d")
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = "#000000"
    ctx.font = "bold 300px Arial"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(text, canvas.width / 2, canvas.height / 2)
    const texture = new THREE.CanvasTexture(canvas)
    texture.generateMipmaps = true
    texture.minFilter = THREE.LinearMipmapLinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.anisotropy = Math.min(8, window.GAME_QUALITY?.maxAnisotropy || 1)
    texture.needsUpdate = true

    return texture

  }, [text])

  // =========================
  // LOGO TEXTURE
  // =========================
  const logoTexture = useMemo(() => {
    const tex = new THREE.TextureLoader().load("/images/logoVert.png")
    tex.anisotropy = Math.min(8, window.GAME_QUALITY?.maxAnisotropy || 1)
    tex.needsUpdate = true
    return tex
  }, [])

  if (!worldGroup) return

  const width = rightX - leftX
  const centerX = (leftX + rightX) / 2
  const worldZ = worldGroup?.current?.position.z || 0
  const playerZ = -worldZ
  const buffer = 150
  const dist = Math.abs(playerZ - triggerZ)

  if (!visibleRef.current && dist < range) {
    visibleRef.current = true
  }

  if (visibleRef.current && dist > range + buffer) {
    visibleRef.current = false
  }

  const isVisible = visibleRef.current

  if (!isVisible) return null

  // =========================
  // CONFIG LOGO
  // =========================
  const logoWidth = bannerHeight * 0.9
  const logoOffset = width * 0.35 // distance du centre

  // =========================
  // RENDER
  // =========================
  return (
    <group position={[0, 0, z]}>

      {/* POTEAU GAUCHE */}
      <mesh position={[leftX, height / 2, 0]} frustumCulled={false}>
        <cylinderGeometry args={[poleRadius, poleRadius, height, 12]} />
        <meshStandardMaterial color={poleColor} />
      </mesh>

      {/* POTEAU DROIT */}
      <mesh position={[rightX, height / 2, 0]} frustumCulled={false}>
        <cylinderGeometry args={[poleRadius, poleRadius, height, 12]} />
        <meshStandardMaterial color={poleColor} />
      </mesh>

      {/* BANDEROLE */}
      <mesh position={[centerX, height - bannerHeight / 2, 0]}>
        <planeGeometry args={[width, bannerHeight]} />
        <meshBasicMaterial color="#ffff40" side={THREE.DoubleSide} />
      </mesh>

      {/* TEXTE */}
      <mesh
        position={[centerX, height - bannerHeight / 2, 0.2]}
        renderOrder={1}
      >
        <planeGeometry args={[width * 0.6, bannerHeight * 0.8]} />
        <meshBasicMaterial
          map={textTexture}
          transparent={false}
          depthWrite={false}
          color="#ffff40"
        />
      </mesh>

      {/* LOGO GAUCHE */}
      <mesh
        position={[
          centerX - logoOffset,
          height - bannerHeight / 2,
          0.21
        ]}
        renderOrder={2}
      >
        <planeGeometry args={[logoWidth * 3, logoWidth]} />
        <meshBasicMaterial
          map={logoTexture}
          transparent={true}
          depthWrite={false}
        />
      </mesh>

      {/* LOGO DROIT */}
      <mesh
        position={[
          centerX + logoOffset,
          height - bannerHeight / 2,
          0.21
        ]}
        renderOrder={2}
      >
        <planeGeometry args={[logoWidth * 3, logoWidth]} />
        <meshBasicMaterial
          map={logoTexture}
          transparent={true}
          depthWrite={false}
        />
      </mesh>

    </group>
  )
}

function Fireworks({
  triggerZ,
  worldGroup,
  onFinish,
  isInvincibleOption,
  score,
  level,
  setGameState
}) {

  const groupRef = useRef()
  const particles = useRef([])
  const hasTriggered = useRef(false)
  const snapshotRef = useRef({ score: 0, level: 0 })
  const MAX = 300
  const GROUND_Y = 0

  // =========================
  // INIT
  // =========================
  useEffect(() => {
    particles.current = Array.from({ length: MAX }, () => ({
      x: 0, y: 0, z: 0,
      vx: 0, vy: 0, vz: 0,
      life: 0,
      active: false,
      color: new THREE.Color(`hsl(${Math.random() * 360},100%,60%)`)
    }))
  }, [])

  // =========================
  // SNAPSHOT LIVE
  // =========================
  useEffect(() => {
    snapshotRef.current = { score, level }
  }, [score, level])

  // =========================
  // EXPLOSION
  // =========================
  const spawnRing = (worldZ) => {
    const centerX = -10
    const centerZ = -worldZ - 25
    const centerY = 14 + Math.random() * 6
    const count = 60
    const radius = 1.8
    const speed = 9 + Math.random() * 4

    for (let i = 0; i < count; i++) {

      const p = particles.current.find(p => !p.active)
      if (!p) break

      const angle = (i / count) * Math.PI * 2

      p.x = centerX + Math.cos(angle) * radius
      p.z = centerZ + Math.sin(angle) * radius
      p.y = centerY
      p.vx = Math.cos(angle) * speed
      p.vz = Math.sin(angle) * speed
      p.vy = 6 + Math.random() * 4
      p.life = 1.8 + Math.random()
      p.active = true
    }
  }

  // =========================
  // UPDATE
  // =========================
  useFrame((_, dt) => {

    const group = groupRef.current
    if (!group) return
    if (!worldGroup) return

    const worldZ = worldGroup?.current?.position.z || 0
    const triggered = worldZ > -triggerZ

    // =========================
    // TRIGGER
    // =========================
    if (triggered && !hasTriggered.current) {
      hasTriggered.current = true

      // 💥 burst initial
      for (let i = 0; i < 3; i++) {
        spawnRing(worldZ)
      }

      setGameState("gameOver")

      // ✅ snapshot figé IMMÉDIATEMENT
      const finalSnapshot = {
        score: snapshotRef.current.score,
        level: snapshotRef.current.level
      }

      setTimeout(() => { if (onFinish && !isInvincibleOption) { onFinish(finalSnapshot) } }, 5000)
    }

    // =========================
    // FIREWORKS CONTINUS
    // =========================
    if (hasTriggered.current) {
      if (Math.random() < 0.08) {
        spawnRing(worldZ)
      }
    }

    // =====================
    // PHYSICS
    // =====================
    for (let i = 0; i < particles.current.length; i++) {

      const p = particles.current[i]
      if (!p.active) continue

      p.vy -= 9 * dt
      p.vx *= 0.99
      p.vz *= 0.99
      p.vy *= 0.98
      p.x += p.vx * dt
      p.y += p.vy * dt
      p.z += p.vz * dt

      if (p.y <= GROUND_Y) {
        p.y = GROUND_Y
        p.active = false
      }

      p.life -= dt

      if (p.life <= 0) {
        p.active = false
      }
    }

    // =====================
    // APPLY
    // =====================
    for (let i = 0; i < particles.current.length; i++) {

      const p = particles.current[i]
      const mesh = group.children[i]
      if (!mesh) continue

      if (!p.active) {
        mesh.visible = false
        continue
      }

      mesh.visible = true
      mesh.position.set(p.x, p.y, p.z)
      mesh.material.color = p.color
    }

  })

  // =========================
  // RENDER
  // =========================
  return (
    <group ref={groupRef}>
      {Array.from({ length: MAX }).map((_, i) => (
        <mesh key={i} visible={false}>
          <sphereGeometry args={[0.22, 8, 8]} />
          <meshBasicMaterial toneMapped={false} />
        </mesh>
      ))}
    </group>
  )
}
const TrafficLights = React.forwardRef(({
  intersections = [],
  greenTime = 10,
  yellowTime = 2.5,
  redTime = 8,
  scale = 1.2
}, ref) => {

  const TOTAL = greenTime + yellowTime + redTime
  const [time, setTime] = React.useState(0)

  // Horloge globale
  useFrame((_, dt) => {
    setTime(t => (t + dt) % TOTAL)
  })

  // États accessibles aux véhicules
  const statesRef = React.useRef([])

  useImperativeHandle(ref, () => ({
    getStates: () => statesRef.current
  }))

  // Matériaux
  const housingMat = React.useMemo(
    () => new THREE.MeshStandardMaterial({
      color: "#111",
      roughness: 0.6,
      metalness: 0.2
    }),
    []
  )

  const poleMat = React.useMemo(
    () => new THREE.MeshStandardMaterial({
      color: "#333",
      roughness: 0.7,
      metalness: 0.3
    }),
    []
  )

  const makeLightMat = (color, isOn) => {
    return new THREE.MeshBasicMaterial({
      color: isOn ? color : "#1a1a1a",
      toneMapped: false
    })
  }

  // 🔥 RESET du tableau à chaque render
  statesRef.current = []

  return (
    <group>

      {intersections.map((inter, i) => {
        const levelIntersections = getLevelFromZ(inter.z)
        const isFromDesSeigneurs =
          inter.z <= DES_SEIGNEURS_INTERSECTION_Z

        if (
          !TRAFFIC_LIGHT_DISABLED_INTERSECTIONS.has(inter.id) &&
          !(levelIntersections===11 || (levelIntersections>=14 && !isFromDesSeigneurs))
        )
        {
          // =========================================
          // 📍 POSITION
          // =========================================

          const position = [-3.6, 0, inter.z - 10]

          // =========================================
          // ⏱️ TIMING DÉCALÉ
          // =========================================

          const offset = i * (TOTAL / Math.max(1, intersections.length))
          const localTime = (time + offset) % TOTAL

          let localState = "red"

          if (localTime < greenTime) localState = "green"
          else if (localTime < greenTime + yellowTime) localState = "yellow"
          else localState = "red"

          // =========================================
          // ⭐ ÉTAT EXPORTÉ (SANS NULL)
          // =========================================

          statesRef.current.push({
            state: localState,
            position,
            intersectionId: inter.id
          })

          const redOn = localState === "red"
          const yellowOn = localState === "yellow"
          const greenOn = localState === "green"

          return (

            <group
              key={inter.id}
              position={position}
              rotation={[0, 0, 0]}
              scale={scale}
            >

              {/* 🧱 MÂT */}
              <mesh position={[0, 2.5, 0]} material={poleMat}>
                <cylinderGeometry args={[0.16, 0.16, 5, 12]} />
              </mesh>

              {/* 🧱 BOÎTIER */}
              <mesh position={[0, 4.5, 0]} material={housingMat}>
                <boxGeometry args={[0.7, 2.1, 0.7]} />
              </mesh>

              {/* 🔴 ROUGE */}
              <group position={[0, 5.2, 0.38]}>
                <mesh material={makeLightMat("#ff3b3b", redOn)}>
                  <circleGeometry args={[0.22, 32]} />
                </mesh>
              </group>

              {/* 🟡 JAUNE */}
              <group position={[0, 4.5, 0.38]}>
                <mesh material={makeLightMat("#ffd84d", yellowOn)}>
                  <circleGeometry args={[0.22, 32]} />
                </mesh>
              </group>

              {/* 🟢 VERT */}
              <group position={[0, 3.8, 0.38]}>
                <mesh material={makeLightMat("#4cff4c", greenOn)}>
                  <circleGeometry args={[0.22, 32]} />
                </mesh>
              </group>
            </group>

          )
        }
      })}

    </group>
  )
})



// --------------------------------------------------
// BOLLARDS
// --------------------------------------------------
function isInLevel(z, level) {
  const start = getLevelStartZ(level)
  const end = start - LEVEL_EFFECTIVE_LENGTH[level]

  return z <= start && z >= end
}

const Bollards = React.forwardRef(({x, texturePath = "/textures/poteau.jpg"}, ref) => {

  const { worldGroup } = useGame()
  const meshRef = useRef()
  const bollards = useRef([])

  // =====================================
  // TEXTURE
  // =====================================

  const texture = useLoader(THREE.TextureLoader, texturePath)

  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter
  texture.generateMipmaps = true;
  texture.anisotropy = Math.min(8, window.GAME_QUALITY?.maxAnisotropy || 1)

  // =====================================
  // CONSTANTS
  // =====================================

  const COUNT = 160

  // =====================================
  // GEOMETRY
  // =====================================

  const geometry = useMemo(() => {
    return new THREE.CylinderGeometry(0.1, 0.1, 2, 32)
  }, [])

  // =====================================
  // MATERIAL
  // =====================================

  const material = useMemo(() => {
  return new THREE.MeshBasicMaterial({
    map: texture,
    depthWrite: true,
    side: THREE.FrontSide,
  });
}, [texture]);

  // =====================================
  // INITIAL SPAWN
  // =====================================

  useEffect(() => {

    if (!meshRef.current) return

    const temp = new THREE.Object3D()

    bollards.current = []
    let index = 0

    for (let i = 0; i < COUNT; i++) {

      const z = -i * 15

      if (!isNearIntersection(z, intersections)) {

        temp.position.set(x, 0.85, z)
        temp.updateMatrix()
        meshRef.current.setMatrixAt(index, temp.matrix)
        bollards.current.push({ x: x, z: z })
        index++
      }
    }

    meshRef.current.count = index
    meshRef.current.instanceMatrix.needsUpdate = true

  }, [intersections, x])

  // =====================================
  // LOOP
  // =====================================

  useFrame(() => {

    if (!meshRef.current || !worldGroup?.current) return

    const worldZ = worldGroup?.current?.position.z || 0
    const temp = new THREE.Object3D()

    for (let i = 0; i < bollards.current.length; i++) {

      const b = bollards.current[i]
      const visibleZ = b.z + worldZ
      const SPACING = 15
      const LOOP = SPACING * COUNT

      // ===============================
      // FORWARD
      // ===============================

      if (visibleZ > SPACING) {

        let newZ = b.z - LOOP
        let attempts = 0

        while (isNearIntersection(newZ, intersections) && attempts < COUNT) {

          newZ -= SPACING
          attempts++
        }

        b.z = newZ
      }

      // ===============================
      // BACKWARD
      // ===============================

      if (visibleZ < -LOOP + SPACING) {

        let newZ = b.z + LOOP
        let attempts = 0

        while (isNearIntersection(newZ, intersections) &&  attempts < COUNT) {

          newZ += SPACING
          attempts++
        }

        b.z = newZ
      }

      // ===============================
      // X OFFSET
      // ===============================

      let offsetX = 0

      switch (true) {

        case isInLevel(b.z, 11):
          offsetX = 3.5
          break

        case isInLevel(b.z, 12):
          offsetX = 0.5
          break

        case isInLevel(b.z, 13):
          offsetX = 0.5
          break

        default:
          offsetX = 0
        break
      }

      b.x = x + offsetX

      temp.position.set(b.x, 0.85, b.z)
      temp.updateMatrix()
      meshRef.current.setMatrixAt(i, temp.matrix)
    }

    meshRef.current.instanceMatrix.needsUpdate = true
  })

  // =====================================
  // RENDER
  // =====================================

  return (

    <instancedMesh
      ref={(node) => {
        meshRef.current = node

        if (ref) {
          ref.current = {
            mesh: node,
            get bollards() { return bollards.current }
          }
        }
      }}
      args={[
        geometry,
        material,
        COUNT
      ]}
      frustumCulled={false}
    />
  )
})

const RandomCones = React.forwardRef(
({ pisteX, pisteW, trottoirX, trottoirW, poteauxX, poteauxW, density, difficulte }, ref) => {

  const { worldGroup, level, departOption, restartVersion } = useGame()
  const meshRef = useRef()
  const materialRef = useRef()
  const cones = useRef([])
  const temp = useMemo(() => new THREE.Object3D(), [])
  const texture = useTexture("/textures/cone.jpg")

  useEffect(() => {
    texture.colorSpace = THREE.SRGBColorSpace
    texture.generateMipmaps = true
    texture.anisotropy = Math.min(8, window.GAME_QUALITY?.maxAnisotropy || 1)
    texture.needsUpdate = true

    if (materialRef.current) {
      materialRef.current.needsUpdate = true
    }
  }, [texture])

  const MAX_CONES = 80

  const getTargetCount = () => {

    const levelCurve = 0.15 + (1 - Math.exp(-level * 0.06)) * 0.45
    const difficultyFactor = [0.75, 1.0, 1.3][difficulte - 1]
    const final = levelCurve * difficultyFactor * (0.6 + density * 0.8)

    return Math.min(MAX_CONES, Math.max(12, Math.floor(MAX_CONES * final)))
  }


const CONE_HEIGHT = 1.7
const BASE_WIDTH = CONE_HEIGHT * 0.36
const TOP_WIDTH = BASE_WIDTH * 0.7
const BASE_Y = 0
const BASE_OFFSET = BASE_WIDTH * 0.3

// ===============================
// SPAWN ORIGINAL
// ===============================

const spawn = (allowVisible = false) => {

  const currentWorldZ = worldGroup?.current?.position?.z || 0

  const r = Math.random()
  let x

  if (r < 0.45)
    x = pisteX + (Math.random() - 0.5) * (pisteW * 0.75)
  else if (r < 0.75)
    x = poteauxX + (Math.random() - 0.5) * (poteauxW * 0.75)
  else
    x = trottoirX + (Math.random() - 0.5) * (trottoirW * 0.75)

  let z

  if (allowVisible) {

    const playerZ = -currentWorldZ

    z = playerZ - (20 + Math.random() * 60)

  } else {

    do {

      z = -currentWorldZ - (120 + Math.random() * 260)

    } while (isNearIntersection(z, intersections))

  }

  return {
    x,
    z,
    rotX: 0,
    rotZ: 0,
    vel: 0,
    hit: false,
    locked: false,
    dir: 0,
    axis: "z"
  }
}

// ===============================
// SPAWN TELEPORT
// ===============================

const spawnTeleport = () => {

  const currentWorldZ = worldGroup?.current?.position?.z || 0
  const playerZ = -currentWorldZ
  const base = spawn(false)

  base.z = playerZ - 40 + Math.random() * 120

  return base
}

// ===============================
// RESET TELEPORT
// ===============================

useEffect(() => {

  if (!worldGroup?.current) return

  const id = requestAnimationFrame(() => {

    cones.current = []

    const target = getTargetCount()

    for (let i = 0; i < target; i++) {
      cones.current.push(spawnTeleport())
    }

  })

  return () => cancelAnimationFrame(id)

}, [departOption, restartVersion])

// ===============================
// INIT ORIGINAL
// ===============================

useEffect(() => {

  if (!worldGroup?.current) return

  cones.current = []

  const target = getTargetCount()

  for (let i = 0; i < target; i++) {
    cones.current.push(spawn(true))
  }

}, [restartVersion])

// ===============================
// ADAPTATION
// ===============================

useEffect(() => {

  if (!worldGroup?.current) return

  const target = getTargetCount()
  const current = cones.current.length

  if (target > current) {

    for (let i = current; i < target; i++) {
      cones.current.push(spawn(false))
    }

  }

  if (target < current) {
    cones.current.splice(target)
  }

}, [level, difficulte, density])

// ===============================
// UPDATE
// ===============================

useFrame((_, dt) => {

  if (!worldGroup?.current) return

  // update logique ici

    const mesh = meshRef.current
    if (!mesh) return

    mesh.__data = cones.current

    for (let i = 0; i < cones.current.length; i++) {

      const c = cones.current[i]
      const currentWorldZ = worldGroup?.current?.position?.z || 0
      const visibleZ = c.z + currentWorldZ

      if (visibleZ > 50) {
        const n = spawn(false)
        Object.assign(c, n, {
          vel: 0,
          locked: false,
          hit: false
        })
      }

      if (visibleZ < -300) {
        const n = spawn(false)
        Object.assign(c, n, {
          vel: 0,
          locked: false,
          hit: false
        })
      }

      if (c.hit && !c.locked) {

        const target =
          c.axis === "x"
            ? -Math.PI * 0.5
            : c.dir * Math.PI * 0.5

        c.vel += 20 * dt
        const delta = c.vel * dt

        if (c.axis === "z") {

          c.rotX = 0
          c.rotZ += delta * Math.sign(target)

          if (
            (target > 0 && c.rotZ >= target) ||
            (target < 0 && c.rotZ <= target)
          ) {
            c.rotZ = target
            c.vel = 0
            c.locked = true
          }

        } else {

          c.rotZ = 0
          c.rotX += delta * Math.sign(target)

          if (
            (target > 0 && c.rotX >= target) ||
            (target < 0 && c.rotX <= target)
          ) {
            c.rotX = target
            c.vel = 0
            c.locked = true
          }
        }
      }

      const y = BASE_Y + BASE_OFFSET
      if (!temp) continue
      temp.position.set(c.x, y, c.z)
      temp.rotation.set(c.rotX, 0, c.rotZ)
      temp.scale.set(1, 1, 1)

      temp.updateMatrix()
      mesh.setMatrixAt(i, temp.matrix)
    }

    mesh.count = cones.current.length
    mesh.instanceMatrix.needsUpdate = true

  })

  const geometry = useMemo(() => {

    const geo = new THREE.CylinderGeometry(
      TOP_WIDTH / 2,
      BASE_WIDTH / 2,
      CONE_HEIGHT,
      16,
      1
    )

    geo.translate(0, CONE_HEIGHT / 2, 0)

    return geo

  }, [])

  const material = useMemo(() => {

    const mat = new THREE.MeshBasicMaterial({
      map: texture,
      color: new THREE.Color(1,1,1),
      alphaTest: 0.5,
      toneMapped: false
    })

    materialRef.current = mat
    return mat

  }, [texture])

  return (
    <instancedMesh
      ref={(node)=>{
        meshRef.current = node
        if (ref) ref.current = node
      }}
      args={[geometry, material, MAX_CONES]}
      frustumCulled={false}
    />
  )
})

const POTHOLE_GEOMETRY = new THREE.PlaneGeometry(1.4, 1.4)
const RandomPotholes =React.memo(
React.forwardRef(
({
  pisteX,
  pisteW,
  routeX,
  routeW,
  trottoirX,
  trottoirW,
  poteauxX,
  poteauxW,
  density,
  difficulte
}, ref) => {

  const { worldGroup, level, departOption, restartVersion } = useGame()
  const group = useRef()
  const meshRef = useRef()
  const materialRef = useRef()
  const potholes = useRef([])
  const initialFillDone = useRef(false)
  const frameCount = useRef(0)
  const MAX_POTHOLES = 200

  const getTargetCount = () => {
    const difficultyFactor = 1 + difficulte * 0.6
    const levelFactor = 1 + level * 0.08

    return Math.min(
      MAX_POTHOLES,
      Math.max(10, Math.floor(MAX_POTHOLES * density * difficultyFactor * levelFactor))
    )
  }

  const SPAWN_AHEAD_MIN = 80
  const SPAWN_AHEAD_MAX = 200
  const VISIBLE_BACK = 20
  const VISIBLE_FRONT = 80
  const DESPAWN_Z = 60

  const applyCommunePotholeX = (
    x,
    z
  ) => {

    if (z <= commune.z) {
      return pisteX * 2 - x
    }

    return x
  }

  // ==================================================
  // TEXTURE
  // ==================================================

  const texture = useTexture("/textures/pothole.png")

  useEffect(() => {

    texture.colorSpace = THREE.SRGBColorSpace
    texture.generateMipmaps = true
    texture.minFilter = THREE.NearestMipmapLinearFilter 
    texture.magFilter = THREE.LinearFilter
    texture.anisotropy =  Math.min(8, window.GAME_QUALITY?.maxAnisotropy || 1)
    texture.needsUpdate = true

  }, [texture])

  // ==================================================
  // MATERIAL
  // ==================================================

  const material = useMemo(() => {

    const mat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: false
    })

    materialRef.current = mat
    return mat

  }, [texture])

  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.needsUpdate = true
    }
  }, [texture])

  // ==================================================
  // SPAWN (inchangé)
  // ==================================================

  const spawn = (allowVisible = false) => {

    const r = Math.random()
    let x, scale
    const RIGHT_SHOULDER_BIAS = 0.35

    if (Math.random() < RIGHT_SHOULDER_BIAS) {
      const rightEdge = routeX + routeW * 0.5
      x = rightEdge - Math.random() * routeW * 0.18
      scale = 1.1 + Math.random() * 1.9
    }
    else if (r < 0.5) {
      x = routeX + (Math.random() - 0.5) * routeW * 0.9
      scale = 1 + Math.random() * 1.8
    }
    else if (r < 0.65) {
      x = pisteX + (Math.random() - 0.5) * pisteW
      scale = 0.8 + Math.random() * 1.4
    }
    else if (r < 0.8) {
      x = poteauxX + (Math.random() - 0.5) * poteauxW
      scale = 0.6 + Math.random() * 1.1
    }
    else {
      x = trottoirX + (Math.random() - 0.5) * trottoirW
      scale = 0.5 + Math.random() * 0.9
    }

    let z

    const currentWorldZ = worldGroup?.current?.position?.z || 0

    if (allowVisible) {
      z = -currentWorldZ - (VISIBLE_BACK + Math.random() * (VISIBLE_FRONT - VISIBLE_BACK))
    } else {
      z = -currentWorldZ - (SPAWN_AHEAD_MIN + Math.random() * (SPAWN_AHEAD_MAX - SPAWN_AHEAD_MIN))
    }

    x = applyCommunePotholeX(x, z)

    return {
      x,
      z,
      rot: Math.random() * Math.PI * 2,
      scale
    }
  }
  
  // ==================================================
  // SPAWN CHANGEMENT DE LEVEL OU DENSITY
  // ==================================================

  useEffect(() => {

  if (!worldGroup?.current) return

  const currentWorldZ = worldGroup.current?.position?.z
  const playerZ = -currentWorldZ
  const target = getTargetCount()
  const current = potholes.current.length

  if (current >= target) return

  const toAdd = target - current

  for (let i = 0; i < toAdd; i++) {

    const p = spawn(false)

    // 🔥 IMPORTANT → forcer loin devant
    p.z = playerZ + SPAWN_AHEAD_MIN + Math.random() * (SPAWN_AHEAD_MAX)
    p.x = applyCommunePotholeX(p.x, p.z)

    potholes.current.push(p)
  }

}, [level, density])


  // ==================================================
  // 🔥 SPAWN TELEPORT (AJOUT)
  // ==================================================

  const spawnTeleport = () => {

    if (!worldGroup) return

    const currentWorldZ = worldGroup.current?.position?.z
    const playerZ = -currentWorldZ
    const base = spawn(false)

    base.z = playerZ - 40 + Math.random() * 120
    base.x = applyCommunePotholeX(base.x, base.z)

    return base
  }

  // ==================================================
  // 🔥 RESET TELEPORT (AJOUT)
  // ==================================================

  useEffect(() => {

  if (!worldGroup?.current) return

    const id = requestAnimationFrame(() => {

    potholes.current = []
    initialFillDone.current = false
    frameCount.current = 0

    const target = getTargetCount()

    for (let i = 0; i < target; i++) {
      potholes.current.push(spawnTeleport())
    }

  })

  return () => cancelAnimationFrame(id)

}, [departOption, difficulte, restartVersion])

  // ==================================================
  // INIT (inchangé)
  // ==================================================

  useEffect(() => {

    if (!worldGroup?.current) return
    potholes.current = []

    const target = getTargetCount()

    for (let i = 0; i < target; i++) {
      const allowVisible = !initialFillDone.current && Math.random() < 0.5
      potholes.current.push(spawn(allowVisible))
    }

  }, [restartVersion])

  // ==================================================
  // UPDATE (inchangé)
  // ==================================================

  const temp = useMemo(() => new THREE.Object3D(), [])

  useFrame(() => {

    const currentWorldZ = worldGroup?.current?.position?.z
    if (currentWorldZ == null) return

    const mesh = meshRef.current
    if (!mesh) return

    mesh.__data = potholes.current
    frameCount.current++

    if (frameCount.current > 60 && !initialFillDone.current) {
      initialFillDone.current = true
    }

    for (let i = 0; i < potholes.current.length; i++) {

      const p = potholes.current[i]
      const currentWorldZ = worldGroup?.current?.position?.z || 0
      const visibleZ = p.z + currentWorldZ

      if (visibleZ > DESPAWN_Z) {

        const n = spawn(false)

        p.x = n.x
        p.z = -currentWorldZ - (SPAWN_AHEAD_MIN + Math.random() * (SPAWN_AHEAD_MAX - SPAWN_AHEAD_MIN))
        p.x = applyCommunePotholeX(p.x, p.z)
        p.rot = n.rot
        p.scale = n.scale
      }

      if (visibleZ < -SPAWN_AHEAD_MAX * 2) {

        const n = spawn(false)

        p.x = n.x
        p.z = -currentWorldZ - (SPAWN_AHEAD_MIN + Math.random() * (SPAWN_AHEAD_MAX - SPAWN_AHEAD_MIN))
        p.x = applyCommunePotholeX(p.x, p.z)
        p.rot = n.rot
        p.scale = n.scale
      }

      if (!temp) continue

      temp.position.set(p.x, 0.09, p.z)
      temp.rotation.set(-Math.PI / 2, 0, p.rot)
      temp.scale.set(p.scale, p.scale, p.scale)
      temp.updateMatrix()
      mesh.setMatrixAt(i, temp.matrix)
    }

    mesh.count = potholes.current.length
    mesh.instanceMatrix.needsUpdate = true

  })

  return (
    <group ref={(node)=>{
      group.current = node
      if (ref) ref.current = node
    }}>
      <instancedMesh
        ref={meshRef}
        args={[ POTHOLE_GEOMETRY, material, MAX_POTHOLES]}
        frustumCulled={false}
        renderOrder={10}
      />
    </group>
  )
}))

const RandomBacs = React.forwardRef(
({ pisteX, pisteW, trottoirX, trottoirW, density = 0.4, difficulte }, ref) => {

  const { worldGroup, level, departOption, restartVersion } = useGame()

  const MAX_BACS = 30
  const difficultyFactor = [0.6, 1.0, 1.6][difficulte - 1]
  const count = Math.min(MAX_BACS * 0.65, Math.max(10, Math.floor(MAX_BACS * density * difficultyFactor)))
  const bacs = useRef([])
  const recycleRef = useRef()
  const compostRef = useRef()
  const trashRef = useRef()
  const groupRef = useRef()
  const dummy = useMemo(()=>new THREE.Object3D(),[])

  const textures = useLoader(
    THREE.TextureLoader,
    [
      "/textures/bacRecyclage.png",
      "/textures/bacCompost.png",
      "/textures/poubelle.png"
    ]
  )

  textures.forEach(t=>{ t.colorSpace = THREE.SRGBColorSpace })

  const geometry = useMemo(()=>{
    const g = new THREE.PlaneGeometry(1.7,2.5)
    g.translate(0,1.25,0)
    return g
  },[])

  const materials = useMemo(()=>[
    new THREE.MeshBasicMaterial({ map:textures[0], alphaTest:0.5, toneMapped:false }),
    new THREE.MeshBasicMaterial({ map:textures[1], alphaTest:0.5, toneMapped:false }),
    new THREE.MeshBasicMaterial({ map:textures[2], alphaTest:0.5, toneMapped:false })
  ],[textures])

  /* -------------------------------------------------- */
  /* 🔥 RESET AU TELEPORT                               */
  /* -------------------------------------------------- */

  useEffect(()=>{

    if (!worldGroup?.current) return

    const id = requestAnimationFrame(()=>{

      const currentWorldZ = worldGroup.current?.position?.z
      const playerZ = -currentWorldZ

      bacs.current = []

      for(let i=0;i<count;i++){

        const onBikeLane = Math.random() < 0.35
        const x = onBikeLane
          ? pisteX + (Math.random()-0.5)*pisteW*0.7
          : trottoirX + (Math.random()-0.5)*trottoirW*0.7

        const type = Math.floor(Math.random()*3)

        let z
        do{
          z = playerZ - 40 + Math.random()*120
        }
        while(isNearIntersection(z, intersections))

        bacs.current.push({
          x,
          z,
          type,
          hit:false,
          dir:0,
          axis:"z",
          rotX:0,
          rotZ:0
        })
      }

    })

    return ()=>cancelAnimationFrame(id)

  },[departOption, difficulte, restartVersion])

  /* -------------------------------------------------- */
  /* INIT + ADAPTATION (MODIFIÉ)                        */
  /* -------------------------------------------------- */

  useEffect(()=>{

    const currentWorldZ = worldGroup?.current?.position.z || 0
    const playerZ = -currentWorldZ

    // ➕ AJOUTER
    while(bacs.current.length < count){

      const onBikeLane = Math.random() < 0.35
      const x = onBikeLane
        ? pisteX + (Math.random()-0.5)*pisteW*0.7
        : trottoirX + (Math.random()-0.5)*trottoirW*0.7

      const type = Math.floor(Math.random()*3)

      let z
      do{
        z = playerZ + 120 + Math.random()*200
      }
      while(isNearIntersection(z, intersections))

      bacs.current.push({
        x,
        z,
        type,
        hit:false,
        dir:0,
        axis:"z",
        rotX:0,
        rotZ:0
      })
    }

    // ➖ SUPPRIMER
    if (bacs.current.length > count) {
      bacs.current.splice(count)
    }

  },[level, difficulte, density])

  /* -------------------------------------------------- */
  /* UPDATE (inchangé)                                  */
  /* -------------------------------------------------- */

  useFrame((_,dt)=>{

    const currentWorldZ = worldGroup?.current?.position?.z || 0
    const refs = [recycleRef.current, compostRef.current, trashRef.current]
    const counters = [0,0,0]

    // ==================================================
    // 🔥 DATA PAR INSTANCED MESH
    // ==================================================

    const recycleData = []
    const compostData = []
    const trashData = []

    for(let i=0;i<bacs.current.length;i++){

      const data = bacs.current[i]
      const visibleZ = data.z + currentWorldZ

      if (visibleZ > -120 && visibleZ < 160)
        data.active = true

      if(visibleZ > 60){

        const onBikeLane = Math.random() < 0.35

        data.x = onBikeLane
          ? pisteX + (Math.random()-0.5)*pisteW*0.7
          : trottoirX + (Math.random()-0.5)*trottoirW*0.7

        let z
        do{
          z = -currentWorldZ - (220 + Math.random()*180)
        }
        while(isNearIntersection(z, intersections))

        data.z = z
        data.hit = false
        data.rotX = 0
        data.rotZ = 0
      }

      if(visibleZ < -400){

        const onBikeLane = Math.random() < 0.35

        data.x = onBikeLane
          ? pisteX + (Math.random()-0.5)*pisteW*0.7
          : trottoirX + (Math.random()-0.5)*trottoirW*0.7

        let z
        do{
          z = -currentWorldZ - (220 + Math.random()*180)
        }
        while(isNearIntersection(z, intersections))

        data.z = z
        data.hit = false
        data.rotX = 0
        data.rotZ = 0
      }

      if(data.hit){

        const target = data.axis==="x" ? -Math.PI*0.38 : data.dir*Math.PI*0.38

        if(data.axis==="z"){
          data.rotZ = THREE.MathUtils.lerp(data.rotZ, target, 10*dt)
        } else {
          data.rotX = THREE.MathUtils.lerp(data.rotX, target, 10*dt)
        }
      }

      const type = data.type
      const id = counters[type]++
      const mesh = refs[type]

      if(!mesh) continue

      // ==================================================
      // 🔥 ASSOCIER BONNE DATA AU BON MESH
      // ==================================================

      if(type===0) recycleData.push(data)
      if(type===1) compostData.push(data)
      if(type===2) trashData.push(data)

      let scale = 1

      if(type===1) scale = 0.6
      if(type===2) scale = 0.8

      if (!dummy) continue

      dummy.position.set(data.x,0,data.z)
      dummy.rotation.set(data.rotX,0,data.rotZ)
      dummy.scale.setScalar(scale)
      dummy.updateMatrix()
      mesh.setMatrixAt(id,dummy.matrix)
    }

    // ==================================================
    // 🔥 __data CORRECT PAR TYPE
    // ==================================================

    if (recycleRef.current) {
      recycleRef.current.__data = recycleData
    }

    if (compostRef.current) {
      compostRef.current.__data = compostData
    }

    if (trashRef.current) {
      trashRef.current.__data = trashData
    }

    refs.forEach((m,i)=>{

      if(!m) return

      m.count = counters[i]
      m.instanceMatrix.needsUpdate = true
    })

  })

  useImperativeHandle(ref,()=>({
    group: groupRef.current,
    get children() {
      return bacs.current.map(b => ({ position: b, userData: b }))
    }
  }))

  return(
    <group ref={groupRef}>
      <instancedMesh ref={recycleRef} args={[geometry,materials[0],MAX_BACS]} frustumCulled={false}/>
      <instancedMesh ref={compostRef} args={[geometry,materials[1],MAX_BACS]} frustumCulled={false}/>
      <instancedMesh ref={trashRef} args={[geometry,materials[2],MAX_BACS]} frustumCulled={false}/>
    </group>
  )
})
const BASE_TEXTURE_HEIGHT = 256
const WALL_HEIGHT = 13
const HEIGHT_SCALE = 1.5
const loader = new THREE.TextureLoader()
const textureCache = new Map()
const textureLoadPromises = new Map()
const globalTextureCache = new Map()
const preloadedLevels = new Set()
const LEVEL_LENGTH_CACHE = {}
const PRIORITY_WALL_TEXTURE_COUNT = 4
const TURN_WALL_PRELOAD_DISTANCE = 900


function getLevelLength(level) {

  return LEVEL_EFFECTIVE_LENGTH[level]
}
function getLevelStartZ(level) {

  const index = LEVELS.indexOf(level)
  if (index === -1) return 0

  let z = 0

  for (let i = 0; i < index; i++) {

    const lvl = LEVELS[i]

    const length = getLevelLength(lvl)
    if (!length) continue

    z -= length

  }
  return z
}
function getLevelFromZ(z) {
  if (!LEVELS.length) return LEVELS[0]

  for (let i = 0; i < LEVELS.length; i++) {
    const level = LEVELS[i]
    const startZ = getLevelStartZ(level)
    const length = getLevelLength(level)

    if (!length) continue

    const endZ = startZ - length

    // ⚠️ Z négatif → intervalle inversé
    if (z <= startZ && z > endZ) {
      return level
    }
  }

  // fallback
  return LEVELS[LEVELS.length - 1]
}

function computeDimensionsFromTexture(texture) {
  if (!texture?.image) return null

  // 🔥 cache directement sur la texture (ultra rapide)
  if (texture.userData?.dims) {
    return texture.userData.dims
  }

  const img = texture.image
  const heightFactor = img.height / BASE_TEXTURE_HEIGHT
  const height = WALL_HEIGHT * HEIGHT_SCALE * heightFactor
  const ratio = img.width / img.height
  const width = height * ratio
  const dims = { width, height }

  texture.userData = texture.userData || {}
  texture.userData.dims = dims

  return dims
}

function getWallTexturePath(level, side, index) {
  const levelInfo = LEVEL_TEXTURES[level]
  const suffix =
    window.GAME_QUALITY?.texturesLow && levelInfo?.low
      ? "_low"
      : ""

  return {
    path: `/decors/niveau${level}/${side}${index}${suffix}.webp`,
    normalPath: `/decors/niveau${level}/${side}${index}.webp`
  }
}

function prepareWallTexture(tex, level) {
  if (!tex?.image) {
    return tex
  }

  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  tex.magFilter = THREE.LinearFilter
  tex.anisotropy = Math.min(8, window.GAME_QUALITY?.maxAnisotropy || 1)

  const hasTurn = TOURNANTS.some(t => t.level === level)
  const useMipmaps =
    hasTurn ||
    !window.GAME_QUALITY?.low

  tex.generateMipmaps = useMipmaps
  tex.minFilter =
    useMipmaps
      ? THREE.LinearMipmapLinearFilter
      : THREE.LinearFilter

  return tex
}

async function loadWallTexture(level, side, index) {
  const { path, normalPath } = getWallTexturePath(level, side, index)
  const cached =
    textureCache.get(path) ||
    textureCache.get(normalPath)

  if (cached) {
    return cached
  }

  const promiseKey = normalPath

  if (textureLoadPromises.has(promiseKey)) {
    return textureLoadPromises.get(promiseKey)
  }

  const promise = loader
    .loadAsync(path)
    .then(tex => {
      if (!tex?.image) {
        return null
      }

      prepareWallTexture(tex, level)
      textureCache.set(path, tex)
      textureCache.set(normalPath, tex)

      return tex
    })
    .catch(err => {
      console.warn(
        "TEXTURE FAILED:",
        path,
        err
      )

      return null
    })
    .finally(() => {
      textureLoadPromises.delete(promiseKey)
    })

  textureLoadPromises.set(promiseKey, promise)

  return promise
}

function getWallBasePath(level, side) {
  return `/decors/niveau${level}/${side}`
}

function getCachedWallTexture(level, side, index) {
  const { path, normalPath } = getWallTexturePath(level, side, index)

  return (
    textureCache.get(path) ||
    textureCache.get(normalPath) ||
    null
  )
}

function getCachedWallTextureList(level, side) {
  const levelInfo = LEVEL_TEXTURES[level]

  if (!levelInfo) {
    return null
  }

  const basePath = getWallBasePath(level, side)
  const cachedList = globalTextureCache.get(basePath)

  if (
    cachedList?.length === levelInfo.count &&
    cachedList.every(tex => tex?.image)
  ) {
    return cachedList
  }

  const textures = []

  for (let i = 1; i <= levelInfo.count; i++) {
    const tex = getCachedWallTexture(level, side, i)

    if (!tex?.image) {
      return null
    }

    textures[i - 1] = tex
  }

  globalTextureCache.set(basePath, textures)
  return textures
}

function areLevelWallTexturesReady(level) {
  return (
    Boolean(getCachedWallTextureList(level, "Est")) &&
    Boolean(getCachedWallTextureList(level, "Ouest"))
  )
}

async function preloadPriorityWallTextures(level) {
  const levelInfo = LEVEL_TEXTURES[level]

  if (!levelInfo) {
    return
  }

  const count = Math.min(
    PRIORITY_WALL_TEXTURE_COUNT,
    levelInfo.count
  )

  const jobs = []

  for (let i = 1; i <= count; i++) {
    jobs.push(loadWallTexture(level, "Est", i))
    jobs.push(loadWallTexture(level, "Ouest", i))
  }

  await Promise.all(jobs)
}

function useMultiTextures(basePath) {

  const [textures, setTextures] =
    React.useState(() => (
      globalTextureCache.get(basePath) || []
    ))

  React.useLayoutEffect(() => {

    let cancelled = false

    async function load() {

      // ============================================
      // CACHE GLOBAL
      // ============================================

      const cachedTextures = globalTextureCache.get(basePath)

      if (cachedTextures) {

        setTextures(
          cachedTextures
        )

        return
      }

      // ============================================
      // EXTRAIRE LE NIVEAU
      // ============================================

      const match =
        basePath.match(/niveau(\d+)/)

      const level =
        match
          ? Number(match[1])
          : null

      const textureCount =
        LEVEL_TEXTURES[level]?.count || 0

      if (!textureCount) {

        setTextures([])
        return
      }

      const sideMatch =
        basePath.match(/\/(Est|Ouest)$/)

      const side =
        sideMatch
          ? sideMatch[1]
          : null

      if (side && level) {
        const cachedList = getCachedWallTextureList(level, side)

        if (cachedList) {
          setTextures(cachedList)
          return
        }
      }

      const loaded = []

      // ============================================
      // CHARGEMENT EXACT
      // ============================================

      const publishProgress = () => {
        if (!cancelled) {
          setTextures([...loaded])
        }
      }

      for (
        let i = 1;
        i <= textureCount;
        i++
      ) {

        const path = `${basePath}${i}.webp`

        try {

          // ========================================
          // PRIORITÉ AU CACHE
          // ========================================

          const tex =
            side && level
              ? await loadWallTexture(level, side, i)
              : textureCache.get(path)

          if (!tex?.image) {
            loaded[i - 1] = null
            publishProgress()
            continue
          }

          loaded[i - 1] = tex

          if (
            i <= PRIORITY_WALL_TEXTURE_COUNT ||
            i % 3 === 0
          ) {
            publishProgress()
          }

        } catch (err) {

          console.warn(
            "TEXTURE FAILED:",
            path,
            err
          )

          loaded[i - 1] = null
          publishProgress()
        }
      }

      if (!cancelled) {

        globalTextureCache.set(
          basePath,
          loaded
        )

        setTextures(loaded)
      }
    }

    load()

    return () => {

      cancelled = true
    }

  }, [
    basePath
  ])

  return textures
}
function useTurnTextures() {

  const paths = React.useMemo(() => {
    return TOURNANTS
      .map(t => t.texture)
      .filter(Boolean)
  }, [])

  const textures = useLoader(
    THREE.TextureLoader,
    paths
  )

  return React.useMemo(() => {

    const map = {}

    let index = 0

    TOURNANTS.forEach(t => {

      if (t.texture) {

        map[t.id] = textures[index]
        index++
      }

    })

    return map

  }, [textures])
}
function usePivotTurnSystem(
  pivotRef,
  cyclistRef,
  setFinishedTurns,
  teleportVersion,
  snappedTurnsRef,
  activePivotTurnRef

) {
  const { worldGroup, playerSpeed } = useGame()
  const lastPlayerZRef = useRef(0)
  const teleportLockRef = useRef(false)
  const turnActiveRef = useRef(false)
  const lastArcRef = useRef(0)
  const pivotBasePosRef = useRef(new THREE.Vector3())

  const PRE = 20

  function getTurnTriggerZ(turn) {
    return turn.centerZ + (turn.triggerOffsetZ ?? 0)
  }

useEffect(() => {

  if (!teleportVersion) return
  if (!worldGroup.current) return

  teleportLockRef.current = true

  const playerZ = -worldGroup.current.position.z

  lastPlayerZRef.current = playerZ

  snappedTurnsRef.current.clear()

  const nextFinishedTurns = new Set()

 for (const turn of TOURNANTS) {
  const center = getTurnTriggerZ(turn) - PRE

  const keepVisibleAfterTeleport =
    turn.id === "Commune" &&
    playerZ > center - 700 &&
    playerZ < center + 200

  if (playerZ < center && !keepVisibleAfterTeleport) {
    snappedTurnsRef.current.add(turn.id)
    nextFinishedTurns.add(turn.level)
  }
}

  setFinishedTurns(nextFinishedTurns)

  requestAnimationFrame(() => {
    teleportLockRef.current = false
  })

}, [teleportVersion])


useFrame((state, dt) => {

  if (!pivotRef.current || !worldGroup.current) return
  if (teleportLockRef.current) return
  if (playerSpeed.current < 0.01) return

  const playerZ = -worldGroup.current.position.z

  let localRot = 0
  let snappedThisFrame = false
  let turnCorrection = 0
  if (activePivotTurnRef) {
    activePivotTurnRef.current = null
  }

  for (const turn of TOURNANTS) {

    // tournant déjà terminé
    if (snappedTurnsRef.current.has(turn.id)) {
      continue
    }

    const startIn = getTurnTriggerZ(turn)
    const center = startIn - PRE

    if (playerZ > startIn) {
      continue
    }

    let t = 0

    if (playerZ <= startIn && playerZ >= center) {

      t =
        (startIn - playerZ) /
        (startIn - center)

    } else if (playerZ < center) {

      t = 1
    }

    t = THREE.MathUtils.clamp(t, 0, 1)

    const eased = t * t * (3 - 2 * t)
    const slowed = Math.sin(eased * Math.PI * 0.5)

    turnCorrection = slowed
    localRot = slowed * -turn.angle


    if (t > 0 && t < 1) {
      if (activePivotTurnRef) {
        activePivotTurnRef.current = turn.id
      }
      
      if (!turnActiveRef.current) {

        turnActiveRef.current = true
        lastArcRef.current = 0

        const bikePos = new THREE.Vector3()

        cyclistRef.current.pivot.getWorldPosition(bikePos)
        pivotBasePosRef.current.copy(pivotRef.current.position)

      }

    } else if (turnActiveRef.current) {

      lastArcRef.current = 0
    }

    //================ SNAP =================
if ((lastPlayerZRef.current >= center && playerZ < center) || t > 0.8) {

  snappedThisFrame = true
  snappedTurnsRef.current.add(turn.id)

  setFinishedTurns(prev => {
    const next = new Set(prev)
    next.add(turn.level)
    return next
  })

  const beforeLimits = getPlayableLimitsAtZ(playerZ)
  const beforeX = Number.isFinite(cyclistRef.current?.position?.x)
    ? cyclistRef.current.position.x
    : 0
  const beforeWidth = beforeLimits.right - beforeLimits.left
  const laneRatio =
    beforeWidth > 0
      ? THREE.MathUtils.clamp(
          (beforeX - beforeLimits.left) / beforeWidth,
          0,
          1
        )
      : 0.5

  turnActiveRef.current = false
  if (activePivotTurnRef) {
    activePivotTurnRef.current = null
  }

  // 🔥 RESET COMPLET DU PIVOT
  pivotRef.current.rotation.y = 0
  pivotRef.current.position.x = 0
  pivotRef.current.position.z = 0

  // 🔥 CORRECTION CRITIQUE : REMETTRE LE MONDE DROIT
  worldGroup.current.rotation.y = 0

  // Décalage Z
  worldGroup.current.position.z += getLevelWidth(turn.level)

  if (turn.id === "Berri" || turn.id === "Mill" || turn.id === "SousBois" || turn.id === "Passerelle Saint-Pierre" || turn.id === "Parc René-Lévesque2") {
    worldGroup.current.position.z += getLevelWidth(turn.level)
  }

  const afterPlayerZ = -worldGroup.current.position.z
  const afterLimits = getPlayableLimitsAtZ(afterPlayerZ, turn.level)
  const mappedX = THREE.MathUtils.lerp(
    afterLimits.left,
    afterLimits.right,
    laneRatio
  )

  // Recentrer le monde
  worldGroup.current.position.x = 0

  // Garder la meme position relative entre les murs apres le snap.
  cyclistRef.current.position.x = THREE.MathUtils.clamp(
    mappedX,
    afterLimits.left,
    afterLimits.right
  )

  if (turn.id === "Commune" && cyclistRef.current) {
    cyclistRef.current.position.x = -27
  }

  lastPlayerZRef.current = -worldGroup.current.position.z
  break
}
  }

  //================ ROTATION =================

const targetRot = localRot

if (cyclistRef.current?.pivot) {

  // Aucun virage actif -> pivot neutre
  if (!turnActiveRef.current) {

    pivotRef.current.rotation.y = 0
    pivotRef.current.position.x = 0
    pivotRef.current.position.z = 0

  } else {

    const bikeWorldPos = new THREE.Vector3()

cyclistRef.current.pivot.getWorldPosition(
  bikeWorldPos
)

    pivotRef.current.position.x = bikeWorldPos.x
    pivotRef.current.position.z = bikeWorldPos.z
    pivotRef.current.rotation.y = targetRot

    const offset = bikeWorldPos.clone()
 
    offset.applyAxisAngle(new THREE.Vector3(0, 1, 0), targetRot)
    pivotRef.current.position.x = bikeWorldPos.x - offset.x
    pivotRef.current.position.z = bikeWorldPos.z - offset.z
    const CORRECTION_MAX = 10

    pivotRef.current.position.z +=turnCorrection * CORRECTION_MAX


  }
}

  if (!snappedThisFrame) {

    lastPlayerZRef.current = playerZ
  }
})
}

function InstancedWallChunk({
  instances,
  texture,
  mirror = false,
  tilt = WALL_TILT,
  wallOffset,
  fixTiltForShortTextures = false
}) {
  const meshRef = useRef()
  const dummy = useMemo(() => new THREE.Object3D(), [])
  const geometry = useMemo(() => new THREE.PlaneGeometry(1, 1), [])
  const normalRotY = mirror ? Math.PI / 2 : -Math.PI / 2

  useLayoutEffect(() => {
    const mesh = meshRef.current

    if (!mesh || !instances?.length) {
      return
    }

    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

    const SIDE_OFFSET = 0.2
    const REFERENCE_IMAGE_HEIGHT = 1024

    const imageHeight =
      texture?.image?.height ?? REFERENCE_IMAGE_HEIGHT

    const shouldFixTilt =
      fixTiltForShortTextures &&
      imageHeight > 0 &&
      imageHeight < REFERENCE_IMAGE_HEIGHT

    for (let i = 0; i < instances.length; i++) {
      const inst = instances[i]

      const z =
        (inst.baseZ ?? inst.z ?? 0) +
        wallOffset +
        (mirror ? -SIDE_OFFSET : SIDE_OFFSET)

      let correctedX = inst.x

      /*
        Correction ciblée :
        - ne change pas la grandeur du mur
        - ne change pas le tilt
        - ne change pas le z
        - ne change pas le y
        - ne change pas le pivot
        - ne s'applique que si fixTiltForShortTextures = true

        Une texture 512 a une hauteur monde plus petite.
        Avec le même tilt, sa base projetée n'arrive pas au même X
        qu'une texture 1024. On compense seulement cette différence.
      */
      if (shouldFixTilt) {
        const referenceHeight =
          inst.height * (REFERENCE_IMAGE_HEIGHT / imageHeight)

        const missingHeight =
          Math.max(0, referenceHeight - inst.height)

        const xCorrection =
          Math.sin(tilt) * missingHeight / 2

        correctedX =
          inst.x + (mirror ? xCorrection : -xCorrection)
      }

      dummy.position.set(correctedX, inst.height / 2, z)
      dummy.rotation.set(0, inst.rotY ?? normalRotY, 0)
      dummy.rotateX(-tilt)
      dummy.scale.set(inst.width, inst.height, 1)
      dummy.updateMatrix()

      mesh.setMatrixAt(i, dummy.matrix)
    }

    mesh.count = instances.length
    mesh.instanceMatrix.needsUpdate = true
    mesh.frustumCulled = false
    mesh.computeBoundingSphere()
  }, [
    instances,
    texture,
    wallOffset,
    tilt,
    mirror,
    normalRotY,
    fixTiltForShortTextures
  ])

  if (!instances?.length) {
    return null
  }

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, null, instances.length]}
      frustumCulled={false}
    >
      <meshBasicMaterial
        map={texture}
        alphaTest={0.5}
        transparent={false}
        depthWrite={true}
        depthTest={true}
        polygonOffset
        polygonOffsetFactor={1}
        polygonOffsetUnits={1}
        side={THREE.FrontSide}
        toneMapped={false}
      />
    </instancedMesh>
  )
}


function WallsLevel({
  level,
  baseZ,
  xLeft,
  xRight,
  isTurnLevel = false,
  finishedTurns,
  turnRotationY = 0
}) {
  const { gl } = useThree()

  const texLeftBases = useMultiTextures(`/decors/niveau${level}/Est`)
  const texRightBases = useMultiTextures(`/decors/niveau${level}/Ouest`)

  const maxTextureAnisotropy = React.useMemo(() => {
    return gl?.capabilities?.getMaxAnisotropy?.() ?? 8
  }, [gl])

  const chunkData = React.useMemo(() => {
    if (!texLeftBases.length || !texRightBases.length) {
      return { left: [], right: [] }
    }

    const validLeft = texLeftBases
    const validRight = texRightBases

    if (!validLeft.length || !validRight.length) {
      return { left: [], right: [] }
    }

    function getImageWidth(img) {
      return img?.naturalWidth || img?.videoWidth || img?.width || 1
    }

    function getImageHeight(img) {
      return img?.naturalHeight || img?.videoHeight || img?.height || 1
    }

    function applyTextureQuality(sourceTexture, targetTexture) {
      targetTexture.wrapS = THREE.ClampToEdgeWrapping
      targetTexture.wrapT = THREE.ClampToEdgeWrapping

      /*
        Important pour éviter le flou sur les murs inclinés.
        Les CanvasTexture n'héritent pas toujours des bons réglages.
      */
      targetTexture.generateMipmaps = true
      targetTexture.minFilter = THREE.LinearMipmapLinearFilter
      targetTexture.magFilter = THREE.LinearFilter
      targetTexture.anisotropy = maxTextureAnisotropy

      targetTexture.flipY = sourceTexture.flipY

      if ("colorSpace" in sourceTexture) {
        targetTexture.colorSpace = sourceTexture.colorSpace
      }

      if ("encoding" in sourceTexture) {
        targetTexture.encoding = sourceTexture.encoding
      }

      targetTexture.needsUpdate = true
    }

    function createVerticalPaddedTexture(texture, originalWorldHeight, targetWorldHeight) {
      if (!texture?.image) return texture
      if (!targetWorldHeight || targetWorldHeight <= originalWorldHeight) return texture

      const img = texture.image

      const imageWidth = getImageWidth(img)
      const imageHeight = getImageHeight(img)

      const heightRatio =
        targetWorldHeight / originalWorldHeight

      const canvasWidth = imageWidth
      const canvasHeight = Math.max(
        imageHeight,
        Math.round(imageHeight * heightRatio)
      )

      const cacheKey =
        `vertical-pad-${canvasWidth}-${canvasHeight}-aniso-${maxTextureAnisotropy}`

      texture.userData = texture.userData || {}
      texture.userData.paddedTextures =
        texture.userData.paddedTextures || {}

      if (texture.userData.paddedTextures[cacheKey]) {
        return texture.userData.paddedTextures[cacheKey]
      }

      const canvas = document.createElement("canvas")
      canvas.width = canvasWidth
      canvas.height = canvasHeight

      const ctx = canvas.getContext("2d")

      ctx.clearRect(0, 0, canvasWidth, canvasHeight)

      /*
        On ne redimensionne PAS l'image 512.
        On la colle en bas du canvas.
        La partie ajoutée en haut reste transparente.
      */
      ctx.imageSmoothingEnabled = false

      ctx.drawImage(
        img,
        0,
        canvasHeight - imageHeight,
        imageWidth,
        imageHeight
      )

      const paddedTexture = new THREE.CanvasTexture(canvas)

      applyTextureQuality(texture, paddedTexture)

      texture.userData.paddedTextures[cacheKey] =
        paddedTexture

      return paddedTexture
    }

    function buildRawChunks(textures) {
      return textures.map((tex, i) => {
        if (!tex?.image) {
          return null
        }

        const dims = computeDimensionsFromTexture(tex)

        return {
          texture: tex,
          width: dims.width,
          height: dims.height,
          id: i
        }
      }).filter(Boolean)
    }

    function normalizePlanHeightOnly(rawChunks) {
      if (!rawChunks.length) {
        return []
      }

      const maxHeight = rawChunks.reduce((max, c) => {
        return Math.max(max, c.height)
      }, 0)

      let acc = 0

      return rawChunks.map((c) => {
        const paddedTexture =
          createVerticalPaddedTexture(
            c.texture,
            c.height,
            maxHeight
          )

        const data = {
          texture: paddedTexture,

          /*
            Important :
            on garde la largeur originale.
            La texture 512 n'est pas agrandie.
          */
          width: c.width,

          /*
            Le plan a la hauteur maximale du côté,
            donc le tilt est uniforme.
          */
          height: maxHeight,

          offset: acc,
          id: c.id,

          originalTexture: c.texture,
          originalWidth: c.width,
          originalHeight: c.height
        }

        acc += c.width

        return data
      })
    }

    const rawLeft = buildRawChunks(validLeft)
    const rawRight = buildRawChunks(validRight)

    const left = normalizePlanHeightOnly(rawLeft)
    const right = normalizePlanHeightOnly(rawRight)

    const leftAcc = left.reduce((sum, c) => {
      return sum + c.width
    }, 0)

    const rightAcc = right.reduce((sum, c) => {
      return sum + c.width
    }, 0)

    const levelLength = Math.max(leftAcc, rightAcc)

    LEVEL_LENGTH_CACHE[level] = levelLength

    return { left, right }
  }, [
    texLeftBases,
    texRightBases,
    level,
    maxTextureAnisotropy
  ])

  const previousWidth =
    level > 1
      ? getLevelWidth(level - 1)
      : getLevelWidth(level)

  const turn = TOURNANTS.find(t => t.level === level)
  const turnWallLayout = getTurnWallLayout(turn)

  const turnOffsetX =
    isTurnLevel && turn
      ? (turn.offsetX ?? 0)
      : 0

  const localOffsetX =
    Math.cos(turnRotationY) * turnOffsetX

  const ouestWallTilt =
    level >= OUEST_WALL_EXTRA_TILT_START_LEVEL
      ? WALL_TILT * OUEST_WALL_EXTRA_TILT_MULTIPLIER
      : WALL_TILT

  const leftInstances = React.useMemo(() => {
    return chunkData.left.map((c) => {
      const rawX = isTurnLevel
        ? baseZ + turnWallLayout.leftX + localOffsetX
        : xLeft

      const rawZ = isTurnLevel && turn
        ? (
            turn.direction === "right"
              ? -c.offset - c.width / 2 + (turn.offsetX ?? 0)
              : -c.offset - c.width / 2 - previousWidth + (turn.offsetX ?? 0)
          )
        : baseZ - c.offset - c.width / 2

      return [{
        x: rawX,
        width: c.width,
        height: c.height,
        baseZ: rawZ,
        rotY: undefined
      }]
    })
  }, [
    chunkData.left,
    xLeft,
    baseZ,
    turn,
    turnWallLayout,
    isTurnLevel,
    previousWidth,
    finishedTurns,
    localOffsetX
  ])

  const rightInstances = React.useMemo(() => {
    return chunkData.right.map((c) => {
      const rawX = isTurnLevel
        ? baseZ + turnWallLayout.rightX + localOffsetX
        : xRight

      const rawZ = isTurnLevel && turn
        ? (
            turn.direction === "right"
              ? -previousWidth - c.offset - c.width / 2 + (turn.offsetX ?? 0)
              : -c.offset - c.width / 2 + (turn.offsetX ?? 0)
          )
        : baseZ - c.offset - c.width / 2

      return [{
        x: rawX,
        width: c.width,
        height: c.height,
        baseZ: rawZ,
        rotY: undefined
      }]
    })
  }, [
    chunkData.right,
    xRight,
    baseZ,
    turn,
    turnWallLayout,
    isTurnLevel,
    previousWidth,
    finishedTurns,
    localOffsetX
  ])

  if (!chunkData.left.length && !chunkData.right.length) {
    return <group />
  }

  return (
    <>
      {chunkData.left.map((c, i) => (
        <InstancedWallChunk
          key={`left-${level}-${c.id}`}
          texture={c.texture}
          mirror
          wallOffset={15}
          instances={leftInstances[i]}
        />
      ))}

      {chunkData.right.map((c, i) => (
        <InstancedWallChunk
          key={`right-${level}-${c.id}`}
          wallOffset={10}
          texture={c.texture}
          tilt={ouestWallTilt}
          instances={rightInstances[i]}
        />
      ))}
    </>
  )
}
const LevelStreamer = React.forwardRef(function LevelStreamer({
  xLeft,
  xRight,
  teleportVersion,
  finishedTurns,
  arbres = []
}, ref) {
  const { worldGroup, setGameState } = useGame()

  const LEVEL_PREVIEW_DISTANCE = 1400
  const LEVEL_PREVIEW_AHEAD_COUNT = 5
  const LEVEL_PREVIEW_BEHIND_COUNT = 1

  const TURN_RENDER_BEFORE = 320
  const TURN_PRE = 20
  const TURN_PERPENDICULAR_PREVIEW_COUNT = 2

  const STARTUP_LEVELS_BEHIND = 1
  const STARTUP_LEVELS_AHEAD = 2

  const STREAM_PRELOAD_DISTANCE = 300
  const UPCOMING_TURN_PRELOAD_DISTANCE = 900

  /*
    Collision arbres :
    LevelStreamer garde seulement les arbres proches du cycliste.
    Comme le monde défile autour du joueur, le cycliste est près de z = 0
    en coordonnées monde.
  */
const COLLISION_Z_RANGE = 140
const COLLISION_MAX_CANDIDATES = 180
const COLLISION_REFRESH_EVERY_FRAMES = 1

  const levelsRef = useRef([])
  const loadingLevelsRef = useRef(new Set())
  const levelTreeRefs = useRef(new Map())
  const backfillTreeRefs = useRef(new Map())
  const collisionTreesRef = useRef([])
  const collisionCursorRef = useRef(0)

  const initializedRef = useRef(false)
  const glbsLoadedRef = useRef(false)
  const teleportLoadingRef = useRef(false)

  const [, forceUpdate] = useState(0)

  function refreshCollisionTrees() {
  const frame =
    refreshCollisionTrees.frame || 0

  refreshCollisionTrees.frame = frame + 1

  if (
    refreshCollisionTrees.frame %
      COLLISION_REFRESH_EVERY_FRAMES !==
    0
  ) {
    return
  }

  const next = []
  const temp =
    refreshCollisionTrees.temp ||
    new THREE.Vector3()

  refreshCollisionTrees.temp = temp

  /*
    Dans ton jeu, le monde défile autour du cycliste.
    Le cycliste est normalement près de z = 0 en world space.
    On garde une zone large pour ne pas rater les arbres,
    surtout dans les virages/backfills.
  */
  const playerWorldZ = 0

  function collectCollisionTrees(api) {
    if (!api?.collisionTrees?.length) return

    for (let i = 0; i < api.collisionTrees.length; i++) {
      const item = api.collisionTrees[i]
      const tree = item?.tree

      if (!tree) continue
      if (tree.visible === false) continue

      tree.updateWorldMatrix(true, false)
      temp.setFromMatrixPosition(tree.matrixWorld)

      const dz = temp.z - playerWorldZ

      if (Math.abs(dz) > COLLISION_Z_RANGE) {
        continue
      }

      next.push({
        ...item,

        /*
          Ces valeurs servent seulement au tri/debug.
          La vraie collision va recalculer la position monde.
        */
        worldX: temp.x,
        worldZ: temp.z,
        distanceToPlayer: Math.abs(dz)
      })
    }
  }

  for (const api of levelTreeRefs.current.values()) {
    collectCollisionTrees(api)
  }

  for (const api of backfillTreeRefs.current.values()) {
    collectCollisionTrees(api)
  }

  next.sort((a, b) => {
    return a.distanceToPlayer - b.distanceToPlayer
  })

  collisionTreesRef.current =
    next.slice(0, COLLISION_MAX_CANDIDATES)
}

  useImperativeHandle(
    ref,
    () => ({
      collisionTreesRef,
      collisionCursorRef,
      pauseHeavyUpdates: () => {}
    }),
    []
  )

  function getTurnForLevel(level) {
    return TOURNANTS.find(turn => turn.level === level)
  }

  function hasTurnForLevel(level) {
    return Boolean(getTurnForLevel(level))
  }

  function isTurnFinished(turn) {
    if (!turn || !finishedTurns) return false

    return (
      finishedTurns.has(turn.id) ||
      finishedTurns.has(turn.level)
    )
  }

  function getTurnWallAngle(turn) {
    if (!turn) return 0

    return turn.direction === "right"
      ? -Math.PI / 2
      : Math.PI / 2
  }

  function getTurnStartZ(turn) {
    return turn.centerZ + (turn.triggerOffsetZ ?? 0)
  }

  function getTurnSnapZ(turn) {
    return getTurnStartZ(turn) - TURN_PRE
  }

  function getCurrentWallLevel(playerZ) {
    const EPS = 0.001

    for (const level of LEVELS) {
      const start = getLevelStartZ(level)
      const length = getLevelLength(level)

      if (!length) continue

      const end = start - length

      if (
        playerZ <= start + EPS &&
        playerZ >= end - EPS
      ) {
        return level
      }
    }

    const firstLevel = LEVELS[0]
    const lastLevel = LEVELS[LEVELS.length - 1]

    if (
      firstLevel &&
      playerZ > getLevelStartZ(firstLevel)
    ) {
      return firstLevel
    }

    if (lastLevel) {
      const lastStart = getLevelStartZ(lastLevel)
      const lastLength = getLevelLength(lastLevel)
      const lastEnd = lastStart - lastLength

      if (playerZ < lastEnd) {
        return lastLevel
      }
    }

    let closestLevel = firstLevel
    let closestDistance = Infinity

    for (const level of LEVELS) {
      const start = getLevelStartZ(level)
      const length = getLevelLength(level)

      if (!length) continue

      const end = start - length

      const distance =
        playerZ > start
          ? Math.abs(playerZ - start)
          : playerZ < end
            ? Math.abs(playerZ - end)
            : 0

      if (distance < closestDistance) {
        closestDistance = distance
        closestLevel = level
      }
    }

    return closestLevel
  }

  function getAllTurnRuns() {
    const runs = []
    let index = 0

    while (index < LEVELS.length) {
      if (!hasTurnForLevel(LEVELS[index])) {
        index++
        continue
      }

      const start = index

      while (
        index < LEVELS.length &&
        hasTurnForLevel(LEVELS[index])
      ) {
        index++
      }

      runs.push({
        start,
        end: index - 1
      })
    }

    return runs
  }

  function getActiveTurnIndexInRun(run, playerZ) {
    for (let index = run.start; index <= run.end; index++) {
      const level = LEVELS[index]
      const turn = getTurnForLevel(level)

      if (!turn) continue

      const startZ = getTurnStartZ(turn)
      const snapZ = getTurnSnapZ(turn)

      if (
        playerZ <= startZ + TURN_RENDER_BEFORE &&
        playerZ >= snapZ
      ) {
        return index
      }
    }

    return null
  }

  function buildVisibleTurnLevels(playerZ) {
    const visibleTurnLevels = new Set()
    const runs = getAllTurnRuns()

    for (const run of runs) {
      const anchorIndex = getActiveTurnIndexInRun(run, playerZ)

      if (anchorIndex !== null) {
        const endIndex = Math.min(
          run.end,
          anchorIndex + TURN_PERPENDICULAR_PREVIEW_COUNT - 1
        )

        for (let index = anchorIndex; index <= endIndex; index++) {
          const level = LEVELS[index]
          const turn = getTurnForLevel(level)

          if (turn && !isTurnFinished(turn)) {
            visibleTurnLevels.add(level)
          }
        }
      }
    }

    return visibleTurnLevels
  }

  function buildStraightWallPlan(playerZ) {
    const currentLevel = getCurrentWallLevel(playerZ)
    const currentIndex = LEVELS.indexOf(currentLevel)

    const safeCurrentIndex =
      currentIndex === -1 ? 0 : currentIndex

    const startIndex = Math.max(
      0,
      safeCurrentIndex - LEVEL_PREVIEW_BEHIND_COUNT
    )

    const plan = []
    let visibleDistance = 0

    for (let index = startIndex; index < LEVELS.length; index++) {
      const level = LEVELS[index]
      const length = getLevelLength(level)

      if (!length) continue

      plan.push({
        renderKey: `straight-${level}`,
        kind: "straight",
        level,
        turn: null,
        isTurnLevel: false,
        hidden: false,
        position: [0, 0, getLevelStartZ(level)],
        rotationY: 0
      })

      if (index < safeCurrentIndex) continue

      visibleDistance += length

      if (visibleDistance >= LEVEL_PREVIEW_DISTANCE) break

      if (
        index - safeCurrentIndex >=
        LEVEL_PREVIEW_AHEAD_COUNT
      ) {
        break
      }
    }

    return plan
  }

  function getParentPivotRotationY() {
    return worldGroup?.current?.parent?.rotation?.y ?? 0
  }

  function convertVisualOffsetToLocal(offsetX, offsetZ, parentRotationY) {
    return {
      x:
        Math.cos(parentRotationY) * offsetX -
        Math.sin(parentRotationY) * offsetZ,

      z:
        Math.sin(parentRotationY) * offsetX +
        Math.cos(parentRotationY) * offsetZ
    }
  }

  function buildTurnWallPlan(playerZ) {
    const visibleTurnLevels = buildVisibleTurnLevels(playerZ)
    const plan = []
    const runs = getAllTurnRuns()

    const RIGHT_WALL_RENDER_OFFSET = 10 + 0.2

    function rotateLocalXZ(x, z, rotationY) {
      const cos = Math.cos(rotationY)
      const sin = Math.sin(rotationY)

      return {
        x: x * cos + z * sin,
        z: -x * sin + z * cos
      }
    }

    function localToParentXZ(position, rotationY, localPoint) {
      const rotated = rotateLocalXZ(
        localPoint.x,
        localPoint.z,
        rotationY
      )

      return {
        x: position[0] + rotated.x,
        z: position[2] + rotated.z
      }
    }

    function getPreviousWidthForTurn(turn) {
      const turnIndex = LEVELS.indexOf(turn.level)
      const previousLevel = LEVELS[turnIndex - 1]

      return previousLevel
        ? getLevelWidth(previousLevel)
        : getLevelWidth(turn.level)
    }

    function getRightWallLocalX(turn) {
      const layout = getTurnWallLayout(turn)

      return layout?.rightX ?? 0
    }

    function getRightWallStartLocalZ(turn) {
      const previousWidth = getPreviousWidthForTurn(turn)
      const offsetX = turn.offsetX ?? 0

      const startZ =
        turn.direction === "right"
          ? -previousWidth + offsetX
          : offsetX

      return startZ + RIGHT_WALL_RENDER_OFFSET
    }

    function getRightWallEndLocalZ(turn) {
      const length = getLevelLength(turn.level)

      return getRightWallStartLocalZ(turn) - length
    }

    function getRightWallStartAnchor(turn) {
      return {
        x: getRightWallLocalX(turn),
        z: getRightWallStartLocalZ(turn)
      }
    }

    function getRightWallEndAnchor(turn) {
      return {
        x: getRightWallLocalX(turn),
        z: getRightWallEndLocalZ(turn)
      }
    }

    for (const run of runs) {
      let previousTurnSegment = null
      let previousTurnForChain = null

      for (let index = run.start; index <= run.end; index++) {
        const level = LEVELS[index]
        const turn = getTurnForLevel(level)

        if (!turn) continue

        const previousLevel = LEVELS[index - 1]
        const previousTurn = getTurnForLevel(previousLevel)

        const previousIsFinished =
          previousTurn &&
          isTurnFinished(previousTurn)

        if (previousIsFinished) {
          previousTurnSegment = null
          previousTurnForChain = null
        }

        const currentOffsetX = turn.offsetX ?? 0

        let position = [
          0,
          0,
          getLevelStartZ(level)
        ]

        let rotationY =
          getTurnWallAngle(turn)

        if (
          previousTurnSegment &&
          previousTurnForChain &&
          !previousIsFinished
        ) {
          rotationY =
            previousTurnSegment.rotationY +
            getTurnWallAngle(turn)

          if (previousTurnForChain.direction === turn.direction) {
            const previousRightWallEnd =
              localToParentXZ(
                previousTurnSegment.position,
                previousTurnSegment.rotationY,
                getRightWallEndAnchor(previousTurnForChain)
              )

            const currentRightWallStart =
              getRightWallStartAnchor(turn)

            const rotatedCurrentAnchor =
              rotateLocalXZ(
                currentRightWallStart.x,
                currentRightWallStart.z,
                rotationY
              )

            position = [
              previousRightWallEnd.x - rotatedCurrentAnchor.x,
              0,
              previousRightWallEnd.z - rotatedCurrentAnchor.z
            ]
          } else {
            const previousLength =
              getLevelLength(previousTurnForChain.level)

            const forwardX =
              -Math.sin(previousTurnSegment.rotationY)

            const forwardZ =
              -Math.cos(previousTurnSegment.rotationY)

            position = [
              previousTurnSegment.position[0] +
                forwardX * previousLength +
                currentOffsetX,
              0,
              previousTurnSegment.position[2] +
                forwardZ * previousLength
            ]
          }
        }

        const segment = {
          renderKey: `turn-${turn.id || level}`,
          kind: "turn",
          level,
          turn,
          isTurnLevel: true,
          hidden: false,
          position,
          rotationY
        }

        if (visibleTurnLevels.has(level)) {
          plan.push(segment)
        }

        previousTurnSegment = segment
        previousTurnForChain = turn
      }
    }

    return plan
  }

  function buildAllTurnTreeSegments() {
    const plan = []
    const runs = getAllTurnRuns()

    const RIGHT_WALL_RENDER_OFFSET = 10 + 0.2

    function rotateLocalXZ(x, z, rotationY) {
      const cos = Math.cos(rotationY)
      const sin = Math.sin(rotationY)

      return {
        x: x * cos + z * sin,
        z: -x * sin + z * cos
      }
    }

    function localToParentXZ(position, rotationY, localPoint) {
      const rotated = rotateLocalXZ(
        localPoint.x,
        localPoint.z,
        rotationY
      )

      return {
        x: position[0] + rotated.x,
        z: position[2] + rotated.z
      }
    }

    function getPreviousWidthForTurn(turn) {
      const turnIndex = LEVELS.indexOf(turn.level)
      const previousLevel = LEVELS[turnIndex - 1]

      return previousLevel
        ? getLevelWidth(previousLevel)
        : getLevelWidth(turn.level)
    }

    function getRightWallLocalX(turn) {
      const layout = getTurnWallLayout(turn)

      return layout?.rightX ?? 0
    }

    function getRightWallStartLocalZ(turn) {
      const previousWidth = getPreviousWidthForTurn(turn)
      const offsetX = turn.offsetX ?? 0

      const startZ =
        turn.direction === "right"
          ? -previousWidth + offsetX
          : offsetX

      return startZ + RIGHT_WALL_RENDER_OFFSET
    }

    function getRightWallEndLocalZ(turn) {
      const length = getLevelLength(turn.level)

      return getRightWallStartLocalZ(turn) - length
    }

    function getRightWallStartAnchor(turn) {
      return {
        x: getRightWallLocalX(turn),
        z: getRightWallStartLocalZ(turn)
      }
    }

    function getRightWallEndAnchor(turn) {
      return {
        x: getRightWallLocalX(turn),
        z: getRightWallEndLocalZ(turn)
      }
    }

    for (const run of runs) {
      let previousTurnSegment = null
      let previousTurnForChain = null

      for (let index = run.start; index <= run.end; index++) {
        const level = LEVELS[index]
        const turn = getTurnForLevel(level)

        if (!turn) continue

        const previousLevel = LEVELS[index - 1]
        const previousTurn = getTurnForLevel(previousLevel)

        const previousIsFinished =
          previousTurn &&
          isTurnFinished(previousTurn)

        if (previousIsFinished) {
          previousTurnSegment = null
          previousTurnForChain = null
        }

        const currentOffsetX = turn.offsetX ?? 0

        let position = [
          0,
          0,
          getLevelStartZ(level)
        ]

        let rotationY =
          getTurnWallAngle(turn)

        if (
          previousTurnSegment &&
          previousTurnForChain &&
          !previousIsFinished
        ) {
          rotationY =
            previousTurnSegment.rotationY +
            getTurnWallAngle(turn)

          if (previousTurnForChain.direction === turn.direction) {
            const previousRightWallEnd =
              localToParentXZ(
                previousTurnSegment.position,
                previousTurnSegment.rotationY,
                getRightWallEndAnchor(previousTurnForChain)
              )

            const currentRightWallStart =
              getRightWallStartAnchor(turn)

            const rotatedCurrentAnchor =
              rotateLocalXZ(
                currentRightWallStart.x,
                currentRightWallStart.z,
                rotationY
              )

            position = [
              previousRightWallEnd.x - rotatedCurrentAnchor.x,
              0,
              previousRightWallEnd.z - rotatedCurrentAnchor.z
            ]
          } else {
            const previousLength =
              getLevelLength(previousTurnForChain.level)

            const forwardX =
              -Math.sin(previousTurnSegment.rotationY)

            const forwardZ =
              -Math.cos(previousTurnSegment.rotationY)

            position = [
              previousTurnSegment.position[0] +
                forwardX * previousLength +
                currentOffsetX,
              0,
              previousTurnSegment.position[2] +
                forwardZ * previousLength
            ]
          }
        }

        const segment = {
          renderKey: `turn-tree-${turn.id || level}`,
          level,
          turn,
          position,
          rotationY
        }

        plan.push(segment)

        previousTurnSegment = {
          renderKey: `turn-${turn.id || level}`,
          kind: "turn",
          level,
          turn,
          isTurnLevel: true,
          hidden: false,
          position,
          rotationY
        }

        previousTurnForChain = turn
      }
    }

    return plan
  }

  function buildVisibleTurnTreeSegments() {
    const visibleLevels = new Set()
    const renderedTurnLevels = new Set()

    levelsRef.current.forEach(segment => {
      if (segment.hidden) return

      const index = LEVELS.indexOf(segment.level)

      if (index === -1) return

      if (
        segment.kind === "turn" &&
        segment.turn
      ) {
        renderedTurnLevels.add(segment.level)
      }

      for (let back = 0; back <= 2; back++) {
        const previousLevel = LEVELS[index - back]

        if (previousLevel) {
          visibleLevels.add(previousLevel)
        }
      }

      const nextLevel = LEVELS[index + 1]

      if (nextLevel) {
        visibleLevels.add(nextLevel)
      }
    })

    const allTurnTreeSegments =
      buildAllTurnTreeSegments()

    const result = []
    const used = new Set()

    allTurnTreeSegments.forEach(segment => {
      const key =
        segment.turn?.id ?? segment.level

      if (used.has(key)) return

      if (!visibleLevels.has(segment.level)) return

      if (renderedTurnLevels.has(segment.level)) return

      used.add(key)
      result.push(segment)
    })

    return result
  }

  function shouldHideStraightLevelForActiveTurn(
    level,
    visibleTurnLevels
  ) {
    if (!visibleTurnLevels.has(level)) return false

    const currentTurn = getTurnForLevel(level)

    if (!currentTurn) return false
    if (isTurnFinished(currentTurn)) return false

    return true
  }

  function buildWallPlan(playerZ, parentRotationY = 0) {
    const straightPlan = buildStraightWallPlan(playerZ)
    const turnPlan = buildTurnWallPlan(playerZ, parentRotationY)

    const visibleTurnLevels =
      new Set(turnPlan.map(segment => segment.level))

    const correctedStraightPlan =
      straightPlan.map(segment => ({
        ...segment,
        hidden:
          shouldHideStraightLevelForActiveTurn(
            segment.level,
            visibleTurnLevels
          )
      }))

    return [
      ...correctedStraightPlan,
      ...turnPlan
    ]
  }

  function getWallPlanKey(plan) {
    return plan
      .map(segment => (
        `${segment.renderKey}:` +
        `${segment.position[0].toFixed(2)}:` +
        `${segment.position[2].toFixed(2)}:` +
        `${segment.rotationY.toFixed(3)}:` +
        `${segment.isTurnLevel ? 1 : 0}:` +
        `${segment.hidden ? 1 : 0}`
      ))
      .join("|")
  }

  function commitWallPlan(newPlan) {
    if (!newPlan || !newPlan.length) return false

    const oldKey = getWallPlanKey(levelsRef.current)
    const newKey = getWallPlanKey(newPlan)

    if (oldKey === newKey) return false

    levelsRef.current = newPlan
    return true
  }

  async function preloadLevelTextures(level) {
    const levelInfo = LEVEL_TEXTURES[level]
    if (!levelInfo) return

    if (areLevelWallTexturesReady(level)) {
      preloadedLevels.add(level)
      return
    }

    const sides = ["Est", "Ouest"]

    for (const side of sides) {
      const cachedList =
        getCachedWallTextureList(level, side)

      if (cachedList) continue

      const sideTextures = []
      let loadedThisFrame = 0

      for (let index = 1; index <= levelInfo.count; index++) {
        const texture =
          await loadWallTexture(level, side, index)

        sideTextures[index - 1] =
          texture?.image ? texture : null

        loadedThisFrame++

        if (loadedThisFrame >= 3) {
          await waitFrames(1)
          loadedThisFrame = 0
        }
      }

      globalTextureCache.set(
        `/decors/niveau${level}/${side}`,
        sideTextures
      )
    }

    preloadedLevels.add(level)
  }

  async function preloadOnce(level) {
    if (!level) return
    if (preloadedLevels.has(level)) return
    if (loadingLevelsRef.current.has(level)) return

    loadingLevelsRef.current.add(level)

    try {
      await preloadLevelTextures(level)
    } finally {
      loadingLevelsRef.current.delete(level)
    }
  }

  function requestPreload(level) {
    if (!level) return
    if (preloadedLevels.has(level)) return
    if (loadingLevelsRef.current.has(level)) return

    preloadOnce(level)
  }

  function getStartupCriticalLevels(playerZ) {
    const levels = new Set()

    const currentLevel = getCurrentWallLevel(playerZ)
    const currentIndex = LEVELS.indexOf(currentLevel)

    if (currentIndex >= 0) {
      for (
        let index = currentIndex - STARTUP_LEVELS_BEHIND;
        index <= currentIndex + STARTUP_LEVELS_AHEAD;
        index++
      ) {
        if (LEVELS[index]) {
          levels.add(LEVELS[index])
        }
      }
    }

    const visibleTurnLevels =
      buildVisibleTurnLevels(playerZ)

    visibleTurnLevels.forEach(level => {
      levels.add(level)
    })

    return [...levels]
  }

  function requestPreloadNearPlayer(playerZ) {
    const currentLevel = getCurrentWallLevel(playerZ)

    if (!currentLevel) return

    const currentIndex = LEVELS.indexOf(currentLevel)

    if (currentIndex < 0) return

    const currentStart = getLevelStartZ(currentLevel)
    const currentLength = getLevelLength(currentLevel)
    const currentEnd = currentStart - currentLength

    const distanceToEnd = playerZ - currentEnd

    if (
      distanceToEnd >= 0 &&
      distanceToEnd < STREAM_PRELOAD_DISTANCE
    ) {
      requestPreload(LEVELS[currentIndex + 1])
      requestPreload(LEVELS[currentIndex + 2])
    }

    const upcomingTurn =
      TOURNANTS.find(turn => {
        const startZ = getTurnStartZ(turn)

        return (
          playerZ <= startZ + UPCOMING_TURN_PRELOAD_DISTANCE &&
          playerZ >= startZ - 250
        )
      })

    if (upcomingTurn) {
      const turnIndex = LEVELS.indexOf(upcomingTurn.level)

      requestPreload(LEVELS[turnIndex])
      requestPreload(LEVELS[turnIndex + 1])
    }
  }

  async function waitFrames(count) {
    for (let index = 0; index < count; index++) {
      await new Promise(resolve =>
        requestAnimationFrame(resolve)
      )
    }
  }

  useEffect(() => {
    if (!worldGroup?.current) return

    let cancelled = false

    async function initLoad() {
      setGameState("loading")

      try {
        const playerZ =
          -worldGroup.current.position.z

        const startupLevels =
          getStartupCriticalLevels(playerZ)

        for (const level of startupLevels) {
          if (cancelled) return

          await preloadOnce(level)
          await waitFrames(1)
        }

        if (!glbsLoadedRef.current) {
          await preloadActorGLBs({})
          glbsLoadedRef.current = true
        }

        if (cancelled) return

        const initialPlan =
          buildWallPlan(playerZ)

        levelsRef.current = initialPlan
        initializedRef.current = true

        forceUpdate(value => value + 1)
        requestPreloadNearPlayer(playerZ)

      } catch (err) {
        console.error("LevelStreamer initLoad failed", err)

      } finally {
        if (!cancelled) {
          setGameState("playing")
        }
      }
    }

    initLoad()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!initializedRef.current) return
    if (!teleportVersion) return
    if (!worldGroup?.current) return

    let cancelled = false

    async function handleTeleport() {
      teleportLoadingRef.current = true
      setGameState("loading")

      try {
        const playerZ =
          -worldGroup.current.position.z

        const criticalLevels =
          getStartupCriticalLevels(playerZ)

        for (const level of criticalLevels) {
          if (cancelled) return

          await preloadOnce(level)
          await waitFrames(1)
        }

        if (cancelled) return

        levelsRef.current =
          buildWallPlan(playerZ)

        forceUpdate(value => value + 1)
        requestPreloadNearPlayer(playerZ)

      } catch (err) {
        console.error("LevelStreamer teleport failed", err)

      } finally {
        teleportLoadingRef.current = false

        if (!cancelled) {
          setGameState("playing")
        }
      }
    }

    handleTeleport()

    return () => {
      cancelled = true
      teleportLoadingRef.current = false
    }
  }, [teleportVersion])

  useLayoutEffect(() => {
    if (!initializedRef.current) return
    if (!worldGroup?.current) return

    const playerZ =
      -worldGroup.current.position.z

    const newPlan =
      buildWallPlan(playerZ)

    if (commitWallPlan(newPlan)) {
      forceUpdate(value => value + 1)
    }

    requestPreloadNearPlayer(playerZ)
  }, [finishedTurns])

  useFrame(() => {
    if (!initializedRef.current) return
    if (teleportLoadingRef.current) return
    if (!worldGroup?.current) return

    refreshCollisionTrees()

    const playerZ =
      -worldGroup.current.position.z

    const newPlan =
      buildWallPlan(playerZ)

    if (commitWallPlan(newPlan)) {
      forceUpdate(value => value + 1)
    }

    requestPreloadNearPlayer(playerZ)
  })

  const turnTreeSegments =
    buildVisibleTurnTreeSegments()

  return (
    <>
      {levelsRef.current.map(segment => {
        const level = segment.level
        const turn = segment.turn

        const levelTextureInfo =
          LEVEL_TEXTURES[level] || LEVEL_TEXTURES[1]

        let levelXLeft =
          xLeft - (levelTextureInfo.offsetLeft ?? 0)

        let levelXRight =
          xRight + (levelTextureInfo.offsetRight ?? 0)

        if (segment.isTurnLevel && turn) {
          const xCenter =
            ((xLeft + xRight) / 2) +
            (turn.offsetX ?? 0)

          levelXLeft =
            xCenter -
            turn.width / 2 -
            (levelTextureInfo.offsetLeft ?? 0)

          levelXRight =
            xCenter +
            turn.width / 2 +
            (levelTextureInfo.offsetRight ?? 0)
        }

        return (
          <group
            key={`wall-${segment.renderKey}`}
            position={segment.position}
            rotation={[0, segment.rotationY, 0]}
            visible={!segment.hidden}
          >
            <WallsLevel
              level={level}
              baseZ={0}
              xLeft={levelXLeft}
              xRight={levelXRight}
              isTurnLevel={segment.isTurnLevel}
              finishedTurns={finishedTurns}
              turnRotationY={segment.rotationY}
            />

            {!segment.hidden && (
              <LevelTrees
                ref={(api) => {
                  if (api) {
                    levelTreeRefs.current.set(segment.renderKey, api)
                  } else {
                    levelTreeRefs.current.delete(segment.renderKey)
                  }
                }}
                level={level}
                isTurnLevel={segment.isTurnLevel}
                arbres={arbres}
              />
            )}
          </group>
        )
      })}

      {turnTreeSegments.map(segment => (
        <group
          key={`backfill-${segment.renderKey}`}
          position={segment.position}
          rotation={[0, segment.rotationY, 0]}
        >
          <LevelTrees
            ref={(api) => {
              if (api) {
                backfillTreeRefs.current.set(segment.renderKey, api)
              } else {
                backfillTreeRefs.current.delete(segment.renderKey)
              }
            }}
            level={segment.level}
            isTurnLevel={true}
            arbres={arbres}
          />
        </group>
      ))}
    </>
  )
})

function Intersections({
  positions = [],
  roadLength = 80,
  xLeft = 0,
  xRight = 0,
  snappedTurnsRef
}) {
  const { gl } = useThree()
  const centerX = (xLeft + xRight) / 2

  const defaultTextures = useLoader(
    THREE.TextureLoader,
    [
      "/textures/intersection1.jpg",
      "/textures/intersection2.jpg",
      "/textures/intersection3.jpg"
    ]
  )

  const turnTextures = useTurnTextures(TOURNANTS)


  useEffect(() => {
    const allTextures = [
      ...defaultTextures,
      ...Object.values(turnTextures)
    ]

    allTextures.forEach(tex => {
      if (!tex || tex.userData?.init) return

      tex.colorSpace = THREE.SRGBColorSpace
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping
      tex.generateMipmaps = true
      tex.minFilter = THREE.LinearMipmapLinearFilter
      tex.magFilter = THREE.LinearFilter
      tex.anisotropy = gl.capabilities.getMaxAnisotropy()
      tex.userData = { init: true }
      tex.needsUpdate = true
    })
  }, [defaultTextures, turnTextures, gl])

  const geometry = useMemo(() => {
    return new THREE.PlaneGeometry(1, 1)
  }, [])

  const defaultMaterials = useMemo(() => {
    return defaultTextures.map(tex =>
      new THREE.MeshBasicMaterial({
        map: tex,
        toneMapped: false,
        polygonOffset: true
      })
    )
  }, [defaultTextures])

  const turnMaterials = useMemo(() => {
    const map = {}
    Object.entries(turnTextures).forEach(([id, tex]) => {
      if (!tex) return
      map[id] = new THREE.MeshBasicMaterial({
        map: tex,
        toneMapped: false
      })
    })
    return map
  }, [turnTextures])

  function getTurnForIntersection(intersection) {
    return TOURNANTS.find(turn =>
      turn.id === intersection.id ||
      Math.abs((turn.centerZ ?? Infinity) - intersection.z) < 1
    )
  }

  useEffect(() => {
    positions.forEach(p => {
      const turn = getTurnForIntersection(p)
      const length = turn ? (turn.intersectionLength ?? LEVEL_EFFECTIVE_LENGTH[turn.level] * 2) : roadLength
      const repeatY = turn ? length / 10 : Math.min(4, length / 30)

      const tex = turn
        ? turnTextures[turn.id]
        : (
            p.width <= 12
              ? defaultTextures[0]
              : p.width <= 16
              ? defaultTextures[1]
              : defaultTextures[2]
          )

      if (!tex) return
      if (tex.userData?.repeatY === repeatY) return

      tex.repeat.set(1, repeatY)
      tex.userData.repeatY = repeatY
    })
  }, [positions, defaultTextures, turnTextures, roadLength])

  return (
    <group>
      {positions.map((p, i) => {

        const correctionZ = 10
        const localZ = p.z //+ correctionZ
        const turn = getTurnForIntersection(p)
        
        const material =
          (turn && turnMaterials[turn.id]) ||
          (p.width <= 12
            ? defaultMaterials[0]
            : p.width <= 16
            ? defaultMaterials[1]
            : defaultMaterials[2])

        const length = turn
          ? (turn.intersectionLength ?? LEVEL_EFFECTIVE_LENGTH[turn.level] * 2)
          : roadLength
        let activeWidth = p.width
        let activeCenterX = centerX

        if (turn) {
          const turnWallLayout = getTurnWallLayout(turn)

          activeWidth = turnWallLayout.width
          activeCenterX =
            centerX +
            (turnWallLayout.rightOffset - turnWallLayout.leftOffset) / 2
        }

        return (
          <group key={i} position={[p.offsetX, 0, localZ]}>
            <mesh
              position={[activeCenterX, 0.02, 0]}
              rotation={[-Math.PI / 2, 0, turn ? (turn.direction === "right" ? -Math.PI / 2 : Math.PI / 2) : Math.PI / 2]}
              geometry={geometry}
              material={material}
              scale={[activeWidth, length, 1]}
            />
          </group>
        )
      })}
    </group>
  )
}

//========================================================
function ParallaxSides({ xLeft = -55, xRight = 25, hiddenDuringTurnRef }) {

  const { worldGroup } = useGame()
  const { gl } = useThree()
  const worldZ = worldGroup?.current?.position.z || 0
  const treesTex = useTexture("/decors/fond/arbres.png")
  const cityTex = useTexture("/decors/fond/ville.png")
  const groupRef = useRef()

  useEffect(() => {
    const maxAnisotropy = gl.capabilities.getMaxAnisotropy()

    ;[treesTex, cityTex].forEach(tex => {
      if (!tex) return

      tex.colorSpace = THREE.SRGBColorSpace
      tex.generateMipmaps = true
      tex.minFilter = THREE.LinearMipmapLinearFilter
      tex.magFilter = THREE.LinearFilter
      tex.anisotropy = maxAnisotropy
      tex.needsUpdate = true
    })
  }, [treesTex, cityTex, gl])

  // ==================================================
  // CONFIG
  // ==================================================

  const HEIGHT_TREES = 18
  const HEIGHT_CITY = 22
  const DEFAULT_RATIO = 1424 / 480

  const getStripSize = (tex, baseHeight) => {

    const image = tex?.image
    const ratio =
      image?.width && image?.height
        ? image.width / image.height
        : DEFAULT_RATIO

    const scale =
      image?.height === BASE_TEXTURE_HEIGHT
        ? 0.5
        : 1

    const height = baseHeight * scale

    return {
      height,
      width: height * ratio,
      scale
    }
  }

  const treesSize = getStripSize(treesTex, HEIGHT_TREES)
  const rawCitySize = getStripSize(cityTex, HEIGHT_CITY)
  const cityHeight = Math.max(rawCitySize.height, treesSize.height)
  const citySize = {
    ...rawCitySize,
    height: cityHeight,
    width: cityHeight * (
      cityTex?.image?.width && cityTex?.image?.height
        ? cityTex.image.width / cityTex.image.height
        : DEFAULT_RATIO
    )
  }
  const COUNT = 12
  const GROUND_OFFSET = -2
  const TURN_PARALLAX_SCALE = 1
  const PARALLAX_END_Z = getLevelStartZ(17)

  // ==================================================
  // DÉSACTIVER PRÈS DES TOURNANTS
  // ==================================================

  const turnBlockZones = useMemo(() => {

    return TOURNANTS.map(turn => {

      const halfWidth = turn.width / 2

      return {
        turn,
        start: turn.centerZ - halfWidth,
        end: turn.centerZ + halfWidth
      }
    })

  }, [])

  const getTurnZone = (z, width) => {
    const start = z - width / 2
    const end = z + width / 2

    return turnBlockZones.find(zone =>
      start <= zone.end &&
      end >= zone.start
    )
  }

  const getTileScale = (data, side, sourceScale) => {
    const onCommuneLeft =
      side === "left" &&
      data.z <= commune.z &&
      data.z >= mcGill.z

    return onCommuneLeft ? 0.5 : sourceScale
  }

  // ==================================================
  // REFS
  // ==================================================

  const trees = useRef([])
  const city = useRef([])

  // ==================================================
  // INIT
  // ==================================================

  useEffect(() => {

    trees.current =
      Array.from(
        { length: COUNT },
        (_, i) => ({
          z: -i * treesSize.width
        })
      )

    city.current =
      Array.from(
        { length: COUNT },
        (_, i) => ({
          z: -i * citySize.width
        })
      )

  }, [treesSize.width, citySize.width])

  // ==================================================
  // UPDATE
  // ==================================================

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.visible = hiddenDuringTurnRef?.current !== "Commune"
    }

    const recycle = (
      arr,
      width
    ) => {

      arr.current.forEach((data) => {

        const visibleZ =
          data.z + worldZ

        // ============================================
        // AVANCER
        // ============================================

        if (visibleZ > 60) {

          let frontZ = Infinity

          for (const d of arr.current) {

            if (d.z < frontZ) {
              frontZ = d.z
            }
          }

          data.z =
            frontZ - width
        }

        // ============================================
        // RECULER
        // ============================================

        if (visibleZ < -800) {

          let backZ = -Infinity

          for (const d of arr.current) {

            if (d.z > backZ) {
              backZ = d.z
            }
          }

          data.z =
            backZ + width
        }
      })
    }

    recycle(trees, treesSize.width)
    recycle(city, citySize.width)

  })

  // ==================================================
  // RENDER
  // ==================================================

  const makeStrip = (
    tex,
    H,
    W,
    x,
    mirror,
    arr,
    side,
    kind
  ) => {

    if (!tex?.image) {
      return null
    }

    const layerOrder = kind === "trees" ? -8 : -9

    return arr.current
      .flatMap((data, i) => {
        const scale = getTileScale(data, side, 1)
        const stripW = W * scale
        const stripH = H * scale
        const y =
          stripH / 2 +
          GROUND_OFFSET +
          (kind === "city" ? stripH * 0.18 : 0)
        const repeatCount = Math.max(1, Math.ceil(W / stripW))
        const meshes = []

        for (let tile = 0; tile < repeatCount; tile++) {
          const z = data.z - tile * stripW
          const turnZone = getTurnZone(z, stripW)
          const hideEastCityAfterCommune =
            kind === "city" &&
            side === "left" &&
            z <= commune.z
          const afterParallaxEnd = z <= PARALLAX_END_Z

          if (turnZone || hideEastCityAfterCommune || afterParallaxEnd) {
            continue
          }

          meshes.push(
            <mesh
              key={`${i}-${tile}`}
              position={[x, y, z]}
              rotation={[
                0,
                mirror
                  ? Math.PI / 2
                  : -Math.PI / 2,
                0
              ]}
              frustumCulled={false}
              renderOrder={layerOrder}
            >
              <planeGeometry
                args={[stripW, stripH]}
              />

        <meshBasicMaterial
          map={tex}
          alphaTest={0.5}
          side={THREE.FrontSide}
          depthWrite={false}
          depthTest={true}
        />

            </mesh>
          )
        }

        return meshes
      })
  }

  const makeTurnStrip = (
    tex,
    H,
    W,
    x,
    side,
    kind
  ) => {

    if (!tex?.image) {
      return null
    }

    const layerOrder = kind === "trees" ? -8 : -9
    const turnSide = side === "left" ? -1 : 1
    const turnDistance = Math.max(14, Math.abs(x) * 0.45)

    return turnBlockZones.flatMap(({ turn }) => {
      if (turn.level >= 17) {
        return []
      }

      if (
        kind === "city" &&
        side === "left" &&
        turn.centerZ <= commune.z
      ) {
        return []
      }

      const length = turn.intersectionLength ?? LEVEL_EFFECTIVE_LENGTH[turn.level] * 2
      const stripW = W * TURN_PARALLAX_SCALE
      const stripH = H * TURN_PARALLAX_SCALE
      const y = stripH / 2 + GROUND_OFFSET
      const count = Math.ceil(length / stripW) + 1
      const z =
        turn.centerZ +
        turnSide * turnDistance +
        (turn.direction === "right" ? 8 : -8)

      return Array.from({ length: count }, (_, i) => {
        const localX = -length / 2 + stripW / 2 + i * stripW

        return (
          <mesh
            key={`${turn.id}-${side}-${i}`}
            position={[localX, y, z]}
            rotation={[
              0,
              side === "left" ? 0 : Math.PI,
              0
            ]}
            frustumCulled={false}
            renderOrder={layerOrder}
          >
            <planeGeometry
              args={[stripW, stripH]}
            />

            <meshBasicMaterial
              map={tex}
              alphaTest={0.5}
              side={THREE.FrontSide}
              depthWrite={false}
              depthTest={true}
            />
          </mesh>
        )
      })
    })
  }

  // ==================================================
  // RETURN
  // ==================================================

  return (
    <group ref={groupRef}>
      {makeStrip(
        cityTex,
        citySize.height,
        citySize.width,
        xLeft,
        true,
        city,
        "left",
        "city"
      )}

      {makeTurnStrip(
        cityTex,
        citySize.height,
        citySize.width,
        xLeft,
        "left",
        "city"
      )}

      {makeStrip(
        cityTex,
        citySize.height,
        citySize.width,
        xRight,
        false,
        city,
        "right",
        "city"
      )}

      {makeTurnStrip(
        cityTex,
        citySize.height,
        citySize.width,
        xRight,
        "right",
        "city"
      )}

      {makeStrip(
        treesTex,
        treesSize.height,
        treesSize.width,
        xLeft + 15,
        true,
        trees,
        "left",
        "trees"
      )}

      {makeTurnStrip(
        treesTex,
        treesSize.height,
        treesSize.width,
        xLeft + 15,
        "left",
        "trees"
      )}

      {makeStrip(
        treesTex,
        treesSize.height,
        treesSize.width,
        xRight - 15,
        false,
        trees,
        "right",
        "trees"
      )}

      {makeTurnStrip(
        treesTex,
        treesSize.height,
        treesSize.width,
        xRight - 15,
        "right",
        "trees"
      )}
    </group>
  )
}

//DÉBUT CODEX


// --------------------------------------------------
// CYCLISTE
// --------------------------------------------------
const Cyclist = React.forwardRef(({
  direction,
  controlLoss,
  isColliding,
  collisionType,
  leftLimit,
  rightLimit,
  flipTilt,
  isInvincibleRef,
  hitCar,
  treeHitDir,
  cyclistHitSide
}, ref) => {

  const { playerSpeed, levelMaxSpeed, gameState, setGameState, controlsEnabled } = useGame()

  const MAX_PLAYER_SPEED = levelMaxSpeed 
  const PEDAL_FRAMES = 8
  const SIDEWALK_Y = 1.72   // hauteur trottoir
  const FLIP_DURATION = 0.65
  const JUMP_HEIGHT = 0.8
  const CRASH_Y = 0.9
  const JUMP_FORCE = 8
  const GRAVITY = -35
  const DOUBLE_TAP_DELAY = 300
  const MIN_STEER_RATIO = IS_MOBILE ? 0.5 : 0.35
  const MAX_STEER_RATIO = 2.0
  const LATERAL_ACCEL = IS_MOBILE ? 50 : 24
  const MAX_LATERAL_SPEED = IS_MOBILE ? 14 : 10
  const MAX_LATERAL_SPEED_LIMIT = IS_MOBILE ? 14 : 7.2
  const MAX_STEP_ABSOLUTE = IS_MOBILE ? 0.25 : 0.18
  const MAX_TILT = 0.1
  const BASE_RESPONSE = 10
  const EXTRA_RESPONSE = 18
  const DEAD_ZONE = 0.001
  const [isCrashed, setIsCrashed] = useState(false)

  const pivot = useRef()
  const sprite = useRef()
  const isJumping = useRef(false)
  const jumpVelocity = useRef(0)
  const lastTap = useRef(0)
  const frame = useRef(0)
  const lateralVelocity = useRef(0)
  const wasColliding = useRef(false)
  const flipTime = useRef(0) // obstacle
  const carCrashTime = useRef(0)
  const cyclistCrashTime = useRef(0)
  const treeCrashTime = useRef(0)
  const treeCrashStarted = useRef(false)
  const treeCrashActive = useRef(false)
  const treeCrashDir = useRef(1)
  const flipStartZ = useRef(0)
  const flipStartX = useRef(0)
  const flipDir = useRef(new THREE.Vector2())
  const flipForwardDistance = useRef(0)
  const crashedRef = useRef(false)
  const crashLocked = useRef(false)
  const isGrounded = useRef(true)
  const isFlipping = useRef(false)
  const crashIndex = useRef(Math.floor(Math.random() * 8))
  const crashEnded = useRef(false)
  const wasCollidingCyclist = useRef(false)
  const crashVelocity = useRef(new THREE.Vector2(0, 0))
  const carCrashActive = useRef(false)
  const cyclistCrashDir = useRef(1)
  

  const isCarCrash =  collisionType === "car" &&  isCrashed
  const isObstacleCrash = collisionType === "obstacle"
  const isTreeCrash = collisionType === "tree"
  
  const texFront = useLoader(THREE.TextureLoader, "/sprites/cycliste11.png")
  const texBack  = useLoader(THREE.TextureLoader, "/sprites/face.png")
  const collisionLastFrame = useLoader(THREE.TextureLoader, "/sprites/collision1.png")
  const carCollisionTex = useLoader(THREE.TextureLoader, "/sprites/collisionVoiture.png")
  const treeCollisionTex = useLoader(THREE.TextureLoader, "/sprites/collisionArbre.png")
  const cyclistCrashLeftTex = useLoader(THREE.TextureLoader, "/sprites/collisionCyclisteGauche.png")
  const cyclistCrashRightTex = useLoader(THREE.TextureLoader, "/sprites/collisionCyclisteDroite.png")
  const activeCyclistCrashTex = useRef(cyclistCrashRightTex)

  useImperativeHandle(ref, () => ({
    getLateralVelocity: () => lateralVelocity.current,
    position: pivot.current?.position,
    scale: pivot.current?.scale,
    pivot: pivot.current,
    sprite: sprite.current,
    jump
  }))

 useEffect(() => {

  function onKeyDown(e) {
    if (gameState !== "playing" || !controlsEnabled || isGameUIEvent(e)) return

    if (e.code === "Space") {
      e.preventDefault()
      jump()
    }
  }

  function onTouchStart(e) {
    if (gameState !== "playing" || !controlsEnabled || isGameUIEvent(e)) return

    const now = Date.now()
    const delta = now - lastTap.current

    if (delta < DOUBLE_TAP_DELAY) {
      jump()
    }

    lastTap.current = now
  }

  window.addEventListener("keydown", onKeyDown)
  window.addEventListener("touchstart", onTouchStart)

  return () => {
    window.removeEventListener("keydown", onKeyDown)
    window.removeEventListener("touchstart", onTouchStart)
  }

  if (gameState !== "playing" || !controlsEnabled) lastTap.current = 0
}, [gameState, isCrashed, controlsEnabled])

  useEffect(() => {
    collisionLastFrame.repeat.set(1 / 5, 1)
    collisionLastFrame.offset.x = 4 / 5
    collisionLastFrame.wrapS = THREE.RepeatWrapping
    collisionLastFrame.wrapT = THREE.ClampToEdgeWrapping
    collisionLastFrame.repeat.set(1 / 5, 1)
    
  }, [])

  const crashTextures = useLoader(THREE.TextureLoader, [
    "/sprites/chute1.png",
    "/sprites/chute2.png",
    "/sprites/chute3.png",
    "/sprites/chute4.png",
    "/sprites/chute5.png",
    "/sprites/chute6.png",
    "/sprites/chute7.png",
    "/sprites/chute8.png"
  ])

  crashTextures.forEach(tex => {

    tex.colorSpace = THREE.SRGBColorSpace

    // ⭐ CLÉ : texture nette
    tex.magFilter = THREE.NearestFilter
    tex.minFilter = THREE.NearestFilter

    // ⭐ SUPPRIME mipmaps (cause halos)
    tex.generateMipmaps = false

    // ⭐ IMPORTANT : alpha propre
    tex.premultiplyAlpha = true

    // ⭐ évite bordures blanches
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping
  })
    
  useEffect(() => {

    texFront.wrapS = THREE.RepeatWrapping
    texFront.repeat.set(1 / 8, 1)

    crashTextures.forEach(tex => {
      tex.colorSpace = THREE.SRGBColorSpace
      tex.generateMipmaps = false
      tex.minFilter = THREE.LinearFilter
      tex.magFilter = THREE.LinearFilter
      tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping
    })

    carCollisionTex.wrapS = THREE.RepeatWrapping
    carCollisionTex.wrapT = THREE.ClampToEdgeWrapping
    carCollisionTex.repeat.set(1 / 3, 1)
    carCollisionTex.offset.set(0, 0)
    carCollisionTex.magFilter = THREE.NearestFilter
    carCollisionTex.minFilter = THREE.NearestFilter
    carCollisionTex.generateMipmaps = false

    treeCollisionTex.wrapS = THREE.RepeatWrapping
    treeCollisionTex.wrapT = THREE.ClampToEdgeWrapping
    treeCollisionTex.repeat.set(1 / 3, 1)
    treeCollisionTex.offset.set(0, 0)
    treeCollisionTex.magFilter = THREE.NearestFilter
    treeCollisionTex.minFilter = THREE.NearestFilter
    treeCollisionTex.generateMipmaps = false

    cyclistCrashLeftTex.wrapS = THREE.RepeatWrapping
    cyclistCrashLeftTex.wrapT = THREE.ClampToEdgeWrapping
    cyclistCrashLeftTex.repeat.set(1 / 5, 1)
    cyclistCrashLeftTex.offset.set(0, 0)
    cyclistCrashLeftTex.magFilter = THREE.NearestFilter
    cyclistCrashLeftTex.minFilter = THREE.NearestFilter
    cyclistCrashLeftTex.generateMipmaps = false

    cyclistCrashRightTex.wrapS = THREE.RepeatWrapping
    cyclistCrashRightTex.wrapT = THREE.ClampToEdgeWrapping
    cyclistCrashRightTex.repeat.set(1 / 5, 1)
    cyclistCrashRightTex.offset.set(0, 0)
    cyclistCrashRightTex.magFilter = THREE.NearestFilter
    cyclistCrashRightTex.minFilter = THREE.NearestFilter
    cyclistCrashRightTex.generateMipmaps = false
  }, [])

useEffect(() => {
  if (gameState === "waitingContinue") {
    crashLocked.current = true
    lateralVelocity.current = 0
    jumpVelocity.current = 0
    crashVelocity.current.set(0, 0)

    if (collisionType === "car") {
      carCrashActive.current = false
      carCrashTime.current = 0

      carCollisionTex.repeat.x = 1 / 3
      carCollisionTex.offset.x = 2 / 3
      carCollisionTex.needsUpdate = true
    }

    if (collisionType === "tree") {
      /*
        On garde treeCrashStarted.current à true ici.
        Il ne faut pas permettre une réinitialisation pendant waitingContinue.
      */
      treeCrashActive.current = false
      treeCrashTime.current = 0

      treeCollisionTex.repeat.x = 1 / 3
      treeCollisionTex.offset.x = 2 / 3
      treeCollisionTex.needsUpdate = true

      if (pivot.current) {
        pivot.current.position.y = BASE_Y + 0.02
      }

      if (sprite.current) {
        sprite.current.position.set(0, 0.22, 0)
        sprite.current.rotation.set(-0.95, 0, 0)
        sprite.current.scale.set(
          treeCrashDir.current * 1.05,
          1.05,
          1
        )
      }
    }
  }

  if (gameState === "playing") {
    carCrashActive.current = false
    treeCrashActive.current = false
    treeCrashStarted.current = false

    crashLocked.current = false
    crashedRef.current = false
    crashEnded.current = false

    carCrashTime.current = 0
    treeCrashTime.current = 0
    cyclistCrashTime.current = 0

    crashVelocity.current.set(0, 0)
    lateralVelocity.current = 0
    jumpVelocity.current = 0

    setIsCrashed(false)

    if (sprite.current) {
      sprite.current.position.set(0, 0, 0)
      sprite.current.rotation.set(0, 0, 0)
      sprite.current.scale.set(1, 1, 1)
    }

    if (pivot.current) {
      pivot.current.position.y = BASE_Y
      pivot.current.position.z = 0

      if (pivot.current.userData) {
        delete pivot.current.userData.carCrashLockedZ
      }
    }

    carCollisionTex.repeat.x = 1 / 3
    carCollisionTex.offset.x = 0
    carCollisionTex.needsUpdate = true

    treeCollisionTex.repeat.x = 1 / 3
    treeCollisionTex.offset.x = 0
    treeCollisionTex.needsUpdate = true
  }
}, [gameState, collisionType])

function jump() {

  if (gameState !== "playing" || isFlipping.current || isCrashed || !pivot.current) return

  const playerX = pivot.current?.position.x
  const isOnSidewalk = playerX > rightLimit - 0.4 || playerX < leftLimit + 0.4
  const INTERSECTION_Y = 0.05

  let groundY = isOnSidewalk ? SIDEWALK_Y : BASE_Y

  // ⭐ ne jamais être sous l’intersection
  groundY = Math.max(groundY, INTERSECTION_Y)

  // ⭐ distance réelle au sol
  const distanceToGround = pivot.current?.position.y - groundY

  // tolérance pour éviter les erreurs flottantes
  if (distanceToGround > 0.05) return

  // ✅ autorisé : on est au sol
  jumpVelocity.current = JUMP_FORCE
  isJumping.current = true
}

useFrame((state, dtRaw) => {

  const moveDt = Math.min(dtRaw, 0.1) // vrai temps réel pour déplacements gameplay
  const dt = Math.min(dtRaw, 1 / 24) // temps stabilisé pour physique/lerp

  if (dt <= 0) return
  if (gameState === "gameOver") return

  const isCrashState = gameState === "crash" || gameState === "gameOverAnim"
  const speedRatio = THREE.MathUtils.clamp(playerSpeed.current / MAX_PLAYER_SPEED,0,1)
  const canSteer = speedRatio > 0.08   // seuil ajustable
  if (gameState !== "playing" && gameState !== "crash" && gameState !== "gameOverAnim") {
    return
  }

  // 🔥 freeze mouvement pendant crash
  if (isCrashState &&
    (
      collisionType === "car" ||
      collisionType === "tree" ||
      collisionType === "obstacle" ||
      collisionType === "cyclist_fast"
    )) {
    lateralVelocity.current = 0
  }

  if (!canSteer) {
    lateralVelocity.current = 0
  }

  // 🛡️ INVINCIBILITY VISUAL
  pivot.current.traverse(obj => {

    if (!obj.isMesh || !obj.material) return

    if (isInvincibleRef.current) {

      const t = state.clock.elapsedTime
      const blink = Math.floor(t * 10) % 2 === 0

      obj.material.transparent = true
      obj.material.opacity = blink ? 0.35 : 0.8
      obj.material.color.setRGB(0.7, 0.9, 1) // halo bleuté

    } else {

      obj.material.opacity = 1
      obj.material.color.setRGB(1, 1, 1)
    }

  })

  // ==================================================
  // VISIBILITÉ
  // ==================================================

  pivot.current.visible = true
  sprite.current.visible = true

  if (!pivot.current || !sprite.current) return

  // ==================================================
  // 🧊 FREEZE STATE
  // ==================================================

  let shouldHide = false

  // 🚗 collision voiture → cacher
  if (collisionType === "car" && gameState === "waitingContinue") {
    shouldHide = true
  }

  // 🎯 appliquer UNE SEULE FOIS
  pivot.current.visible = !shouldHide
  sprite.current.visible = !shouldHide

  if (shouldHide) return
      const playerX = pivot.current?.position.x

    // ==================================================
    // 🧱 HAUTEUR + SAUT
    // ==================================================

    // ⭐ CRASH → hauteur fixe (au-dessus du sol)
    if (isCrashed) {

      const CRASH_GROUND_Y = Math.max(BASE_Y, 0.02) // intersection ≥ route

      pivot.current.position.y = CRASH_GROUND_Y + 0.1
      jumpVelocity.current = 0
      isGrounded.current = true
      lateralVelocity.current = 0

    } else {

      const isOnSidewalk = playerX > rightLimit - 0.4 || playerX < leftLimit + 0.4
      const groundY = isOnSidewalk ? SIDEWALK_Y : BASE_Y
      const gravity = jumpVelocity.current > 0 ? GRAVITY * 0.6 : GRAVITY * 1.5

      jumpVelocity.current += gravity * moveDt
      pivot.current.position.y += jumpVelocity.current * moveDt

      if (pivot.current.position.y <= groundY) {

        pivot.current.position.y = groundY
        jumpVelocity.current = 0
        isJumping.current = false
        isGrounded.current = true

      } else {

        isJumping.current = true
        isGrounded.current = false
      }
    }
  // --------------------------------------------------------------------------
  // 🚫 IMMOBILE → PAS DE GLISSEMENT
  // --------------------------------------------------------------------------

  if (playerSpeed.current < 0.02 && !isCrashed && !isFlipping.current) {

    const MAX_TILT = 0.1
    const targetTilt = -direction * MAX_TILT

    lateralVelocity.current = 0
    sprite.current.rotation.z = THREE.MathUtils.lerp(sprite.current.rotation.z, targetTilt, 1 - Math.exp(-6 * moveDt))
  }

  // ==================================================
  // 🚴 MOUVEMENT LATÉRAL RÉALISTE
  // ==================================================
  const minX = Math.min(leftLimit, rightLimit)
  const maxX = Math.max(leftLimit, rightLimit)
  if (
    gameState === "playing" &&
    !isCrashed &&
    !isFlipping.current &&
    playerSpeed.current >= 0.02 && 
    !(isColliding && (collisionType === "obstacle" || collisionType === "car"))
  ){

  // --------------------------------------------------
  // INPUT
  // --------------------------------------------------

  let inputDir = direction

  // bruit si perte de contrôle
  if (Math.abs(direction) > 0.01 && controlLoss > 0) {
    inputDir = THREE.MathUtils.clamp(
      direction + (Math.random() - 0.5) * controlLoss,
      -1,
      1
    )
  }

  // --------------------------------------------------
  // DEPENDANCE VITESSE AVANT
  // --------------------------------------------------

  const steerRatio = THREE.MathUtils.lerp(MIN_STEER_RATIO, MAX_STEER_RATIO, speedRatio)
  const screenFactor = THREE.MathUtils.clamp(900 / window.innerWidth, 0.65, 1) // vitesse latérale cible
  const targetLatSpeed = inputDir * MAX_LATERAL_SPEED * steerRatio * screenFactor

  // --------------------------------------------------
  // INERTIE LATÉRALE
  // --------------------------------------------------

  if (IS_MOBILE) {

    // 🔥 instantané
    const accel = LATERAL_ACCEL * 1.2
    const delta = targetLatSpeed - lateralVelocity.current
    const maxDelta = accel * moveDt

    lateralVelocity.current += THREE.MathUtils.clamp(delta, -maxDelta, maxDelta)

  } else {

    const reversing = Math.sign(targetLatSpeed) !== Math.sign(lateralVelocity.current)
    const accel = reversing ? LATERAL_ACCEL * 1.6 : LATERAL_ACCEL
    const maxDelta = accel * moveDt
    const delta = targetLatSpeed - lateralVelocity.current

    lateralVelocity.current += THREE.MathUtils.clamp(delta, -maxDelta, maxDelta)
  }

  
  // dead zone anti tremblement
  if (Math.abs(lateralVelocity.current) < DEAD_ZONE) {
    lateralVelocity.current = 0
  }

  // --------------------------------------------------
  // DÉPLACEMENT LATÉRAL
  // --------------------------------------------------

  const step = lateralVelocity.current * moveDt
  const maxStepTime = MAX_LATERAL_SPEED_LIMIT * moveDt
  const maxStep = Math.min(maxStepTime, MAX_STEP_ABSOLUTE)

  pivot.current.position.x += THREE.MathUtils.clamp(step, -maxStep, maxStep)

  // limites piste

  const currentX = Number.isFinite(pivot.current.position.x)
    ? pivot.current.position.x
    : 0

  pivot.current.position.x = THREE.MathUtils.clamp(currentX, minX, maxX)

// ==================================================
// 🎯 INCLINAISON STABLE (améliorée)
// ==================================================

// intensité du braquage réel (−1 → 1)
const isStopped = playerSpeed.current < 0.02
const steerAmount = isStopped ? direction : lateralVelocity.current / MAX_LATERAL_SPEED
const targetTilt = isStopped ? -direction * MAX_TILT : -steerAmount * MAX_TILT * speedRatio

// réponse dépend du braquage
const response = BASE_RESPONSE + EXTRA_RESPONSE * Math.pow(Math.abs(steerAmount), 0.6)

// interpolation exponentielle stable FPS
if (Math.abs(sprite.current.rotation.z - targetTilt) > 0.0005) {
    sprite.current.rotation.z = THREE.MathUtils.lerp(sprite.current.rotation.z, targetTilt, 1 - Math.exp(-response * moveDt))
  }
}

  if (!isCrashState && !isFlipping.current) {
    pivot.current.position.x = THREE.MathUtils.clamp(
      pivot.current.position.x,
      minX,
      maxX
    )
  }

  // ==================================================
  // 🚴 ANIMATION PEDALAGE
  // ==================================================
  const PEDAL_STOP_RATIO = 0.06
  const MAX_CADENCE = 16
  const pedalRatio = Math.pow(speedRatio, 1.8)
  const cadence = THREE.MathUtils.lerp(0, MAX_CADENCE, pedalRatio)

  if (speedRatio > PEDAL_STOP_RATIO && !(isColliding && (collisionType === "obstacle" || collisionType === "car"))) {

    frame.current += moveDt * cadence

    const frameIndex = Math.floor(frame.current) % PEDAL_FRAMES
    texFront.offset.x = frameIndex / PEDAL_FRAMES
    texBack.offset.x = frameIndex / PEDAL_FRAMES
  }
  // ==================================================
  // 🚗 INIT COLLISION VOITURE (VECTORIEL)
  // ==================================================

  if (
  collisionType === "car" &&
  isColliding &&
  isCrashState &&
  !carCrashActive.current &&
  !crashedRef.current &&
  !crashEnded.current
) {

    isFlipping.current = false
    treeCrashActive.current = false
    carCrashActive.current = true
    crashedRef.current = true
    carCrashTime.current = 0

    // ==================================================
    // 🔥 RESET VISUEL
    // ==================================================

    sprite.current.rotation.set(0, 0, 0)
    sprite.current.scale.set(1.2, 1.2, 1.2)

    // ==================================================
    // 🔥 POSITION INITIALE
    // ==================================================

    pivot.current.position.y = CRASH_Y + 0.05

    // ==================================================
    // ↔️ DIRECTION LATÉRALE
    // ==================================================

    const playerPos = pivot.current?.position
    const carPos = hitCar
      ? getPositionInFrame(hitCar, pivot.current.parent, new THREE.Vector3())
      : null

    let side = 1

    if (carPos) {

      side = Math.sign(playerPos.x - carPos.x)

      if (side === 0) {
        side = 1
      }
    }

    // ==================================================
    // 💥 FORCE SELON VITESSE
    // ==================================================

    const speedFactor = THREE.MathUtils.clamp(playerSpeed.current / MAX_PLAYER_SPEED, 0, 1)
    const X_FORCE = 6 + 4 * speedFactor
    const Z_FORCE = 3 + 2 * speedFactor
    const MAX_VEL = 12

    let vx = THREE.MathUtils.clamp(side * X_FORCE, -MAX_VEL, MAX_VEL)
    let vz = THREE.MathUtils.clamp(-Z_FORCE, -MAX_VEL, MAX_VEL)

    crashVelocity.current.set(vx, vz)
  }
// ==================================================
// 🌳 INIT COLLISION ARBRE
// ==================================================

if (
  isCrashState &&
  collisionType === "tree" &&
  !treeCrashStarted.current &&
  !treeCrashActive.current &&
  !crashEnded.current
) {
  treeCrashStarted.current = true
  treeCrashActive.current = true
  crashedRef.current = true
  crashEnded.current = false
  treeCrashTime.current = 0

  isFlipping.current = false
  carCrashActive.current = false

  treeCrashDir.current =
    treeHitDir === -1 || treeHitDir === 1
      ? treeHitDir
      : 1

  lateralVelocity.current = 0
  jumpVelocity.current = 0
  isJumping.current = false
  isGrounded.current = true

  /*
    Recul physique :
    - X : sens inverse de l’arbre.
    - Z : recul vers la caméra / arrière du choc.

    Si le Z recule du mauvais bord dans ton monde,
    change TREE_RECOIL_Z_FORCE à -6.5.
  */
  const TREE_RECOIL_X_FORCE = 4.5
  const TREE_RECOIL_Z_FORCE = 6.5

  crashVelocity.current.set(
    -treeCrashDir.current * TREE_RECOIL_X_FORCE,
    TREE_RECOIL_Z_FORCE
  )

  sprite.current.position.set(0, 0, 0)
  sprite.current.rotation.set(0, 0, 0)
  sprite.current.scale.set(1, 1, 1)

  pivot.current.position.y = BASE_Y + 0.04

  treeCollisionTex.repeat.x = 1 / 3
  treeCollisionTex.offset.x = 0
  treeCollisionTex.needsUpdate = true

  setIsCrashed(true)
}

 // ==================================================
// 🚴 INIT COLLISION CYCLISTE RAPIDE
// ==================================================

if (
  collisionType === "cyclist_fast" &&
  isColliding &&
  !wasCollidingCyclist.current &&
  !isInvincibleRef.current
  ) {

    // =====================================
    // 🔥 RESET GLOBAL
    // =====================================

    crashedRef.current = true
    cyclistCrashTime.current = 0
    crashEnded.current = false

    // =====================================
    // 🔥 DÉSACTIVE AUTRES CRASHS
    // =====================================

    carCrashActive.current = false
    treeCrashActive.current = false
    isFlipping.current = false

    // =====================================
    // 🔥 ÉTAT CRASH
    // =====================================

    setIsCrashed(true)

    // =====================================
    // 🔥 RESET VISUEL
    // =====================================

    sprite.current.rotation.set(0, 0, 0)
    sprite.current.scale.set(1, 1, 1)

    // =====================================
    // 🔥 POSITION INITIALE
    // =====================================

    pivot.current.position.y = CRASH_Y + 0.05
    lateralVelocity.current = 0

    // =====================================
    // ↔️ DIRECTION IMPACT
    // =====================================

    cyclistCrashDir.current = cyclistHitSide === -1 ? 1 : -1
    activeCyclistCrashTex.current = cyclistCrashDir.current === 1 ? cyclistCrashRightTex : cyclistCrashLeftTex

    // fallback sécurité
    if (
      cyclistCrashDir.current !== 1 &&
      cyclistCrashDir.current !== -1
    ) {
      cyclistCrashDir.current = 1
    }

    // =====================================
    // 💥 FORCE SELON VITESSE
    // =====================================

    const speedFactor = THREE.MathUtils.clamp(
      playerSpeed.current / MAX_PLAYER_SPEED,
      0,
      1
    )

    // projection latérale
    const X_FORCE = 4 + 5 * speedFactor

    // projection avant
    const Z_FORCE = 2 + 6 * speedFactor
    const MAX_VEL = 10

    const vx = THREE.MathUtils.clamp(
      cyclistCrashDir.current * X_FORCE,
      -MAX_VEL,
      MAX_VEL
    )

    const vz = THREE.MathUtils.clamp(
      -Z_FORCE,
      -MAX_VEL,
      MAX_VEL
    )

    crashVelocity.current.set(vx, vz)

    // =====================================
    // 🎞️ RESET TEXTURES
    // =====================================

    activeCyclistCrashTex.current.offset.set(0, 0)
    activeCyclistCrashTex.current.needsUpdate = true

    // =====================================
    // 💾 MÉMO COLLISION
    // =====================================

    wasCollidingCyclist.current = true
  }

  // ==================================================
  // 🔓 RESET COLLISION
  // ==================================================

  if (!isColliding) {
    wasCollidingCyclist.current = false
  }

// ==================================================
// 🚴 RESET VISUEL COLLISION LENTE
// ==================================================

if (collisionType === "cyclist_slow" && !isInvincibleRef.current) {

  sprite.current.scale.set(1, 1, 1)
  sprite.current.rotation.x = 0
  sprite.current.rotation.z = 0
}

// ==================================================
// 🚴 ANIMATION COLLISION CYCLISTE RAPIDE
// ==================================================

if (collisionType === "cyclist_fast" && isCrashState) {

  const FRAME_COUNT = 5
  const FRAME_DURATION = 0.12
  const TOTAL = FRAME_COUNT * FRAME_DURATION

  cyclistCrashTime.current += moveDt

  // =====================================
  // FRAME SPRITESHEET
  // =====================================

  const frameIndex = Math.min(FRAME_COUNT - 1, Math.floor(cyclistCrashTime.current / FRAME_DURATION))
  const cyclistTex = activeCyclistCrashTex.current

  if (cyclistTex) {

    // =====================================
    // ▶️ ou ◀️ selon direction
    // =====================================

    const texFrame = cyclistCrashDir.current === 1 ? frameIndex : FRAME_COUNT - 1 - frameIndex

    cyclistTex.offset.x = texFrame / FRAME_COUNT
    cyclistTex.needsUpdate = true
  }

  // =====================================
  // PHYSIQUE IMPACT
  // =====================================

  crashVelocity.current.multiplyScalar(0.94)
  pivot.current.position.x += crashVelocity.current.x * moveDt
  pivot.current.position.z += crashVelocity.current.y * moveDt

  // =====================================
  // 🔍 COMPENSATION PERSPECTIVE
  // =====================================

  const scaleProgress = cyclistCrashTime.current / TOTAL
  const dynamicScale = THREE.MathUtils.lerp(1.0, 1.75, scaleProgress)

  sprite.current.scale.set(dynamicScale, dynamicScale, 1)

  // =====================================
  // GARDE AU SOL
  // =====================================

  const MIN_Y = CRASH_Y + 0.02

  if (pivot.current?.position.y < MIN_Y) {
    pivot.current.position.y = MIN_Y
  }

  // =====================================
  // RESET VISUEL
  // =====================================

  sprite.current.rotation.x = 0
  sprite.current.rotation.z = 0
  //sprite.current.scale.set(1, 1, 1)

  // =====================================
  // FIN
  // =====================================

  if (cyclistCrashTime.current > TOTAL && !crashEnded.current) {

    crashEnded.current = true

    
    if (gameState === "gameOverAnim") {

      
      setGameState("gameOver")

    } else {

      setGameState("waitingContinue")
    }
  }
}

// ==================================================
// 🚗 ANIMATION COLLISION VOITURE
// ==================================================

if (carCrashActive.current) {
  const FRAME_COUNT = 3
  const FRAME_DURATION = 0.12

  const ANIM_TOTAL =
    (FRAME_COUNT - 1) * FRAME_DURATION

  const HOLD_LAST_FRAME_DURATION = 0.18
  const TOTAL = ANIM_TOTAL + HOLD_LAST_FRAME_DURATION

  const safeDt = Math.min(moveDt, 0.033)

  // =====================================
  // 🔒 VERROUILLAGE Z AU DÉBUT DU CRASH
  // =====================================

  if (pivot.current.userData.carCrashLockedZ === undefined) {
    pivot.current.userData.carCrashLockedZ =
      pivot.current.position.z
  }

  const lockedZ =
    pivot.current.userData.carCrashLockedZ

  function setCarCrashFrame(frameIndex) {
    const safeFrame = THREE.MathUtils.clamp(
      frameIndex,
      0,
      FRAME_COUNT - 1
    )

    carCollisionTex.repeat.x = 1 / FRAME_COUNT
    carCollisionTex.offset.x = safeFrame / FRAME_COUNT
    carCollisionTex.needsUpdate = true
  }

  function forceLastCarCrashPose() {
    setCarCrashFrame(FRAME_COUNT - 1)

    sprite.current.rotation.x = -0.9
    sprite.current.scale.set(5, 1.6, 1.6)

    // Très important : aucun glissement en Z.
    pivot.current.position.z = lockedZ

    const MIN_Y = CRASH_Y + 0.02

    if (pivot.current.position.y < MIN_Y) {
      pivot.current.position.y = MIN_Y
    }
  }

  carCrashTime.current += safeDt

  // =====================================
  // 🚫 ANNULER TOUTE FORCE EN Z
  // =====================================

  /*
    crashVelocity.current.y était utilisé comme vitesse Z.
    On le remet à zéro à chaque frame pour empêcher un glissement
    vers l’auto ou vers -z après l’impact.
  */
  crashVelocity.current.y = 0

  /*
    On verrouille le Z à chaque frame.
    Même si une autre logique modifie le pivot avant/après,
    ce bloc le remet à la bonne position pendant le crash.
  */
  pivot.current.position.z = lockedZ

  // ==================================================
  // 🎞️ FRAME SPRITESHEET
  // ==================================================

  if (
  carCrashTime.current >= TOTAL &&
  !crashEnded.current
) {
  crashEnded.current = true

  forceLastCarCrashPose()

  crashVelocity.current.set(0, 0)
  lateralVelocity.current = 0
  jumpVelocity.current = 0

  carCrashActive.current = false
  carCrashTime.current = 0

  /*
    IMPORTANT :
    Ne pas remettre crashEnded.current = false ici.
    Ne pas remettre crashedRef.current = false ici.

    On les garde à true jusqu’au retour à playing,
    sinon l’init voiture peut repartir et remettre offset.x = 0.
  */

  if (gameState === "gameOverAnim") {
    setGameState("gameOver")
  } else {
    setGameState("waitingContinue")
  }
} else {
    const frameIndex = Math.floor(
      carCrashTime.current / FRAME_DURATION
    )

    setCarCrashFrame(frameIndex)

    // ==================================================
    // 🔥 GROS IMPACT VISUEL
    // ==================================================

    sprite.current.rotation.x =
      THREE.MathUtils.lerp(
        sprite.current.rotation.x,
        -0.9,
        1 - Math.exp(-12 * safeDt)
      )

    sprite.current.scale.x =
      THREE.MathUtils.lerp(
        sprite.current.scale.x,
        5,
        1 - Math.exp(-10 * safeDt)
      )

    sprite.current.scale.y =
      THREE.MathUtils.lerp(
        sprite.current.scale.y,
        1.6,
        1 - Math.exp(-10 * safeDt)
      )

    sprite.current.scale.z =
      THREE.MathUtils.lerp(
        sprite.current.scale.z,
        1.6,
        1 - Math.exp(-10 * safeDt)
      )

    // ==================================================
    // 💥 MOUVEMENT IMPACT LATÉRAL SEULEMENT
    // ==================================================

    crashVelocity.current.multiplyScalar(0.88)

    /*
      On garde seulement le déplacement X.
      Aucun déplacement Z.
    */
    pivot.current.position.x +=
      crashVelocity.current.x * safeDt

    pivot.current.position.z = lockedZ

    // ==================================================
    // 🔥 EMPÊCHE DE PASSER SOUS LE SOL
    // ==================================================

    const MIN_Y = CRASH_Y + 0.02

    if (pivot.current.position.y < MIN_Y) {
      pivot.current.position.y = MIN_Y
    }

    // ==================================================
    // 🔥 PETIT REBOND
    // ==================================================

    if (carCrashTime.current < 0.16) {
      pivot.current.position.y +=
        0.018 * Math.sin(carCrashTime.current * 44)
    }
  }

  // ==================================================
  // 🛑 FIN ANIMATION
  // ==================================================

  if (
    carCrashTime.current >= TOTAL &&
    !crashEnded.current
  ) {
    crashEnded.current = true

    forceLastCarCrashPose()

    /*
      Nettoyage complet.
      Important pour empêcher le glissement après l’animation.
    */
    crashVelocity.current.set(0, 0, 0)

    pivot.current.position.z = lockedZ

    delete pivot.current.userData.carCrashLockedZ

    carCrashActive.current = false
    carCrashTime.current = 0
    crashEnded.current = false

    if (gameState === "gameOverAnim") {
      setGameState("gameOver")
    } else {
      setGameState("waitingContinue")
    }
  }
}

// ==================================================
// 🌳 ANIMATION COLLISION ARBRE
// ==================================================

if (treeCrashActive.current) {
  const FRAME_COUNT = 3
  const FRAME_DURATION = 0.08

  const ANIM_TOTAL =
    (FRAME_COUNT - 1) * FRAME_DURATION

  const HOLD_LAST_FRAME_DURATION = 0.32

  const TOTAL =
    ANIM_TOTAL + HOLD_LAST_FRAME_DURATION

  const safeDt = Math.min(moveDt, 0.033)

  const TREE_GROUND_Y = BASE_Y + 0.02
  const TREE_FINAL_SPRITE_Y = 0.22

  function setTreeCrashFrame(frameIndex) {
    const safeFrame = THREE.MathUtils.clamp(
      frameIndex,
      0,
      FRAME_COUNT - 1
    )

    treeCollisionTex.repeat.x = 1 / FRAME_COUNT
    treeCollisionTex.offset.x = safeFrame / FRAME_COUNT
    treeCollisionTex.needsUpdate = true
  }

  function forceLastTreeCrashPose() {
    setTreeCrashFrame(FRAME_COUNT - 1)

    pivot.current.position.y = TREE_GROUND_Y

    sprite.current.position.y = TREE_FINAL_SPRITE_Y
    sprite.current.position.z = 0

    sprite.current.rotation.x = -0.95
    sprite.current.rotation.y = 0
    sprite.current.rotation.z = 0

    sprite.current.scale.x = treeCrashDir.current * 1.05
    sprite.current.scale.y = 1.05
    sprite.current.scale.z = 1
  }

  treeCrashTime.current += safeDt

  playerSpeed.current = 0
  lateralVelocity.current = 0
  jumpVelocity.current = 0

  // ==================================================
  // 💥 RECUL PHYSIQUE
  // ==================================================

  if (treeCrashTime.current < ANIM_TOTAL + 0.18) {
    /*
      Moins de damping = recul plus visible.
      Si ça ne recule pas assez, mets 0.92.
      Si ça recule trop, mets 0.80.
    */
    const damping =
      Math.pow(0.88, safeDt * 60)

    pivot.current.position.x +=
      crashVelocity.current.x * safeDt

    pivot.current.position.z +=
      crashVelocity.current.y * safeDt

    crashVelocity.current.multiplyScalar(damping)

    pivot.current.position.x =
      THREE.MathUtils.clamp(
        pivot.current.position.x,
        minX,
        maxX
      )
  } else {
    crashVelocity.current.set(0, 0)
  }

  // ==================================================
  // 🎞️ FRAME SPRITESHEET
  // ==================================================

  if (treeCrashTime.current >= ANIM_TOTAL) {
    forceLastTreeCrashPose()
  } else {
    const frameIndex = Math.floor(
      treeCrashTime.current / FRAME_DURATION
    )

    setTreeCrashFrame(frameIndex)

    pivot.current.position.y =
      THREE.MathUtils.lerp(
        pivot.current.position.y,
        TREE_GROUND_Y,
        1 - Math.exp(-22 * safeDt)
      )

    sprite.current.rotation.x =
      THREE.MathUtils.lerp(
        sprite.current.rotation.x,
        -0.95,
        1 - Math.exp(-18 * safeDt)
      )

    sprite.current.scale.x =
      THREE.MathUtils.lerp(
        sprite.current.scale.x,
        treeCrashDir.current * 1.05,
        1 - Math.exp(-18 * safeDt)
      )

    sprite.current.scale.y =
      THREE.MathUtils.lerp(
        sprite.current.scale.y,
        1.05,
        1 - Math.exp(-18 * safeDt)
      )

    sprite.current.scale.z = 1

    /*
      Important :
      ne pas remonter à 0.95.
      Ça donnait l’impression que le cycliste flotte.
    */
    sprite.current.position.y =
      THREE.MathUtils.lerp(
        sprite.current.position.y,
        TREE_FINAL_SPRITE_Y,
        1 - Math.exp(-20 * safeDt)
      )

    sprite.current.position.z = 0
  }

  // ==================================================
  // 🛑 FIN ANIMATION
  // ==================================================

  if (
    treeCrashTime.current >= TOTAL &&
    !crashEnded.current
  ) {
    crashEnded.current = true

    forceLastTreeCrashPose()

    crashVelocity.current.set(0, 0)
    lateralVelocity.current = 0
    jumpVelocity.current = 0

    treeCrashActive.current = false
    treeCrashTime.current = 0

    /*
      IMPORTANT :
      Ne pas remettre ici :
      treeCrashStarted.current = false
      crashEnded.current = false

      Ces resets doivent se faire au retour à playing.
      Sinon la collision suivante peut repartir au mauvais moment
      et rester longtemps sur le premier frame.
    */

    if (gameState === "gameOverAnim") {
      setGameState("gameOver")
    } else {
      setGameState("waitingContinue")
    }
  }
}
  // ==================================================
  // 🤸 FLIP OBSTACLE
  // ==================================================

  if (isColliding && !wasColliding.current && collisionType === "obstacle") {

    wasColliding.current = true
    crashIndex.current = Math.floor(Math.random() * 8)
    flipTime.current = 0
    flipStartZ.current = pivot.current?.position.z
    flipStartX.current = pivot.current?.position.x

    // ==================================================
    // 🏆 DISTANCE PROPORTIONNELLE À LA VITESSE
    // ==================================================

    // ⭐ paramètres d’équilibrage
    const MIN_FLIP_DIST = 1.8
    const MAX_EXTRA_DIST = 4.0

    flipForwardDistance.current = MIN_FLIP_DIST + MAX_EXTRA_DIST * speedRatio

    // ==================================================
    // 🚴 Direction = direction actuelle du joueur
    // ==================================================

    const forward = -1   // joggeuse → vers l’écran
    const lateralDir = THREE.MathUtils.clamp(lateralVelocity.current, -1, 1)

    flipDir.current.set(lateralDir, forward).normalize()
    isFlipping.current = true
  }

  if (!isColliding) wasColliding.current = false

  if (collisionType === "obstacle" && isFlipping.current) {

    flipTime.current += moveDt

    const t = THREE.MathUtils.clamp(flipTime.current / FLIP_DURATION, 0, 1)
    const ease = 1 - Math.pow(1 - t, 3)
    const move = flipForwardDistance.current * ease

    // ==================================================
    // ⭐ ROTATION FLIP
    // ==================================================

    sprite.current.rotation.x = -Math.PI * t

    // ==================================================
    // ⭐ SCALE PROGRESSIF (EFFET PERSPECTIVE)
    // ==================================================

    const scale = THREE.MathUtils.lerp(1.3, 1, t)
    sprite.current.scale.set(scale, scale, 1)

    // ==================================================
    // ⭐ POSITION LATÉRALE
    // ==================================================

    pivot.current.position.x = THREE.MathUtils.clamp(flipStartX.current + flipDir.current.x * move, minX, maxX)

    // 🚫 joggeuse → Z fixe
    pivot.current.position.z = flipStartZ.current

    // ⭐ déplacement visuel uniquement (illusion profondeur)
    sprite.current.position.z = flipDir.current.y * move

    // ⭐ hauteur du saut
    pivot.current.position.y = Math.sin(t * Math.PI) * JUMP_HEIGHT + CRASH_Y

    // ⭐ tilt visuel
    sprite.current.rotation.y = flipTilt * ease

  // ==================================================
  // 🔒 FIN DU FLIP
  // ==================================================

  if (t >= 1) {

    isFlipping.current = false

    // ⭐ reset visuel
    sprite.current.position.z = 0
    sprite.current.scale.set(1, 1, 1)

    crashedRef.current = true
    setIsCrashed(true)

    // hauteur finale
    pivot.current.position.y = CRASH_Y + 0.06

    // orientation crash
    sprite.current.rotation.x = -1.0
    sprite.current.rotation.y = 0.15 * Math.sign(flipDir.current.x || 1)

    // =====================================
    // FIN STANDARDISÉE
    // =====================================

    if (!crashEnded.current) {

        crashEnded.current = true

        setTimeout(() => {

          if (gameState === "gameOverAnim") {

            setGameState("gameOver")

          } else {

            setGameState("waitingContinue")
          }

        }, 180)
      }
    }
  }
   // ==================================================

})

  const currentFrontTexture = (() => {

 if (collisionType === "cyclist_fast") {
    return activeCyclistCrashTex.current
  }

  // 🌳 arbre
  if (collisionType === "tree") {
    return treeCollisionTex
  }

  // 🧱 obstacle → chute random APRÈS flip
  if (collisionType === "obstacle") {
    return crashTextures[crashIndex.current]
  }

  // 🚗 voiture → animation sprite sheet
  if (collisionType === "car" && gameState !== "playing") {
    return carCollisionTex
  }

  return texFront
})()

  return (
      <group
        ref={pivot}
        scale={
          isCarCrash
            ? 1.8
            : isTreeCrash
              ? 0.9
              : 0.6
        }
      >
        <group ref={sprite}>
          {/* ===== FRONT ===== */}
          <mesh>
            <planeGeometry
              args={
                isCarCrash
                  ? [10, 5.6]
                  : isTreeCrash
                    ? [4.2, 2.1]
                    : isObstacleCrash
                      ? [2.6, 3.8]
                      : [1.6, 3.8]
              }
            />
            <meshBasicMaterial
              map={currentFrontTexture}
              transparent
              alphaTest={0.7}
              polygonOffset
              polygonOffsetFactor={-1}
              polygonOffsetUnits={-1}
              depthWrite
              depthTest
              toneMapped={false}
              side={THREE.FrontSide}
              dithering
            />
          </mesh>

          {/* ===== BACK ===== */}
          <mesh rotation={[0, Math.PI, 0]}>
            <planeGeometry
              args={
                isCarCrash
                  ? [10, 5.6]
                  : isTreeCrash
                    ? [4.2, 2.1]
                    : isObstacleCrash
                      ? [2.6, 3.8]
                      : [1.6, 3.8]
              }
            />
            <meshBasicMaterial
              map={texBack}
              transparent
              alphaTest={0.7}
              polygonOffset
              polygonOffsetFactor={-1}
              polygonOffsetUnits={-1}
              depthWrite
              depthTest
              toneMapped={false}
              side={THREE.FrontSide}
              dithering
            />
          </mesh>
        </group>
      </group>
    )
})

const GLB_WALK_SPEED = 2
const GLB_RUN_SPEED = 5

const pedestrianConfig = {
  baseSpeed: GLB_WALK_SPEED,
  maxSpeed: GLB_WALK_SPEED,
  accel: 1.1,
  animSpeed: 1,
  halfWidth: 0.6,
  halfLength: 0.6,
}

const runFastConfig = {
  baseSpeed: GLB_RUN_SPEED,
  maxSpeed: GLB_RUN_SPEED,
  accel: 3.2,
  animSpeed: 1
}

const runJogConfig = {
  baseSpeed: GLB_RUN_SPEED,
  maxSpeed: GLB_RUN_SPEED,
  accel: 2.8,
  animSpeed: 1
}

const runSlowConfig = {
  baseSpeed: GLB_RUN_SPEED,
  maxSpeed: GLB_RUN_SPEED,
  accel: 2.8,
  animSpeed: 1
}

const runTrotConfig = {
  baseSpeed: GLB_RUN_SPEED,
  maxSpeed: GLB_RUN_SPEED,
  accel: 2.6,
  animSpeed: 1
}

const bikePedConfig = {
  baseSpeed: 8,
  maxSpeed: 10,
  accel: 5,
  animSpeed: 2,
  halfWidth: 0.7,
  halfLength: 1.2,
}

function pickGLBAnimChoice(model) {
  if (model?.bikeMode) return "bike"

  return weightedRandom([
    ["run", 0.55],
    ["walk", 0.18],
    ["text", 0.10],
    ["text_female", 0.08],
    ["child", 0.06],
  ])
}

function isBikeGLBAnimationName(lower) {
  return (
    lower.includes("bike") ||
    lower.includes("riding")
  )
}

function getBikeGLBAnimationPlayback(actor, gameState, dt, previousPosition) {
  const distance = Math.hypot(
    actor.x - previousPosition.x,
    actor.z - previousPosition.z
  )
  previousPosition.x = actor.x
  previousPosition.z = actor.z

  const moved = Number.isFinite(distance) && distance > 0.0001
  const measuredSpeed = moved && dt > 0 ? distance / dt : 0
  const reportedSpeed = Number.isFinite(actor.currentSpeed)
    ? Math.max(0, actor.currentSpeed)
    : 0
  const speed = Math.max(reportedSpeed, measuredSpeed)
  const speedRatio = Math.max(speed / bikePedConfig.baseSpeed, 0.65)
  // Facteur historique : la cadence dépend aussi de bikePedConfig.animSpeed.
  const timeScale = THREE.MathUtils.clamp(
    speedRatio * bikePedConfig.animSpeed * (actor.animSpeedMultiplier || 1),
    0,
    4
  )

  return {
    paused: gameState !== "playing" || (!moved && reportedSpeed < 0.15),
    timeScale: Math.round(timeScale * 10) / 10
  }
}

function getAvailableGLBAnimChoices(lowerNames, bikeMode = false) {
  if (bikeMode) {
    return lowerNames.some(item => isBikeGLBAnimationName(item.lower))
      ? ["bike"]
      : []
  }

  const choices = []

  if (lowerNames.some(item => item.lower.includes("run") || item.lower.includes("jog") || item.lower.includes("trot"))) {
    choices.push("run")
  }

  if (lowerNames.some(item => item.lower.includes("walk") && !item.lower.includes("text") && !item.lower.includes("femenine") && !item.lower.includes("child"))) {
    choices.push("walk")
  }

  if (lowerNames.some(item => item.lower.includes("text") && !item.lower.includes("femenine"))) {
    choices.push("text")
  }

  if (lowerNames.some(item => item.lower.includes("femenine") || item.lower.includes("female"))) {
    choices.push("text_female")
  }

  if (lowerNames.some(item => item.lower.includes("child"))) {
    choices.push("child")
  }

  if (lowerNames.some(item => item.lower.includes("idle") && item.lower.includes("talk"))) {
    choices.push("idle_talk")
  }

  if (lowerNames.some(item => item.lower.includes("baby") && item.lower.includes("wav"))) {
    choices.push("baby_waving")
  }

  return choices
}

function pickAvailableGLBAnimChoice(actor, lowerNames) {
  const choices = getAvailableGLBAnimChoices(lowerNames, actor?.bikeMode)

  if (actor?.bikeMode) return choices.includes("bike") ? "bike" : null
  if (!choices.length) return null

  const weights = [
    ["run", 0.55],
    ["walk", 0.18],
    ["text", 0.10],
    ["text_female", 0.08],
    ["child", 0.06],
    ["idle_talk", 0.02],
    ["baby_waving", 0.01]
  ].filter(([choice]) => choices.includes(choice))

  return weightedRandom(weights)
}

function getModeForGLBAnimChoice(animChoice, bikeMode = false) {
  if (bikeMode || animChoice === "bike") return "bike"
  if (animChoice === "run") return "jog"
  if (animChoice === "idle_talk" || animChoice === "baby_waving") return "idle"

  return "walk"
}

function getRunConfig(runVariant) {
  if (runVariant === "fast") return runFastConfig
  if (runVariant === "slow") return runSlowConfig
  if (runVariant === "trot") return runTrotConfig

  return runJogConfig
}

function getGLBMovementConfig(actor) {
  if (actor?.mode === "jog") return getRunConfig(actor.runVariant)
  if (actor?.bikeMode || actor?.mode === "bike") return bikePedConfig

  return pedestrianConfig
}

function getGLBSpeedVariation(actor) {
  if (actor?.bikeMode || actor?.mode === "bike") return THREE.MathUtils.randFloat(1.0, 1.15)

  if (actor?.mode === "jog") {
    return 1
  }

  return 1
}

function applyGLBSpeedProfile(actor) {
  const cfg = getGLBMovementConfig(actor)
  const profileKey = `${actor.mode || "walk"}:${actor.runVariant || ""}:${actor.animChoice || ""}`
  const profileChanged = actor.speedProfileKey !== profileKey
  const firstProfile = actor.speedProfileKey === undefined

  if (profileChanged || !Number.isFinite(actor.speedVariation)) {
    actor.speedVariation = getGLBSpeedVariation(actor)
    actor.speedProfileKey = profileKey
  }

  actor.speed = actor.speedVariation
  actor.targetSpeed = (cfg.baseSpeed || 1) * actor.speedVariation

  if (profileChanged) {
    if (firstProfile || !Number.isFinite(actor.currentSpeed)) {
      actor.currentSpeed = actor.targetSpeed
    } else if (actor.mode === "walk" && actor.currentSpeed > actor.targetSpeed) {
      actor.currentSpeed = actor.targetSpeed
    } else if (actor.mode === "jog" && actor.currentSpeed < actor.targetSpeed * 0.85) {
      actor.currentSpeed = actor.targetSpeed * 0.85
    }
  }
}

function applyGLBSpeedProfileFromAnimation(actor, animationName) {
  const lower = String(animationName || "").toLowerCase()
  const isBikeAnimation =
    lower.includes("bike") ||
    lower.includes("riding")

  const isIdleAnimation =
    lower.includes("idle") ||
    lower.includes("wav")

  const isWalkAnimation =
    lower.includes("walk") ||
    lower.includes("text") ||
    lower.includes("phone") ||
    lower.includes("cell") ||
    lower.includes("mobile") ||
    lower.includes("child")

  const isRunAnimation =
    lower.includes("run") ||
    lower.includes("jog") ||
    lower.includes("trot")

  if (actor?.bikeMode || isBikeAnimation) {
    actor.mode = "bike"
    applyGLBSpeedProfile(actor)
    return
  }

  if (isIdleAnimation) {
    actor.mode = "idle"
    actor.targetSpeed = 0
    actor.currentSpeed = 0
    actor.speed = 0
    return
  }

  if (isWalkAnimation) {
    actor.mode = "walk"

    if (lower.includes("child")) actor.animChoice = "child"
    else if (lower.includes("femenine") || lower.includes("female")) actor.animChoice = "text_female"
    else if (
      lower.includes("text") ||
      lower.includes("phone") ||
      lower.includes("cell") ||
      lower.includes("mobile")
    ) actor.animChoice = "text"
    else actor.animChoice = "walk"

    applyGLBSpeedProfile(actor)
    return
  }

  if (isRunAnimation) {
    actor.mode = "jog"
    actor.animChoice = "run"

    if (lower.includes("fast")) actor.runVariant = "fast"
    else if (lower.includes("slow")) actor.runVariant = "slow"
    else if (lower.includes("trot")) actor.runVariant = "trot"
    else actor.runVariant = "jog"

    applyGLBSpeedProfile(actor)
    return
  }

  actor.mode = "walk"

  if (lower.includes("child")) actor.animChoice = "child"
  else if (lower.includes("femenine") || lower.includes("female")) actor.animChoice = "text_female"
  else if (lower.includes("text")) actor.animChoice = "text"
  else actor.animChoice = "walk"

  applyGLBSpeedProfile(actor)
}

const ACTOR_CONFIG = {

  // ==================================================
  // SPRITES
  // ==================================================

  cyclist: {
    frames: 6,
    baseSpeed: 9,
    levelGain: 1.4,
    maxSpeed: 10,
    animSpeed: 8.5,
    accel: 3.4,
    halfWidth: 0.7,
    halfLength: 1.1
  },

  cycliste4: {
    frames: 14,
    baseSpeed: 8,
    levelGain: 1.4,
    maxSpeed: 9,
    animSpeed: 6.5,
    accel: 3.4,
    halfWidth: 0.7,
    halfLength: 1.1
  },

  tri: {
    frames: 3,
    baseSpeed: 3.6,
    levelGain: 1.0,
    maxSpeed: 5.5,
    animSpeed: 4.5,
    accel: 2.8,
    halfWidth: 1.2,
    halfLength: 2.0
  },

  scoot: {
    frames: 5,
    baseSpeed: 8,
    levelGain: 1.3,
    maxSpeed: 10,
    animSpeed: 6.5,
    accel: 4.0,
    halfWidth: 0.6,
    halfLength: 0.9
  },

  bixi: {
    frames: 5,
    baseSpeed: 8,
    levelGain: 1.1,
    maxSpeed: 10,
    animSpeed: 5.5,
    accel: 3.4,
    zigIntervalMin: 3.0,
    zigIntervalMax: 5.5,
    zigSmooth: 0.05,
    halfWidth: 0.7,
    halfLength: 1.0
  },

  sport: {
    frames: 5,
    baseSpeed: 10,
    levelGain: 0.8,
    maxSpeed: 12,
    animSpeed: 5.5,
    accel: 6.0,
    halfWidth: 0.7,
    halfLength: 1.1
  },

  joggeuse: {
    frames: 4,
    baseSpeed: 4.8,
    levelGain: 0.4,
    maxSpeed: 6,
    animSpeed: 6.5,
    accel: 4.5,
    halfWidth: 0.45,
    halfLength: 0.45
  },

  jogger: {
    frames: 9,
    baseSpeed: 4.8,
    levelGain: 0.4,
    maxSpeed: 6,
    animSpeed: 6.5,
    accel: 4.5,
    halfWidth: 0.45,
    halfLength: 0.45
  },

  trailer: {
    frames: 3,
    baseSpeed: 4.8,
    levelGain: 0.4,
    maxSpeed: 6.8,
    animSpeed: 3.5,
    accel: 2.5,
    halfWidth: 1.0,
    halfLength: 1.8
  },

  sportif: {
    frames: 6,
    baseSpeed: 11,
    levelGain: 1.3,
    maxSpeed: 13,
    animSpeed: 5.5,
    accel: 7.0,
    halfWidth: 0.75,
    halfLength: 1.2
  },

  child: {
    frames: 6,
    baseSpeed: 5.8,
    maxSpeed: 6,
    animSpeed: 7.5,
    accel: 2.4,
    zigAmplitude: 2.5,
    zigInterval: 2.8,
    animFps: 5,
    halfWidth: 0.35,
    halfLength: 0.35
  },

  // ==================================================
  // GLB WALKERS
  // ==================================================

  womanA: pedestrianConfig,
  womanB: pedestrianConfig,
  womanC: pedestrianConfig,
  manA: pedestrianConfig,
  manB: pedestrianConfig,
  manC: pedestrianConfig,
  girl: pedestrianConfig,
  boy: pedestrianConfig,
  baby: pedestrianConfig,

  // ==================================================
  // GLB BIKES
  // ==================================================

  manC_bike: bikePedConfig,
  womanA_bike: bikePedConfig,
  boy_bike: bikePedConfig,
  sportManA: bikePedConfig,
  sportManB: bikePedConfig,
  sportWomanA: bikePedConfig,
  sportWomanB: bikePedConfig,
  sportWomanC: bikePedConfig
}

const GLB_ACTORS = {

  womanA: {
    url: "/modeles/womanA.glb",
    scale: 1.5,
    offset: [0.3, 0.1, 0], // -2.7 + 3
    bikeMode: false
  },

  womanB: {
    url: "/modeles/womanB.glb",
    scale: 1.5,
    offset: [-0.9, 0.1, 0], // -3.9 + 3
    bikeMode: false
  },

  womanC: {
    url: "/modeles/womanC.glb",
    scale: 1.5,
    offset: [-1.9, 0.1, 0], // -4.9 + 3
    bikeMode: false
  },

  manA: {
    url: "/modeles/manA.glb",
    scale: 1.5,
    offset: [-2.9, 0, 0], // -5.9 + 3
    bikeMode: false
  },

  manB: {
    url: "/modeles/manB.glb",
    scale: 1.5,
    offset: [-3.9, 0.1, 0], // -6.9 + 3
    bikeMode: false
  },

  manC: {
    url: "/modeles/manC.glb",
    scale: 1.5,
    offset: [0, 0.1, 0], // -7.9 + 3
    bikeMode: false
  },

  girl: {
    url: "/modeles/girl.glb",
    scale: 1.7,
    offset: [-6.3, 0.1, 0], // -9.3 + 3
    bikeMode: false
  },

  boy: {
    url: "/modeles/boy.glb",
    scale: 1.7,
    offset: [-7.3, 0.1, 0], // -10.3 + 3
    bikeMode: false
  },

  baby: {
    url: "/modeles/baby.glb",
    scale: 1.9,
    offset: [-8.4, 0.1, 0], // -11.4 + 3
    bikeMode: false
  },
  manC_bike: {
    url: "/modeles/manC_bike.glb",
    scale: 2.1,
    offset: [0, 0.1, 0], // -2.7 + 3
    bikeMode: true
  },

  womanA_bike: {
    url: "/modeles/womanA_bike.glb",
    scale: 1.5,
    offset: [0.3, 0.1, 0], // -2.7 + 3
    bikeMode: true
  },

  boy_bike: {
    url: "/modeles/boy_bike.glb",
    scale: 1.7,
    offset: [-7.1, 0.1, 0], // -7.1 + 3
    bikeMode: true
  },
   sportManA: {
    url: "/modeles/sportManA.glb",
    scale: 1.5,
    offset: [0, 0.1, 0], // -10.1 + 3
    bikeMode: true
  },
  sportManB: {
    url: "/modeles/sportManB.glb",
    scale: 1.5,
    offset: [0, 0.1, 0], // -10.1 + 3
    bikeMode: true
  },
  sportWomanA: {
    url: "/modeles/sportWomanA.glb",
    scale: 1.5,
    offset: [0, 0.1, 0], // -10.1 + 3
    bikeMode: true
  },
  sportWomanB: {
    url: "/modeles/sportWomanB.glb",
    scale: 1.5,
    offset: [0, 0.1, 0], // -10.1 + 3
    bikeMode: true
  },
   sportWomanC: {
    url: "/modeles/sportWomanC.glb",
    scale: 1.5,
    offset: [0, 0.1, 0], // -10.1 + 3
    bikeMode: true
  }

}

// ==================================================
// PRELOAD ALL ACTOR GLBS
// ==================================================

export async function preloadActorGLBs({

  onProgress = null

} = {}) {

const loader = gltfLoader

  // ==================================================
  // URLS UNIQUES
  // ==================================================

  const urls = [

    ...new Set(

      Object.values(GLB_ACTORS)
        .map(actor => actor.url)
    )
  ]

  // ==================================================
  // LOAD SEQUENTIEL
  // ==================================================

  // IMPORTANT:
  // Séquentiel = beaucoup plus stable
  // pour mobile/GPU faible

  for (let i = 0; i < urls.length; i++) {

    const url = urls[i]

    try {

      await new Promise((resolve, reject) => {

        loader.load(

          url,

          (gltf) => {

            // =====================================
            // PREWARM
            // =====================================

            // force init meshes/materials/
            // skeletons/animations

            gltf.scene.traverse(obj => {

              obj.frustumCulled = false

              if (!obj.isMesh)
                return

              obj.castShadow = false
              obj.receiveShadow = false

              // compile bounding
              obj.geometry?.computeBoundingSphere?.()
            })

            resolve(gltf)
          },

          undefined,

          (err) => {

            console.error(
              "GLB LOAD ERROR:",
              url,
              err
            )

            reject(err)
          }
        )
      })

      // =====================================
      // PROGRESS CALLBACK
      // =====================================

      onProgress?.({

        current: i + 1,
        total: urls.length,
        progress: (i + 1) / urls.length,
        url
      })

      // =====================================
      // LAISSER RESPIRER LE GPU
      // =====================================

      await new Promise(resolve =>
        requestAnimationFrame(() =>
          requestAnimationFrame(resolve)
        )
      )

    } catch (e) {

      console.error(
        "FAILED TO PRELOAD:",
        url,
        e
      )
    }
  }

  dracoLoader.dispose()
}

function GLBActors({
  actorsRef
}) {

  if (!actorsRef?.current)
    return null

  const actors = actorsRef.current.filter(a => a.glb)

  return (
    <group>

      {actors.map(actor => (

        <GLBActor
          key={actor.id}
          actor={actor}
        />
      ))}

    </group>
  )
}
// ==================================================
// SINGLE ACTOR
// ==================================================
  function weightedRandom(list) {

  const total =
    list.reduce(
      (sum, [,w]) => sum + w,
      0
    )

  let r =
    Math.random() * total

  for (const [value, weight] of list) {

    r -= weight

    if (r <= 0) {
      return value
    }
  }

  return list[0][0]
}

const GLBActor = React.memo(
  React.forwardRef(
    function GLBActor({
      actor
    }, ref) {

  // ==================================================
  // LOAD MODEL
  // ==================================================
  const { gameState } = useGame()
  const { scene, animations } = useGLTF(actor.url)

  // ==================================================
  // REFS
  // ==================================================

  const groupRef = useRef()
  const currentAction = useRef(null)
  const actionsArrayRef = useRef([])
  const previousPosition = useRef({ x: actor.x, z: actor.z })


  // ==================================================
  // CLONE
  // ==================================================

  const clone = useMemo(() => {

  
    const c = SkeletonUtils.clone(scene)

    c.layers.set(0)
    c.traverse(obj => {

      if (!obj.isMesh)
        return

      obj.castShadow = false
      obj.receiveShadow = false
      obj.frustumCulled = true
      obj.matrixAutoUpdate = false
    })

    return c

  }, [scene])

  // ==================================================
  // ANIMATIONS
  // ==================================================


  
  const { actions, names } = useAnimations(animations, clone)
  const lowerNames = useMemo(() => {

    return names.filter(n => typeof n === "string").map(name => ({ original: name, lower: name.toLowerCase() }))

  }, [names])

  // ==================================================
  // PLAY ANIMATION
  // ==================================================

useEffect(() => {

  if (!names?.length) {
    if (actor.bikeMode) {
      console.warn("Cycliste GLB sans animation vélo", { url: actor.url, names })
    }
    return
  }

  let chosen = null

  // ==================================================
  // 🚴 PLAY ALL BIKE ANIMATIONS
  // ==================================================

  if (actor.bikeMode) {

    const bikeActions = lowerNames

      .filter(item =>
        isBikeGLBAnimationName(item.lower)
      )

      .map(item =>
        actions[item.original]
      )

      .filter(Boolean)

    if (!bikeActions.length) {
      console.warn("Cycliste GLB sans animation vélo", { url: actor.url, names })
      return
    }

    actor.mode = "bike"
    applyGLBSpeedProfile(actor)

    currentAction.current = bikeActions
    actionsArrayRef.current = bikeActions

    bikeActions.forEach(action => {

      action.reset()
      action.fadeIn(0.3)
      action.setLoop(THREE.LoopRepeat)
      action.play()
      action.timeScale = bikePedConfig.animSpeed || 1
    })

    return () => {

      bikeActions.forEach(action => {

        action.fadeOut(0.2)
        action.stop()
      })
    }
  }

  // ==================================================
  // 🚶 PEDESTRIANS
  // ==================================================

  const availableChoices = getAvailableGLBAnimChoices(lowerNames, actor.bikeMode)

  if (!actor.animChoice || !availableChoices.includes(actor.animChoice)) {
    actor.animChoice =
      pickAvailableGLBAnimChoice(actor, lowerNames) ||
      "walk"
  }

  // ==================================================
  // 🏃 RUN / JOG
  // ==================================================

  if (actor.animChoice === "run") {

    actor.mode = "jog"

    // ===============================================
    // FIND ALL RUN VARIANTS
    // ===============================================

    const runVariants = lowerNames.filter(item => item.lower.includes("run"))

    if (runVariants.length) {

      const weightedRuns = [

        "fast",
        "fast",

        "jog",
        "jog",
        "jog",

        "slow",
        "slow",

        "trot"
      ]

      const picked = weightedRuns[Math.floor(Math.random() * weightedRuns.length)]

      let selected = null

      // ===========================================
      // FAST
      // ===========================================

      if (picked === "fast") {

        selected = runVariants.find(v => v.lower.includes("fast"))
        actor.runVariant = "fast"
      }

      // ===========================================
      // JOG
      // ===========================================

      else if (picked === "jog") {

        selected = runVariants.find(v => v.lower.includes("jog"))
        actor.runVariant = "jog"
      }

      // ===========================================
      // SLOW
      // ===========================================

      else if (picked === "slow") {

        selected = runVariants.find(v => v.lower.includes("slow"))
        actor.runVariant = "slow"
      }

      // ===========================================
      // TROT
      // ===========================================

      else if (picked === "trot") {

        selected = runVariants.find(v => v.lower.includes("trot"))
        actor.runVariant = "trot"
      }

      // ===========================================
      // FALLBACK
      // ===========================================

      selected ??= runVariants[0]
      chosen =  selected?.original
    }

    applyGLBSpeedProfile(actor)
  }

  // ==================================================
  // 🚶 WALK
  // ==================================================

  else if (actor.animChoice === "walk") {

    actor.mode = "walk"
    applyGLBSpeedProfile(actor)

    chosen = lowerNames.find(item => {

      const lower = item.lower

      return (

        lower.includes("walk") &&

        !lower.includes("text") &&
        !lower.includes("femenine") &&
        !lower.includes("child")
      )

    })?.original
  }

  // ==================================================
  // 👶 CHILD
  // ==================================================

  else if (actor.animChoice === "child") {

    actor.mode = "walk"
    applyGLBSpeedProfile(actor)

    chosen = lowerNames.find(item => item.lower.includes("child"))?.original
  }

  // ==================================================
  // 📱 TEXT FEMALE
  // ==================================================

  else if (actor.animChoice === "text_female") {

    actor.mode = "walk"
    applyGLBSpeedProfile(actor)

    chosen = lowerNames.find(item =>
      item.lower.includes(
        "femenine"
      )
    )?.original
  }

  // ==================================================
  // 📱 TEXT
  // ==================================================

  else if (actor.animChoice === "text") {

    actor.mode = "walk"
    applyGLBSpeedProfile(actor)

    chosen = lowerNames.find(item => {

      const lower = item.lower

      return (lower.includes("text") && !lower.includes("femenine" )
      )

    })?.original
  }

  // ==================================================
  // 💤 IDLE TALK
  // ==================================================

  else if (actor.animChoice === "idle_talk") {

    actor.mode = "idle"
    actor.targetSpeed = 0
    actor.currentSpeed = 0
    actor.speed = 0

    chosen = lowerNames.find(item => {

      const lower = item.lower

      return (lower.includes("idle") && lower.includes("talk"))

    })?.original
  }

  // ==================================================
  // 👶 BABY WAVING
  // ==================================================

  else if (actor.animChoice === "baby_waving") {

    actor.mode = "idle"
    actor.targetSpeed = 0
    actor.currentSpeed = 0
    actor.speed = 0

    chosen = lowerNames.find(item => {

      const lower = item.lower

      return (lower.includes("baby") && lower.includes("wav"))

    })?.original
  }

  // ==================================================
  // FALLBACK
  // ==================================================

  if (!chosen) {

    chosen = lowerNames.find(item => {

      const lower = item.lower

      return (lower.includes("walk") || lower.includes("run"))

    })?.original
  }

  if (!chosen) {

    chosen = names[0]
  }

  applyGLBSpeedProfileFromAnimation(actor, chosen)

  // ==================================================
  // ACTION
  // ==================================================

  const action = actions[chosen]

  if (!action)
    return

  currentAction.current = action
  actionsArrayRef.current = [action]
  action.reset()
  action.fadeIn(0.3)
  action.setLoop(THREE.LoopRepeat)
  action.play()

  // ==================================================
  // INITIAL SPEED CONFIG
  // ==================================================

  let cfg = getGLBMovementConfig(actor)

  action.timeScale = cfg?.animSpeed || 1

  // ==================================================
  // CLEANUP
  // ==================================================

  return () => {

    action.fadeOut(0.2)
    action.stop()
  }

}, [
  actions,
  names,
  lowerNames,
  actor.bikeMode,
  actor.type,
  actor.url,
  actor.animChoice,
  actor.animationVersion
])
  // ==================================================
  // UPDATE
  // ==================================================

  useFrame((_, dt) => {

  const obj = groupRef.current

  if (!obj)
    return

  // ==================================================
  // CONFIG
  // ==================================================

  let cfg = getGLBMovementConfig(actor)

  const actionsArray = actionsArrayRef.current
  const bikePlayback = actor.bikeMode
    ? getBikeGLBAnimationPlayback(actor, gameState, dt, previousPosition.current)
    : null

  // ==================================================
  // CHANGED
  // ==================================================

  let changed = false
  if (actor.commune) {
    changed = true
  }

  // ==================================================
  // BOB
  // ==================================================

  let bobY = 0

  if (actor.mode === "jog") {

    actor.bobTime ??= 0

    const bobSpeed =

      actor.runVariant === "fast"
        ? 16

      : actor.runVariant === "slow"
        ? 7

      : actor.runVariant === "trot"
        ? 5

      : 10

    const bobAmount =

      actor.runVariant === "fast"
        ? 0.06

      : actor.runVariant === "slow"
        ? 0.03

      : actor.runVariant === "trot"
        ? 0.02

      : 0.04

    actor.bobTime += dt * bobSpeed

    bobY = Math.sin(actor.bobTime) * bobAmount
  }

  // ==================================================
  // POSITION
  // ==================================================

  if (obj.position.x !== actor.x || obj.position.y !== bobY || obj.position.z !== actor.z) {

    obj.position.x = actor.x
    obj.position.y = bobY
    obj.position.z = actor.z
    changed = true
  }

  // ==================================================
  // ROTATION
  // ==================================================

if (actor.mode === "idle") {

  // ===============================================
  // COMMUNE IDLE
  // ===============================================

  if (actor.commune) {

    obj.rotation.y = actor.rotationY || 0
  }

  // ===============================================
  // NORMAL IDLE
  // ===============================================

  else {

    obj.rotation.y = Math.PI
  }

  obj.rotation.z = 0
  changed = true
}

// ==================================================
// COMMUNE WALKERS
// ==================================================

else if (actor.commune) {

  obj.rotation.y = actor.rotationY || 0
  obj.rotation.z = 0
  changed = true
}

// ==================================================
// NORMAL ACTORS
// ==================================================

else {

  const latVel = actor.latVel ?? 0
  const targetTurn = THREE.MathUtils.clamp(latVel * 0.12, -0.45, 0.45)

  actor.turnRot ??= 0
  actor.turnRot = THREE.MathUtils.lerp(actor.turnRot, targetTurn, 7 * dt)

  const rotFactor = actor.bikeMode ? 1.8 : 1.2

  // ==================================================
  // 🚴 DIRECTION BASE
  // ==================================================

  const baseRotY = actor.bikeMode && actor.direction === 1 ? 0 : Math.PI

  // ==================================================
  // ↔️ TURN
  // ==================================================

  // ==================================================
  // 🚴 INVERSION SENS
  // ==================================================

  const turnMultiplier = actor.bikeMode && actor.direction === 1 ? -1 : 1

  // ==================================================
  // ROTATION
  // ==================================================

  obj.rotation.y = baseRotY - actor.turnRot * rotFactor * turnMultiplier

    // ===============================================
    // BIKE LEAN
    // ===============================================

    if (actor.bikeMode) {

      obj.rotation.z = -actor.turnRot * 0.18
    }

    // ===============================================
    // WALKER LEAN
    // ===============================================

    else {

      obj.rotation.z = -actor.turnRot * 0.08
    }

    changed = true
  }

  // ==================================================
  // ANIMATION SPEED
  // ==================================================

  for (let i = 0; i < actionsArray.length; i++) {

    const action = actionsArray[i]

    if (!action)
      continue

    if (!cfg)
      continue

    const base = Math.max(cfg.baseSpeed, 0.01)
    const baseSpeed = cfg.baseSpeed || 1
    const rawRatio = (actor.currentSpeed || 0) / baseSpeed
    const minAnimSpeed = actor.mode === "idle" ? 1 : 0.65
    const speedRatio = Math.max(rawRatio, minAnimSpeed)
    const targetScale = bikePlayback?.timeScale ??
      Math.round(speedRatio * (actor.animSpeedMultiplier || 1) * 10) / 10

    if (Math.abs(action.timeScale - targetScale) > 0.05) {

      action.timeScale = bikePlayback
        ? THREE.MathUtils.damp(action.timeScale, targetScale, 3, Math.max(0, dt))
        : THREE.MathUtils.lerp(action.timeScale, targetScale, 3 * dt)
    }

    // ===============================================
    // PAUSE
    // ===============================================

    const shouldPause = bikePlayback?.paused ??
      (gameState !== "playing" || (actor.mode !== "idle" && actor.currentSpeed < 0.15))

    if (action.paused !== shouldPause) {

      action.paused = shouldPause
    }
  }

  // ==================================================
  // MATRIX UPDATE
  // ==================================================

  if (changed) {

    obj.updateMatrix()
  }

})

  // ==================================================
  // RENDER
  // ==================================================

  return (
    <group
      ref={groupRef}
      scale={actor.scale}
    >
      <group position={actor.offset}>
        <primitive object={clone} />
      </group>
    </group>
  )
  
    }
))

let NEXT_ACTOR_ID = 1
const TEXTURE_POOLS = {
    cyclist: [],
    sport: [],
    child: [],
    cycliste4: [],
    tri: [],
    scoot: [],
    bixi: [],
    joggeuse: [],
    jogger: [],
    trailer: [],
    sportif: []
  }
const RandomBikeTraffic = React.forwardRef(({
  pisteX,
  pisteW,
  conesRef,
  potholesRef,
  bollardsRef,
  staticCarsRef,
  bacsRef,
  trafficLightsRef,
  playerRef,
  density
}, ref) => {

  const { level, departOption, worldGroup, gameState, restartVersion } = useGame()
  const gameSpeed = worldGroup?.current?.userData?.worldSpeed ?? 0
  const [renderActors, setRenderActors] = useState([])

  // population totale ------------------------------------------
  const MIN_CYCLISTS = 20
  const MAX_CYCLISTS = 30
  const count = Math.min(MAX_CYCLISTS, Math.max(MIN_CYCLISTS, Math.floor(MAX_CYCLISTS * density)))

 const proportions = {

  // ==================================================
  // 🚴 SPRITES BIKES
  // ==================================================

  cyclist: 0.15,
  cycliste4: 0.05,
  sport: 0.08,
  tri: 0.04,
  scoot: 0.05,
  bixi: 0.1,
  trailer: 0.06,
  sportif: 0.07,

  // ==================================================
  // 🧒 AUTRES SPRITES
  // ==================================================

  joggeuse: 0.05,
  child: 0.06,

  // ==================================================
  // 🚴 GLB BIKES (PLUS FRÉQUENTS)
  // ==================================================

  manC_bike: 0.05,
  womanA_bike: 0.05,
  boy_bike: 0.04,
  sportManA: 0.04,
  sportManB: 0.04,
  sportWomanA: 0.04,
  sportWomanB: 0.04,
  sportWomanC: 0.05,

  // ==================================================
  // 🚶 GLB WALKERS (PRÉSENTS MAIS RARES)
  // ==================================================

  womanA: 0.02,
  womanB: 0.02,
  womanC: 0,

  manA: 0.02,
  manB: 0.015,
  manC: 0.02,

  girl: 0,
  boy: 0,
}

  const STOP_LINE_OFFSET = 9 // Marge avant le bord de l'intersection au feu rouge
  const STOP_DISTANCE = 15 // Distance ou ils commencent à freiner avant un feu
  const LIGHT_DETECTION_DISTANCE = 50   // voir le feu
  const LIGHT_ASSIGN_DISTANCE = 20      // prendre le feu

  // ======================================================
  // 🌍 RESPAWN
  // ======================================================
  const RESPAWN_FRONT = -260
  const RESPAWN_BACK  = 80
  // Marge de sécurité autour des limites de respawn.
  // Empêche les acteurs de spawn trop près des seuils,
  // ce qui éviterait un respawn immédiat (flicker / disparition).
  const SPAWN_MARGIN = 10

  // Profondeur de la zone de spawn.
  // Définit à quelle distance des limites les acteurs peuvent apparaître.
  // Plus grand = spawn plus étalé, plus naturel mais moins dense.
  // Plus petit = spawn plus concentré.
  const SPAWN_RANGE  = 40

  //--------------------------------------------------------

  const STREET_TOLERANCE_LEFT = 2.4   // peut mordre un peu dans la rue
  const WALL_CLEARANCE_RIGHT  = 0.3   // ne jamais toucher le mur
  const SIDEWALK_EXTRA_RIGHT  = 3.2   // trottoir complet autorisé

  const CYCLIST4_GROUPS = [
  [0,1],       // extrême gauche
  [2,3],       // gauche
  [4,5,6],     // avant gauche
  [7,8,9],     // avant droite
  [10,11],     // droite
  [12,13]      // extrême droite
]

  // ==================================================
  // 🚴 VOIES CYCLABLES
  // ==================================================

  // voie inverse (gauche)
  const LEFT_LANE_MIN = pisteX - 3.8
  const LEFT_LANE_MAX = pisteX - 0.6

  // voie normale (droite)
  const RIGHT_LANE_MIN = pisteX + 0.3
  const RIGHT_LANE_MAX = pisteX + 3.65

  const COMMUNE_LEFT_LANE_MIN = pisteX - 5.2 + COMMUNE_ACTOR_LIMIT_SHIFT_X
  const COMMUNE_LEFT_LANE_MAX = pisteX - 2.0 + COMMUNE_ACTOR_LIMIT_SHIFT_X
  const COMMUNE_RIGHT_LANE_MIN = pisteX + 2.0 + COMMUNE_ACTOR_LIMIT_SHIFT_X
  const COMMUNE_RIGHT_LANE_MAX = pisteX + 5.2 + COMMUNE_ACTOR_LIMIT_SHIFT_X

  function getCanalCyclistsBlend(playerZ) {
    if (typeof playerZ !== "number") {
      return 0
    }

    return THREE.MathUtils.clamp(
      (CANAL_CYCLISTS_SHIFT_START_Z - playerZ) /
        CANAL_CYCLISTS_SHIFT_BLEND_DISTANCE,
      0,
      1
    )
  }

  function getCanal2CyclistsBlend(playerZ) {
    if (typeof playerZ !== "number") {
      return 0
    }

    return THREE.MathUtils.clamp(
      (CANAL2_CYCLISTS_SHIFT_START_Z - playerZ) /
        CANAL_CYCLISTS_SHIFT_BLEND_DISTANCE,
      0,
      1
    )
  }

  function getCanal3CyclistsBlend(playerZ) {
    if (typeof playerZ !== "number") {
      return 0
    }

    return THREE.MathUtils.clamp(
      (CANAL3_CYCLISTS_SHIFT_START_Z - playerZ) /
        CANAL_CYCLISTS_SHIFT_BLEND_DISTANCE,
      0,
      1
    )
  }


  function getCanalCyclistsShiftX(playerZ) {
    const t =
      getCanalCyclistsBlend(playerZ)

    return THREE.MathUtils.lerp(
      0,
      CANAL_CYCLISTS_SHIFT_X,
      t
    )
  }

  function getCanal2CyclistsShiftX(playerZ) {
    const t =
      getCanal2CyclistsBlend(playerZ)

    return THREE.MathUtils.lerp(
      0,
      CANAL2_CYCLISTS_SHIFT_X,
      t
    )
  }

   function getCanal3CyclistsShiftX(playerZ) {
    const t =
      getCanal3CyclistsBlend(playerZ)

    return THREE.MathUtils.lerp(
      0,
      CANAL3_CYCLISTS_SHIFT_X,
      t
    )
  }


  function getCanalCyclistsLaneCloserX(playerZ) {
    return THREE.MathUtils.lerp(
      0,
      CANAL_CYCLISTS_LANE_CLOSER_X,
      getCanalCyclistsBlend(playerZ)
    )
  }

  function getLateCyclistsShiftX(playerZ) {
    if (typeof playerZ !== "number") {
      return 0
    }

    return playerZ <= LATE_CYCLISTS_SHIFT_START_Z
      ? LATE_CYCLISTS_SHIFT_X
      : 0
  }

  function getFinalCyclistsShiftX(playerZ) {
    if (typeof playerZ !== "number") {
      return 0
    }

    return playerZ <= FINAL_CYCLISTS_SHIFT_START_Z
      ? FINAL_CYCLISTS_SHIFT_X
      : 0
  }

  function getCyclistsShiftX(playerZ) {
    return (
      getCanalCyclistsShiftX(playerZ) +
      getCanal2CyclistsShiftX(playerZ) +
      getCanal3CyclistsShiftX(playerZ) +
      getLateCyclistsShiftX(playerZ) +
      getFinalCyclistsShiftX(playerZ)
    )
  }

  function getBikeLaneBounds(
    lane,
    playerZ
  ) {

    const afterCommune =
      typeof playerZ === "number" &&
      playerZ < commune.z

    const afterMill =
      typeof playerZ === "number" &&
      playerZ <= mill.z

    const totalShiftX =
      getCyclistsShiftX(playerZ)

    const canalLaneCloserX =
      getCanalCyclistsLaneCloserX(playerZ)

    if (lane === "left") {

      const min = afterCommune ? COMMUNE_LEFT_LANE_MIN : LEFT_LANE_MIN
      const max = afterCommune ? COMMUNE_LEFT_LANE_MAX : LEFT_LANE_MAX
      const centerShift = afterMill ? MILL_ACTOR_CENTER_SHIFT_X : 0

      return {
        min: min + centerShift + totalShiftX,
        max:
          max +
          centerShift +
          totalShiftX +
          canalLaneCloserX * 0.5
      }
    }

    const min = afterCommune ? COMMUNE_RIGHT_LANE_MIN : RIGHT_LANE_MIN
    const max = afterCommune ? COMMUNE_RIGHT_LANE_MAX : RIGHT_LANE_MAX
    const centerShift = afterMill ? MILL_ACTOR_CENTER_SHIFT_X : 0

    return {
      min:
        min -
        centerShift +
        totalShiftX -
        canalLaneCloserX * 0.5,
      max: max - centerShift + totalShiftX
    }
  }

  const group = useRef()
  const actorsRef = useRef([])
  const liveCountsRef = useRef({})
  const currentLiveRef = useRef({})

  const OPPOSITE_GLBS = [

  "manC_bike",
  "womanA_bike",
  "boy_bike",

  "sportManA",
  "sportManB",

  "sportWomanA",
  "sportWomanB",
  "sportWomanC"
]
  
  /* TEXTURES */

    const cyclistTextures = useLoader(
    THREE.TextureLoader,
    ["/sprites/cycliste1.png","/sprites/cycliste3.png",]
  )

  const cycliste4Texture = useLoader(
    THREE.TextureLoader,
    "/sprites/cycliste4.png"
  )

  const sportTextures = useLoader(
  THREE.TextureLoader,
  [
    "/sprites/cycliste2.png"   // femme
    // "/sprites/cyclisteSportHomme.png"  ← plus tard
  ]
)

  const triTexture = useLoader(
    THREE.TextureLoader,
    "/sprites/triporteur.png"
  )

  const scootTexture = useLoader(
  THREE.TextureLoader,
  "/sprites/trottinette.png"
)

  const bixiTexture = useLoader(
    THREE.TextureLoader,
    "/sprites/bixi.png"
  )

  const joggeuseTexture = useLoader(
    THREE.TextureLoader,
    "/sprites/joggeuse.png"
  )

  const joggerTexture = useLoader(
    THREE.TextureLoader,
    "/sprites/joggeur.png"
  )

  const trailerTexture = useLoader(
    THREE.TextureLoader,
    "/sprites/remorque.png"
  )

  const sportifTexture = useLoader(
    THREE.TextureLoader,
    "/sprites/sportif.png"
  )

  const childTextures = useLoader(
    THREE.TextureLoader,
    ["/sprites/enfant1.png", "/sprites/enfant2.png"]
  )

  /* GEOMETRIES */

  const cyclistGeometry = useMemo(
    ()=>new THREE.PlaneGeometry(1.8,3.6),[]
  )

  const triGeometry = useMemo(
    ()=>new THREE.PlaneGeometry(2.6,3.6),[]
  )

 useEffect(() => {

  const prepareTexture = (
    tex,
    frames
  ) => {
    tex.wrapS = THREE.RepeatWrapping
    tex.repeat.set(1 / frames, 1)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.needsUpdate = true
  }

  const createPool = (baseTexture, frames, count = 100) => {

    const pool = []

    for (let i = 0; i < count; i++) {

      const tex = baseTexture.clone()

      prepareTexture(tex, frames)
      pool.push(tex)
    }

    return pool
  }

  // ==================================================
  // CYCLISTS
  // ==================================================

  cyclistTextures.forEach(tex => {
    TEXTURE_POOLS.cyclist.push(...createPool(tex, ACTOR_CONFIG.cyclist.frames, 6))
  })

  // ==================================================
  // SPORT
  // ==================================================

  sportTextures.forEach(tex => {
    TEXTURE_POOLS.sport.push(...createPool(tex, ACTOR_CONFIG.sport.frames, 6))
  })

  // ==================================================
  // CHILD
  // ==================================================

  childTextures.forEach(tex => {
    TEXTURE_POOLS.child.push(...createPool(tex, ACTOR_CONFIG.child.frames, 6))
  })

  // ==================================================
  // SINGLES
  // ==================================================

  TEXTURE_POOLS.cycliste4 = createPool(cycliste4Texture, ACTOR_CONFIG.cycliste4.frames)
  TEXTURE_POOLS.tri = createPool(triTexture, ACTOR_CONFIG.tri.frames)
  TEXTURE_POOLS.scoot = createPool(scootTexture, ACTOR_CONFIG.scoot.frames)
  TEXTURE_POOLS.bixi = createPool(bixiTexture, ACTOR_CONFIG.bixi.frames)
  TEXTURE_POOLS.joggeuse = createPool(joggeuseTexture, ACTOR_CONFIG.joggeuse.frames)
  TEXTURE_POOLS.jogger = createPool(joggerTexture, ACTOR_CONFIG.jogger.frames)
  TEXTURE_POOLS.trailer = createPool(trailerTexture, ACTOR_CONFIG.trailer.frames)
  TEXTURE_POOLS.sportif = createPool(sportifTexture, ACTOR_CONFIG.sportif.frames)
}, [])

//----------------------- SPAWN --------------------------------
useEffect(() => {

  if (!playerRef?.current)
    return

  // ==================================================
  // POSITION JOUEUR (SAFE TELEPORT)
  // ==================================================

  const currentWorldZ = worldGroup?.current?.position?.z || 0
  const playerZ = -currentWorldZ
  const list = actorsRef.current

  // ==================================================
  // 📐 ZONES DÉRIVÉES
  // ==================================================

  const SPAWN_FRONT_MIN = RESPAWN_FRONT - SPAWN_RANGE
  const SPAWN_FRONT_MAX = RESPAWN_FRONT - SPAWN_MARGIN
  const SPAWN_BACK_LIMIT = RESPAWN_BACK - SPAWN_MARGIN

  // ==================================================
  // 🚀 START LEVEL / TELEPORT
  // ==================================================

  if (departOption != null) {

    // ================================================
    // RESET LISTE
    // ================================================

    actorsRef.current = []

    liveCountsRef.current = {}
    currentLiveRef.current = {}

    const counts =
      computeActorCounts(
        count,
        proportions
      )

    liveCountsRef.current = {
      ...counts
    }

    for (const type in counts) {

      currentLiveRef.current[type] = 0
    }

    const actors = []

    // ================================================
    // SPAWN IMMÉDIAT DANS ZONE VISIBLE
    // ================================================

    for (const type in counts) {

      for (let i = 0; i < counts[type]; i++) {

        const actor = createActor(type, playerZ)

        if (!actor)
          continue

        currentLiveRef.current[type]++

        // ============================================
        // ZONE VISIBLE COMPLETE
        // ============================================

        actor.z =
          playerZ +
          THREE.MathUtils.randFloat(
            RESPAWN_FRONT + SPAWN_MARGIN,
            RESPAWN_BACK - SPAWN_MARGIN
          )

        actors.push(actor)
      }
    }

    // ================================================
    // COMMIT FINAL
    // ================================================

    actorsRef.current = actors
    setRenderActors([...actors])

    return
  }

  // ==================================================
  // ⭐ INITIAL SPAWN NORMAL
  // ==================================================

  if (list.length === 0) {

    const counts =
      computeActorCounts(
        count,
        proportions
      )

    liveCountsRef.current = {
      ...counts
    }

    currentLiveRef.current = {}

    for (const type in counts) {

      currentLiveRef.current[type] = 0
    }

    const actors = []

    for (const type in counts) {

      for (let i = 0; i < counts[type]; i++) {

        const actor = createActor(type, playerZ)

        if (!actor)
          continue

        currentLiveRef.current[type]++

        // ============================================
        // ZONE VISIBLE COMPLETE
        // ============================================

        actor.z =
          playerZ +
          THREE.MathUtils.randFloat(
            RESPAWN_FRONT + SPAWN_MARGIN,
            RESPAWN_BACK - SPAWN_MARGIN
          )

        actors.push(actor)
      }
    }

    actorsRef.current = actors
    return
  }

  // ==================================================
  // ⭐ LEVEL UP
  // ==================================================

  const missing = count - list.length

  if (missing <= 0)
    return

  for (let i = 0; i < missing; i++) {

    const type = randomType(proportions)
    const actor = createActor(type, playerZ)

    if (!actor)
      continue

    // ================================================
    // SPAWN DEVANT
    // ================================================

    actor.z = playerZ + THREE.MathUtils.randFloat(SPAWN_FRONT_MIN, SPAWN_FRONT_MAX)

    // ================================================
    // SÉCURITÉ
    // ================================================

    if (actor.z - playerZ > SPAWN_BACK_LIMIT) {
      actor.z = playerZ + SPAWN_BACK_LIMIT
    }

    list.push(actor)
  }

}, [departOption, restartVersion])

// ==================================================
// ⭐ AJOUT ACTEURS AU CHANGEMENT DE LEVEL
// ==================================================

useEffect(() => {

  if (!playerRef?.current)
    return

  const list = actorsRef.current

  if (!list)
    return

  // ==================================================
  // POSITION JOUEUR
  // ==================================================

  const currentWorldZ = worldGroup?.current?.position?.z || 0
  const playerZ = -currentWorldZ

  // ==================================================
  // NOUVEAU COUNT SELON DENSITÉ / LEVEL
  // ==================================================

  const targetCount = count

  // ==================================================
  // ACTEURS MANQUANTS
  // ==================================================

  const missing = targetCount - list.length

  if (missing <= 0)
    return

  // ==================================================
  // ZONES DE SPAWN
  // ==================================================

  const SPAWN_FRONT_MIN = RESPAWN_FRONT - SPAWN_RANGE
  const SPAWN_FRONT_MAX = RESPAWN_FRONT - SPAWN_MARGIN

  // ==================================================
  // AJOUT
  // ==================================================

  const newActors = []

  for (let i = 0; i < missing; i++) {
    const type = pickBalancedType()
    const actor = createActor(type, playerZ)

    if (!actor)
      continue

    // ================================================
    // SPAWN DEVANT
    // ================================================

    actor.z = playerZ + THREE.MathUtils.randFloat(SPAWN_FRONT_MIN, SPAWN_FRONT_MAX)
    currentLiveRef.current[type] = (currentLiveRef.current[type] || 0) + 1

    list.push(actor)
    newActors.push(actor)
  }

  // ==================================================
  // IMPORTANT :
  // force le render React
  // ==================================================

  setRenderActors([...list])

}, [level, count])

function pickBalancedType() {

  const targets = liveCountsRef.current
  const current = currentLiveRef.current

  let bestTypes = []
  let biggestNeed = -Infinity

  for (const type in targets) {

    const target = targets[type] ?? 0
    const live = current[type] ?? 0

    const need = target - live

    if (need > biggestNeed) {

      biggestNeed = need
      bestTypes = [type]

    } else if (need === biggestNeed) {

      bestTypes.push(type)
    }
  }

  // fallback sécurité
  if (bestTypes.length === 0) {
    return randomType(proportions)
  }

  return bestTypes[
    Math.floor(Math.random() * bestTypes.length)
  ]
}

function computeActorCounts(
  totalActors,
  proportions
) {

  const counts = {}

  // ============================================
  // NORMALISER
  // ============================================

  const entries =
    Object.entries(proportions)

  const totalWeight =
    entries.reduce(
      (s, [, p]) => s + p,
      0
    )

  // ============================================
  // QUOTAS THÉORIQUES
  // ============================================

  let assigned = 0

  const remainders = []

  for (const [type, p] of entries) {

    const exact =
      (p / totalWeight) *
      totalActors

    // base garantie
    let count =
      Math.floor(exact)

    // IMPORTANT :
    // petits types visibles
    if (
      p >= 0.02 &&
      count === 0
    ) {
      count = 1
    }

    counts[type] = count

    assigned += count

    remainders.push({

      type,

      remainder:
        exact - Math.floor(exact)
    })
  }

  // ============================================
  // DISTRIBUER RESTE
  // ============================================

  let remaining =
    totalActors - assigned

    

  // plus grands restes d’abord
  remainders.sort(
    (a, b) =>
      b.remainder - a.remainder
  )

  let i = 0

  while (remaining > 0) {

    const item =
      remainders[
        i % remainders.length
      ]

    counts[item.type]++

    remaining--
    i++
  }

  // ============================================
  // ANTI-DUPLICATION
  // ============================================

  const maxPerType =
    Math.ceil(totalActors * 0.22)

  for (const type in counts) {

    counts[type] = Math.min(
      counts[type],
      maxPerType
    )
  }

  return counts
}

function randomType(proportions){

  const r = Math.random()
  let sum = 0

  for (const [type, p] of Object.entries(proportions)) {
    sum += p
    if (r <= sum) return type
  }

  // sécurité
  return Object.keys(proportions)[0]
}

function spawnBaseXForType(
  type,
  opposite = false,
  playerZ = null
) {

  // ==================================================
  // 🚴 SENS INVERSE
  // ==================================================

  if (opposite) {

    const bounds = getBikeLaneBounds("left", playerZ)

    return THREE.MathUtils.randFloat(
      bounds.min,
      bounds.max
    )
  }

  // ==================================================
  // 🚴 TRAFIC NORMAL
  // ==================================================

  const bounds = getBikeLaneBounds("right", playerZ)

  return THREE.MathUtils.randFloat(
    bounds.min,
    bounds.max
  )
}

function isOppositeBike(type, playerZ) {

  // seulement après Berri
  if (playerZ < berri.z) {

    return OPPOSITE_GLBS.includes(type)
  }

  return false
}

function createActor(type,playerZ){

  const opposite = isOppositeBike(type, playerZ)
  // sécurité
  if (opposite && playerZ > berri.z) {

    return null
  }
  const baseX = spawnBaseXForType(type, opposite, playerZ)
  const z = randomActiveZ(playerZ)
  const cfg = ACTOR_CONFIG[type]

  let texture = null
  let frame = 0
  let frames = 1      // ⭐ nb de frames sprite
  let variant = null  // ⭐ pour joggeuse seulement
  let extra = {}

  switch(type){

    case "cyclist": 
      texture = TEXTURE_POOLS.cyclist[Math.floor(Math.random() * TEXTURE_POOLS.cyclist.length)]
      frames = cfg.frames
      frame = Math.random() * frames
      break

    case "cycliste4": 
      texture = TEXTURE_POOLS.cycliste4[Math.floor(Math.random() * TEXTURE_POOLS.cycliste4.length)]
      frames = cfg.frames
      frame = 6
      extra = {
        turnGroup: 2,
        targetGroup: 2,
        animIndex: 0,
        animTimer: 0
      }
      break

    case "sport":
      texture = TEXTURE_POOLS.sport[Math.floor(Math.random() * TEXTURE_POOLS.sport.length)]
      frames = cfg.frames
      frame = Math.random() * frames
      break

    case "tri": 
      texture = TEXTURE_POOLS.tri[Math.floor(Math.random() * TEXTURE_POOLS.tri.length)]
      frames = cfg.frames
      frame = 1
      break

    case "scoot": 
      texture = TEXTURE_POOLS.scoot[Math.floor(Math.random() * TEXTURE_POOLS.scoot.length)]
      frames = cfg.frames
      frame = 2
      extra = {
        scootFrame: 2,
        scootLatVel: 0,
        scootTurnCooldown: 0
      }
      break

    case "bixi":
      texture = TEXTURE_POOLS.bixi[Math.floor(Math.random() * TEXTURE_POOLS.bixi.length)]
      frames = cfg.frames
      frame = 2
      extra = {
        bixiFrame: 2,
        bixiLatVel: 0,
        bixiTurnCooldown: 0,
        turnGroup: 2,
        animTimer: 0,
        turnTimer: 0
      }
      break

    case "joggeuse":
      const isMale = Math.random() < 0.5
      const pool = isMale ? TEXTURE_POOLS.jogger : TEXTURE_POOLS.joggeuse
      texture = pool[Math.floor(Math.random() * pool.length)]
      const runCfg = isMale ? ACTOR_CONFIG.jogger : ACTOR_CONFIG.joggeuse
      frames = runCfg.frames
      variant = isMale ? "jogger" : "joggeuse"
      frame = 0
      break

    case "trailer":
      texture = TEXTURE_POOLS.trailer[Math.floor(Math.random() * TEXTURE_POOLS.trailer.length)]
      frames = cfg.frames
      frame = 1
      break

    case "sportif": 
      texture = TEXTURE_POOLS.sportif[Math.floor(Math.random() * TEXTURE_POOLS.sportif.length)]
      frames = cfg.frames
      frame = 2
      break

    case "child":
      texture = TEXTURE_POOLS.child[Math.floor(Math.random() * TEXTURE_POOLS.child.length)]
      frames = cfg.frames
      frame = 2
      extra = {
        zigTimer: Math.random() * cfg.zigInterval,
        latVel: 0,
        speed: 1,
        frameTimer: 0,
        state: "center"
      }
      break

    case "womanA":
    case "womanB":
    case "womanC":
    case "manA":
    case "manB":
    case "manC":
    case "girl":
    case "boy":
    case "baby":
    case "manC_bike":
    case "womanA_bike":
    case "boy_bike":
    case "sportManA":
    case "sportManB":
    case "sportWomanA":
    case "sportWomanB":
    case "sportWomanC":

      const model = GLB_ACTORS[type]
      const animChoice = pickGLBAnimChoice(model)

      extra = {
        glb: true,
        glbData: model,
        bikeMode: model.bikeMode,
        scale: model.scale,
        offset: model.offset,
        url: model.url,
        mode: getModeForGLBAnimChoice(animChoice, model.bikeMode),
        animChoice,
        runVariant: undefined,
        speedVariation: undefined,
        speedProfileKey: undefined,
        targetSpeed: undefined,
        animationVersion: 0,
        modeTimer:  Math.random() * 10,
        isPedestrian: !model.bikeMode,
        isBike: model.bikeMode,
      }
      break

    default:
      const defCfg = ACTOR_CONFIG.cyclist
      texture = cyclistTextures[0]
      texture.wrapS = THREE.RepeatWrapping
      texture.repeat.set(1 / defCfg.frames, 1)
      frames = defCfg.frames
  }
  
  return {
    type,
    variant,
    frames,
    accel:cfg.accel,
    animSpeed:cfg.animSpeed,
    baseX,
    offsetX: 0,
    targetOffset: 0,
    avoidSide: 0,
    avoidTimer: 0,
    avoidTargetX: undefined,
    avoidLatSpeed: 0,
    prevX: baseX,
    x: baseX,
    z,
    texture,
    speed: THREE.MathUtils.randFloat(0.9, 1.1),
    currentSpeed: 0,
    halfWidth: cfg.halfWidth,
    halfLength: cfg.halfLength,
    frame,
    spawnJitter: Math.random() * 120,
    direction: opposite ? 1 : -1,
    lane: opposite ? "left" : "right",
    isOpposite: opposite,
    brakingForLight: false,
    targetLightId: null,
    committedToCross: false,
    mesh: null,
    map: texture,
    id: NEXT_ACTOR_ID++,
    ...extra   
  }
}

function randomActiveZ() {

  const r = Math.random()
  const visibleZ =
    r < 0.6
      ? THREE.MathUtils.randFloat(-180, -60)  // devant
      : THREE.MathUtils.randFloat(0, 80)      // derrière

  const currentWorldZ = worldGroup?.current?.position?.z || 0

  return visibleZ - currentWorldZ
}

// ======================================================
// 🔄 RESPAWN 
// ======================================================

function respawn(data, playerWorldZ) {

  // ==================================================
  // SAFETY
  // ==================================================

  if (!data)
    return false

  if (data.z === undefined)
    return false

  // ==================================================
  // POSITION RELATIVE
  // ==================================================

  const dz = data.z - playerWorldZ

  // ==================================================
  // ZONES SAFE
  // ==================================================

  const SPAWN_BACK_MIN = RESPAWN_BACK - SPAWN_RANGE
  const SPAWN_BACK_MAX = RESPAWN_BACK - SPAWN_MARGIN
  const SPAWN_FRONT_MIN = RESPAWN_FRONT - SPAWN_RANGE
  const SPAWN_FRONT_MAX = RESPAWN_FRONT - SPAWN_MARGIN

  // ==================================================
  // 🔴 TROP LOIN DEVANT
  // → recycler derrière
  // ==================================================

  // ==================================================
  // 🔴 TROP LOIN DEVANT
  // ==================================================

  const tooFarFront = data.direction === -1 ? dz < RESPAWN_FRONT : dz > RESPAWN_BACK

  if (tooFarFront) {

    // ================================================
    // POSITION SELON DIRECTION
    // ================================================

    if (data.direction === -1) {

      // trafic normal
      data.z = playerWorldZ + THREE.MathUtils.randFloat(SPAWN_BACK_MIN, SPAWN_BACK_MAX)

    } else {

      // sens inverse
      data.z = playerWorldZ + THREE.MathUtils.randFloat(SPAWN_FRONT_MIN, SPAWN_FRONT_MAX)
    }

    // ================================================
    // RETIRER ANCIEN TYPE
    // ================================================

    if (currentLiveRef.current[data.type] !== undefined) {

      currentLiveRef.current[data.type]--
    }

    // ================================================
    // NOUVEAU TYPE
    // ================================================

    const newType = pickBalancedType()
    const newActor = createActor(newType, playerWorldZ)

    if (!newActor)
      return false

    // ================================================
    // CONSERVER MESH / ID
    // ================================================

    newActor.mesh = data.mesh
    newActor.id = data.id

    // ================================================
    // APPLY
    // ================================================

    const previousAnimationVersion = data.animationVersion ?? 0

    Object.assign(data, newActor)
    data.animationVersion = previousAnimationVersion + 1

    // ================================================
    // AJOUTER NOUVEAU TYPE
    // ================================================

    currentLiveRef.current[newType]++

    // ================================================
    // RESET
    // ================================================

    resetActor(data, playerWorldZ)

    return true
  }

  // ==================================================
  // 🔵 TROP LOIN DERRIÈRE
  // → recycler devant
  // ==================================================

  if (dz > RESPAWN_BACK) {

    // ================================================
    // POSITION DEVANT
    // ================================================

    data.z = playerWorldZ + THREE.MathUtils.randFloat(SPAWN_FRONT_MIN, SPAWN_FRONT_MAX)

    // ================================================
    // RESET
    // ================================================

    resetActor(data, playerWorldZ)

    return true
  }

  return false
}
//----------------------------------------------------
// ============================================================
// 🚧 OBSTACLES GÉNÉRIQUES
// ============================================================

function checkObstacles(
  group,
  actor,
  vision,
  halfW,
  halfL,
  type
) {

  const trackedObstacle =
    Array.isArray(group) && group.length === 1
      ? group[0]
      : null

  // ==========================================================
  // INSTANCED MESH
  // ==========================================================

  let mesh = null

  if (trackedObstacle?.mesh?.isInstancedMesh) {

    mesh = trackedObstacle.mesh

  } else if (group?.current?.isInstancedMesh) {

    mesh = group.current

  } else if (group?.current?.children?.[0]?.isInstancedMesh) {

    mesh = group.current.children[0]
  }

  // ==========================================================
  // DONNÉES
  // ==========================================================

  const data = mesh?.__data || mesh?.parent?.__data

  // ==========================================================
  // ACTEUR
  // ==========================================================

  const actorHalfW = actor.halfWidth ?? 0.6
  const actorHalfL = actor.halfLength ?? 1.0

  // ==========================================================
  // TEMP
  // ==========================================================

  const tempMatrix = new THREE.Matrix4()
  const tempPos = new THREE.Vector3()
  const tempQuat = new THREE.Quaternion()
  const tempScale = new THREE.Vector3()

  // ==========================================================
  // RÉSULTAT
  // ==========================================================

  let obstacle = null
  let closest = Infinity

  // ==========================================================
  // INSTANCED
  // ==========================================================

  if (mesh && data) {

    const startIndex =
      trackedObstacle?.mesh === mesh && Number.isInteger(trackedObstacle.index)
        ? trackedObstacle.index
        : 0

    const endIndex =
      trackedObstacle?.mesh === mesh && Number.isInteger(trackedObstacle.index)
        ? trackedObstacle.index + 1
        : mesh.count

    for (let i = startIndex; i < endIndex; i++) {

      const item = data[i]

      if (!item) continue

      if (item.hit || item.locked) {
        continue
      }

      // ======================================================
      // MATRICE
      // ======================================================

      mesh.getMatrixAt(i, tempMatrix)
      tempMatrix.decompose(tempPos, tempQuat, tempScale)

      // ======================================================
      // POSITION
      // ======================================================

      const x = tempPos.x
      const z = tempPos.z

      // ======================================================
      // DISTANCE
      // ======================================================

      const dz = actor.z - z

      // derrière
      if (dz <= 0) {
        continue
      }

      // trop loin
      if (dz > vision) {
        continue
      }

      const dx = actor.x - x

      // ======================================================
      // SCALE RÉEL
      // ======================================================

      const scale = tempScale.x || 1

      // ======================================================
      // 📦 DIMENSIONS RÉELLES DU GEOMETRY
      // ======================================================

      const geometry = mesh.geometry

      // cache dimensions
      if (
        geometry &&
        (
          geometry.userData.baseHalfW === undefined ||
          geometry.userData.baseHalfL === undefined
        )
      ) {

        if (!geometry.boundingBox) {
          geometry.computeBoundingBox()
        }

        const size = new THREE.Vector3()

        geometry.boundingBox.getSize(size)

        // ==================================================
        // 🔥 DÉTECTION AUTOMATIQUE PLAN / OBJET 3D
        // ==================================================

        const isFlat = size.z < 0.05

        geometry.userData.baseHalfW =
          size.x * 0.5

        geometry.userData.baseHalfL =
          isFlat
            ? size.y * 0.5
            : size.z * 0.5
      }

      // ======================================================
      // 📦 BASE
      // ======================================================

      const baseHalfW =
        geometry?.userData?.baseHalfW ?? halfW

      const baseHalfL =
        geometry?.userData?.baseHalfL ?? halfL

      // ======================================================
      // 📦 SCALE FINAL
      // ======================================================

      const realHalfW = baseHalfW * scale
      const realHalfL = baseHalfL * scale

      // ======================================================
      // HITBOX
      // ======================================================

      const detectWidth =
        actorHalfW +
        realHalfW +
        0.6

      if (Math.abs(dx) > detectWidth) {
        continue
      }

      // ======================================================
      // DISTANCE FINALE
      // ======================================================

      const dist =
        dz - (actorHalfL + realHalfL)

      // ======================================================
      // PLUS PROCHE
      // ======================================================

      if (dist < closest) {

        closest = dist

        obstacle = {
          x,
          z,
          localX: dx,
          worldDist: dz,
          halfW: realHalfW,
          halfL: realHalfL,
          avoidHalfW: realHalfW,
          type,
          source: {
            mesh,
            index: i,
            data: item
          },
          distance: Math.max(0, dist)
        }

        // imminent
        if (dist < 1) {
          break
        }
      }
    }
  }

  // ==========================================================
  // OBJETS THREE NORMAUX (BACS)
  // ==========================================================

  else {

    const list =
      Array.isArray(group)
        ? group
        : group?.current?.children

    if (!list?.length) {
      return null
    }

    for (let i = 0; i < list.length; i++) {

      const o = list[i]

      if (!o) continue

      if (o.userData?.hit) {
        continue
      }

      // ======================================================
      // POSITION
      // ======================================================

      const x = o.position.x
      const z = o.position.z

      // ======================================================
      // DISTANCE
      // ======================================================

      const dz = actor.z - z

      // derrière
      if (dz <= 0) {
        continue
      }

      // trop loin
      if (dz > vision) {
        continue
      }

      const dx = actor.x - x

      // ======================================================
      // HITBOX
      // ======================================================

      const detectWidth =
        actorHalfW +
        halfW +
        0.6

      if (Math.abs(dx) > detectWidth) {
        continue
      }

      // ======================================================
      // DISTANCE FINALE
      // ======================================================

      const dist =
        dz - (actorHalfL + halfL)

      // ======================================================
      // PLUS PROCHE
      // ======================================================

      if (dist < closest) {

        closest = dist

        obstacle = {
          x,
          z,
          localX: dx,
          worldDist: dz,
          halfW,
          halfL,
          avoidHalfW: halfW,
          type,
          source: o,
          distance: Math.max(0, dist)
        }
      }
    }
  }

  return obstacle
}
function checkCars(
  list,
  data,
  vision,
  defaultHalfW = 1,
  defaultHalfL = 1,
  type = "obstacle",
  keepTrackedCar = false
) {

  if (!list?.length) {
    return null
  }

  let obstacle = null
  let closestDist = Infinity

  // ==================================================
  // HITBOX ACTEUR
  // ==================================================

  const actorHalfW = data.halfWidth ?? 0.6
  const actorHalfL = data.halfLength ?? 1.0

  // ==================================================
  // BOUCLE
  // ==================================================

  for (let i = 0; i < list.length; i++) {

    // ==================================================
    // CAR
    // ==================================================

    const wrapper = list[i]

    if (!wrapper?.position) continue

    let instanceScale = 1

    if (
      wrapper.scale &&
      typeof wrapper.scale === "number"
    ) {

      instanceScale = wrapper.scale
    }

    // ==================================================
    // POSITION
    // ==================================================

    const ox =
      wrapper.position.x +
      (wrapper.userData?.collisionOffsetX || 0)

    const oz =
      wrapper.position.z +
      (wrapper.userData?.collisionOffsetZ || 0)

    // ==================================================
    // POSITION RELATIVE
    // ==================================================

    const dx = data.x - ox
    const dz = data.z - oz
    const aheadDist =
      data.direction === 1
        ? -dz
        : dz

    // ==================================================
    // DEVANT
    // ==================================================

    const trackedBehindDist =
      keepTrackedCar
        ? defaultHalfL + actorHalfL + 4
        : 0

    if (aheadDist <= -trackedBehindDist) continue
    if (aheadDist > vision) continue

    // ==================================================
    // SCALE
    // ==================================================

    const scaleX =
      Math.abs(wrapper.scale?.x || 1)

    const scaleZ =
      Math.abs(wrapper.scale?.z || 1)

    // ==================================================
    // HITBOX
    // ==================================================

    let halfW
    let halfL

    // véhicules GLB
    if (wrapper.userData?.halfWidth) {

      halfW =
        wrapper.userData.halfWidth * scaleX

      halfL =
        wrapper.userData.halfLength * scaleZ
    }

    // instanced potholes
    else {

      halfW = defaultHalfW * instanceScale
      halfL = defaultHalfL * instanceScale
    }

    // ==================================================
    // ROTATION
    // ==================================================

    const rotY = wrapper.rotation?.y || 0
    const cos = Math.cos(rotY)
    const sin = Math.sin(rotY)

    // ==================================================
    // ESPACE LOCAL
    // ==================================================

    const localX = dx * cos + dz * sin
    const localZ = -dx * sin + dz * cos

    // ==================================================
    // AUTO TOURNÉE
    // ==================================================

    const turned = Math.abs(Math.sin(rotY)) > 0.7

    // ==================================================
    // DISTANCES
    // ==================================================

    const frontDist =
      aheadDist - (halfL + actorHalfL)

    const straightCarSideExtra =
      turned ? 0 : STRAIGHT_CAR_ACTOR_AVOID_SIDE_EXTRA

    const collisionHalfW =
      (turned ? halfL : halfW) + straightCarSideExtra

    const detectWidth =
      collisionHalfW +
      actorHalfW +
      2 +
      (turned ? 0 : STRAIGHT_CAR_ACTOR_DETECT_SIDE_EXTRA)

    if (!keepTrackedCar && Math.abs(localX) > detectWidth) {
      continue
    }

    // ==================================================
    // PLUS PROCHE
    // ==================================================

    const dist = Math.max(0, frontDist)

    if (dist < closestDist) {

      closestDist = dist

      obstacle = {
        x: ox,
        z: oz,
        localX,
        localZ,
        worldDist: aheadDist,
        halfW,
        halfL,
        avoidHalfW: collisionHalfW,
        type,
        turned,
        source: wrapper,
        distance: dist
      }

      if (dist < 1) {
        break
      }
    }
  }

  return obstacle
}
function updateChildBehavior(data, dt) {

  const cfg = ACTOR_CONFIG.child

  data.zigTimer += dt

  if (data.zigTimer > cfg.zigInterval) {

    data.zigTimer = 0

    const dir = Math.random() < 0.5 ? -1 : 1

    data.targetOffset = dir * cfg.zigAmplitude
  }

  const dx = data.targetOffset - data.offsetX

  data.offsetX += dx * dt * 0.4
  data.x = data.baseX + data.offsetX
}

function validateGLBSpeedAfterRespawn(data) {
  if (!data?.glb) return

  if (data.mode === "idle") {
    data.targetSpeed = 0
    data.currentSpeed = 0
    data.speed = 0
    return
  }

  applyGLBSpeedProfile(data)
  data.currentSpeed = data.targetSpeed
}

function randomizeGLBAnimationAfterRespawn(data) {
  if (!data?.glb) return

  data.runVariant = undefined
  data.speedVariation = undefined
  data.speedProfileKey = undefined

  if (data.bikeMode) {
    data.animChoice = "bike"
    data.mode = "bike"
    // Les deux clips vélo restent actifs sur le même clone pendant le respawn.
    return
  } else {
    data.animChoice = undefined
    data.mode = "walk"
  }

  data.animationVersion = (data.animationVersion ?? 0) + 1
  data.needsAnimationRefresh = true
}

function resetActor(data, playerZ = null){

  const baseX = spawnBaseXForType(
    data.type,
    data.isOpposite,
    playerZ
  )

  data.baseX = baseX
  data.offsetX = 0
  data.targetOffset = 0
  data.dir = 0
  data.x = baseX
  data.prevX = baseX
  data.latVel = 0
  data.currentSpeed = 0
  data.speedFactor = 1
  data.lightSlowFactor = 1
  data.playerAvoidSide = 0
  data.brakingForLight = false
  data.targetLightId = null
  data.committedToCross = false
  data.turnVel = 0
  data.avoidSide = 0
  data.avoidDir = undefined
  data.avoidTargetX = undefined
  data.avoidLatSpeed = 0
  data.currentObstacle = null
  data.carAvoidLock = 0
  data.lastCarAvoidDir = undefined
  randomizeGLBAnimationAfterRespawn(data)
  validateGLBSpeedAfterRespawn(data)
  
}
//----------------- ANIMATION ------------------------

function applyFrame(map, frame, totalFrames) {
  if (!map) return
  map.offset.x = frame / totalFrames
}

function updateSimpleAnim(data, dt, frames) {
  data.frame += dt * data.animSpeed
  data.frame %= frames
  return Math.floor(data.frame)
}
// ==================================================
// 🎬 ANIMATIONS ACTEURS — VERSION CONFIG CLEAN
// ==================================================

function updateSport(data, dt, map) {
  const cfg = ACTOR_CONFIG.sport
  const frame = updateSimpleAnim(data, dt, cfg.frames)
  applyFrame(map, frame, cfg.frames)
}

// --------------------------------------------------

function updateCyclist(data, dt, map) {
  const cfg = ACTOR_CONFIG.cyclist

  data.frame += dt * data.animSpeed
  data.frame %= cfg.frames

  applyFrame(
    map,
    Math.floor(data.frame),
    cfg.frames
  )
}

// --------------------------------------------------

function updateCycliste4(data, dt, map) {

  const lat = data.latVel || 0

  const TURN1 = 0.3
  const TURN2 = 0.8
  const TURN3 = 1.5

  // ==================================================
  // 🎯 TARGET GROUP
  // ==================================================

  let target

  if (lat < -TURN3) target = 0
  else if (lat < -TURN2) target = 1
  else if (lat < -TURN1) target = 2
  else if (lat > TURN3) target = 5
  else if (lat > TURN2) target = 4
  else if (lat > TURN1) target = 3
  else target = 2

  // ==================================================
  // 🧠 INIT SAFE STATE
  // ==================================================

  data.turnGroup ??= 2
  data.animIndex ??= 0
  data.animTimer ??= 0
  data.turnTimer ??= 0

  // ==================================================
  // 🔒 CLAMP
  // ==================================================

  data.turnGroup = THREE.MathUtils.clamp(data.turnGroup, 0, 5)
  target = THREE.MathUtils.clamp(target, 0, 5)

  // ==================================================
  // ⏱️ TIMER
  // ==================================================

  data.turnTimer += dt

  // 🔥 délai dynamique = plus naturel
  const speedFactor = Math.min(1, Math.abs(lat) / 2)
  const TURN_DELAY = THREE.MathUtils.lerp(0.45, 0.18, speedFactor)

  // ==================================================
  // 🔄 TRANSITION PROGRESSIVE AVEC DÉLAI
  // ==================================================

  if (data.turnTimer > TURN_DELAY) {

    if (data.turnGroup < target) {
      data.turnGroup += 1
    } else if (data.turnGroup > target) {
      data.turnGroup -= 1
    }

    data.turnTimer = 0
  }

  // ==================================================
  // 🎬 FRAMES
  // ==================================================

  const frames = CYCLIST4_GROUPS[data.turnGroup] || [6]

  // ==================================================
  // 🔒 SÉCURITÉ INDEX
  // ==================================================

  if (data.animIndex >= frames.length) {
    data.animIndex = 0
  }

  // ==================================================
  // ⏱️ ANIMATION (pédalage)
  // ==================================================

  data.animTimer += dt

  // 🔥 animation légèrement plus lente = plus lisible
  const ANIM_SPEED = 0.14

  if (data.animTimer > ANIM_SPEED) {
    data.animTimer = 0
    data.animIndex = (data.animIndex + 1) % frames.length
  }

  // ==================================================
  // 🎯 FRAME FINAL
  // ==================================================

  let frame = frames[data.animIndex]

  if (!Number.isFinite(frame)) frame = 6
  frame = THREE.MathUtils.clamp(frame, 0, 13)

  // ==================================================
  // 🎨 APPLY
  // ==================================================

  applyFrame(map, frame, 14)
}

// --------------------------------------------------

function updateScoot(data, dt, map) {
  const cfg = ACTOR_CONFIG.scoot

  const rawLat = data.latVel || 0

  data.scootLatVel ??= rawLat
  data.scootFrame ??= 2
  data.scootTurnCooldown ??= 0

  const smooth =
    1 - Math.exp(-7 * dt)

  data.scootLatVel =
    THREE.MathUtils.lerp(
      data.scootLatVel,
      rawLat,
      smooth
    )

  data.scootTurnCooldown =
    Math.max(0, data.scootTurnCooldown - dt)

  const lat = data.scootLatVel
  const TURN1 = 0.55
  const TURN2 = 1.35
  const RELEASE = 0.22
  const SWITCH_DELAY = 0.18

  let frame = data.scootFrame

  if (data.scootTurnCooldown <= 0) {

    if (lat < -TURN2) frame = 0
    else if (lat < -TURN1) frame = 1
    else if (lat > TURN2) frame = 4
    else if (lat > TURN1) frame = 3
    else if (Math.abs(lat) < RELEASE) frame = 2

    if (frame !== data.scootFrame) {
      data.scootTurnCooldown = SWITCH_DELAY
      data.scootFrame = frame
    }
  }

  data.frame = data.scootFrame
  applyFrame(map, data.frame, cfg.frames)
}

function updateBixi(data, dt, map) {

  const rawLat =
    data.latVel || 0

  data.bixiLatVel ??= rawLat
  data.bixiFrame ??= 2
  data.bixiTurnCooldown ??= 0

  const smooth =
    1 - Math.exp(-6 * dt)

  data.bixiLatVel =
    THREE.MathUtils.lerp(
      data.bixiLatVel,
      rawLat,
      smooth
    )

  data.bixiTurnCooldown =
    Math.max(0, data.bixiTurnCooldown - dt)

  const lat =
    data.bixiLatVel

  const TURN1 = 0.18
  const TURN2 = 0.55
  const RELEASE = 0.08
  const SWITCH_DELAY = 0.22

  let frame =
    data.bixiFrame

  if (data.bixiTurnCooldown <= 0) {

    if (lat < -TURN2) {
      frame = 0

    } else if (lat < -TURN1) {
      frame = 1

    } else if (lat > TURN2) {
      frame = 4

    } else if (lat > TURN1) {
      frame = 3

    } else if (Math.abs(lat) < RELEASE) {
      frame = 2
    }

    if (frame !== data.bixiFrame) {
      data.bixiTurnCooldown = SWITCH_DELAY
      data.bixiFrame = frame
    }
  }

  applyFrame(
    map,
    data.bixiFrame,
    5
  )
}
// --------------------------------------------------

function updateRunner(data, dt, map) {

  const lat = data.latVel || 0
  const TURN1 = 0.25

  data.frame += dt * data.animSpeed

  if (data.variant === "jogger") {

    const cfg = ACTOR_CONFIG.jogger

    data.frame %= 3

    let base = 3
    if (lat < -TURN1) base = 0
    else if (lat > TURN1) base = 6

    const animIndex = Math.floor(data.frame)
    const finalFrame = base + animIndex

    applyFrame(map, finalFrame, cfg.frames)

  } else {

    const cfg = ACTOR_CONFIG.joggeuse

    data.frame %= cfg.frames
    applyFrame(map, Math.floor(data.frame), cfg.frames)
  }
}

// --------------------------------------------------

function updateChild(data, dt, map) {

  const cfg = ACTOR_CONFIG.child

  updateChildBehavior(data, dt)

  const dx = data.targetOffset - data.offsetX
  const prev = data.state

  if (Math.abs(dx) < 0.15) data.state = "center"
  else if (dx < 0) data.state = prev === "right" ? "center" : "left"
  else data.state = prev === "left" ? "center" : "right"

  data.frameTimer += dt

  if (data.frameTimer > 1 / cfg.animFps) {

    data.frameTimer = 0

    let frames =
      data.state === "left" ? [0,1] :
      data.state === "center" ? [2,3] :
      [4,5]

    data.frame = frames[(data.frame + 1) % 2]
  }

  applyFrame(map, data.frame, cfg.frames)
}

// --------------------------------------------------

function updateSportif(data, dt, map) {

  const cfg = ACTOR_CONFIG.sportif

  const lat = data.latVel || 0
  const TURN = 0.35
  const TURN_SPEED = 10

  let target = 2.5
  if (lat < -TURN) target = 0
  else if (lat > TURN) target = 5

  data.turnFrame = data.turnFrame ?? target

  if (data.turnFrame < target)
    data.turnFrame = Math.min(data.turnFrame + TURN_SPEED * dt, target)
  else if (data.turnFrame > target)
    data.turnFrame = Math.max(data.turnFrame - TURN_SPEED * dt, target)

  let frameIndex

  if (data.turnFrame > 1.5 && data.turnFrame < 3.5) {

    data.frame += dt * data.animSpeed
    data.frame %= 2

    frameIndex = 2 + Math.floor(data.frame)

  } else {

    frameIndex = Math.round(data.turnFrame)
  }

  applyFrame(map, frameIndex, cfg.frames)
}

// --------------------------------------------------

function updateTrailer(data, dt, map) {

  const cfg = ACTOR_CONFIG.trailer

  const TURN = 0.35
  const lat = data.latVel || 0

  if (lat < -TURN) data.frame = 0
  else if (lat > TURN) data.frame = 2
  else data.frame = 1

  applyFrame(map, data.frame, cfg.frames)
}

// --------------------------------------------------

function updateTri(data, dt, map) {

  const cfg = ACTOR_CONFIG.tri
  const TURN = 0.45

  data.turnVel = THREE.MathUtils.lerp(
    data.turnVel || 0,
    data.latVel,
    2.2 * dt
  )

  const lat = data.turnVel || 0

  if (lat < -TURN) data.frame = 0
  else if (lat > TURN) data.frame = 2
  else data.frame = 1

  applyFrame(map, data.frame, cfg.frames)
}

  /* UPDATE */
  useFrame((_, dtRaw) => {
  const dt = Math.min(dtRaw,  0.04)

    if(gameState==="waitingContinue") return
    if(!group.current) return
    
    const actors = actorsRef.current
    const cars = staticCarsRef?.current?.children || []
    const lights = trafficLightsRef?.current?.getStates?.() || []

    for (let i = 0; i < actors.length; i++) {

      const data = actors[i]

      if (!data) continue

      const mesh = data.mesh
      if (data.z === undefined) continue

      const currentWorldZ = worldGroup?.current?.position?.z || 0
      const playerWorldZ = -currentWorldZ
      let visibleZ = data.z - playerWorldZ

      if (respawn(data, playerWorldZ)) {

        if (mesh) {

          mesh.position.x = data.x
          mesh.position.z = data.z
        }

        continue
      }

      let stopZ = null
      
      data.zigTimer ??= 0
      data.offsetX ??= 0
      data.targetOffset ??= 0
      data.currentSpeed ??= 0
      data.latVel ??= 0
      
      // ==================================================
      // 🚦 FEU ROUGE — VERSION SAFE & STABLE
      // ==================================================

      let myLight = null

      // 1️⃣ récupérer feu assigné
      if (data.targetLightId) {

        myLight = lights.find(l => l && l.id === data.targetLightId && l.position)

      }

      // 2️⃣ sinon chercher le plus proche
      if (!myLight) {

        let bestDist = Infinity

        for (const light of lights) {

          if (!light || !light.position) continue

          const dz = light.position[2] - data.z
          const isAhead = data.direction === -1 ? dz < 0 : dz > 0

          if (!isAhead) continue

          const dist = Math.abs(dz)

          if (dist < bestDist && dist < LIGHT_DETECTION_DISTANCE) {
            bestDist = dist
            myLight = light
          }
        }
      }

      // ==================================================
      // ❌ PAS DE FEU → RESET
      // ==================================================

      if (!myLight || !myLight.position) {

        data.brakingForLight = false
        data.lightSlowFactor = 1
        data.targetLightId = null
        data.committedToCross = false
        stopZ = null

      } else {

      // ==================================================
      // 3️⃣ CALCUL LIGNE D’ARRÊT (SAFE)
      // ==================================================

      const inter = intersections.find(
        i =>
          i &&
          (
            i.id === myLight.intersectionId ||
            Math.abs(i.z - myLight.position[2]) < 18
          )
      )

      const halfWidth = inter?.width ? inter.width * 0.5 : 0
      const quarterWidth = (inter?.width ?? 16) * 0.25

      stopZ =
        data.direction === -1
          ? myLight.position[2] + halfWidth + STOP_LINE_OFFSET
          : myLight.position[2] - halfWidth - STOP_LINE_OFFSET

      const distToLine = Math.abs(data.z - stopZ)
      const signedDistToLine =
        data.direction === -1
          ? data.z - stopZ
          : stopZ - data.z
      const beforeStopLine =
        data.direction === -1
          ? data.z > stopZ
          : data.z < stopZ
      const pastStopLineDistance = Math.max(0, -signedDistToLine)
      const pastCommitLimit = pastStopLineDistance > quarterWidth

      // ==================================================
      // 4️⃣ ASSIGNATION
      // ==================================================

      if (!data.targetLightId && !pastCommitLimit && distToLine > 0 && distToLine < LIGHT_ASSIGN_DISTANCE) {
        data.committedToCross = false
        data.targetLightId = myLight.id
      }

      const isMyLight = data.targetLightId === myLight.id
      if (isMyLight && pastCommitLimit) {
        data.committedToCross = true
      }

      // ==================================================
      // 5️⃣ LOGIQUE SIMPLE (🔥 IMPORTANT)
      // ==================================================

      if (!isMyLight) {

        data.brakingForLight = false
        data.lightSlowFactor = 1
        data.committedToCross = false
        stopZ = null

      } else if (data.committedToCross) {

        data.brakingForLight = false
        data.lightSlowFactor = 1
        data.targetLightId = null
        stopZ = null

      } else if ((myLight.state === "red" || myLight.state === "yellow") && !pastCommitLimit) {

        // frein si proche
        if (!beforeStopLine) {

          data.lightSlowFactor = 0
          data.currentSpeed = 0
          data.brakingForLight = true
          stopZ = null

        } else if (distToLine > 0 && distToLine < STOP_DISTANCE) {

          // ratio 0 → 1
          const t = distToLine / STOP_DISTANCE

          // easing (plus naturel)
          const slowFactor = Math.pow(t, 1.5)

          data.lightSlowFactor = slowFactor
          data.brakingForLight = true

        } else {
          data.lightSlowFactor = 1
        }

      } else {
        // feu vert
        data.brakingForLight = false
        data.targetLightId = null
        data.committedToCross = false
        stopZ = null
        data.lightSlowFactor = 1
      }
    }


  //-------- VITESSE -----------------------------

  const cfg = data.glb ? getGLBMovementConfig(data) : ACTOR_CONFIG[data.type]
  const BASE_SPEED = cfg.baseSpeed +(cfg.levelGain ?? 0) * level
  const realSpeedBase = BASE_SPEED
  let speedFactor = 1

  if (data.brakingForLight) {
    speedFactor = 0
  }

  // sécurité : jamais bloqué sans raison
  if (!data.brakingForLight && speedFactor === 0) {
    speedFactor = 1
  }

  let realSpeed = Math.min(realSpeedBase,cfg.maxSpeed ?? realSpeedBase)
  if (!Number.isFinite(realSpeed)) {
    realSpeed = cfg.baseSpeed
  }

  /* VISION */

  const speedForVision = Math.max(data.currentSpeed ?? 0, realSpeedBase)

  let vision = THREE.MathUtils.clamp(speedForVision * 3.0, 8, 30)

// ============================================================
// 🚧 COLLISIONS
// ============================================================

data.currentObstacle ??= null
data.avoidDir ??= undefined
data.carAvoidLock ??= 0
data.lastCarAvoidDir ??= undefined

let obstacle = data.currentObstacle

// ============================================================
// 🔄 RECALCUL OBSTACLE COURANT
// ============================================================

if (obstacle) {

  // ========================================================
  // 🚗 AUTOS
  // ========================================================

  if (obstacle.type === "car") {
    obstacle = checkCars([obstacle.source], data, vision * 1.9, obstacle.halfW, obstacle.halfL, obstacle.type, true)
  }

  // ========================================================
  // 🚧 OBSTACLES SIMPLES
  // ========================================================

  else {
    obstacle = checkObstacles([obstacle.source], data, vision, obstacle.halfW,  obstacle.halfL, obstacle.type)
  }

  // obstacle perdu
  if (!obstacle) {

    data.currentObstacle = null
    data.avoidDir = undefined
    data.avoidTargetX = undefined
    data.avoidLatSpeed = 0
    data.targetOffset = 0

  } else {
    data.currentObstacle = obstacle
  }
}

if (data.currentObstacle?.type === "car" && data.avoidDir !== undefined) {
  data.carAvoidLock = 0.9
  data.lastCarAvoidDir = data.avoidDir
} else if (data.carAvoidLock > 0) {
  data.carAvoidLock = Math.max(0, data.carAvoidLock - dt)
  if (data.carAvoidLock === 0) {
    data.lastCarAvoidDir = undefined
  }
}

// ============================================================
// ♻️ OBSTACLE INVALIDE
// ============================================================

if (obstacle) {

  const actorHalfL = data.halfLength ?? 1

// ========================================================
// DIMENSIONS RÉELLES
// ========================================================

const halfL2 = obstacle.halfL * 2
const halfW2 = obstacle.halfW * 2
const length = Math.max(halfL2, halfW2)
const width = Math.min(halfL2, halfW2)

// ========================================================
// PROFONDEUR
// ========================================================

// IMPORTANT :
// autos non tournées doivent garder
// beaucoup plus de profondeur

const profondeur = obstacle.turned ? width * 2.6 : length * 2.8

// ========================================================
// DISTANCE LOCALE
// ========================================================

// utiliser localZ plutôt que monde Z
// beaucoup plus fiable

const localZ = obstacle.localZ
const signedForwardDist =
  obstacle.worldDist ?? localZ

// ========================================================
// DÉPASSÉ
// ========================================================

const passed = signedForwardDist < -(profondeur + actorHalfL + 3)

// ========================================================
// TROP LOIN
// ========================================================

const tooFar = obstacle.distance > vision * 2

// ========================================================
// RESET
// ========================================================

if (passed || tooFar) {
  obstacle = null
  data.currentObstacle = null
  data.avoidDir = undefined
  data.avoidTargetX = undefined
  data.avoidLatSpeed = 0
  data.targetOffset = 0
}
}

// ============================================================
// 🚗 AUTO PRIORITAIRE
// ============================================================

const carObstacle = checkCars(cars, data, vision * 1.6, 2, 4, "car")

// ============================================================
// 🚗 AUTO REMPLACE TOUT
// ============================================================

if (carObstacle) {

  // nouvelle auto ?
  const changedCar = !data.currentObstacle || data.currentObstacle.source !== carObstacle.source

  // recalcul direction
  if (changedCar) {
    data.avoidDir = undefined
    data.avoidTargetX = undefined
    data.avoidLatSpeed = 0
  }

  obstacle = carObstacle
  data.currentObstacle = carObstacle
}

// ============================================================
// 🔍 RECHERCHE OBSTACLE NORMAL
// ============================================================

else if (!obstacle) {
  let closestDist = Infinity

  // ========================================================
  // 🧱 CONES
  // ========================================================

  const coneObstacle = checkObstacles(conesRef, data, vision, 0.31, 0.31, "cone")

  if (coneObstacle && coneObstacle.distance < closestDist) {
    obstacle = coneObstacle
    closestDist = coneObstacle.distance
  }

  // ========================================================
  // 🕳️ POTHOLES
  // ========================================================

  const potholeObstacle = checkObstacles(potholesRef, data, vision, 1, 1, "pothole")

  if (potholeObstacle && potholeObstacle.distance < closestDist) {

    obstacle = potholeObstacle
    closestDist = potholeObstacle.distance
  }

  // ========================================================
  // 🗑️ BACS
  // ========================================================

  const bacObstacle = checkObstacles(bacsRef, data, vision, 0.68, 0.32, "bac")

  if (bacObstacle && bacObstacle.distance < closestDist) {
    obstacle = bacObstacle
    closestDist = bacObstacle.distance
  }

  // ========================================================
  // 💾 SAVE
  // ========================================================

  data.currentObstacle = obstacle
}

// ============================================================
// ♻️ RESET
// ============================================================
if (obstacle) {

  // ========================================================
// DISTANCE DE SORTIE
// ========================================================

const exitDistance = obstacle.turned ? obstacle.halfW : obstacle.halfL 
  // ========================================================
  // AUTO DÉPASSÉE
  // ========================================================

  const passedEnough = obstacle.worldDist < -exitDistance

  // ========================================================
  // RESET
  // ========================================================

  if (passedEnough) {

    obstacle = null
    data.currentObstacle = null
    data.avoidDir = undefined
    data.avoidTargetX = undefined
    data.avoidLatSpeed = 0
    data.targetOffset = 0
  }
}
  // ============================================================
  // 🚗 EVITEMENT
  // ============================================================

  // ============================================================
  // 🚴 LOCAL ACTOR SEPARATION
  // ============================================================

  let separationX = 0
  let actorSeparationX = 0

  // ============================================================
  // 🚴 ÉVITEMENT JOUEUR
  // ============================================================

  const player = playerRef?.current

  if (player) {

    const px = player.position.x
    const pz = player.position.z
    const dzPlayer = data.z - pz

    // seulement proche du joueur
    if (dzPlayer > -3 && dzPlayer < 8) {

      const dxPlayer = data.x - px
      const actorHalfW = data.halfWidth ?? 0.6
      const playerHalfW = 0.7
      const avoidWidth = actorHalfW + playerHalfW + 0.6

      if (Math.abs(dxPlayer) < avoidWidth) {

        // côté opposé au joueur
        const pushDir = dxPlayer < 0 ? -1 : 1

        // force progressive
        const strength = 1 - Math.abs(dxPlayer) / avoidWidth

        separationX += pushDir * strength * 4
      }
    }
  }

  //=================================================

  for (let j = 0; j < actors.length; j++) {

    if (i === j) continue

    const other = actors[j]

    if (!other) continue

    // ========================================================
    // DISTANCES
    // ========================================================

    const actorHalfW = data.halfWidth ?? 0.6
    const otherHalfW = other.halfWidth ?? 0.6
    const actorHalfL = data.halfLength ?? 1.0
    const otherHalfL = other.halfLength ?? 1.0
    const minGap = actorHalfW + otherHalfW + 0.15     // largeur collision
    const minLongGap = actorHalfL + otherHalfL + 0.25

    const dx = data.x - other.x
    const dz = data.z - other.z

    // zone locale seulement
    if (Math.abs(dz) > minLongGap + 1.2) continue

    // ========================================================
    // ÉVITER SENS INVERSE
    // ========================================================

    if (data.direction !== other.direction) {

      // collision frontale plus forte
      if (Math.abs(dx) < minGap * 1.4 && Math.abs(dz) < minLongGap) {

        const pushDir =
          Math.abs(dx) > 0.05
            ? Math.sign(dx)
            : data.lane === "left"
              ? -1
              : 1

        const overlap = minGap * 1.4 - Math.abs(dx)

        actorSeparationX += pushDir * THREE.MathUtils.clamp(overlap * 5, 2.5, 7)
      }

      continue
    }

    // ========================================================
    // TROP PROCHE
    // ========================================================

    if (Math.abs(dx) < minGap && Math.abs(dz) < minLongGap) {

      let pushDir

      // ======================================================
      // 🚗 RESPECTER ÉVITEMENT AUTO
      // ======================================================

      if (Math.abs(dx) <= 0.05) {

        pushDir =
          data.id < other.id
            ? -1
            : 1

      } else if (data.currentObstacle?.type === "car" && data.avoidDir !== undefined) {

        pushDir = data.avoidDir

      } else {

        pushDir = dx < 0 ? -1 : 1
      }

      const strength = 1 - Math.abs(dx) / minGap
      const overlap = minGap - Math.abs(dx)

      actorSeparationX += pushDir * Math.max(strength * 2.2, overlap * 4)

      const otherAhead =
        data.direction === -1
          ? other.z < data.z
          : other.z > data.z

      if (otherAhead && Math.abs(dx) < minGap * 0.8) {
        const otherStoppedForLight =
          other.brakingForLight ||
          (other.currentSpeed ?? 0) < 0.15

        speedFactor = Math.min(speedFactor, otherStoppedForLight ? 0 : 0.45)
      }
    }
  }

  // ============================================================
  // APPLY
  // ============================================================

  const carSafetyDir =
    data.currentObstacle?.type === "car" && data.avoidDir !== undefined
      ? data.avoidDir
      : data.carAvoidLock > 0
        ? data.lastCarAvoidDir
        : undefined

  if (carSafetyDir !== undefined && separationX * carSafetyDir < 0) {
    separationX = 0
  }

  if (carSafetyDir !== undefined && actorSeparationX * carSafetyDir < 0) {
    actorSeparationX = 0
  }

  data.offsetX += (separationX + actorSeparationX) * dt

  if (obstacle) {

    const actorHalfW = data.halfWidth ?? 0.6
    const selfX = data.x
    const afterCommune = playerWorldZ < commune.z
    const cyclistsShiftX =
      getCyclistsShiftX(playerWorldZ)

    // ========================================================
    // CHOIX CÔTÉ
    // ========================================================

    if (data.avoidDir === undefined) {

      const laneBounds = getBikeLaneBounds(
        data.lane,
        playerWorldZ
      )

      const laneLimitMargin = 0.25
      const outsideLeft = selfX <= laneBounds.min + laneLimitMargin
      const outsideRight = selfX >= laneBounds.max - laneLimitMargin
      const obstacleHalfW = obstacle.avoidHalfW ?? obstacle.halfW ?? 1
      const poteauxW = pisteW / 5.5
      const hardMin = afterCommune ? pisteX - 5.5 + COMMUNE_ACTOR_LIMIT_SHIFT_X + cyclistsShiftX : pisteX - pisteW / 2 - poteauxW
      const hardMax = afterCommune ? pisteX + 5.5 + COMMUNE_ACTOR_LIMIT_SHIFT_X + cyclistsShiftX : pisteX + pisteW / 2

      const targetForDir = (dir) => {
        const padding =
          obstacle.type === "car"
            ? (
                obstacle.turned
                  ? 1.8
                  : dir > 0
                    ? 2.35
                    : 1.65
              )
            : 0.5

        return obstacle.x + dir * (obstacleHalfW + actorHalfW + padding)
      }

      const leftTarget = targetForDir(-1)
      const rightTarget = targetForDir(1)
      const edgeMargin = 0.12
      const leftValid = leftTarget >= hardMin + edgeMargin
      const rightValid = rightTarget <= hardMax - edgeMargin
      const obstacleSideDeadZone = Math.max(0.08, actorHalfW * 0.15)
      const sideAwayFromObstacle =
        selfX < obstacle.x - obstacleSideDeadZone
          ? -1
          : selfX > obstacle.x + obstacleSideDeadZone
            ? 1
            : 0

      const isDirValid = (dir) => dir < 0 ? leftValid : rightValid
      const chooseAvoidDir = (preferredDir, fallbackDir = 0) => {
        const primaryDir = preferredDir || sideAwayFromObstacle

        if (primaryDir && isDirValid(primaryDir)) return primaryDir
        if (sideAwayFromObstacle) return sideAwayFromObstacle
        if (fallbackDir && isDirValid(fallbackDir)) return fallbackDir
        if (rightValid) return 1
        if (leftValid) return -1

        return selfX < (hardMin + hardMax) * 0.5 ? 1 : -1
      }

      // auto tournée = gauche forcée
      // ========================================================
      // 🚗 AUTO TOURNÉE
      // ========================================================

      if (obstacle.type === "car" && obstacle.turned && !afterCommune) {
        data.avoidDir = -1
      } else if (obstacle.type === "car" && afterCommune) {
        data.avoidDir =
          data.isOpposite || data.direction === 1
            ? -1
            : 1
      } else if (outsideLeft) {
        data.avoidDir = 1
      } else if (outsideRight) {
        data.avoidDir = -1
      } else if (obstacle.type === "car" && obstacle.turned) {

        const leftDelta = Math.abs(leftTarget - selfX)
        const rightDelta = Math.abs(rightTarget - selfX)

        if (sideAwayFromObstacle) {
          data.avoidDir = chooseAvoidDir(sideAwayFromObstacle)
        } else if (leftValid && rightValid) {
          data.avoidDir = rightDelta < leftDelta ? 1 : -1
        } else if (rightValid) {
          data.avoidDir = 1
        } else if (leftValid) {
          data.avoidDir = -1
        } else {
          data.avoidDir = selfX < obstacle.x ? -1 : 1
        }

      } else if (obstacle.type === "car") {

        const laneCenter = (laneBounds.min + laneBounds.max) * 0.5
        const laneHalfWidth = Math.max(0.01, (laneBounds.max - laneBounds.min) * 0.5)
        const centerDeadZone = laneHalfWidth * 0.22
        const distFromLaneCenter = selfX - laneCenter
        let preferredDir

        if (sideAwayFromObstacle) {
          preferredDir = sideAwayFromObstacle
        } else if (distFromLaneCenter > centerDeadZone) {
          preferredDir = 1
        } else if (distFromLaneCenter < -centerDeadZone) {
          preferredDir = -1
        } else {
          preferredDir = Math.random() < 0.5 ? -1 : 1
        }

        data.avoidDir = chooseAvoidDir(preferredDir)

        if (!sideAwayFromObstacle && !leftValid && !rightValid) {
          data.avoidDir = THREE.MathUtils.clamp(
            targetForDir(preferredDir),
            hardMin,
            hardMax
          ) > selfX ? 1 : -1
        }

      } else {

        const preferredDir = sideAwayFromObstacle || (selfX < obstacle.x ? -1 : 1)

        data.avoidDir = chooseAvoidDir(preferredDir)
      }
    }

    if (obstacle.type === "car" && data.avoidDir !== undefined) {
      data.carAvoidLock = 0.9
      data.lastCarAvoidDir = data.avoidDir
    }

    const dir = data.avoidDir

    // ========================================================
    // LARGEUR RÉELLE
    // ========================================================

    const obstacleHalfW = obstacle.avoidHalfW

    // ========================================================
    // POSITION X À ATTEINDRE
    // ========================================================

    const beforeCommuneTurnedCar =
      obstacle.type === "car" &&
      obstacle.turned &&
      !afterCommune

    const avoidPadding =
      obstacle.type === "car"
        ? (
            obstacle.turned
              ? 1.8
              : dir > 0
                ? 2.35
                : 1.65
          )
        : 0.5

    let targetX = obstacle.x + dir * ( obstacleHalfW + actorHalfW + avoidPadding)

    if (beforeCommuneTurnedCar) {
      const poteauxW = pisteW / 5.5
      const poteauxX = pisteX - pisteW / 2 - poteauxW / 2
      const poteauxMinX = poteauxX - poteauxW * 0.35
      const poteauxMaxX = poteauxX + poteauxW * 0.45

      targetX = THREE.MathUtils.clamp(
        Math.min(targetX, poteauxMaxX),
        poteauxMinX,
        poteauxMaxX
      )
    }

    const hardPoteauxW = pisteW / 5.5
    const hardMin = afterCommune ? pisteX - 5.5 + COMMUNE_ACTOR_LIMIT_SHIFT_X + cyclistsShiftX : pisteX - pisteW / 2 - hardPoteauxW
    const hardMax =
      afterCommune && obstacle.type === "car" && dir > 0
        ? pisteX + 8.5 + COMMUNE_ACTOR_LIMIT_SHIFT_X + cyclistsShiftX
        : afterCommune
          ? pisteX + 5.5 + COMMUNE_ACTOR_LIMIT_SHIFT_X + cyclistsShiftX
          : pisteX + pisteW / 2

    targetX = THREE.MathUtils.clamp(targetX, hardMin, hardMax)

    if (obstacle.type === "car") {
      const previousTarget = Number.isFinite(data.avoidTargetX)
        ? data.avoidTargetX
        : targetX

      data.avoidTargetX = THREE.MathUtils.damp(
        previousTarget,
        targetX,
        beforeCommuneTurnedCar ? 12 : 8,
        dt
      )

      targetX = THREE.MathUtils.clamp(data.avoidTargetX, hardMin, hardMax)
    } else {
      data.avoidTargetX = undefined
    }

    // ========================================================
    // DELTA LATÉRAL RESTANT
    // ========================================================

    const deltaX = targetX - selfX

    // ========================================================
    // DISTANCE DEVANT
    // ========================================================

    const dist =
      beforeCommuneTurnedCar
        ? Math.max(0.01, obstacle.worldDist - actorHalfW)
        : Math.max(0.01, obstacle.worldDist)

    // ========================================================
    // VITESSE COURANTE
    // ========================================================

    const speed = Math.max(0.1, data.currentSpeed ?? 0)

    // ========================================================
    // TEMPS DISPONIBLE
    // ========================================================

    const timeToImpact = (dist / speed) 

    // ========================================================
    // VITESSE LATÉRALE NÉCESSAIRE
    // ========================================================

    let latSpeed = Math.abs(deltaX) < 0.035
      ? 0
      : deltaX / Math.max(0.38, timeToImpact)

    // ========================================================
    // CAPACITÉ LATÉRALE
    // ========================================================

    // distance à parcourir
    const requiredShift = Math.abs(deltaX)

    // plus le shift est grand,
    // plus on autorise vitesse latérale

    const lateralFactor = THREE.MathUtils.clamp(requiredShift / 3, 1, 2.5)

    const avoidUrgency = 1 - THREE.MathUtils.clamp(
      dist / (vision * (obstacle.type === "car" ? 1.45 : 0.9)),
      0,
      1
    )

    // ========================================================
    // LIMITE PHYSIQUE
    // ========================================================

    const naturalLatCap =
      obstacle.type === "car"
        ? (obstacle.turned ? (beforeCommuneTurnedCar ? 4.2 : 3.4) : 3.9)
        : 2.8

    const emergencyLatCap = THREE.MathUtils.clamp(
      speed * lateralFactor,
      naturalLatCap,
      obstacle.type === "car" ? (beforeCommuneTurnedCar ? 11 : 9) : 6
    )

    const maxLatSpeed = THREE.MathUtils.lerp(
      naturalLatCap,
      emergencyLatCap,
      Math.pow(avoidUrgency, 2)
    )

    // ========================================================
    // CLAMP
    // ========================================================

    latSpeed = THREE.MathUtils.clamp(latSpeed, -maxLatSpeed, maxLatSpeed)

    if (obstacle.type === "car") {
      data.avoidLatSpeed = THREE.MathUtils.damp(
        data.avoidLatSpeed ?? 0,
        latSpeed,
        beforeCommuneTurnedCar ? 16 : 10,
        dt
      )

      latSpeed = THREE.MathUtils.clamp(data.avoidLatSpeed, -maxLatSpeed, maxLatSpeed)
    } else {
      data.avoidLatSpeed = latSpeed
    }

    const lateralTimeNeeded =
      requiredShift / Math.max(0.01, maxLatSpeed)

    const timeShortage =
      beforeCommuneTurnedCar
        ? THREE.MathUtils.clamp(
            (lateralTimeNeeded - timeToImpact) / Math.max(0.1, lateralTimeNeeded),
            0,
            1
          )
        : 0

    const turnedCar = obstacle.type === "car" && obstacle.turned
    const fastApproach =
      turnedCar
        ? THREE.MathUtils.clamp(
            (speed - realSpeed * 0.85) / Math.max(0.1, realSpeed * 0.7),
            0,
            1
          )
        : 0
    const lateralPressure =
      turnedCar
        ? THREE.MathUtils.clamp(requiredShift / 3.2, 0, 1)
        : 0
    const turnedCarUrgency =
      turnedCar
        ? Math.max(
            avoidUrgency,
            THREE.MathUtils.clamp(1 - dist / (vision * 1.35), 0, 1),
            fastApproach * lateralPressure
          )
        : 0

    const slowTarget =
      beforeCommuneTurnedCar
        ? THREE.MathUtils.lerp(0.55, 0.24, timeShortage)
        : obstacle.type === "car"
          ? (
              obstacle.turned
                ? THREE.MathUtils.lerp(0.66, 0.44, Math.max(fastApproach, lateralPressure))
                : 0.7
            )
          : 0.84
    const slowUrgency =
      turnedCar
        ? Math.max(turnedCarUrgency, timeShortage)
        : Math.max(avoidUrgency, timeShortage)

    data.collisionSlowFactor = THREE.MathUtils.damp(
      data.collisionSlowFactor ?? 1,
      THREE.MathUtils.lerp(1, slowTarget, slowUrgency),
      beforeCommuneTurnedCar ? 6 : turnedCar ? 4.5 : 3,
      dt
    )

    data.targetOffset = data.offsetX
    // ========================================================
    // APPLICATION
    // ========================================================

    data.offsetX += latSpeed * dt

  } else {

      // ========================================================
      // RETOUR CENTRE NATUREL
      // ========================================================

      const returnLaneBounds = getBikeLaneBounds(
        data.lane,
        playerWorldZ
      )
      const laneCenter =
        (returnLaneBounds.min + returnLaneBounds.max) * 0.5
      const centerOffset =
        laneCenter - data.baseX
      const offsetDelta =
        centerOffset - data.offsetX

      const isGLBWalker =
        data.glb &&
        !data.bikeMode &&
        data.mode !== "jog"

      const returnSpeed = isGLBWalker
        ? THREE.MathUtils.clamp(
            (data.currentSpeed ?? pedestrianConfig.baseSpeed) * 0.45 + Math.abs(offsetDelta) * 0.08,
            0.35,
            0.85
          )
        : THREE.MathUtils.clamp(
            1.3 + Math.abs(offsetDelta) * 0.3,
            1.4,
            2.8
          )

      const returnStep = THREE.MathUtils.clamp(
        offsetDelta,
        -returnSpeed * dt,
        returnSpeed * dt
      )

      data.offsetX += returnStep
      data.targetOffset = centerOffset

      if (Math.abs(offsetDelta) < 0.025) {
        data.offsetX = centerOffset
      }

      data.avoidTargetX = undefined
      data.avoidLatSpeed = THREE.MathUtils.damp(data.avoidLatSpeed ?? 0, 0, 8, dt)

      // ========================================================
      // FRICTION LATÉRALE
      // ========================================================

      data.latVel = THREE.MathUtils.lerp(data.latVel ?? 0, 0, 1.2 * dt)

      // ========================================================
      // RALENTISSEMENT PROGRESSIF
      // ========================================================

      data.collisionSlowFactor =
        THREE.MathUtils.damp(
          data.collisionSlowFactor ?? 1,
          1,
          3.2,
          dt
        )
      if (data.collisionSlowFactor > 0.995) {
          data.collisionSlowFactor = 1
        }
      }
      // ========================================================
      // 🚴 POSITION CIBLE
      // ========================================================

      const targetX = data.baseX + data.offsetX

      // ========================================================
      // 🌆 LIMITES SELON ZONE
      // ========================================================

      const afterCommune = playerWorldZ < commune.z
      const cyclistsShiftX =
        getCyclistsShiftX(playerWorldZ)
      const poteauxW = pisteW / 5.5
      const HARD_MIN = afterCommune ? pisteX - 5.5 + COMMUNE_ACTOR_LIMIT_SHIFT_X + cyclistsShiftX : pisteX - pisteW / 2 - poteauxW
      const HARD_MAX =
        afterCommune &&
        data.currentObstacle?.type === "car" &&
        data.avoidDir > 0
          ? pisteX + 8.5 + COMMUNE_ACTOR_LIMIT_SHIFT_X + cyclistsShiftX
          : afterCommune
            ? pisteX + 5.5 + COMMUNE_ACTOR_LIMIT_SHIFT_X + cyclistsShiftX
            : pisteX + pisteW / 2

      // ========================================================
      // 🚗 ÉVITEMENT
      // ========================================================

      if (data.currentObstacle) {

        data.x = THREE.MathUtils.clamp(targetX, HARD_MIN, HARD_MAX)
        data.offsetX = data.x - data.baseX
      }

      // ========================================================
      // 🚴 MODE NORMAL
      // ========================================================

      else {

        // ========================================================
        // 🚴 LIMITES DE VOIE
        // ========================================================

        const laneBounds = getBikeLaneBounds(
          data.lane,
          playerWorldZ
        )

        const laneMin = laneBounds.min
        const laneMax = laneBounds.max

        // ========================================================
        // 🚗 LIMITES TEMPORAIRES
        // ========================================================

        const currentMin = laneMin
        const currentMax = laneMax


        // ========================================================
        // 🚴 RETOUR DOUX DANS LES LIMITES
        // ========================================================

        const minOffset = currentMin - data.baseX
        const maxOffset = currentMax - data.baseX

        if (data.offsetX < minOffset) {

          const distanceToLane = minOffset - data.offsetX
          const laneReturnSpeed = THREE.MathUtils.clamp(
            1.8 + distanceToLane * 0.35,
            1.8,
            4
          )

          data.offsetX += Math.min(distanceToLane, laneReturnSpeed * dt)
        }

        else if (data.offsetX > maxOffset) {

          const distanceToLane = data.offsetX - maxOffset
          const laneReturnSpeed = THREE.MathUtils.clamp(
            1.8 + distanceToLane * 0.35,
            1.8,
            4
          )

          data.offsetX -= Math.min(distanceToLane, laneReturnSpeed * dt)
        }
        // ========================================================
        // POSITION FINALE
        // ========================================================

        data.x = THREE.MathUtils.clamp(
          data.baseX + data.offsetX,
          HARD_MIN,
          HARD_MAX
        )
        data.offsetX = data.x - data.baseX
      }
       // ==================================================
      // ↔️ VITESSE LATÉRALE
      // ==================================================

      if (data.prevX === undefined) {
        data.prevX = data.x
      }

      data.latVel = (data.x - data.prevX) /  Math.max(dt, 0.0001)
      data.prevX = data.x

      // ==================================================
      // 🚗 VITESSE FINALE
      // ==================================================

      let targetSpeed = data.glb
        ? (
            (Number.isFinite(data.targetSpeed) ? data.targetSpeed : realSpeed) *
            speedFactor *
            (data.lightSlowFactor ?? 1) *
            (data.speedFactor ?? 1) *
            (data.collisionSlowFactor ?? 1)
          )
        : (
            realSpeed *
            speedFactor *
            (data.speed ?? 1) *
            (data.lightSlowFactor ?? 1) *
            (data.speedFactor ?? 1) *
            (data.collisionSlowFactor ?? 1)
          )

      // sécurité
      if (!Number.isFinite(targetSpeed)) {
        targetSpeed = 0
      }

      // ==================================================
      // ⚡ ACCEL / FREINAGE
      // ==================================================

      const accel = data.accel ?? cfg.accel

      data.currentSpeed ??= 0

      if (data.currentSpeed < targetSpeed) {

        data.currentSpeed += accel * dt

        if (data.currentSpeed > targetSpeed) {
          data.currentSpeed = targetSpeed
        }

      } else {

        data.currentSpeed -= accel * dt * 1.4

        if (data.currentSpeed < targetSpeed) {
          data.currentSpeed = targetSpeed
        }
      }

      // ==================================================
      // 🔥 LIMITE
      // ==================================================

      data.currentSpeed = Math.min(data.currentSpeed, cfg.maxSpeed ?? 10)

    // ==================================================
    // 🚗 MOUVEMENT Z
    // ==================================================

    let nextZ

    if (data.direction === -1) {

      nextZ = data.z - data.currentSpeed * dt

    } else {

      nextZ = data.z + data.currentSpeed * dt
    }

    // ==================================================
    // 🛑 FEU ROUGE
    // ==================================================

    if (stopZ !== null && data.brakingForLight) {
      const crossesStopLine =
        data.direction === -1
          ? data.z > stopZ && nextZ <= stopZ
          : data.z < stopZ && nextZ >= stopZ

      if (crossesStopLine) {
        nextZ = stopZ
        data.currentSpeed = 0
      }
    }

    // ==================================================
    // 🔥 ANTI-NaN
    // ==================================================

    if (!Number.isFinite(nextZ)) {
      nextZ = data.prevZ ?? data.z ?? 0
    }

    // ==================================================
    // ✅ COMMIT
    // ==================================================

    data.z = nextZ

    // ==================================================
    // 🎬 VITESSE ANIMATION
    // ==================================================

    data.effectiveSpeed = data.currentSpeed

    if (data.prevZ === undefined) {
      data.prevZ = data.z
    }

    data.prevZ = data.z

    const playerMoving = Math.abs(gameSpeed) > 0.1
  

    if (playerMoving && visibleZ > 60 &&  data.type !== "sport") {

      data.effectiveSpeed *= 1.8
    }
    /* ANIMATION */

    // ==================================================
    // 🛑 LOGICAL STOP
    // ==================================================

    // arrêt réel
    const isStopped = (data.currentSpeed ?? 0) < 0.1
    const logicalStop = isStopped || (stopZ !== null && data.brakingForLight) // arrêt logique global
    let map = null

    if (mesh) {

      const material =  mesh.material || mesh.children?.[0]?.material
      map = material?.map || null
    }

    // ==================================================
    // PAS D'ANIM GLB
    // ==================================================

    if (!data.glb && !logicalStop) {

      switch (data.type) {

        case "sport":
          updateSport(data, dt, map)
          break

        case "cyclist":
          updateCyclist(data, dt, map)
          break

        case "cycliste4":
          updateCycliste4(data, dt, map)
          break

        case "joggeuse":
          updateRunner(data, dt, map)
          break

        case "sportif":
          updateSportif(data, dt, map)
          break

        case "child":
          updateChild(data, dt, map)
          break

        case "scoot":
          updateScoot(data, dt, map)
          break

        case "bixi":
          updateBixi(data, dt, map)
          break

        case "trailer":
          updateTrailer(data, dt,  map)
          break

        default:
          updateTri(data, dt, map)
      }
    }

    // ==================================================
    // 🔥 ANTI-NaN
    // ==================================================

    if (!Number.isFinite(data.x)) {
      data.x = data.prevX ?? data.baseX
    }

    if (!Number.isFinite(data.z)) {
      data.z = data.prevZ ?? data.z ?? 0
    }



    // ==================================================
    // ♻️ RESPAWN
    // ==================================================

    if (respawn(data, playerWorldZ)) {

      visibleZ = data.z + currentWorldZ

      if (data.needsAnimationRefresh) {
        data.needsAnimationRefresh = false
        setRenderActors([...actorsRef.current])
      }

      if (mesh && !data.glb) {

        mesh.position.x = data.x
        mesh.position.z = data.z
      }
    }

    // ==================================================
    // 📍 POSITION
    // ==================================================
    if (!mesh) continue

    mesh.position.x = data.x
    mesh.position.z = data.z

    // ==================================================
    // 🏃 SAUT VERTICAL
    // ==================================================

    if (data.type === "joggeuse") {

      // fréquence animation
      const freq =
        (data.animSpeed ?? 1) * 0.5

      // phase
      data.bobTime =
        (data.bobTime ?? 0) +
        dt * freq

      // vitesse relative
      const speedRatio =
        THREE.MathUtils.clamp(
          (data.currentSpeed ?? 0) /
          (ACTOR_CONFIG.joggeuse.maxSpeed || 5),
          0,
          1
        )

      // amplitude
      const amplitude =
        THREE.MathUtils.lerp(
          0.02,
          0.12,
          speedRatio
        )

      // oscillation
      const bob =
        Math.sin(
          data.bobTime * Math.PI * 2
        ) * amplitude

      mesh.position.y = 1.5 + bob

    } else {

      // ==================================================
      // HAUTEUR PAR DÉFAUT
      // ==================================================

      mesh.position.y =
        data.type === "trailer" ? 1.4 :
        data.type === "tri" ? 1.8 :
        data.type === "scoot" ? 1.35 :
        data.type === "bixi" ? 1.35 :
        data.type === "sport" ? 1.32 :
        1.3
    }
  }
})

  /* RENDER */

  return(

  <group ref={(node)=>{
    group.current=node
    if(ref) ref.current=node
  }}>

    {renderActors.map((a)=>{

      if (a.glb) {

        return (
          <GLBActor key={a.id} actor={a} />
        )
      }
      const tex = a.texture

      return(

        <mesh
          ref={(node)=>{

            if (node) {
              a.mesh = node
            }
          }}

          key={a.id}
          position={[
            a.x,
            a.type==="trailer" ? 1.4 :
            a.type==="joggeuse" ? 1.5 :
            a.type==="tri" ? 1.6 :
            a.type==="scoot" ? 1.35 :
            a.type==="bixi" ? 1.35 :
            a.type==="sport" ? 1.32 :
            1.3,
            a.z
          ]}

          scale={
            a.type==="trailer" ? 0.75 :
            a.type==="joggeuse" ? 0.7 :
            a.type==="tri" ? 0.6 :
            a.type==="scoot" ? 0.65 :
            a.type==="bixi" ? 0.7 :
            a.type==="sport" ? 0.75 :
            a.type==="sportif" ? 0.75 :
            a.type==="cycliste4" ? 0.75 :
            0.65
          }

          geometry={ a.type==="tri" ? triGeometry : cyclistGeometry }
        >
          <meshBasicMaterial
            map={tex}
            transparent={true}
            alphaTest={0.5}                // 👈 augmente (très important)
            depthWrite={true}             // 👈 évite artefacts
            premultipliedAlpha={true}      // 👈 CRUCIAL pour halo
            depthTest={true}
            polygonOffset={true}
            polygonOffsetFactor={-1}
            polygonOffsetUnits={-1}
            side={THREE.FrontSide}
          />
        </mesh>
      )
    })}
  </group>
)
})


const Pietons = React.forwardRef(
({ pisteX, pisteW, trottoirX, trottoirW }, ref) => {

  const { level, worldGroup, gameState } = useGame()
  const worldZ = worldGroup?.current?.position?.z || 0
  const meshRefs = useRef([])
  const group = useRef()
  const actorsRef = useRef([])
  const ANIM_SPEED = 0.2

  // =========================
  // 🔥 CONFIG
  // =========================
  const BASE_COUNT = 1
  const PER_LEVEL = 0
  const RESPAWN_FRONT = -180
  const RESPAWN_BACK  = 80
  const SPAWN_MARGIN  = 10
  const SPAWN_RANGE   = 80
  const SPAWN_LEVEL_FRONT_MIN = -260
  const SPAWN_LEVEL_FRONT_MAX = -180

  const texture = useLoader(
    THREE.TextureLoader,
    "/sprites/pieton3.png"
  )

  useEffect(() => {
    texture.wrapS = THREE.RepeatWrapping
    texture.repeat.set(1 / 20, 1)
    texture.colorSpace = THREE.SRGBColorSpace
  }, [])

  const minX = Math.min(pisteX - pisteW/2, trottoirX - trottoirW/2)
  const maxX = Math.max(pisteX + pisteW/2, trottoirX + trottoirW/2) - 2
  const FRAMES = {
    LEFT:  [0,1,2,3,4,5,6,7],
    DOWN:  [8,9],
    UP:    [10,11],
    RIGHT: [12,13,14,15,16,17,18,19]
  }

  function isOnIntersection(z) {
  for (const inter of intersections) {
    const half = inter.width * 0.5
    if (z > inter.z - half && z < inter.z + half) {
      return true
    }
  }
  return false
}
  // =========================
  // DIRECTION
  // =========================
  function randomDirection() {
    const angle = Math.random() * Math.PI * 2

    let dirX = Math.cos(angle) * 0.7
    let dirZ = Math.sin(angle) * 1.2

    const len = Math.hypot(dirX, dirZ)
    dirX /= len
    dirZ /= len

    return { dirX, dirZ }
  }

  // =========================
  // RESPAWN 
  // =========================
  function respawnPieton(p, playerWorldZ) {

    const dz = p.z - playerWorldZ
    const SPAWN_FRONT_MIN = RESPAWN_FRONT + SPAWN_MARGIN
    const SPAWN_FRONT_MAX = RESPAWN_FRONT + SPAWN_RANGE

    if (dz > RESPAWN_BACK) {

      p.z = playerWorldZ + THREE.MathUtils.randFloat(
        SPAWN_FRONT_MIN,
        SPAWN_FRONT_MAX
      )

      p.x = THREE.MathUtils.randFloat(minX, maxX)

      const dir = randomDirection()
      p.dirX = dir.dirX
      p.dirZ = dir.dirZ
      p.dirTimer = 3 + Math.random() * 5

      return true
    }

    return false
  }

  // =========================
  // SPAWN
  // =========================
  useEffect(() => {

    const list = actorsRef.current
    const playerWorldZ = -worldZ
    const total = BASE_COUNT + level * PER_LEVEL

    // -------------------------
    // INITIAL SPAWN
    // -------------------------
    if (list.length === 0) {

      for (let i = 0; i < total; i++) {

        const tex = texture
        tex.wrapS = THREE.RepeatWrapping
        tex.repeat.set(1 / 20, 1)

        const dir = randomDirection()

        let z
        do {
          z = playerWorldZ + THREE.MathUtils.randFloat(
            RESPAWN_FRONT + SPAWN_MARGIN,
            RESPAWN_BACK - SPAWN_MARGIN
          )
        } while (isOnIntersection(z))

        list.push({
          x: THREE.MathUtils.randFloat(minX, maxX),
          z,
          dirX: dir.dirX,
          dirZ: dir.dirZ,
          speed: THREE.MathUtils.randFloat(0.6, 1.4),
          animTimer: Math.random(),
          animIndex: 0,     
          state: "RIGHT",
          prevState: "RIGHT",
          texture: tex
        })
      }

      return
    }

    // -------------------------
    // LEVEL UP SPAWN (hors écran)
    // -------------------------
    const missing = total - list.length
    if (missing <= 0) return

   for (let i = 0; i < missing; i++) { 

      const tex = texture
      tex.wrapS = THREE.RepeatWrapping
      tex.repeat.set(1 / 20, 1)

      const dir = randomDirection()

      list.push({
        x: THREE.MathUtils.randFloat(minX, maxX),

        z: playerWorldZ + THREE.MathUtils.randFloat(
          SPAWN_LEVEL_FRONT_MIN,
          SPAWN_LEVEL_FRONT_MAX
        ),

        dirX: dir.dirX,
        dirZ: dir.dirZ,

        speed: THREE.MathUtils.randFloat(0.6, 1.4),

        animTimer: Math.random(),
        animIndex: 0,
        dirTimer: Math.random() * 2,

        state: "RIGHT",
        prevState: "RIGHT",

        texture: tex
      })
    }

  }, [level])

  // =========================
  // UPDATE
  // =========================
  useFrame((_, dt) => {

    if (gameState === "waitingContinue") return

    const list = actorsRef.current
    const playerWorldZ = -worldZ

    for (let i = 0; i < list.length; i++) {

      const p = list[i]

      // DIRECTION
      p.dirTimer -= dt
      if (p.dirTimer <= 0) {
        const dir = randomDirection()
        p.dirX = dir.dirX
        p.dirZ = dir.dirZ
        p.dirTimer = 4 + Math.random() * 6
      }

      // MOUVEMENT
      p.x += p.dirX * p.speed * dt * 2
      p.z += p.dirZ * p.speed * dt * 3

      for (const inter of intersections) {

        const half = inter.width * 0.5 + 2 // 👈 marge

        const inZone =
          p.z > inter.z - half &&
          p.z < inter.z + half

        if (inZone) {

          // 🔥 repousser hors de l'intersection
          if (p.dirZ > 0) {
            p.z = inter.z - half
          } else {
            p.z = inter.z + half
          }

          // 🔄 inverser direction Z
          p.dirZ *= -1

          break
        }
      }

      if (p.x < minX || p.x > maxX) {
        p.dirX *= -1
      }

      // STATE
      if (Math.abs(p.dirX) > Math.abs(p.dirZ)) {
        p.state = p.dirX > 0 ? "RIGHT" : "LEFT"
      } else {
        p.state = p.dirZ > 0 ? "DOWN" : "UP"
      }

      // ANIMATION
      p.animTimer += dt

      const speedFactor = THREE.MathUtils.clamp(p.speed, 0.6, 1.4)

      let animSpeed = ANIM_SPEED / (0.5 + speedFactor * 0.5)

      if (p.state === "UP" || p.state === "DOWN") {
        animSpeed *= 2
      }

      if (p.animTimer > animSpeed) {

        p.animTimer = 0

        const frames = FRAMES[p.state]

        if (p.prevState !== p.state) {
          p.animIndex = 0
          p.prevState = p.state
        }

        p.animIndex = (p.animIndex + 1) % frames.length

        const frame = frames[p.animIndex]
        p.texture.offset.x = frame / 20
      }

      // RESPAWN
      respawnPieton(p, playerWorldZ)

      // APPLY POSITION
      const mesh = meshRefs.current[i]
      if (mesh) {
        mesh.position.set(p.x, 1.3, p.z)
      }
    }
  })

  return (
    <group ref={(node)=>{
      group.current=node
      if(ref) ref.current=node
    }}>
      {actorsRef.current.map((p, i) => (
        <mesh
          key={i}
          ref={(el) => (meshRefs.current[i] = el)}
          scale={0.8}
        >
          <planeGeometry args={[2.8, 3.2]} />
          <meshBasicMaterial
            map={p.texture}
            alphaTest={0.5}
            transparent={false}   
            depthWrite={true}
            depthTest={true}
          />
        </mesh>
      ))}
    </group>
  )
})

const SUN_GEOMETRY = new THREE.PlaneGeometry(160, 160)
const SunBillboard = React.memo(
function SunBillboard() {

  const meshRef = useRef()

  useFrame(({ camera }) => {

  if (!meshRef.current) return

  const forward = new THREE.Vector3()
  camera.getWorldDirection(forward)

  // 🔥 direction mixée : toujours vers l'avant MAIS vers le haut
  const skyDir = forward.clone().add(new THREE.Vector3(0, 0.6, 0)).normalize()

  const pos = camera.position.clone()
    .add(skyDir.multiplyScalar(80)) // distance

  meshRef.current.position.copy(pos)
  meshRef.current.quaternion.copy(camera.quaternion)
})

  return (
    <mesh
      ref={meshRef}
      frustumCulled={false}
      depthTest={true}
      depthWrite={false}
      renderOrder={1}
      >
      <primitive object={SUN_GEOMETRY} attach="geometry"/>
      <shaderMaterial
      transparent
      depthTest={true}
      depthWrite={false}
      blending={THREE.NormalBlending} // 🔥 IMPORTANT (pas Additive)

      vertexShader={`
        varying vec2 vUv;
        void main(){
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
        }
      `}

      fragmentShader={`
        varying vec2 vUv;

        void main(){

          vec2 p = vUv - 0.5;
          float r = length(p);

          // 🔥 disque IDENTIQUE à ton ancien shader
          float core = smoothstep(0.03, 0.005, r);

          // 🔥 halo IDENTIQUE
          float glow = exp(-r * 25.0) * 0.25;

          float alpha = clamp(core + glow, 0.0, 1.0);

          // 🔥 coupe nette du carré
          if(alpha < 0.02) discard;

          // 🔥 JAUNE exact
          vec3 color = vec3(1.0, 0.82, 0.12);

          gl_FragColor = vec4(color, alpha);
        }
      `}
    />
    </mesh>
  )
})


const SKY_GEOMETRY = new THREE.PlaneGeometry(2, 2)
const SkyBackground = React.memo(
function SkyBackground() {

  const tex = useLoader(THREE.TextureLoader, "/textures/clouds.png")
  const matRef = useRef()
  const material = useMemo(() => {

    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    tex.colorSpace = THREE.SRGBColorSpace
    tex.minFilter = THREE.LinearFilter
    tex.magFilter = THREE.LinearFilter
    tex.generateMipmaps = false

    return new THREE.ShaderMaterial({

      precision: "highp",

      uniforms: {
        uMap: { value: tex },
        uTime: { value: 0 },
        uCamRot: { value: new THREE.Vector2(0, 0) } // 🔥 ajouté
      },

      depthWrite: false,
      depthTest: false,
      transparent: false,

      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,

      fragmentShader: `
        uniform sampler2D uMap;
        uniform float uTime;
        uniform vec2 uCamRot;
        varying vec2 vUv;

        void main() {

          // 🔥 STABILISATION UV
          vec2 uv = vUv;
          uv = floor(uv * 1024.0) / 1024.0;

          // ===== CIEL
          float h = uv.y;

          vec3 sky = mix(
            vec3(0.82,0.90,1.0),
            vec3(0.28,0.58,0.96),
            h
          );

          vec3 color = sky;

          // ===== NUAGES
          vec2 uv1 = uv;
          uv1.x += uTime * 0.003;
          float c1 = texture2D(uMap, uv1 * 0.6).a;

          vec2 uv2 = uv;
          uv2.x += uTime * 0.010;
          float c2 = texture2D(uMap, uv2 * 1.4).a;

          float clouds = max(c1 * 0.6, c2);

          vec3 cloudColor = vec3(0.97);

          color = mix(color, cloudColor, clouds * 0.95);

          

          // sécurité GPU
          color = clamp(color, 0.0, 1.0);

          gl_FragColor = vec4(color, 1.0);
        }
      `
    })

  }, [tex])

  const smoothRot = useRef(new THREE.Vector2(0, 0))

useFrame(({ camera }, dt) => {
  if (!matRef.current) return

  matRef.current.uniforms.uTime.value += dt

  const dir = new THREE.Vector3()
  camera.getWorldDirection(dir)

  let yaw   = Math.atan2(dir.x, dir.z)
  let pitch = Math.asin(dir.y)

  // 🔥 UNWRAP yaw (corrige le saut ±PI)
  let prevYaw = smoothRot.current.x
  let delta = yaw - prevYaw

  if (delta > Math.PI)  yaw -= Math.PI * 2
  if (delta < -Math.PI) yaw += Math.PI * 2

  // 🔥 SMOOTH stable (critically damped)
  const lerp = 1.0 - Math.exp(-dt * 6.0)

  smoothRot.current.x += (yaw   - smoothRot.current.x) * lerp
  smoothRot.current.y += (pitch - smoothRot.current.y) * lerp

  matRef.current.uniforms.uCamRot.value.copy(smoothRot.current)
})

  return (
    <mesh frustumCulled={false} renderOrder={-1000}>
      <primitive object={SKY_GEOMETRY} attach="geometry" />
      <primitive object={material} attach="material" ref={matRef} />
    </mesh>
  )
})


const LevelTrees = React.forwardRef(function LevelTrees({
  level,
  isTurnLevel = false,
  arbres = []
}, ref) {
  const { scene } = useGLTF("/modeles/arbres.glb")

  const materialCacheRef = React.useRef(new Map())
  const planeMaterialCacheRef = React.useRef({})

  const turn =
    isTurnLevel
      ? TOURNANTS.find(t => Number(t.level) === Number(level))
      : null

  const LOW =
    window.GAME_QUALITY?.low

  const TREE_GREEN_TINT = "#c8ff9a"

  const ROAD_CENTER_X = -14
  const ROAD_HALF_WIDTH = 16

  const LEFT_WALL_LIMIT = -34.5
  const RIGHT_WALL_LIMIT = 6.5

  const SCALE_MIN =
    LOW ? 6.2 : 7.2

  const SCALE_MAX =
    LOW ? 8.5 : 10.8

  const JITTER_NORMAL = 1.2

  const TURN_TREE_MARGIN = 8
  const STRAIGHT_TREE_MARGIN = 0

  /*
    Ajustements fins Cherrier :
    - gauche : ajusté selon ta dernière version
    - droite : 1 unité vers la droite
    - centre : conserve le +2 demandé.
  */
  const CHERRIER_SIDE_INWARD_EXTRA = 3.2
  const CHERRIER_CENTER_SHIFT = 2
  const CHERRIER_LEFT_SIDE_SHIFT = -2.5
  const CHERRIER_RIGHT_SIDE_SHIFT = 1

  /*
    Commune :
    tous les arbres du tournant étaient 1 unité trop à gauche.
  */
  const COMMUNE_ALL_TREES_SHIFT = 1
  const COMMUNE_CENTER_SHIFT = 0
  const CANAL1_TREE_OUTWARD_X_SHIFT = 4
  const PARC_RENE_LEVESQUE2_LEFT_TREE_SHIFT = -8
  const PARC_RENE_LEVESQUE2_RIGHT_TREE_SHIFT = 6

  /*
    Correction stable en z :
    Cherrier et Commune n'ont pas le même décalage.
  */
  const TURN_TREE_FORWARD_Z_SHIFT = 10
  const COMMUNE_TURN_TREE_FORWARD_Z_SHIFT = 25
  const CANAL1_TURN_TREE_FORWARD_Z_SHIFT = 30

  const TREE_PLANE_GEOMETRY = React.useMemo(
    () => new THREE.PlaneGeometry(2, 2),
    []
  )

  const baseTextures = React.useMemo(() => {
    const loader = new THREE.TextureLoader()

    return [1, 2, 3, 4, 5, 6].map(i => {
      const tex =
        loader.load(`/textures/base${i}.jpg`)

      tex.wrapS = THREE.RepeatWrapping
      tex.wrapT = THREE.RepeatWrapping
      tex.anisotropy = Math.min(
        8,
        window.GAME_QUALITY?.maxAnisotropy || 1
      )

      return tex
    })
  }, [])

  const normalizeSide = (entry) => {
    const raw = String(
      entry?.cote ??
      entry?.side ??
      ""
    ).toLowerCase()

    if (
      raw === "gauche" ||
      raw === "left" ||
      raw === "-1"
    ) {
      return -1
    }

    if (
      raw === "droit" ||
      raw === "droite" ||
      raw === "right" ||
      raw === "1"
    ) {
      return 1
    }

    return 1
  }

  const seeded01 = (value) => {
    const x =
      Math.sin(value) * 10000

    return x - Math.floor(x)
  }

  const isBarkMesh = (obj) => {
    const name =
      obj?.name?.toLowerCase?.() || ""

    if (
      name.includes("bark") ||
      name.includes("tronc") ||
      name.includes("trunk")
    ) {
      return true
    }

    return obj?.parent?.children?.find(c => c.isMesh) === obj
  }

  const getTreeMaterial = (mat, obj) => {
    const bark =
      isBarkMesh(obj)

    const key =
      `${mat.uuid}_${LOW ? "low" : "high"}_${bark ? "bark" : "leaf"}`

    const cache =
      materialCacheRef.current

    if (!cache.has(key)) {
      const next =
        new THREE.MeshLambertMaterial({
          map: mat.map,
          alphaMap: mat.alphaMap,
          transparent: false,
          alphaTest: 0.35,
          side: THREE.FrontSide
        })

      next.color = bark
        ? (mat.color?.clone?.() || new THREE.Color("#ffffff"))
        : new THREE.Color(TREE_GREEN_TINT)

      next.depthWrite = true
      next.depthTest = true
      next.dithering = !LOW

      if (next.map) {
        next.map.anisotropy = LOW
          ? 1
          : Math.min(
              8,
              window.GAME_QUALITY?.maxAnisotropy || 1
            )

        next.map.generateMipmaps = true
        next.map.needsUpdate = true
      }

      cache.set(key, next)
    }

    return cache.get(key)
  }

  function createGroundPlane(x, z, visible = true) {
    /*
      Respecte la liste :
      { plan: false } => aucune base.
    */
    if (!visible) return null
    if (!baseTextures.length) return null

    const tex =
      baseTextures[
        Math.abs(Math.floor(z)) % baseTextures.length
      ]

    const cache =
      planeMaterialCacheRef.current

    if (!cache[tex.uuid]) {
      cache[tex.uuid] =
        new THREE.MeshStandardMaterial({
          map: tex
        })
    }

    const plane =
      new THREE.Mesh(
        TREE_PLANE_GEOMETRY,
        cache[tex.uuid]
      )

    plane.rotation.x = -Math.PI / 2
    plane.position.set(x, 0.05, z)
    plane.frustumCulled = false

    return plane
  }

  const templates = React.useMemo(() => {
    if (!scene) return []

    return scene.children
      .filter(o =>
        o.name.startsWith("Arbre")
      )
      .map(template => {
        const box =
          new THREE.Box3().setFromObject(template)

        const width =
          box.max.x - box.min.x
        const depth =
          box.max.z - box.min.z

        return {
          template,
          halfWidth: width * 0.5,
          halfDepth: depth * 0.5
        }
      })
  }, [scene])

  function getLevelLocalStartZ(level) {
    if (typeof getLevelStartZ === "function") {
      return getLevelStartZ(level)
    }

    return 0
  }

  function getStraightZRange(level) {
    const start =
      getLevelLocalStartZ(level)

    const length =
      typeof getLevelLength === "function"
        ? getLevelLength(level)
        : 0

    const end =
      start - length

    return {
      start,
      end,
      min: Math.min(start, end) - STRAIGHT_TREE_MARGIN,
      max: Math.max(start, end) + STRAIGHT_TREE_MARGIN
    }
  }

  function getTurnZRange(turn) {
    const customRange =
      turn && typeof getTurnTreeRange === "function"
        ? getTurnTreeRange(turn)
        : null

    if (
      customRange &&
      Number.isFinite(Number(customRange.start)) &&
      Number.isFinite(Number(customRange.end))
    ) {
      const start =
        Number(customRange.start)

      const end =
        Number(customRange.end)

      return {
        start,
        end,
        min: Math.min(start, end) - TURN_TREE_MARGIN,
        max: Math.max(start, end) + TURN_TREE_MARGIN
      }
    }

    if (
      turn &&
      typeof getLevelStartZ === "function" &&
      typeof getLevelLength === "function"
    ) {
      const start =
        getLevelStartZ(turn.level)

      const end =
        start - getLevelLength(turn.level)

      return {
        start,
        end,
        min: Math.min(start, end) - TURN_TREE_MARGIN,
        max: Math.max(start, end) + TURN_TREE_MARGIN
      }
    }

    const start =
      turn.centerZ + (turn.intersectionLength ?? 400) * 0.5

    const end =
      turn.centerZ - (turn.intersectionLength ?? 400) * 0.5

    return {
      start,
      end,
      min: Math.min(start, end) - TURN_TREE_MARGIN,
      max: Math.max(start, end) + TURN_TREE_MARGIN
    }
  }

  function shouldKeepEntryForTurn(entry, turn, range) {
    const z =
      Number(entry.z)

    if (
      !Number.isFinite(z) ||
      z < range.min ||
      z > range.max
    ) {
      return false
    }

    return true
  }

  const preparedEntries = React.useMemo(() => {
    if (!arbres?.length) {
      return []
    }

    const normalized =
      arbres
        .filter(a =>
          Number.isFinite(Number(a?.z))
        )
        .map(a => ({
          ...a,
          z: Number(a.z),

          /*
            On garde level seulement pour la clé/debug.
            La sélection ne s'y fie pas.
          */
          level: Number.isFinite(Number(a?.level))
            ? Number(a.level)
            : a.level,

          offsetX: Number.isFinite(Number(a?.offsetX))
            ? Number(a.offsetX)
            : 0
        }))

    const range =
      turn
        ? getTurnZRange(turn)
        : getStraightZRange(level)

    return normalized
      .filter(entry => {
        if (turn) {
          return shouldKeepEntryForTurn(
            entry,
            turn,
            range
          )
        }

        return (
          entry.z >= range.min &&
          entry.z <= range.max
        )
      })
      .sort((a, b) => b.z - a.z)
  }, [
    arbres,
    level,
    turn
  ])

  function getStraightTreeX({
    halfWidth,
    entry,
    side,
    scale,
    index
  }) {
    const clearance =
      halfWidth * scale + 3

    const xJitter =
      Math.sin(entry.z * 0.043 + index) *
      JITTER_NORMAL

    let x =
      ROAD_CENTER_X +
      side * (ROAD_HALF_WIDTH + clearance)

    x += xJitter

    x =
      THREE.MathUtils.clamp(
        x,
        LEFT_WALL_LIMIT,
        RIGHT_WALL_LIMIT
      )

    return x + entry.offsetX
  }

  function getTurnTreeX({
    turn,
    turnWallLayout,
    entry,
    side,
    index
  }) {
    const leftX =
      turnWallLayout?.leftX ?? -24

    const rightX =
      turnWallLayout?.rightX ?? 24

    const turnWidth =
      turn?.width ?? 40

    const entryOffsetX =
      entry.offsetX || 0

    const isCenterOrSpecialRow =
      Math.abs(entryOffsetX) > 8

    const xJitterRange =
      isCenterOrSpecialRow
        ? 0.15
        : 0.65

    const xJitter =
      Math.sin(entry.z * 0.043 + index) *
      xJitterRange

    /*
      Base de l'ancien TurnLevelTrees :
      - gauche : leftX + clearance vers le centre
      - droite : rightX - clearance vers le centre
    */
    let sideClearance =
      turnWidth * 0.055

    if (
      turn?.id === "Cherrier" &&
      !isCenterOrSpecialRow
    ) {
      sideClearance += CHERRIER_SIDE_INWARD_EXTRA
    }

    const sideClearanceFactor =
      isCenterOrSpecialRow
        ? THREE.MathUtils.clamp(
            1 - Math.abs(entryOffsetX) / (turnWidth * 0.35),
            0,
            1
          )
        : 1

    const inwardPush =
      -side *
      sideClearance *
      sideClearanceFactor

    const cherrierSideShift =
      turn?.id === "Cherrier" &&
      !isCenterOrSpecialRow
        ? (
            side < 0
              ? CHERRIER_LEFT_SIDE_SHIFT
              : CHERRIER_RIGHT_SIDE_SHIFT
          )
        : 0

    const cherrierCenterShift =
      turn?.id === "Cherrier" &&
      isCenterOrSpecialRow
        ? CHERRIER_CENTER_SHIFT
        : 0

    const communeGlobalShift =
      turn?.id === "Commune"
        ? COMMUNE_ALL_TREES_SHIFT
        : 0

    const communeCenterShift =
      turn?.id === "Commune" &&
      isCenterOrSpecialRow
        ? COMMUNE_CENTER_SHIFT
        : 0

    const canal1OutwardShift =
      turn?.id === "Canal1"
        ? side * CANAL1_TREE_OUTWARD_X_SHIFT
        : 0

    const parcReneLevesque2SideShift =
      turn?.id === "Parc René-Lévesque2"
        ? (
            side < 0
              ? PARC_RENE_LEVESQUE2_LEFT_TREE_SHIFT
              : PARC_RENE_LEVESQUE2_RIGHT_TREE_SHIFT
          )
        : 0

    return (
      (side < 0 ? leftX : rightX) +
      entryOffsetX +
      inwardPush +
      cherrierSideShift +
      cherrierCenterShift +
      communeGlobalShift +
      communeCenterShift +
      canal1OutwardShift +
      parcReneLevesque2SideShift +
      xJitter
    )
  }

  function getTurnTreeZShift(turn) {
    if (turn?.id === "Cherrier") {
      return TURN_TREE_FORWARD_Z_SHIFT
    }

    if (turn?.id === "Commune") {
      return COMMUNE_TURN_TREE_FORWARD_Z_SHIFT
    }

    if (turn?.id === "Canal1") {
      return CANAL1_TURN_TREE_FORWARD_Z_SHIFT
    }

    return 0
  }

  const items = React.useMemo(() => {
    if (
      !scene ||
      !templates.length ||
      !preparedEntries.length
    ) {
      return []
    }

    const turnWallLayout =
      turn && typeof getTurnWallLayout === "function"
        ? getTurnWallLayout(turn)
        : null

    /*
      Correction principale contre les arbres qui reculent :
      on utilise l'origine stable du niveau, soit getLevelStartZ(level),
      pour convertir entry.z en z local.
    */
    const levelStartZ =
      getLevelLocalStartZ(level)

    const localZOffset =
      turn?.offsetZ ?? 0

    return preparedEntries.map((entry, i) => {
      const data =
        templates[i % templates.length]

      const template =
        data.template

      const tree =
        typeof SkeletonUtils !== "undefined"
          ? SkeletonUtils.clone(template)
          : template.clone(true)

      const side =
        normalizeSide(entry)

      const scaleSeed =
        seeded01(entry.z * 0.017 + i * 3.71)

      const rotSeed =
        seeded01(entry.z * 0.031 + i * 2.17)

      const scale =
        THREE.MathUtils.lerp(
          SCALE_MIN,
          SCALE_MAX,
          scaleSeed
        )

      let x = 0
      let z = 0

      if (turn) {
        x =
          getTurnTreeX({
            turn,
            turnWallLayout,
            entry,
            side,
            index: i
          })

        /*
          Stable pendant la rotation :
          entry.z est global, levelStartZ est l'origine du groupe,
          donc z local ne change pas pendant le tournant.
        */
        z =
          (entry.z - levelStartZ) +
          localZOffset +
          getTurnTreeZShift(turn)
      } else {
        x =
          getStraightTreeX({
            halfWidth: data.halfWidth,
            entry,
            side,
            scale,
            index: i
          })

        z =
          entry.z - levelStartZ
      }

      tree.position.set(
        x,
        0,
        z
      )

      tree.scale.set(
        scale * 0.95,
        scale * 1.08,
        scale
      )

      tree.rotation.y =
        rotSeed * Math.PI * 2

      tree.updateMatrix()
      tree.updateMatrixWorld(true)

      tree.traverse(obj => {
        if (!obj.isMesh) return

        obj.material =
          getTreeMaterial(obj.material, obj)

        obj.castShadow = false
        obj.receiveShadow = false
        obj.frustumCulled = false
      })

      const plane =
        createGroundPlane(
          x,
          z,
          entry.plan !== false
        )

      return {
        key:
          `${isTurnLevel ? "turn" : "level"}-` +
          `${level}-` +
          `${entry.z}-` +
          `${entry.cote}-` +
          `${entry.offsetX}-` +
          `${entry.plan === false ? "noplan" : "plan"}-` +
          `${i}`,
        tree,
        plane,
        halfX: THREE.MathUtils.clamp(scale * 0.14, 0.65, 1.2),
        halfZ: THREE.MathUtils.clamp(scale * 0.16, 0.75, 1.35)
      }
    })
  }, [
    scene,
    templates,
    preparedEntries,
    turn,
    isTurnLevel,
    level,
    baseTextures
  ])

  useImperativeHandle(
    ref,
    () => ({
      collisionTrees: items.map(item => ({
        tree: item.tree,
        halfX: item.halfX,
        halfZ: item.halfZ
      }))
    }),
    [items]
  )

  if (!items.length) {
    return null
  }

  return (
    <group>
      {items.map(item => (
        <React.Fragment key={item.key}>
          {item.plane && (
            <primitive
              object={item.plane}
            />
          )}

          <primitive
            object={item.tree}
          />
        </React.Fragment>
      ))}
    </group>
  )
})
function VehicleLabel({ name, y }) {

  const texture = useMemo(() => {

    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    canvas.width = 512
    canvas.height = 128

    ctx.fillStyle = "white"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = "black"
    ctx.font = "bold 60px Arial"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(name, canvas.width / 2, canvas.height / 2)

    return new THREE.CanvasTexture(canvas)

  }, [name])

  return (
    <sprite position={[0, y, 0]}>
      <spriteMaterial map={texture} depthTest={false} />
    </sprite>
  )
}

function GLBVehicle({ name, reverse, speed = 0 }) {

  const { scene } = useGLTF("/modeles/vehicules.glb")
  const wheels = useRef([])
  const container = useRef()
  const { gameState } = useGame()
  const SIDE_EXTRA = -0.25
  const fix = VEHICLE_FIX[name] || {
    modelRot: [0, 0, 0],
    forward: 1,
    posY: 0,
    scale: 1.4
  }
  const rotY = fix.modelRot[1] + (reverse ? Math.PI : 0)
  const model = useMemo(() => {
  let root = null
  

  // =====================================
  // 🔁 CACHE / CLONE
  // =====================================

  if (VEHICLE_CACHE[name]) {

    root = SkeletonUtils.clone(VEHICLE_CACHE[name])

  } else {

  scene.traverse(o => {
    if (o.name === name) root = o
  })

  if (!root) return null

  const source = SkeletonUtils.clone(root)

  source.traverse(child => {

    const isRenderable = child.isMesh || child.type === "Group" ||  child.type === "Object3D"

    if (!isRenderable) return

    const mat = child.material

    if (mat?.map) {

      const texUUID = mat.map.uuid

      if (!VEHICLE_MATERIAL_CACHE[texUUID]) {

        VEHICLE_MATERIAL_CACHE[texUUID] = new THREE.MeshBasicMaterial({
          map: mat.map,
          transparent: false,
          alphaTest: 0.5,
          toneMapped: false
        })
      }

      child.material = VEHICLE_MATERIAL_CACHE[texUUID]
    }
  })

  VEHICLE_CACHE[name] = source
  root = source.clone(true)
}

// =====================================
// 🛞 DETECTION ROUES
// =====================================

wheels.current = []

root.traverse(child => {

  if (!child.isMesh || !child.geometry) return

  const n = child.name.toLowerCase()

  if (n.includes("teker") || n.includes("wheel") || n.includes("lastik")) {

    wheels.current.push({
      mesh: child,
      axis: WHEEL_AXIS_VECTORS.x
    })
  }
})

// =====================================
// 📦 BOUNDING BOX CORRIGÉE
// =====================================

if (VEHICLE_BOUNDS[name]) {

  VEHICLE_LENGTH_CACHE[name] = VEHICLE_BOUNDS[name].halfLength
  root.userData.halfWidth = VEHICLE_BOUNDS[name].halfWidth
  root.userData.halfLength = VEHICLE_BOUNDS[name].halfLength

} else {

  const temp = root.clone()

  temp.rotation.set(fix.modelRot[0], rotY, fix.modelRot[2])
  temp.scale.setScalar(fix.scale)
  temp.updateMatrixWorld(true)

  const box = new THREE.Box3().setFromObject(temp)
  const size = new THREE.Vector3()

  box.getSize(size)

  const halfWidth = Math.max(0.5, size.x * 0.5 + SIDE_EXTRA)
  const halfLength = Math.max(1.0, size.z * 0.5 + SIDE_EXTRA)

  root.userData.halfWidth = halfWidth
  root.userData.halfLength = halfLength

  VEHICLE_BOUNDS[name] = { halfWidth, halfLength }
  VEHICLE_LENGTH_CACHE[name] = halfLength
}

// =====================================
// 🎯 RECENTRER LE MODÈLE
// =====================================

const recenterBox = new THREE.Box3().setFromObject(root)
const recenterCenter = new THREE.Vector3()

recenterBox.getCenter(recenterCenter)
root.position.sub(recenterCenter)
root.updateMatrixWorld(true)

let tris = 0

root.traverse(o => {

  if (o.isMesh && o.geometry) {

    tris +=
      o.geometry.index
        ? o.geometry.index.count / 3
        : o.geometry.attributes
            .position.count / 3
  }
})

return root

}, [scene, name, rotY, fix])


useLayoutEffect(() => {

  if (!container.current || !model)
    return

  container.current.userData.halfWidth = model.userData.halfWidth
  container.current.userData.halfLength = model.userData.halfLength

}, [model])

useLayoutEffect(() => {

  if (!container.current) return

  container.current.userData.wheels = wheels.current.filter(w => w.mesh)

}, [model])

if (!model) return null

return (
  <group
    ref={container}
    onUpdate={(self) => {
      const d = self.parent?.userData

      if (!d) return

      d.halfLength = model.userData.halfLength
      d.halfWidth = model.userData.halfWidth
    }}
  >
    <primitive
      object={model}
      rotation={[fix.modelRot[0], rotY, fix.modelRot[2]]}
      position={[0, fix.posY, 0]}
      scale={fix.scale}
    />
    {DEBUG && (
      <mesh
        position={[0, fix.posY + 0.9, 0]}
      >
      <boxGeometry
        args={[model.userData.halfWidth * 2, 1.8, model.userData.halfLength * 2]}
      />
      <VehicleLabel
        name={name}
        y={3.2}
      />
      <meshBasicMaterial
        color="red"
        wireframe
      />
    </mesh>
    )}
    {DEBUG ? (
      <VehicleLabel
        name={name}
        y={3.2}
      />
    ) : ""}
    </group>
  )
}


function DebugWorldZ() {
  const { worldGroup } = useGame()
  const { camera, scene } = useThree()

  const spriteRef = React.useRef()
  const canvasRef = React.useRef()
  const ctxRef = React.useRef()

  // -----------------------------
  // INIT SPRITE
  // -----------------------------
  React.useEffect(() => {
    const canvas = document.createElement("canvas")
    canvas.width = 256
    canvas.height = 128

    const ctx = canvas.getContext("2d")

    const texture = new THREE.CanvasTexture(canvas)

    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false
    })

    const sprite = new THREE.Sprite(material)
    sprite.scale.set(2, 1, 1)

    scene.add(sprite)

    canvasRef.current = canvas
    ctxRef.current = ctx
    spriteRef.current = sprite

    return () => {
      scene.remove(sprite)
      texture.dispose()
      material.dispose()
    }
  }, [scene])

  // -----------------------------
  // UPDATE
  // -----------------------------
  useFrame(() => {
    if (!worldGroup?.current || !spriteRef.current || !ctxRef.current) return

    const sprite = spriteRef.current
    const ctx = ctxRef.current
    const canvas = canvasRef.current
    const worldZ = -worldGroup.current?.position?.z

    ctx.clearRect(0, 0, canvas.width, canvas.height)// clear
    ctx.fillStyle = "rgba(0,0,0,0.6)"// background
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = "white"
    ctx.font = "bold 48px monospace"// text
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(`Z: ${worldZ.toFixed(0)}`, canvas.width / 2, canvas.height / 2)
    sprite.material.map.needsUpdate = true
    sprite.position.copy(camera.position) // ✅ position devant la caméra
    sprite.quaternion.copy(camera.quaternion)
    sprite.position.add(new THREE.Vector3(-7,3,-5).applyQuaternion(camera.quaternion))
  })

  return null
}


function DebugGLTF() {

  const gltf = useGLTF("/modeles/vehicules.glb")

  useEffect(() => {

    console.log("📦 GLTF =", gltf)
    console.log("📦 NODES :")
    console.log(Object.keys(gltf.nodes))
    console.log("📦 MATERIALS :")
    console.log(Object.keys(gltf.materials))

  }, [gltf])

  return null
}

const IntersectionTraffic = React.forwardRef(
({
  intersections = [],
  trafficLightsRef
}, ref) => {

  const group = useRef()
  const meshRefs = useRef({})

  const { worldGroup, gameState, level, departOption } = useGame()
  const worldZ = worldGroup?.current?.position.z || 0

  const MAX_INTERSECTION_CARS = 20
  const SPEED = 22
  const SPAWN_DELAY = 2.2
  const MAX_ACTIVE_INTERSECTIONS = 2
  const INTERSECTION_Z_OFFSET = 0
  const INTERSECTION_Y_OFFSET = 0
  const START_X_OFFSET = 40
  const LANE_GAP = 2
  const ACTIVE_MIN = -40
  const ACTIVE_MAX = 200

  // ==================================================
  // 🔥 STATE SEULEMENT POUR RENDER INITIAL
  // ==================================================

  const [cars, setCars] = useState([])

  // ==================================================
  // 🔥 REF PRINCIPAL
  // ==================================================

  const carsRef = useRef([])
  const timersRef = useRef({})

  useImperativeHandle(
    ref,
    () => group.current
  )

  // ==================================================
  // 🔒 NORMALISATION DIRECTION
  // ==================================================

  function getDirection(inter) {

    const raw = inter.direction

    if (raw === -1 || raw === "-1") {

      return -1
    }

    if (raw === 1 || raw === "1") {

      return 1
    }

    if (raw === 0 || raw === "0") {

      return Math.random() < 0.5 ? -1 : 1
    }

    return -1
  }

  useEffect(() => {

  const pool = []

  for (let i = 0; i < MAX_INTERSECTION_CARS; i++) {

    pool.push({
      id: i,
      active: false,
      x: 99999,
      z: 99999,
      dir: -1,
      vehicleName: VEHICLE_NAMES[0],
      prevWheelPos: new THREE.Vector3(99999, INTERSECTION_Y_OFFSET, 99999)
    })
  }

  carsRef.current = pool
  setCars(pool)

}, [])
  // ==================================================
  // 🚗 UPDATE
  // ==================================================

  useFrame((_, dtRaw) => {

    const playerWorldZ = -worldZ

    if (departOption >= 14 && playerWorldZ > DES_SEIGNEURS_INTERSECTION_Z + ACTIVE_MAX) {

      let changed = false

      for (const c of carsRef.current) {

        if (c.active) {
          c.active = false
          c.x = 99999
          c.z = 99999
          changed = true
        }
      }

      if (changed) {

        setCars([...carsRef.current])
      }

      return
    }

    const dt = Math.min(dtRaw, 0.025)

    if (gameState === "waitingContinue") {

      return
    }

    if (!group.current) {
      return
    }

    const lights = trafficLightsRef?.current?.getStates?.() || []
    const lightsByIntersectionId = new Map(
      lights.map(light => [light.intersectionId, light])
    )

    // ==================================================
    // 🔥 INTERSECTIONS ACTIVES
    // ==================================================

    const active = intersections
        .filter(isVehicleTrafficIntersectionEnabled)
        .map((inter, i) => ({
          inter,
          light: lightsByIntersectionId.get(inter.id),
          index: i
        }))
        .filter(o => {
          const dist = playerWorldZ - o.inter.z

          return (dist >= ACTIVE_MIN && dist <= ACTIVE_MAX)
        })
        .sort((a, b) =>Math.abs(playerWorldZ - a.inter.z) - Math.abs(playerWorldZ - b.inter.z))
        .slice(0, MAX_ACTIVE_INTERSECTIONS)

    // ==================================================
    // 🔥 SPAWN
    // ==================================================

    let changed = false

    active.forEach(({inter, light, index}) => {

      if (!light || light.state === "green") {

        timersRef.current[index] = 0
        return
      }

      timersRef.current[index] = (timersRef.current[index] || 0) + dt

      if (timersRef.current[index] > SPAWN_DELAY) {

        timersRef.current[index] = 0

        const dir = getDirection(inter)
        const spawnX = dir === -1 ? START_X_OFFSET : -START_X_OFFSET

        let laneOffsetZ = INTERSECTION_Z_OFFSET

        // ==================================================
        // séparation visuelle bidirectionnel
        // ==================================================

        if (inter.direction === 0 || inter.direction === "0" ) {

          laneOffsetZ +=  dir === -1 ? -LANE_GAP : LANE_GAP + 2
        }

        const vehicleName = VEHICLE_NAMES[Math.floor(Math.random() * VEHICLE_NAMES.length)]
        const freeCar = carsRef.current.find(c => !c.active)

        if (freeCar) {
          freeCar.active = true
          freeCar.x = spawnX
          freeCar.z = inter.z + laneOffsetZ
          freeCar.dir = dir
          freeCar.vehicleName = vehicleName
          freeCar.intersectionId = index
          freeCar.prevWheelPos.set(spawnX, INTERSECTION_Y_OFFSET, inter.z + laneOffsetZ)
        }
        changed = true
      }
    })

    // ==================================================
    // 🔥 UPDATE POSITIONS
    // ==================================================

    for (let i = carsRef.current.length - 1; i >= 0; i--) {

      const c = carsRef.current[i]

      c.x +=  SPEED * dt * c.dir

      // ==================================================
      // DESPAWN
      // ==================================================

      if (Math.abs(c.x) > START_X_OFFSET + 40) {
        c.active = false
        c.x = 99999
        c.z = 99999
        c.prevWheelPos.set(c.x, INTERSECTION_Y_OFFSET, c.z)
        changed = true
        continue
      }

      // ==================================================
      // APPLY DIRECT
      // ==================================================

      const mesh = meshRefs.current[c.id]

      if (!mesh) {
        continue
      }

      mesh.position.set(c.x, INTERSECTION_Y_OFFSET, c.z)
      mesh.rotation.y = c.dir === 1 ? -Math.PI / 2 : Math.PI / 2

      const vehicleNode = mesh.children[0]
      const wheels = vehicleNode?.userData?.wheels

      if (wheels?.length) {

        const dx = mesh.position.x - c.prevWheelPos.x
        const dz = mesh.position.z - c.prevWheelPos.z
        const distance = Math.sqrt(dx * dx + dz * dz)

        if (distance > 0.0001 && distance < 10) {

          const WHEEL_RADIUS = 0.35
          const rot = distance / WHEEL_RADIUS

          WHEEL_MOVE_DIR.set(dx / distance, 0, dz / distance)
          WHEEL_TARGET_AXIS.crossVectors(WHEEL_WORLD_UP, WHEEL_MOVE_DIR).normalize()

          mesh.updateMatrixWorld(true)

          wheels.forEach(wheel => {

            const wheelMesh = wheel?.mesh || wheel

            if (wheelMesh) {
              const axis = wheel?.axis || WHEEL_AXIS_VECTORS.x
              WHEEL_WORLD_AXIS.copy(axis).transformDirection(wheelMesh.matrixWorld).normalize()
              const axisSign = Math.sign(WHEEL_WORLD_AXIS.dot(WHEEL_TARGET_AXIS)) || 1

              wheelMesh.rotateOnAxis(
                axis,
                axisSign * rot
              )
            }
          })
        }

        c.prevWheelPos.copy(mesh.position)
      }
    }

    // ==================================================
    // 🔥 RERENDER SEULEMENT
    // SPAWN / DESPAWN
    // ==================================================

    if (changed) {
      setCars([...carsRef.current])
    }

  })

  // ==================================================
  // RENDER
  // ==================================================

  return (
    <group ref={group}>
      {cars.filter(c => c.active).map(c => (
        <group
          key={c.id}
          ref={(node) => { if (node) { meshRefs.current[c.id] = node }}}
          position={[c.x, INTERSECTION_Y_OFFSET, c.z]}
          rotation={[0, c.dir === 1 ? -Math.PI / 2 : Math.PI / 2, 0]}
        >
          <GLBVehicle
            name={c.vehicleName}
            reverse={false}
            speed={SPEED}
          />
        </group>
      ))}
    </group>
  )
})


const RandomTrafficCars = React.forwardRef(
({
  routeX,
  routeW,
  density = 0.18,
  intersections = [],
  trafficLightsRef,
  bikeTrafficRef,
  cyclistRef
}, ref) => {


  const { worldGroup, level, gameState, restartVersion } = useGame()

  const vehiclePool = useRef([])
  const vehicleIndex = useRef(0)
  const nextCarId = useRef(1)
  const worldZ = worldGroup?.current?.position.z || 0

  const MARGE_ROUTE = 4.3
  const RECYCLE_FRONT = 300   // devant (plus court)
  const RECYCLE_BACK  = 320   // derrière (plus long)
  const STOP_DISTANCE = 90        // distance de détection du feu
  const STOP_LINE_OFFSET = 20     // position d’arrêt avant feu
  const MAX_BRAKE = 80            // puissance freinage
  const CYCLIST_YIELD_RADIUS = 8
  const PLAYER_YIELD_RADIUS  = 10
  const BASE_SPEED = 15  //14 → trafic urbain lent, 18 → normal, 22 → autoroute
  const TURN_PROBABILITY = 0.25
  const TURN_START_DISTANCE = 20 // Zone de déclenchement pour tourner 
  const TURN_TRIGGER_ZONE = 25
  const MAX_ACCEL = 18
  const TURN_YIELD_ANGLE_DEG = 35     // angle avant céder (0–90)
  const TURN_SPEED_FACTOR = 0.55   // facteur de vitesse en entrée de virage (0–1, plus bas = plus lent)
  const TURN_RECOVER_AFTER = 0.65  // progression t (0–1) à partir de laquelle l’auto recommence à accélérer
  const TURN_EXIT_ACCEL = 2.0      // multiplicateur de vitesse en sortie de virage (>1 = accélération)
  const TURN_DURATION = 1.4   // secondes pour compléter le virage
  const OFFSET_COMMUNE = 15
  const ST_LOUIS_Z = intersections.find(i => i.id === "Saint-Louis")?.z
  const COMMUNE_Z = intersections.find(i => i.id === "Commune")?.z
  const group = useRef()
  const textGroup = useRef()
  const texts = useRef([])
  const [cars, setCars] = useState([])

  // position du joueur dans le monde
  let playerWorldZ = -worldZ

  // le joueur a dépassé Crémazie
  let isAfterCremazie = playerWorldZ >= Math.abs(CREMAZIE_Z)
  const isAfterCommuneTraffic =
    typeof COMMUNE_Z === "number" &&
    (playerWorldZ <= COMMUNE_Z || Math.abs(playerWorldZ) >= Math.abs(COMMUNE_Z))
  const isAfterStLouisTraffic =
    typeof ST_LOUIS_Z === "number" &&
    (playerWorldZ <= ST_LOUIS_Z || Math.abs(playerWorldZ) >= Math.abs(ST_LOUIS_Z))
  const isBetweenStLouisAndCommune =
    isAfterStLouisTraffic &&
    !isAfterCommuneTraffic
  const allowOppositeTrafficSpawn = !isBetweenStLouisAndCommune

  function getSafeDistance(a, b = null) {
    const lenA = a?.halfLength || 6
    const lenB = b?.halfLength || 6
    const maxLen = Math.max(lenA, lenB)
    const GAP = THREE.MathUtils.lerp(3.5, 8, THREE.MathUtils.clamp((maxLen - 4) / 7, 0, 1))

    return (lenA + lenB + GAP)
  }

  function getRightTurnLaneIndex(lanes = lanesConfig) {
    let rightTurnLaneIndex = -1

    for (let i = 0; i < lanes.length; i++) {
      if (lanes[i]?.dir === -1) {
        rightTurnLaneIndex = i
      }
    }

    return rightTurnLaneIndex
  }

  function getLaneIndexForDirection(direction, lanes = lanesConfig) {
    const fallbackIndex = direction === -1
      ? getRightTurnLaneIndex(lanes)
      : lanes.findIndex(lane => lane?.dir === direction)

    return fallbackIndex >= 0 ? fallbackIndex : 0
  }

  function getSpawnLaneIndices(lanes = lanesConfig) {
    const allowed = lanes
      .map((lane, index) => ({ lane, index }))
      .filter(({ lane }) => allowOppositeTrafficSpawn || lane?.dir !== +1)
      .map(({ index }) => index)

    return allowed.length ? allowed : lanes.map((_, index) => index)
  }
  // ==================================================
  // VOIES DYNAMIQUES
  // ==================================================

  const lanesConfig = useMemo(() => {

    const left = routeX - routeW / 2

    if (!isAfterCremazie) {

      const laneWidth = routeW / 3

      return [
        { x: left + laneWidth * 0.5, dir: -1 },
        { x: left + laneWidth * 1.5, dir: -1 },
        { x: left + laneWidth * 2.5, dir: -1 }
      ]
    }

  const laneWidth = routeW / 2

  // Décalages indépendants
  const SHIFT_FORWARD = -laneWidth * 0.15   // 🔥 ramène vers centre
  const OPPOSITE_BASE_SHIFT = laneWidth * 0.25

  return [
    { x: left + laneWidth * 0.5 + SHIFT_FORWARD, dir: -1 }, // vers joueur
    { x: left + laneWidth * 1.5 + OPPOSITE_BASE_SHIFT, dir: +1 } // opposé
  ]

  }, [routeX, routeW, isAfterCremazie])


  
// ==================================================
// POOL
// ==================================================
useEffect(() => {

  // shuffle Fisher-Yates (meilleur que sort random)
  const arr = [...VEHICLE_NAMES]

  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }

  vehiclePool.current = arr
  vehicleIndex.current = 0

}, [])
// ==================================================
// SPAWN
// ==================================================

useEffect(() => {

  if (level > 18) {
    setCars([])
    return
  }
  if (!lanesConfig?.length) return

  const LANES = lanesConfig.length
  const BASE_PER_LANE = 20
  const adjustedDensity = density * (isAfterCommuneTraffic ? COMMUNE_TRAFFIC_DENSITY_FACTOR : 1)
  const PER_LANE = Math.max(2, Math.floor(BASE_PER_LANE * adjustedDensity))
  const TARGET = LANES * PER_LANE

  setCars(prev => {

    // =========================
    // 🛑 HARD LIMIT (anti crash GPU)
    // =========================
    if (prev.length > TARGET) return prev.slice(0, TARGET)
    if (prev.length === TARGET) return prev

    const playerZ = -worldZ
    const lanes = lanesConfig
    const missing = TARGET - prev.length

    // =========================
    // 🧪 DEBUG MODE (FIXED ONCE)
    // =========================
    if (DEBUG_ALL_CARS && prev.length === 0) {
      const baseCars = VEHICLE_NAMES.map((name, i) => ({
        id: `debug_${name}`, // 🔥 stable
        vehicleName: name,
      }))
      return placeDebugCars(baseCars, lanesConfig, worldZ)
    }

    const newCars = []
    const laneSpawnCursor = {}
    const spawnLaneIndices = getSpawnLaneIndices(lanes)

    for (let i = 0; i < missing; i++) {

      let next = vehiclePool.current[vehicleIndex.current]

      if (prev.length > 0 && next === prev[prev.length - 1].vehicleName) {

        vehicleIndex.current = (vehicleIndex.current + 1) % vehiclePool.current.length
        next = vehiclePool.current[vehicleIndex.current]
      }

      const vehicleName = next

      vehicleIndex.current = (vehicleIndex.current + 1) % vehiclePool.current.length

      const id = nextCarId.current++
      const laneIndex = spawnLaneIndices[Math.floor(Math.random() * spawnLaneIndices.length)]
      const direction = lanes[laneIndex].dir

      // ==================================================
      // 🚗 ESPACEMENT INITIAL BASÉ SUR
      // LES VRAIES DIMENSIONS GLB
      // ==================================================

      if (laneSpawnCursor[laneIndex] === undefined) {

        laneSpawnCursor[laneIndex] = playerZ + 120 - Math.random() * 20
      }

      // ==================================================
      // LONGUEUR VÉHICULE COURANT
      // ==================================================

      const currentHalfLength = VEHICLE_LENGTH_CACHE[vehicleName] || 6

      // ==================================================
      // LONGUEUR VÉHICULE PRÉCÉDENT
      // ==================================================

      const previousHalfLength = laneSpawnCursor[`${laneIndex}_lastHalf`] || 6

      // ==================================================
      // GAP VARIABLE NATUREL
      // ==================================================

      const GAP = 2 + Math.random() * 3

      // ==================================================
      // DISTANCE TOTALE
      // ==================================================

      const MIN_GAP = currentHalfLength + previousHalfLength + GAP

      // ==================================================
      // PETIT DÉCALAGE NATUREL
      // ==================================================

      const jitter = (Math.random() - 0.5) * 2
      const spawnZ = laneSpawnCursor[laneIndex] + jitter

      // ==================================================
      // AVANCER CURSOR
      // ==================================================

      laneSpawnCursor[laneIndex] -= MIN_GAP

      // ==================================================
      // MÉMORISER TAILLE
      // ==================================================

      laneSpawnCursor[`${laneIndex}_lastHalf`] = currentHalfLength
      // ==================================================
      const laneWidth = routeW / lanes.length
      const randomOffset = (Math.random() - 0.5) * laneWidth * 0.25
      const laneOffset = randomOffset + getLaneOffset(laneWidth, direction, spawnZ)

      newCars.push({
        id,
        vehicleName,
        laneIndex,
        direction,
        x: lanes[laneIndex].x + laneOffset,
        z: spawnZ,
        speedFactor: 0.8 + Math.random() * 0.6,
        speed: 0,
        laneOffset,
        turning: false,
        turned: false,
        slowing: false,
        justSpawned: true,
        turnCurve: null,
        turnT: 0,
        lastTestedIntersection: -1,
        isTraffic: true,
        passedCremazie: spawnZ < CREMAZIE_Z,
        halfLength: 6
      })
    }
    return [...prev, ...newCars]

  })

}, [allowOppositeTrafficSpawn, density, isAfterCommuneTraffic, level, lanesConfig, restartVersion])

function getCommuneOffset(z) {

  if (typeof COMMUNE_Z !== "number") return 0

  // 🔥 même logique que Crémazie (progressive)
  const TRANSITION = 60

  const t = THREE.MathUtils.clamp(
    (COMMUNE_Z - z) / TRANSITION,
    0,
    1
  )

  return t * OFFSET_COMMUNE
}

function getLaneOffset(laneWidth, direction, z) {

  const base = (Math.random() - 0.5) * laneWidth * 0.25

  let bias = direction === +1 ? laneWidth * 0.3 : 0

  const isAfterCremazieLocal = z < CREMAZIE_Z
  const isAfterCommuneLocal = z < COMMUNE_Z

  let offset = base + bias

  // =====================================
  // 🚇 APRÈS CRÉMAZIE
  // =====================================

  if (isAfterCremazieLocal) {

    offset -= 0.5

    // 🚗 voie inverse plus à droite
    if (direction === +1) {

      offset += 4
    }
  }

  // =====================================
  // 🌆 APRÈS COMMUNE
  // =====================================

  if (isAfterCommuneLocal) {

    offset += COMMUNE_TRAFFIC_OFFSET
  }

  return offset
}
function respawnFar(data, car) {
  // ==================================================
  // POSITION DE SPAWN
  // ==================================================
  const spawningBehind = Math.random() < 0.5
  const jitter = (Math.random() - 0.5) * 30
  const spawnZ = spawningBehind ? -worldZ + 20 + jitter : -worldZ - 300 + jitter
  const pastCremazie= spawnZ < CREMAZIE_Z ? true : false
  const lanes = lanesConfig
  const wasLateral = Math.abs(Math.cos(car.rotation.y)) < 0.5
  const laneWidth = routeW / lanes.length

  data.vehicleName = vehiclePool.current[vehicleIndex.current]

  vehicleIndex.current = (vehicleIndex.current + 1) % vehiclePool.current.length

  if (pastCremazie) {

    // 🔥 FIX FINAL
    if (wasLateral) {
      // voiture sort d’un virage → FORCER flux principal
      data.direction = -1
      data.laneIndex = getLaneIndexForDirection(data.direction, lanes)

    } else {

      data.direction = allowOppositeTrafficSpawn && Math.random() < 0.5 ? +1 : -1
      data.laneIndex = getLaneIndexForDirection(data.direction, lanes)
    }
    data.offsetX = data.direction === -1 ? -MARGE_ROUTE : + MARGE_ROUTE
  } else {

    // avant Crémazie → uniquement vers joueur
    data.direction = -1
    data.laneIndex = Math.floor(Math.random() * lanes.length)
  }

  // ==================================================
  // RESET
  // ==================================================
  data.laneOffset = getLaneOffset(laneWidth, data.direction,spawnZ)
  data.x = lanes[data.laneIndex].x + data.laneOffset
  data.z = spawnZ
  data.lastTestedIntersection = -1
  data.slowing = false
  data.justSpawned = true
  data.spawnTimer = 0
  data.passedCremazie = false
  data.turning = false
  data.turned = false
  data.turnDir = null
  data.turnCurve = null
  data.turnOpposite = false
  data.fromOppositeTurn = false
  data.visualOffsetX = 0
  data.spawnedBeforeCremazie = data.z > CREMAZIE_Z
  
  car.rotation.y = data.direction === -1 ? 0 : Math.PI
  car.position.set(data.x, 0, data.z)
  car.userData = data

}

//---------------------------------------------
function cyclistConflictAt(x, z) {

  const bikes = bikeTrafficRef?.current?.children

  const radius2Bikes  = CYCLIST_YIELD_RADIUS * CYCLIST_YIELD_RADIUS
  const radius2Player = PLAYER_YIELD_RADIUS  * PLAYER_YIELD_RADIUS

  // ==================================================
  // 🚴 CYCLISTES IA (monde local)
  // ==================================================

  if (bikes) {

    for (const b of bikes) {

      const dx = b?.position?.x - x
      const dz = b?.position?.z - z

      if (dx * dx + dz * dz < radius2Bikes)
        return true
    }
  }

  // ==================================================
  // 🚴 JOUEUR (repère joggeuse)
  // ==================================================

  const cyclist = cyclistRef?.current

  if (cyclist) {

    const px = cyclist?.position?.x
    const pz = 0   // joueur fixe dans joggeuse

    // convertir point conflit → espace joueur
    const conflictZ = z + worldZ

    const dx = px - x
    const dz = pz - conflictZ

    if (dx * dx + dz * dz < radius2Player)
      return true
  }

  return false
}

function placeDebugCars(cars, lanesConfig, worldZ) {

  const playerZ = -worldZ
  const FRONT = 180
  const START = playerZ - 20
  const perLane = Math.ceil(cars.length / lanesConfig.length)
  const placed = []
  let index = 0

  for (let laneIndex = 0; laneIndex < lanesConfig.length; laneIndex++) {

    const lane = lanesConfig[laneIndex]

    for (let i = 0; i < perLane; i++) {

      if (index >= cars.length) break

      const z = START - (i / perLane) * FRONT

      placed.push({
        ...cars[index],
        laneIndex,
        x: lane.x,
        z,
        speed: 0,
        speedFactor: 0,
        direction: lane.direction,
        turning: false,
        turned: false,
        slowing: false,
        isDebug: true
      })

      index++
    }
  }

  return placed
}

function setCarPosition(car, data, dt) {

  const targetX = data.x + (data.visualOffsetX || 0)
  const targetZ = data.z

  // ==================================================
  // 🚗 VITESSE MAX DE RATTRAPAGE
  // ==================================================

  const MAX_CATCHUP = 35 * dt

  // ==================================================
  // Z
  // ==================================================

  const dz = targetZ - car.position.z

  car.position.z +=
    THREE.MathUtils.clamp(
      dz,
      -MAX_CATCHUP,
      MAX_CATCHUP
    )

  // ==================================================
  // X
  // ==================================================

  const dx = targetX - car.position.x

  car.position.x += THREE.MathUtils.clamp(dx, -MAX_CATCHUP, MAX_CATCHUP)
}

  // ==================================================
  // UPDATE
  // ==================================================

 useFrame((_, dtRaw) => {

  if (level > 18) {

    if (cars.length) {
      setCars([])
    }

    return
  }
  // 🔥 rapproche la vitesse du trafic du vrai temps réel mobile
  const dt = Math.min(dtRaw, 1 / 20)

  if (dt <= 0) return
  if (gameState === "gameOver") return
  if (!group.current) return
  if(gameState==="waitingContinue") return
  
  isAfterCremazie = -worldZ >= Math.abs(CREMAZIE_Z)

  const lights = trafficLightsRef?.current?.getStates?.() || []

  group.current.userData.laneBuckets = null
  group.current.children.forEach((car, i) => {

  const data = cars[i]

  if (!data) return

  if (data.prevWheelPos === undefined) {
    data.prevWheelPos = new THREE.Vector3(car.position.x, car.position.y, car.position.z)
  }

  // ==================================================
  // 🚗 MOUVEMENT LATÉRAL DYNAMIQUE
  // ==================================================

  if (data.roadDrift === undefined) {

    data.roadDrift = 0
    data.roadDriftTarget = 0
    data.roadDriftTimer = 0
  }

    const finishFrame = () => {

      car.userData = data

      // ==================================================
      // 🚗 POSITION NORMALE SEULEMENT
      // ==================================================

      if (!data.turning) {

        setCarPosition(car, data, dt)

        car.position.x = data.x + (data.visualOffsetX || 0) + (data.roadDrift || 0)
      }

      // =============== ROUES ====================
      const vehicleNode = car.children[0]
      const wheels = vehicleNode?.userData?.wheels

      if (wheels?.length) {

        const dx = car.position.x - data.prevWheelPos.x
        const dz = car.position.z - data.prevWheelPos.z
        const distance = Math.sqrt(dx * dx + dz * dz)

        if (distance > 0.0001 && distance < 10) {

          const WHEEL_RADIUS = 0.35
          const rot = distance / WHEEL_RADIUS
          WHEEL_MOVE_DIR.set(dx / distance, 0, dz / distance)
          WHEEL_TARGET_AXIS.crossVectors(WHEEL_WORLD_UP, WHEEL_MOVE_DIR).normalize()

          car.updateMatrixWorld(true)

          wheels.forEach(wheel => {

            const mesh = wheel?.mesh || wheel
            if (mesh) {
              const axis = wheel?.axis || WHEEL_AXIS_VECTORS.x
              WHEEL_WORLD_AXIS.copy(axis).transformDirection(mesh.matrixWorld).normalize()
              const axisSign = Math.sign(WHEEL_WORLD_AXIS.dot(WHEEL_TARGET_AXIS)) || 1

              mesh.rotateOnAxis(
                axis,
                axisSign * rot
              )
            }
          })
        }

        data.prevWheelPos.copy(car.position)
      }
    }
    if (data.isDebug) {
      car.position.set(data.x, 0, data.z)
      spawnText(textGroup, texts, data.x, data.z, data.vehicleName, "yellow")
      return
    }

    group.current.userData.speed = data.speed
    const visibleZ = data.z + worldZ
    const SPEED = BASE_SPEED * data.speedFactor * (1 + level * 0.12)
    const isBehindPlayerForward = data.direction === -1 && visibleZ > 0
    const isFarBehind = data.direction === -1 ? visibleZ > 60 : visibleZ < -60
    
    // ==================================================
    // SYNCHRONISATION ROTATION ↔ DIRECTION (ANTI-RECUL)
    // ==================================================

    if (!data.turning && !data.turned) {
      car.rotation.y = data.direction === -1 ? 0 : Math.PI
    }

    // ==================================================
    // MODE CHASSE (trafic vers joueur seulement)
    // ==================================================

    if (data.direction === -1 && visibleZ > 60)
      data.speed += MAX_ACCEL * 1.8 * dt


    // ==================================================

    if (data.turning && data.turnCurve) {

      // ==================================================
      // 🎯 ROTATION EN DEUX PHASES
      // ==================================================

      const startRot = data.turnStartRot

      const totalAngle =
        THREE.MathUtils.degToRad(
          data.turnDir === "right" ? -90 : 90
        )

      const angle1 =
        THREE.MathUtils.degToRad(
          data.turnDir === "right"
            ? -TURN_YIELD_ANGLE_DEG
            : TURN_YIELD_ANGLE_DEG
        )

      const angle2 = totalAngle - angle1

      const yieldT = THREE.MathUtils.clamp(
        TURN_YIELD_ANGLE_DEG / 90,
        0,
        0.95
      )

      // ==================================================
      // 🎯 PROFIL DE VITESSE DU VIRAGE
      // ==================================================

      const t0 = data.turnT

      let speedFactor = TURN_SPEED_FACTOR

      if (t0 > TURN_RECOVER_AFTER) {

        const k =
          (t0 - TURN_RECOVER_AFTER) /
          (1 - TURN_RECOVER_AFTER)

        speedFactor =
          TURN_SPEED_FACTOR +
          k * (TURN_EXIT_ACCEL - TURN_SPEED_FACTOR)
      }

      let nextT = Math.min(
        t0 + (dt / TURN_DURATION) * speedFactor,
        1
      )

      // ==================================================
      // 🚦 FEU LE PLUS PROCHE
      // ==================================================

      let lightState = "green"

      for (const light of lights) {

        const d =
          Math.abs(light.position[2] - data.z)

        if (d < 12) {
          lightState = light.state
          break
        }
      }

      // ==================================================
      // 🔴 FEU ROUGE → ENGAGEMENT
      // ==================================================

      if (lightState === "red") {

        data.turnCommitted = true
        data.waitingForYield = false

        if (nextT <= yieldT) {
          nextT = yieldT + 0.001
        }
      }

      // ==================================================
      // 🚴 CÉDER AUX CYCLISTES
      // ==================================================

      else if (
        data.turnDir === "right" &&
        !data.turnCommitted
      ) {

        const posYield =
          data.turnCurve.getPoint(yieldT)

        const conflict =
          cyclistConflictAt(
            posYield.x,
            posYield.z
          )

        // ==================================================
        // 🚴 STOP STABLE AU YIELD
        // ==================================================

        if (conflict) {

          data.waitingForYield = true

          data.roadDrift = 0
          data.roadDriftTarget = 0

          // ==================================================
          // 🚗 STOP SMOOTH AU YIELD
          // ==================================================

          nextT = Math.min(
            nextT,
            yieldT
          )
        }

        // ==================================================
        // 🚗 PLUS DE CONFLIT
        // ==================================================

        else {

          if (data.waitingForYield) {
            data.waitingForYield = false
          }

          if (nextT >= yieldT) {
            data.turnCommitted = true
          }
        }
      }

      // ==================================================
      // 🚗 APPLICATION
      // ==================================================

      data.turnT = nextT

      const t = data.turnT

      const pos =
        data.turnCurve.getPoint(t)

      car.position.copy(pos)

      // ==================================================
      // 🎯 ROTATION
      // ==================================================

      let targetRot

      if (t < yieldT) {

        const k = t / yieldT

        targetRot =
          startRot + angle1 * k

      } else {

        const k =
          (t - yieldT) / (1 - yieldT)

        targetRot =
          startRot +
          angle1 +
          angle2 * k
      }

      let current = car.rotation.y

      let delta =
        targetRot - current

      delta =
        Math.atan2(
          Math.sin(delta),
          Math.cos(delta)
        )

      // ==================================================
      // 🚗 ROTATION ADAPTATIVE
      // ==================================================

      let rotLerp

      if (t < yieldT) {

        // ⭐ début du virage :
        // rotation très fluide immédiate

        rotLerp = 1 - Math.exp(-12 * dt)

      } else {

        // ⭐ fin du virage :
        // rotation plus douce

        rotLerp = 1 - Math.exp(-6 * dt)
      }

      car.rotation.y += delta * rotLerp

      // ==================================================
      // 🏁 FIN DU VIRAGE
      // ==================================================

      if (t >= 1) {

        car.rotation.y =
          startRot + totalAngle

        data.turning = false
        data.turned = true
        data.turnCurve = null
        data.turnCommitted = false
        data.waitingForYield = false

        const prevX = data.x

        data.exitDirX =
          Math.sign(pos.x - prevX) || -1

        data.x =
          pos.x - (data.offsetX || 0)

        data.z = pos.z
      }

      finishFrame()
      return
    }


    // ==================================================
    // VOITURE SORTIE DE LA ROUTE APRÈS VIRAGE
    // ==================================================

    if (data.turned) {

      const speed = SPEED * 0.7 * dt

      const dirX = data.exitDirX ?? -1   // 🔥 direction réelle

      data.x += dirX * speed

      car.rotation.y = dirX > 0 ? -Math.PI / 2 : Math.PI / 2

      

      if (data.x < routeX - routeW - 20 || data.x > routeX + routeW + 20) {
        respawnFar(data, car)
      }

      finishFrame()
      return
    }

    
    // ==================================================
    // 🚦 FEUX ROUGES — VERSION BIDIRECTIONNELLE STABLE
    // ==================================================

    if (!data.turning) {

      let closestLight = null
      let bestDist = Infinity

      // 🔍 feu devant selon la direction
      for (const light of lights) {

        const dist = light.position[2] - data.z

        const isAhead =
          data.direction === -1
            ? dist < 0   // trafic vers −Z
            : dist > 0   // trafic vers +Z

        if (!isAhead) continue

        const absDist = Math.abs(dist)

        if (absDist < bestDist) {
          bestDist = absDist
          closestLight = light
        }
      }

      if (closestLight) {

        // ⭐ ligne d'arrêt dans le bon sens
        // trouver l'intersection correspondante
        const inter = intersections.find(i => Math.abs(i.z - closestLight.position[2]) < 5 )

        const halfWidth = inter ? inter.width * 0.5 : 0
        const vehicleHalfLength =
          data.halfLength || VEHICLE_LENGTH_CACHE[data.vehicleName] || 6
        const stopBuffer = vehicleHalfLength + 2.5
        const dynamicStopDistance = STOP_DISTANCE + vehicleHalfLength * 1.5

        const stopZ =
          data.direction === -1
            ? closestLight.position[2] + halfWidth + STOP_LINE_OFFSET + stopBuffer
            : closestLight.position[2] - halfWidth - STOP_LINE_OFFSET - stopBuffer

        const distToLine =
          data.direction === -1
            ? data.z - stopZ
            : stopZ - data.z

        const STOP_EPS = Math.max(0.75, Math.min(2, vehicleHalfLength * 0.15))
        const atLine = Math.abs(distToLine) <= STOP_EPS

        // 🚦 surveiller si proche OU déjà en freinage
        if ((distToLine > 0 && distToLine < dynamicStopDistance) || data.brakingForLight){

          // 🔴 ROUGE / ORANGE
          if (closestLight.state === "red") {

            
            if (distToLine < 0) {
              const overshoot = Math.abs(distToLine)
              const lateOvershootLimit = Math.max(6, vehicleHalfLength + 2)

              if (!data.committedToCross && overshoot <= lateOvershootLimit) {
                data.z = stopZ
                data.speed = 0
                data.brakingForLight = true

                finishFrame()
                return
              }

              data.committedToCross = true
              data.brakingForLight = false
              data.speed = Math.max(data.speed, SPEED * 0.4) // ⭐ relance

              finishFrame()
              return
            }

            // 🛑 arrêter seulement si pas engagée
            if (atLine && distToLine > 0 && !data.committedToCross) {

              data.z += (stopZ - data.z) * Math.min(1, 10 * dt)
              data.speed = THREE.MathUtils.lerp(data.speed, 0, 8 * dt)
              data.brakingForLight = true

              finishFrame()
              return
            }

            // 🧠 freinage progressif
            const brakingDist =
              (data.speed * data.speed) / (2 * MAX_BRAKE)

            const MUST_STOP =
              brakingDist >= distToLine - 1.5

          if (MUST_STOP && distToLine > STOP_EPS) {

      data.brakingForLight = true

      data.speed = THREE.MathUtils.lerp(
        data.speed,
        0,
        0.25
      )

      const nextZ =
        data.direction === -1
          ? data.z - data.speed * dt
          : data.z + data.speed * dt

      // ⭐ CAS 1 — arrêt à la ligne
      // ⭐ ne jamais arrêter si la voiture a déjà franchi la ligne
    const passedLine =
      data.direction === -1
        ? data.z <= stopZ
        : data.z >= stopZ

    if (
      !data.committedToCross &&
      !passedLine &&
      (
        data.direction === -1
          ? nextZ <= stopZ
          : nextZ >= stopZ
      )
    ) {
      data.z += (stopZ - data.z) * Math.min(1, 10 * dt)
      data.speed = THREE.MathUtils.lerp(data.speed, 0, 8 * dt)
    }
    else {
      data.z = nextZ
    }
    // ==================================================
    // 🚦 CLAMP ABSOLU FEU ROUGE
    // ==================================================

    const crossedStopLine =
      data.direction === -1
        ? data.z < stopZ
        : data.z > stopZ

    if (!data.committedToCross && crossedStopLine) {
      data.z = stopZ
      data.speed = 0
      data.brakingForLight = true
    }
      finishFrame()
      return
    }
        } else {
          // 🟢 FEU VERT → repartir
          data.brakingForLight = false 
        }
      }
    }
  }
  
  // ==================================================
  // DISTANCE AVEC VOITURE DEVANT — FILE D’ATTENTE STABLE
  // ==================================================
  if (!data.turning) {
    // ==================================================
// 🚗 BUCKET PAR VOIE/DIRECTION (OPTIMISÉ)
// ==================================================

const key =
  `${data.direction}_${data.laneIndex}`

if (!group.current.userData.laneBuckets) {

  const buckets = {}

  for (let k = 0; k < cars.length; k++) {

    const c = cars[k]

    if (!c || c.turned) continue

    const bucketKey =
      `${c.direction}_${c.laneIndex}`

    if (!buckets[bucketKey]) {
      buckets[bucketKey] = []
    }

    buckets[bucketKey].push(c)
  }

  // ==================================================
  // TRI Z
  // ==================================================

  for (const b in buckets) {

    buckets[b].sort((a, b) => a.z - b.z)
  }

  group.current.userData.laneBuckets = buckets
}

const laneCars =
  group.current.userData.laneBuckets[key] || []

let frontCar = null
let frontDist = Infinity

// ==================================================
// 🚗 TROUVER VOITURE DEVANT
// ==================================================

for (let j = 0; j < laneCars.length; j++) {

  const o = laneCars[j]

  if (o === data) continue

  const dz = o.z - data.z

  if (data.direction === -1) {

    if (dz < 0) {

      const dist = -dz

      if (dist < frontDist) {

        frontDist = dist
        frontCar = o
      }
    }

  } else {

    if (dz > 0 && dz < frontDist) {

      frontDist = dz
      frontCar = o
    }
  }
}
    if (frontCar) {

      const gap = frontDist
      const TARGET = getSafeDistance(data, frontCar)

      // --------------------------------------------------
      // 🚫 SI LA VOITURE DEVANT EST ARRÊTÉE → STOP FILE
      // --------------------------------------------------

      if (frontCar.speed < 0.5 || frontCar.brakingForLight) {

        const desiredZ =
          frontCar.z + TARGET * data.direction

        // ne jamais dépasser la position cible
        if (
          (data.direction === -1 && data.z <= desiredZ) ||
          (data.direction === +1 && data.z >= desiredZ)
        ) {
          data.z += (desiredZ - data.z) * Math.min(1, 10 * dt)
          data.speed = THREE.MathUtils.lerp(data.speed, 0, 8 * dt)
          finishFrame()
          return
        }
      }

      // --------------------------------------------------
      // RALENTISSEMENT NORMAL
      // --------------------------------------------------

      const SLOW_ZONE = TARGET * 2.5

      if (gap < SLOW_ZONE) {

        const t = THREE.MathUtils.clamp(
          (gap - TARGET) / (SLOW_ZONE - TARGET),
          0,
          1
        )

        let targetSpeed = SPEED * t
        if (!Number.isFinite(targetSpeed)) {
          targetSpeed = 0
        }
        data.speed = THREE.MathUtils.lerp(
          data.speed,
          targetSpeed,
          0.15
        )
        
        if (gap < TARGET) data.speed = THREE.MathUtils.lerp(data.speed, 0, 8 * dt)
      }
    }

    // ==================================================
      // 🚧 CONTRAINTE ABSOLUE ANTI-COLLISION (CRITIQUE)
      // ==================================================

      if (frontCar) {

        const TARGET = getSafeDistance(data, frontCar)

        // position maximale autorisée
        const limitZ = frontCar.z + TARGET * data.direction

        if (data.direction === -1) {

          // ne jamais dépasser la voiture devant
          if (data.z <= limitZ) {
            data.z = limitZ
            data.speed = Math.min(data.speed, frontCar.speed)
            finishFrame()
            return
          }

        } else {

          if (data.z >= limitZ) {
            data.z = limitZ
            data.speed = Math.min(data.speed, frontCar.speed)
            finishFrame()
            return
          }
        }
      }

    // --------------------------------------------------
  // ⭐ SUIVI DE LA VOITURE DEVANT (STABLE)
  // --------------------------------------------------

  if (frontCar) {

    let TARGET = getSafeDistance(data, frontCar)
    if (frontCar.turning) TARGET *= 0.45

    const gap = Math.abs(frontCar.z - data.z)

    // trop proche
    if (gap <= TARGET + 0.05) {

      // --------------------------------------------------
      // 🚗 DEVANT ARRÊTÉ → STOP
      // --------------------------------------------------

      if (frontCar.speed < 0.5 || frontCar.brakingForLight) {
        data.speed = 0
        finishFrame()
        return
      }

      // --------------------------------------------------
      // 🚗 DEVANT ROULE → SUIVRE SA VITESSE
      // --------------------------------------------------

      data.speed = frontCar.speed

      // ne pas dépasser
      const desiredZ = frontCar.z + TARGET * data.direction

      const reached =
        (data.direction === -1 && data.z <= desiredZ) ||
        (data.direction === +1 && data.z >= desiredZ)

      if (reached) {
        data.z += (desiredZ - data.z) * Math.min(1, 10 * dt)
      }
      finishFrame()
      return
    }
  }
  }
  
  // ==================================================
  // DEPLACEMENT NORMAL
  // ==================================================
  
  if (!data.brakingForLight ) {
    data.speed = THREE.MathUtils.lerp(data.speed, SPEED, 2 * dt)
  }
  data.speed = THREE.MathUtils.clamp(data.speed, 0, SPEED)

  if (!data.turned) {
    data.z += data.speed * dt * data.direction
    const communeOffset = getCommuneOffset(data.z)

    data.x = lanesConfig[data.laneIndex].x + data.laneOffset //+ communeOffset
  }

  // ==================================================
  // 🚗 GLISSEMENT APRÈS CRÉMAZIE (VOIE DROITE)
  // ==================================================

  // ==================================================
  // 🚗 MERGE CRÉMAZIE (SEULEMENT SI SPAWN AVANT)
  // ==================================================

  if (
    data.direction === -1 &&
    !data.turning &&
    !data.turned &&
    !data.brakingForLight &&
    data.speed > 0.5
  ){

    const INTERSECTION_LENGTH = 50
    const startZ = CREMAZIE_Z + INTERSECTION_LENGTH
    const endZ   = CREMAZIE_Z

    if (data.z < startZ && data.z > endZ) {

      const t = THREE.MathUtils.clamp(
        (startZ - data.z) / INTERSECTION_LENGTH,
        0,
        1
      )

      const tSmooth = t * t * (3 - 2 * t)

      data.visualOffsetX = -MARGE_ROUTE * tSmooth

    } else if (data.z <= endZ) {

      data.visualOffsetX = -MARGE_ROUTE

    } else {

      data.visualOffsetX = 0
    }
  }
  // ==================================================
  // INTERSECTION CRÉMAZIE — VERSION FINALE FIABLE
  // ==================================================

  const inCremazieZone =
  !data.turning &&
  !data.passedCremazie &&
  Math.abs(data.z - CREMAZIE_Z) < 25

const closeToIntersection = Math.abs(data.z - CREMAZIE_Z) < 3

  if (
    inCremazieZone &&
    (
      !data.committedToCross ||   // cas normal
      closeToIntersection         // 🔥 exception CRITIQUE
    )
  ){
  // --------------------------------------------------
  // FEU ROUGE → ARRÊT (trafic vers joueur seulement)
  // --------------------------------------------------

  if (data.direction === -1) {

    let bestDist = Infinity

    for (const light of lights) {

      const d = Math.abs(light.position[2] - data.z)

      if (d < bestDist && d < 40) {
        bestDist = d
      }
    }

    // 🔴 FEU ROUGE → ARRÊT ABSOLU
    let lightState = "green"

    for (const light of lights) {
      const d = Math.abs(light.position[2] - data.z)
      if (d < 12) {
        lightState = light.state
        break
      }
    }

    if (lightState === "red") {
      finishFrame()
      return
    }
    data.passedCremazie = true
  }

  // ==================================================
  // 🚗 TRAFIC VERS JOUEUR (dir = -1)
  // ==================================================

  if (data.direction === -1) {

    // ⭐ voie droite → continue
    if (data.laneIndex === 2) {

      data.originLaneCount = 2
      data.keepX = true
      data.passedCremazie = true

      // ==================================================
      // 🚗 INIT GLISSEMENT VISUEL (NE TOUCHE PAS À data.x)
      // ==================================================

      if (data.visualOffsetX === undefined) {
        data.visualOffsetX = 0
      }

      // cible vers la gauche
      data.targetOffsetX = -MARGE_ROUTE
    }

    // ⭐ autres voies → virage gauche
    else {

      data.turning = true
      data.turnCommitted = true
      data.turnDir = "left"
      data.turnT = 0
      data.turnStartRot = car.rotation.y
      data.fromCremazieLeft = true

      

      const laneX = car.position.x

      data.turnCurve =
        new THREE.CubicBezierCurve3(
          new THREE.Vector3(laneX, 0, data.z),
          new THREE.Vector3(laneX - 2, 0, data.z - 8),
          new THREE.Vector3(laneX - routeW * 0.7, 0, CREMAZIE_Z - 8),
          new THREE.Vector3(laneX - routeW * 0.7, 0, CREMAZIE_Z)
        )

      finishFrame()
      return
    }
  }

  // ==================================================
  // 🚗 TRAFIC OPPOSÉ (dir = +1)
  // TOURNE À GAUCHE ET SORT À GAUCHE
  // ==================================================

  else if (data.direction === +1 ) {

    data.turning = true
    data.turnDir = "right"
    data.turnT = 0
    data.turnStartRot = car.rotation.y
    data.turnOpposite = false   // ⭐ IMPORTANT
    data.fromOppositeTurn = true
    const laneX = car.position.x

    // courbe vers −X (gauche écran)
    data.turnCurve =
      new THREE.CubicBezierCurve3(
        new THREE.Vector3(laneX, 0, data.z),
        new THREE.Vector3(laneX - 2, 0, data.z + 8),
        new THREE.Vector3(laneX - routeW * 0.7, 0, CREMAZIE_Z + 8),
        new THREE.Vector3(laneX - routeW * 0.7, 0, CREMAZIE_Z)
      )

    finishFrame()
    return
  }
  }
  // ==================================================
  // VIRAGE À DROITE — INTERSECTIONS NORMALES (FIX)
  // ==================================================

  const nearCremazie = Math.abs(data.z - CREMAZIE_Z) < TURN_TRIGGER_ZONE + 5
  const rightTurnLaneIndex = getRightTurnLaneIndex()

  if (
    !nearCremazie &&
    data.direction === -1 &&
    rightTurnLaneIndex >= 0 &&
    data.laneIndex === rightTurnLaneIndex &&
    !isBehindPlayerForward &&
    !isFarBehind &&
    !data.turning &&
    !data.turned
  ) {

    for (let k = 0; k < intersections.length; k++) {

      const inter = intersections[k]

      if (!isVehicleTrafficIntersectionEnabled(inter)) {
        continue
      }

      const dz = data.z - inter.z
      const forceRightTurn = TRAFFIC_CAR_FORCE_RIGHT_TURNS.has(inter.id)
      const blockRightTurn = TRAFFIC_CAR_BLOCK_RIGHT_TURNS.has(inter.id)

      if (dz < -TURN_TRIGGER_ZONE) continue

      if (dz < TURN_START_DISTANCE && dz > 0) {

      // --------------------------------------------------
      // Vérifier feu
      // --------------------------------------------------

      let canTurn = true

      // 🚦 feu
      for (const light of lights) {
        const dLight = Math.abs(inter.z - light.position[2])
        if (dLight < 12) {
          if (light.state !== "green") {
            canTurn = false
          }
          break
        }
      }

      // 🚫 règle intersection
      if (blockRightTurn || (!forceRightTurn && inter.direction === -1)) {
        canTurn = false
      }

      if (!canTurn) {
        break
      }

      if (data.lastTestedIntersection !== k) {

        data.lastTestedIntersection = k

        if (forceRightTurn || Math.random() < TURN_PROBABILITY) {


          // ==================================================
          // 🚗 VIRAGE AUTORISÉ
          // ==================================================

          data.turning = true
          data.turnDir = "right"
          data.turnStarted = true
          data.forceFinishTurn = false
          data.turnStartRot = car.rotation.y
          data.turnT = 0
          data.preparingTurn = true
          data.slowing = false
          data.turnStartRot = car.rotation.y
        
          const start = new THREE.Vector3(car.position.x, 0, data.z)

          const c1 = new THREE.Vector3(data.x + 2, 0, data.z - 6)
          const c2 = new THREE.Vector3(data.x + inter.width * 0.6, 0, inter.z - 6)
          const end = new THREE.Vector3(data.x - (data.offsetX || 0) + inter.width * 0.6, 0, inter.z)

          data.turnCurve = new THREE.CubicBezierCurve3(start, c1, c2, end)
          
        }
      }

      break
    }
  }
  
} 

// ==================================================
// RECYCLAGE STABLE — VERSION FINALE CORRIGÉE
// ==================================================

let shouldRespawn = false

if (data.direction === -1) {
  // Vers -z
  if (visibleZ < -RECYCLE_FRONT || visibleZ > RECYCLE_BACK) {
    shouldRespawn = true
  }
} else {
  // Vers +z
  // 🔥 NE despawn QUE trop loin derrière
  if (visibleZ > RECYCLE_BACK) {
    shouldRespawn = true
  }
}

if (shouldRespawn) {
  respawnFar(data, car)
  return
}

//-------------------------------------------------------
    // ==================================================
    // 🚗 OFFSET X ALÉATOIRE
    // ==================================================

    if (
      data.direction === -1 &&
      !data.turning &&
      !data.turned
    ) {

      data.roadDriftTimer -= dt

      if (data.roadDriftTimer <= 0) {

        data.roadDriftTimer = 2 + Math.random() * 5
        data.roadDriftTarget = (Math.random() - 0.5) * 5
      }

    } else {

      data.roadDriftTarget = 0
      if (data.turning) {
        data.roadDrift = 0
      }
    }

    data.roadDrift += (data.roadDriftTarget - data.roadDrift) * (1 - Math.exp(-0.7 * dt))

    finishFrame()
  })

})

  // ==================================================
  // RENDER
  // ==================================================

  return (
    <>
    <group ref={textGroup} />
    <group ref={(node) => {
      group.current = node
      if (ref) ref.current = node
    }}>

     {cars.map((c) => (

          <group
            key={c.id}              
            visible={Math.abs(c.z + worldGroup.current?.position.z) > -400}
            position={[0, 0, 0]}
            userData={c}           
          >
          <GLBVehicle
            name={c.vehicleName}
            reverse={false}
            speed={c.speed ?? 0}
          />
        </group>

      ))}

    </group>
    </>
  )
})


const StaticBikeLaneCars = React.forwardRef(
({ pisteX, pisteW, density = 0.4, difficulte = 2 }, ref) => {

  const { worldGroup, level, restartVersion } = useGame()
  const worldZ = worldGroup?.current?.position.z || 0
  const group = useRef()
  const cars = useRef([])
  const carRefs = useRef([]) // 🔥 FIX PRINCIPAL
  const [, forceStaticCarRender] = useState(0)
  const MAX_CARS = 20
  const SIDE_OFFSET = pisteW * 0.3
  const INTERSECTION_MARGIN = 20

  useImperativeHandle(ref, () => group.current)

  // --------------------------------------------------
  // COUNT
  // --------------------------------------------------

  const getTargetCount = () => {
    const base = 1 + Math.floor(level * 0.4)
    const difficultyFactor = [0.7, 1.0, 1.4][difficulte - 1]
    const final = Math.floor(base * density * difficultyFactor)

    return Math.min(MAX_CARS, Math.max(1, final))
  }

  // --------------------------------------------------
  // VALIDATION Z
  // --------------------------------------------------

  function isValidZ(z) {
    return !intersections.some(inter => {
      const half = inter.width / 2 + INTERSECTION_MARGIN
      return z >= inter.z - half && z <= inter.z + half
    })
  }

  function pickStaticVehicle(excludedVehicle = null) {
    const pool = PARKED_VEHICULES.filter(name => name !== excludedVehicle)
    const source = pool.length ? pool : PARKED_VEHICULES

    return source[Math.floor(Math.random() * source.length)]
  }

  // --------------------------------------------------
  // SPAWN
  // --------------------------------------------------

  const spawn = (zOverride = null, excludedVehicle = null) => {

  let z

  if (zOverride !== null) {
    z = zOverride
  } else {

    let valid = false

    while (!valid) {

      z = -worldZ - (220 + Math.random() * 300)

      valid = isValidZ(z)
    }
  }

  const isSide = Math.random() < 0.4

  return {
    x: isSide
      ? pisteX + SIDE_OFFSET
      : pisteX + (Math.random() - 0.5) * (pisteW * 0.5),

    z,

    rotY: isSide
      ? Math.PI / 2
      : (Math.random() < 0.5 ? 0 : Math.PI),

    vehicleName: pickStaticVehicle(excludedVehicle)
  }
}

  // --------------------------------------------------
  // INIT
  // --------------------------------------------------

  useEffect(() => {

    cars.current = []

    const target = getTargetCount()

    for (let i = 0; i < target; i++) {

      let z

      if (i === 0) {
        z = -worldZ - (30 + Math.random() * 50)
      } else {
        z = -worldZ - (150 + i * 140 + Math.random() * 80)
      }

      const safeZ = isValidZ(z) ? z : -worldZ - 200

      cars.current.push(spawn(safeZ))
    }

  }, [restartVersion])

  // --------------------------------------------------
  // ADAPTATION
  // --------------------------------------------------

  useEffect(() => {

    const target = getTargetCount()

    if (cars.current.length < target) {
      for (let i = cars.current.length; i < target; i++) {
        const z = -worldZ - (200 + i * 140)
        cars.current.push(spawn(z))
      }
    }

    if (cars.current.length > target) {
      cars.current.splice(target)
    }

  }, [level, difficulte, density])

  // --------------------------------------------------
  // UPDATE
  // --------------------------------------------------

  useFrame(() => {

    for (let i = 0; i < cars.current.length; i++) {

      const data = cars.current[i]
      const car = carRefs.current[i]

      if (!data || !car) continue

      const visibleZ = data.z + worldZ

      // respawn
      if (visibleZ > 80) {
        const n = spawn(null, data.vehicleName)
        const vehicleChanged = n.vehicleName !== data.vehicleName

        Object.assign(data, n)

        if (vehicleChanged) {
          forceStaticCarRender(v => v + 1)
        }
      }

      // 🔥 POSITION DIRECTE SUR LE BON OBJET
      car.position.set(data.x, 0, data.z)
      car.rotation.y = data.rotY
    }

  })

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <group ref={group}>
      {cars.current.map((c, i) => (
        <group
          key={i}
          ref={el => (carRefs.current[i] = el)} // 🔥 FIX CRITIQUE
        >
          <GLBVehicle key={c.vehicleName} name={c.vehicleName} isStatic />
        </group>
      ))}
    </group>
  )
})
const ParkedStreetCars = React.forwardRef(
({ routeX, routeW, count = 6  }, ref) => {

  const { worldGroup, departOption, restartVersion } = useGame()
  const worldZ = worldGroup?.current?.position.z || 0
  const group = useRef()
  const [cars, setCars] = useState([])
  const carsRef = useRef([])
  const CAR_GAP = 0.15
  const INTERSECTION_MARGIN = 10
  const RIGHT_OFFSET = routeW * 0.38
  const LEFT_OFFSET = routeW * -0.32
  const LOOP_DISTANCE = 800
  let NEXT_PARKED_CAR_ID = 1

  // ==================================================
  // 🔥 seuil niveau 14
  // ==================================================

  const LEVEL14_START_Z = getLevelStartZ(14)

  // ==================================================
  // 🔥 autoriser uniquement
  // avant niveau 14
  // ==================================================

  const CAN_SPAWN = (z) => z > LEVEL14_START_Z

  // ==================================================
  // 🔥 intersections
  // ==================================================

  function adjustZOutsideIntersection(z) {

    for (const inter of intersections) {

      if (inter.z > CREMAZIE_Z) continue

      const half = inter.width / 2 + INTERSECTION_MARGIN

      if (z >= inter.z - half && z <= inter.z + half) {

        return (inter.z - half - 5)
      }
    }

    return z
  }

  // --------------------------------------------------
  // INIT
  // --------------------------------------------------

  useEffect(() => {

    if (!worldGroup?.current)
      return

    const id = requestAnimationFrame(() => {

      const newCars = []

      // ================================================
      // POSITION JOUEUR
      // ================================================

      const currentWorldZ = worldGroup?.current?.position?.z || 0
      const playerZ = -currentWorldZ
      let zCursor

      // ================================================
      // APRÈS CRÉMAZIE
      // ================================================

      if (playerZ < CREMAZIE_Z) {

        // spawn proche du joueur
        zCursor = Math.abs(playerZ) + 20

      } else {

        // avant Crémazie

        zCursor = Math.abs(CREMAZIE_Z) + 30
      }

      for (let i = 0; i < count; i++) {

        const worldSpawnZ = -Math.abs(zCursor)

        // ================================================
        // STOP NIVEAU 14
        // ================================================

        if (!CAN_SPAWN(worldSpawnZ)) {
          break
        }

        const isRight = (i % 2 === 0)
        const x = isRight ? routeX + RIGHT_OFFSET : routeX + LEFT_OFFSET
        const rotY = isRight ? 0 : Math.PI

        // ================================================
        // VRAIES DIMENSIONS GLB
        // ================================================

        const vehicleName = VEHICLE_NAMES[Math.floor(Math.random() * VEHICLE_NAMES.length)]
        const halfLength = VEHICLE_LENGTH_CACHE[vehicleName] || 6
        const length = halfLength * 2

        // ================================================
        // ESPACEMENT
        // ================================================

        const parkingFactor = 0.7
        const spacing = (length * parkingFactor) + CAR_GAP

        newCars.push({
          id: NEXT_PARKED_CAR_ID++,
          x,
          z: worldSpawnZ,
          rotY,
          length,
          vehicleName,
          initialized: false
        })

        // ================================================
        // RÉPARTITION VERS LOIN DEVANT
        // ================================================

        zCursor += spacing

        const adjusted = adjustZOutsideIntersection(-zCursor)

        zCursor = -adjusted
      }

      carsRef.current = newCars
      setCars(newCars)

    })

    return () => {

      cancelAnimationFrame(id)
    }

  }, [count, routeX, routeW, departOption, restartVersion])

  // --------------------------------------------------
  // UPDATE
  // --------------------------------------------------

  useFrame(() => {

    if (!group.current)
      return

    const currentWorldZ = worldGroup.current?.position.z || 0
    const playerZ = -currentWorldZ
    const children = group.current.children

    children.forEach((car, i) => {

      const data = carsRef.current[i]

      if (!data)
        return

      const visibleZ = data.z - (-worldZ)

      car.visible = visibleZ > -300 && visibleZ < 60

      // ==================================================
      // 🔥 VRAIES DIMENSIONS GLB
      // ==================================================

      const realHalfLength =

        car.children[0]
          ?.userData
          ?.halfLength

      if (realHalfLength) {

        data.length =
          realHalfLength * 2
      }

      // --------------------------------------------------
      // 🔥 VISIBILITÉ
      // --------------------------------------------------

      if (
        !CAN_SPAWN(data.z)
      ) {

        car.visible = false

        return

      } else {

        car.visible = true
      }

      // ================================================
      // LAISSER LES AUTOS INITIALES
      // VIVRE LEUR POSITION
      // ================================================

      if (!data.initialized) {

        const dist =

          Math.abs(
            data.z - (playerZ)
          )

        // quand le joueur approche
        // l’auto devient recyclable

        if (dist < 400) {

          data.initialized = true

        } else {

          // IMPORTANT :
          // ne pas recycler encore

          car.position.set(data.x, 0, data.z)
          car.rotation.y = data.rotY

          return
        }
      }

      // --------------------------------------------------
      // 🔥 FORWARD
      // recycle derrière joueur
      // --------------------------------------------------

      if (data.z > playerZ + 100) {

        let minZ = Infinity
        let minCar = null

        carsRef.current.forEach(c => {

          if (c === data)
            return

          if (c.z < minZ) {

            minZ = c.z
            minCar = c
          }
        })

        const newLength = data.length || 6
        const otherLength = minCar?.length || 6

        let newZ

        if (isFinite(minZ)) {

          const parkingFactor =  0.32

          newZ =
            minZ - (

              newLength *
              parkingFactor +

              otherLength *
              parkingFactor +

              CAR_GAP
            )

        } else {

          // ================================================
          // 🔥 RESPAWN LOIN DEVANT
          // ================================================

          newZ = playerZ- 2200
        }

        newZ = adjustZOutsideIntersection(newZ)

        // ================================================
        // 🔥 PAS AVANT CRÉMAZIE
        // ================================================

        if (newZ > CREMAZIE_Z) {

          newZ = CREMAZIE_Z - 50
        }

        // ================================================
        // 🔥 STOP NIVEAU 14
        // ================================================

        if (!CAN_SPAWN(newZ)) {

          return
        }

        data.z = newZ
      }

      // --------------------------------------------------
      // 🔥 BACKWARD
      // recycle très loin devant
      // --------------------------------------------------

      if (data.z < playerZ - LOOP_DISTANCE) {

        let maxZ = -Infinity
        let maxCar = null

        carsRef.current.forEach(c => {

          if (c === data)
            return

          if (c.z > maxZ) {

            maxZ = c.z
            maxCar = c
          }
        })

        const newLength = data.length || 6
        const otherLength = maxCar?.length || 6

        let newZ

        if (!isFinite(maxZ) || maxZ < playerZ - LOOP_DISTANCE / 2) {

          newZ = playerZ - 200

        } else {

          // ================================================
          // 🔥 GAP DYNAMIQUE
          // ================================================

          const dynamicGap =

            CAR_GAP +

            (
              (
                newLength +
                otherLength
              ) * 0.08
            )

          newZ = maxZ + (newLength * 0.5 + otherLength * 0.5 + dynamicGap)
        }

        newZ = adjustZOutsideIntersection(newZ)

        // ================================================
        // 🔥 PAS AVANT CRÉMAZIE
        // ================================================

        if (newZ > CREMAZIE_Z) {

          newZ = CREMAZIE_Z - 50
        }

        // ================================================
        // 🔥 STOP NIVEAU 14
        // ================================================

        if (!CAN_SPAWN(newZ)) {

          return
        }

        data.z = newZ
      }

      // --------------------------------------------------
      // POSITION
      // --------------------------------------------------

      const isRight =  (i % 2 === 0)

      data.x = isRight
        ? routeX + RIGHT_OFFSET
        : routeX + LEFT_OFFSET

      data.rotY = isRight ? 0 : Math.PI

      // --------------------------------------------------
      // APPLY
      // --------------------------------------------------

      car.position.set(

        data.x,

        0,

        data.z
      )

      car.rotation.y =
        data.rotY

    })

  })

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (

    <group
      ref={(node) => {
        group.current = node

        if (ref)
          ref.current = node
      }}
    >
      {cars.map((c, i) => (
        <group key={c.id} rotation={[0, c.rotY, 0]}>
          <GLBVehicle
            name={c.vehicleName}
            isStatic
          />
        </group>
      ))}
    </group>
  )
})
function PietonsTest() {
  // piétons
  const walkers = [
    {
      url: "/modeles/womanA.glb",
      scale: 1.6,
      offset: [0, 0, 0],
    },

    {
      url: "/modeles/womanB.glb",
      scale: 1.6,
      offset: [0, 0, 0],
    },

    {
      url: "/modeles/womanC.glb",
      scale: 1.6,
      offset: [0, 0, 0],
    },

    {
      url: "/modeles/manA.glb",
      scale: 1.6,
      offset: [0, 0, 0],
    },

    {
      url: "/modeles/manB.glb",
      scale: 1.6,
      offset: [0, 0, 0],
    },

    {
      url: "/modeles/manC.glb",
      scale: 1.6,
      offset: [0, 0, 0],
    },

    {
      url: "/modeles/girl.glb",
      scale: 1.8,
      offset: [-0.5, 0, 0],
    },

    {
      url: "/modeles/boy.glb",
      scale: 1.8,
      offset: [-1, 0, 0],
    },

    {
      url: "/modeles/baby.glb",
      scale: 1.9,
      offset: [-2, 0.1, 0],
    },
  ]

  // cyclistes
  const bikers = [
    {
      url:
        "/modeles/manC_bike.glb",
      scale: 2.5,
    },
    {
      url:
        "/modeles/womanA_bike.glb",

      scale: 1.6,
    },

    {
      url:
        "/modeles/boy_bike.glb",

      scale: 1.8,
    },
     {
      url:
        "/modeles/sportManA.glb",

      scale: 1.8,
    },
    {
      url:
        "/modeles/sportManB.glb",

      scale: 1.8,
    },
    {
      url:
        "/modeles/sportWomanA.glb",

      scale: 1.8,
    },
    {
      url:
        "/modeles/sportWomanB.glb",

      scale: 1.8,
    },
    {
      url:
        "/modeles/sportWomanC.glb",

      scale: 1.8,
    },
  ]

  return (
    <>
      {/* rangée piétons */}
      {walkers.map((p, i) => (
        <Pedestrian
          key={p.url}
          url={p.url}
          scale={p.scale}
          offset={p.offset}
          bikeMode={false}
          position={[-30 + i * 3,0, -40]}
        />
      ))}

      {/* rangée vélos */}
      {bikers.map((p, i) => (
        <Pedestrian
          key={p.url}
          url={p.url}
          scale={p.scale}
          offset={p.offset}
          bikeMode={true}
          position={[-30 + i * 3, 0, -45]}
          rotation={[0, 0,0]}
        />
      ))}
    </>
  )
}

function Pedestrian({
  url,
  scale,
  position,
  rotation,
  offset,
  bikeMode,
}) {
  
  const { scene, animations } = useGLTF(url)
  const clone = useMemo(() => {
    
  const c = SkeletonUtils.clone(scene)

  // ROOT
  c.layers.set(0)

  c.traverse(obj => {

    // TOUS les objets
    obj.layers.set(0)

    if (!obj.isMesh) return

    obj.castShadow = false
    obj.receiveShadow = false
  })

  return c

}, [scene])

  const { actions, names } =  useAnimations(animations, clone)

  useEffect(() => {
    if (!names.length) return

    let currentAction = null

    // =====================================
    // CYCLISTES
    // =====================================

    if (bikeMode) {
 
      const playing = []

      names.forEach((name) => {
        const lower =
          name.toLowerCase()

        // jouer toutes les animations vélo
        if (
          lower.includes(
            "bike"
          ) ||
          lower.includes(
            "riding"
          )
        ) {
          const action = actions[name]

          if (!action) return

          action.reset()
          action.fadeIn(0.3)
          action.setLoop(THREE.LoopRepeat)
          action.play()
          playing.push(name)
        }
      })

      return () => {
        Object.values(actions).forEach(
          (action) => {
            action.stop()
          }
        )
      }
    }

    // =====================================
    // PIÉTONS
    // =====================================

    const validAnimations =
      names.filter((n) => {
        const lower =
          n.toLowerCase()

        // éviter idle et vélo
        if (
          lower.includes(
            "idle"
          ) ||
          lower.includes(
            "bike"
          ) ||
          lower.includes(
            "riding"
          )
        ) {
          return false
        }

        return true
      })

    if (!validAnimations.length)
      return

    function playAnimation(
      name
    ) {
      if (currentAction) {
        currentAction.fadeOut(0.3)
      }

      const action = actions[name]

      if (!action) return

      currentAction = action
      action.reset()
      action.fadeIn(0.3)
      action.setLoop(THREE.LoopRepeat)
      action.play()
    }

    // première animation
    let currentName =
      validAnimations[
        Math.floor(
          Math.random() *
            validAnimations.length
        )
      ]

    playAnimation(currentName)

    // changer animation
    const interval =
      setInterval(() => {
        const available =
          validAnimations.filter(
            (n) =>
              n !== currentName
          )

        if (!available.length)
          return

        currentName =
          available[
            Math.floor(
              Math.random() *
                available.length
            )
          ]

        playAnimation(
          currentName
        )
      }, 4000)

    return () => {
      clearInterval(interval)

      Object.values(actions).forEach(
        (action) => {
          action.stop()
        }
      )
    }
  }, [
    actions,
    names,
    bikeMode,
    url,
  ])

  return (
    <group
      position={position}
      scale={scale}
      rotation={rotation}
    >
      <group position={offset}>
        <primitive object={clone} />
      </group>
    </group>
  )
}

const PietonsCommune = React.forwardRef(
function PietonsCommune({
  leftX,
  rightX,
  density = 0.9,
  active = false,
  limitZ
}, ref) {

  // ==================================================
  // GAME
  // ==================================================

  const { worldGroup } = useGame()
  const [, forceUpdate] = useState(0)
  const groupRef = useRef()

  // ==================================================
  // CONFIG
  // ==================================================

  const MAX_PEDESTRIANS = 30
  const SPAWN_FRONT_MIN = 80
  const SPAWN_FRONT_MAX = 140
  const DESPAWN_BACK = 80
  const SPACING_MIN = 8
  const SPACING_MAX = 22

  const count = Math.max(
    10,
    Math.floor(
      MAX_PEDESTRIANS * density
    )
  )

  // ==================================================
  // ACTORS
  // ==================================================

  const actorsRef = useRef([])

  // ==================================================
  // MODELES
  // ==================================================

  const pedestrianTypes = [
    "womanA",
    "womanB",
    "womanC",
    "manA",
    "manB",
    "manC",
    "girl",
    "boy",
    "baby",
  ]

  // ==================================================
  // CREATE
  // ==================================================

  function createPedestrian(z, index) {

    const key =

      pedestrianTypes[
        Math.floor(
          Math.random() *
          pedestrianTypes.length
        )
      ]

    const cfg = GLB_ACTORS[key]

    if (!cfg)
      return null

    // ===============================================
    // POSITION
    // ===============================================

    const x = THREE.MathUtils.randFloat(
      leftX,
      rightX
    )

    const rotationY =
      THREE.MathUtils.randFloat(
        0,
        Math.PI * 2
      )

    // ===============================================
    // ANIMATION
    // ===============================================

    const isBaby =
      key === "baby"

    const isChild =
      key === "boy" ||
      key === "girl"

    let animChoice = "walk"

    const r = Math.random()

    // ==============================================
    // BABY
    // ==============================================

    if (isBaby) {

      if (r < 0.45) {

        animChoice =
          "baby_waving"
      }

      else {

        animChoice =
          "child"
      }
    }

    // ==============================================
    // CHILDREN
    // ==============================================

    else if (isChild) {

      if (r < 0.25) {

        animChoice =
          "idle_talk"
      }

      else {

        animChoice =
          "child"
      }
    }

    // ==============================================
    // ADULTS
    // ==============================================

    else {

      if (r < 0.50) {

        animChoice = "walk"
      }

      else if (r < 0.72) {

        animChoice = "text"
      }

      else if (r < 0.87) {

        animChoice = "text_female"
      }

      else {

        animChoice = "idle_talk"
      }
    }

    // ===============================================
    // SPEED
    // ===============================================

    let targetSpeed = pedestrianConfig.baseSpeed
    let mode = "walk"
    let actorAnimSpeed = 1
    let moveSpeed = THREE.MathUtils.randFloat(0.085, 0.11)

    // ==============================================
    // IDLE
    // ==============================================

    if (animChoice === "idle_talk" || animChoice === "baby_waving") {

      mode = "idle"
      targetSpeed = 0
      moveSpeed = 0
      actorAnimSpeed = 1.25
    }

    // ==============================================
    // CHILD
    // ==============================================

    else if (animChoice === "child") {

      mode = "walk"
      targetSpeed = GLB_WALK_SPEED
      moveSpeed = THREE.MathUtils.randFloat(0.075, 0.1)
      actorAnimSpeed = 1.15
    }

    // ==============================================
    // TEXT
    // ==============================================

    else if (animChoice === "text" || animChoice === "text_female") {

      mode = "walk"
      targetSpeed = GLB_WALK_SPEED
      moveSpeed = THREE.MathUtils.randFloat(0.07, 0.095)
    }

    // ==============================================
    // WALK
    // ==============================================

    else {

      mode = "walk"

      targetSpeed = GLB_WALK_SPEED
      moveSpeed = THREE.MathUtils.randFloat(0.085, 0.11)
    }

    const animSpeedMultiplier =
      actorAnimSpeed

    // ===============================================
    // ACTOR
    // ===============================================

    return {

      id:
        "commune_" + index,

      glb: true,

      url: cfg.url,

      scale: cfg.scale,

      offset: cfg.offset,

      bikeMode: false,

      x,
      z,

      rotationY,

      moveSpeed:
        moveSpeed,

      latVel: 0,

      mode,

      animChoice,

      animSpeedMultiplier,

      targetSpeed,

      currentSpeed:
        targetSpeed,

      speedVariation:
        1,

      commune: true,

      targetRotationY:
        rotationY,

      turnSpeed:
        THREE.MathUtils.randFloat(
          1.5,
          3
        ),
    }
  }

  // ==================================================
  // CREATE INITIAL
  // ==================================================

  useEffect(() => {

    // ==============================================
    // CLEAR SI INACTIF
    // ==============================================

    if (!active) {

      actorsRef.current = []
      forceUpdate(v => v + 1)

      return
    }

    if (!worldGroup?.current)
      return

    // ==============================================
    // ÉVITER DUPLICATION
    // ==============================================

    if (actorsRef.current.length > 0)
      return

    const currentWorldZ = worldGroup?.current?.position?.z || 0

    const playerZ = -currentWorldZ

    let z = playerZ - 60

    for (let i = 0; i < count; i++) {

      const actor =
        createPedestrian(z, i)

      if (actor) {

        actorsRef.current.push(
          actor
        )
      }

      z -=
        THREE.MathUtils.randFloat(
          SPACING_MIN,
          SPACING_MAX
        )
    }

    forceUpdate(v => v + 1)

  }, [active])

  // ==================================================
  // UPDATE
  // ==================================================

  useFrame((state, dt) => {

    // ==============================================
    // INACTIF
    // ==============================================

    if (!active)
      return

    const allowRespawn = active

    if (!worldGroup?.current)
      return

    const currentWorldZ = worldGroup?.current?.position?.z || 0

    const playerZ = -currentWorldZ

    for (const actor of actorsRef.current) {

      // ========================================
      // RÉACTIVER SI LA ZONE REDEVIENT ACTIVE
      // ========================================

      if (!actor.visible && allowRespawn) {
        actor.visible = true
      }
      let visibleZ = actor.z + currentWorldZ

      // =============================================
      // ROTATION PROGRESSIVE
      // =============================================

      actor.rotationY =
        THREE.MathUtils.lerp(
          actor.rotationY,
          actor.targetRotationY,
          actor.turnSpeed * dt
        )

      // =============================================
      // MOVE
      // =============================================

      if (actor.mode !== "idle") {

        const moveSpeed =
          actor.moveSpeed *
          actor.speedVariation *
          dt * 8

        actor.x +=
          Math.sin(actor.rotationY) *
          moveSpeed

        actor.z +=
          Math.cos(actor.rotationY) *
          moveSpeed
      }

      // =============================================
      // LIMITES
      // =============================================

      if (actor.z < limitZ) {

        actor.z = limitZ

        // regarder vers le joueur

        actor.targetRotationY =
          THREE.MathUtils.randFloat(
            -0.75,
            0.75
          )
      }

      if (actor.x < leftX) {

        actor.x = leftX

        // tourner vers la droite

        actor.targetRotationY =
          THREE.MathUtils.randFloat(
            0.2,
            Math.PI - 0.2
          )
      }

      else if (actor.x > rightX) {

        actor.x = rightX

        // tourner vers la gauche

        actor.targetRotationY =
          THREE.MathUtils.randFloat(
            -Math.PI + 0.2,
            -0.2
          )
      }

      // =============================================
      // RESPAWN DEVANT
      // =============================================

      if (visibleZ > DESPAWN_BACK) {

        // ===========================================
        // PAS DE RESPAWN SI INACTIF
        // ===========================================

        if (!allowRespawn) {
          continue
        }

        // ===========================================
        // TROUVER LE PIÉTON LE PLUS LOIN
        // ===========================================

        let furthestZ = playerZ - SPAWN_FRONT_MIN

        for (const other of actorsRef.current) {

          if (other !== actor) {

            furthestZ = Math.min(
              furthestZ,
              other.z
            )
          }
        }

        // ===========================================
        // ESPACEMENT
        // ===========================================

        const spacing =
          THREE.MathUtils.randFloat(
            SPACING_MIN,
            SPACING_MAX
          )

        // ===========================================
        // POSITION DEVANT
        // ===========================================

        const newZ =   furthestZ - spacing

        // ========================================
        // PLUS DE PLACE -> CACHER LE PIÉTON
        // ========================================

        if (newZ < limitZ) {

          actor.visible = false
          continue
        }

        // ========================================
        // RESPAWN NORMAL
        // ========================================

        actor.visible = true
        actor.z = newZ

        // ===========================================
        // NE PAS DÉPASSER LA LIMITE
        // ===========================================

        if (newZ < limitZ) {

          newZ =
            limitZ +
            THREE.MathUtils.randFloat(
              5,
              25
            )
        }

        actor.z = newZ

        // ===========================================
        // X RANDOM
        // ===========================================

        actor.x =
          THREE.MathUtils.randFloat(
            leftX,
            rightX
          )

        // ===========================================
        // ROTATION RANDOM
        // ===========================================

        actor.rotationY =
          THREE.MathUtils.randFloat(
            0,
            Math.PI * 2
          )

        actor.targetRotationY =
          actor.rotationY

        // ===========================================
        // RESET
        // ===========================================

        actor.currentSpeed =
          actor.targetSpeed

        actor.latVel = 0
      }
    }
  })

  // ==================================================
  // API
  // ==================================================

  useImperativeHandle(
    ref,
    () => ({
      group: groupRef.current,
      get actors() { return actorsRef.current }
    })
  )

  // ==================================================
  // RENDER
  // ==================================================

  return (

    <group ref={groupRef}>

      {
        actorsRef.current.map(
          actor => (

            <GLBActor
              key={actor.id}
              actor={actor}
            />
          )
        )
      }

    </group>
  )
})

function getLevelWidth(level) {

  let width = 44

  for (const section of roadSections) {

    if (section.level > level) {
      break
    }

    width = section.width
  }

  return width
}

function getPlayableLimitsAtZ(playerZ, forcedTextureLevel = null) {
  const roadSection = getRoadSectionAtZ(playerZ)
  const textureLevel = forcedTextureLevel ?? getLevelFromZ(playerZ)
  const bounds = getLevelWallBounds(
    roadSection.width,
    textureLevel
  )

  return {
    left: Math.min(bounds.leftLimit, bounds.rightLimit),
    right: Math.max(bounds.leftLimit, bounds.rightLimit)
  }
}

// --------------------------------------------------
// SCENE
// --------------------------------------------------
function Scene({setLives, setLevelAnimData, levelAnimPlaying, setLastIntersection, isInvincibleOption, difficulte, setEnergy,setContinueHandler,score, setScore,setGameFinished, resetWorldRef }) { 

  const {
  worldGroup,
  level,
  setLevel,
  departOption,
  playerSpeed,
  gameState,
  controlsEnabled,
  setGameState,
  runStartZRef,
  traveledBeforeCurrentRunKmRef,
  routeProgressDistanceKmRef
} = useGame()
  const { camera } = useThree()
  const worldPivotRef = useRef()
  const routeActorsRef = useRef()
  const cameraPivot = useRef()
  const cameraRef = useRef()
  const collisionPlayerPosition = useMemo(() => new THREE.Vector3(), [])
  const collisionPlayerWorldPosition = useMemo(() => new THREE.Vector3(), [])
  const collisionNpcWorldPosition = useMemo(() => new THREE.Vector3(), [])
  const snappedTurnsRef = useRef(new Set())
  const activePivotTurnRef = useRef(null)
  const [totalWidth, setTotalWidth] = useState(ROAD_WIDTH)

  const [routeProgress, setRouteProgress] = useState(() =>
    calculateRouteProgressKmFromZ({
      currentZ: 0,
      runStartZ: 0,
      intersections,
      traveledBeforeCurrentRunKm: 0
    })
  )


  const playerZ = -(worldGroup.current?.position?.z ?? 0)
  const currentRoadSection = getRoadSectionAtZ(playerZ)
  const currentTextureLevel = getLevelFromZ(playerZ)
  const wallBounds = getLevelWallBounds(
    currentRoadSection.width,
    currentTextureLevel
  )
  const {
    routeW,
    pisteW,
    trottoirW,
    poteauxW,
    pisteX,
    poteauxX,
    routeX,
    trottoirX,
    trottoirLeftX,
    leftWallX,
    rightWallX,
    leftLimit,
    rightLimit
  } = wallBounds

  const [groundVersion, setGroundVersion] = useState(0)
  const [finishedTurns, setFinishedTurns] = useState(new Set())


useEffect(() => {
  routeProgressDistanceKmRef.current = routeProgress.distanceKm || 0
}, [routeProgress.distanceKm])

useEffect(() => {

  function handleVisibility() {

    if (!document.hidden) {

      setGroundVersion(v => v + 1)
    }
  }

  document.addEventListener(
    "visibilitychange",
    handleVisibility
  )

  return () =>
    document.removeEventListener(
      "visibilitychange",
      handleVisibility
    )

}, [])

  const gameSpeed = BASE_GAME_SPEED + LEVEL_SPEED_GAIN
  const INVINCIBLE_DURATION = (DEBUG) ? 2500 : 2.5 // secondes 
  const baseDensity = 0.15 + (1 - Math.exp(-level * 0.08)) * 0.45
  const difficultyFactor = [0.6, 1.0, 1.8][difficulte - 1]
  const obstacleDensity = Math.min(0.85, baseDensity * difficultyFactor)
  const BASE_FATAL_PROB = 0.65   // POURCENTAGE DE COLLISIONS FATALES

//=============== CAMERA ============================================
  const FOV = IS_MOBILE ? 72 : 70
  const LOOK_FORWARD = IS_MOBILE ? 28 : 26
  const cameraBaseZ = IS_MOBILE ? 8.2 : 7
  const LOOK_HEIGHT = IS_MOBILE ? 1.8 : 2
  const cameraCrashZ = IS_MOBILE ? 6.8 : 6.5

  const [teleportVersion, setTeleportVersion] = useState(0)
  const [treeRefreshVersion, setTreeRefreshVersion] = useState(0)
  const spawnProtectionRef = useRef(0)
  const crashSequenceLock = useRef(false)
  const invincibleTimer = useRef(INVINCIBLE_DURATION)
  const [showCrashSprite, setShowCrashSprite] = useState(false)
  const [crashLock, setCrashLock] = useState(false)
  const [braking, setBraking] = useState(false)
  const [direction, setDirection] = useState(0)
  const [isColliding, setIsColliding] = useState(false)
  const [isCrashing, setIsCrashing] = useState(false)
  const [collisionType, setCollisionType] = useState(null)
  const [controlLoss, setControlLoss] = useState(0)
  const [camShake, setCamShake] = useState(0)
  const [, forceUpdate] = useState(0)
  const pietonsCommuneRef = useRef(null)
  const pietonsMcGillRef = useRef(null)
  const cameraTargetZ = useRef(cameraBaseZ)
  const lastTeleportTime = useRef(0)
  const isInvincible = useRef(true)
  const accelerating = useRef(false)
  const camShakeTime = useRef(0)
  const hitStopRef = useRef(0) // 🧊 stocke la durée restante du freeze (hit stop)
  const collisionEventRef = useRef(null)
  const tickRef = useRef(0)
  const waitingContinueTriggered = useRef(false)
  const scoreAccumRef = useRef(0)
  const levelStartTime = useRef(0)
  const isCrashingRef = useRef(false)
  const cyclistRef = useRef()
  const roadsideTreesRef = useRef()
  const bollardsRef = useRef()
  const communeBollardsRef = useRef()
  const conesRef = useRef()
  const potholesRef = useRef()
  const bacsRef = useRef()
  const wasOnSmallPothole = useRef(false)
  const wasOnBigPothole   = useRef(false)
  const cyclistsRef = useRef()
  const collisionTimer = useRef(0)
  const cyclistCollisionLock = useRef(false)
  const pietonCollisionLock = useRef(false)
  const cyclistWasColliding = useRef(false)
  const pietonWasColliding = useRef(false)
  const trafficCarsRef = useRef()
  const staticCarsRef = useRef()
  const parkedStreetCarsRef = useRef()
  const trafficLightsRef = useRef()
  const crashBackDistance = useRef(0)
  const carHitRef = useRef(null)
  const crashImpactForce = useRef(0)
  const collisionLock = useRef(false)
  const cyclistHitDir = useRef(1)
  const boostActive = useRef(false)
  const boostFactor = useRef(1)
  const intersectionTrafficRef = useRef()
  const pietonsRef = useRef()
  const redLightPenaltyLock = useRef(false)
  const prevInIntersection = useRef(false)
  const textGroup = useRef()
  const texts = useRef([])
  const flipData = useRef({t: 0, duration: 0.65, startX: 0, dirX: 0, dirZ: 0, distance: 0})
  const cyclistHitSide = useRef(1)
  const lastTriggeredLevel = useRef(1)
  const startOverlapLocksRef = useRef({
    obstacle: false,
    pothole: false,
    cyclist: false,
    pieton: false,
    car: false,
    tree: false
  })
 
 
  const addScore = (value) => { if (!isInvincibleOption) setScore(prev => prev + value) }
  const addEnergy = (value) => {
    setEnergy(prev => {
      const next = Math.min(100, Math.max(0, prev + value))
      return next
    })
  }

  function resetCollisionGuards() {
    collisionLock.current = false
    collisionEventRef.current = null
    waitingContinueTriggered.current = false
    cyclistCollisionLock.current = false
    pietonCollisionLock.current = false
    cyclistWasColliding.current = false
    pietonWasColliding.current = false
    wasOnSmallPothole.current = false
    wasOnBigPothole.current = false
    crashBackDistance.current = 0
    crashImpactForce.current = 0
    collisionTimer.current = 0
    carHitRef.current = null
    crashSequenceLock.current = false
    startOverlapLocksRef.current = {
      obstacle: false,
      pothole: false,
      cyclist: false,
      pieton: false,
      car: false,
      tree: false
    }
  }

  usePivotTurnSystem(worldPivotRef, cyclistRef,setFinishedTurns, teleportVersion, snappedTurnsRef, activePivotTurnRef)

  useEffect(() => {
    levelStartTime.current = performance.now()
  }, [level])


  useEffect(() => {
    isCrashingRef.current = isCrashing
  }, [isCrashing])

  useEffect(() => {
    setContinueHandler(() => handleContinue)
  }, [])

  // ==================================================
  // 🚀 TELEPORT + LOADING
  // ==================================================

useEffect(() => {
  if (!departOption) return
  if (!worldGroup.current) return
  if (!runStartZRef) return
  if (!traveledBeforeCurrentRunKmRef) return

  let zStart = getLevelStartZ(departOption)

  // ============ PATCH pour Sherbrooke ================
  const ROAD_WIDTH = 44

  if (departOption === 12) {
    zStart -= ROAD_WIDTH
  }

  // ==================================================
  // 🔥 RESET CRASH STATE SUR TÉLÉPORT
  // ==================================================

  collisionLock.current = false
  collisionEventRef.current = null
  waitingContinueTriggered.current = false
  cyclistCollisionLock.current = false
  pietonCollisionLock.current = false
  cyclistWasColliding.current = false
  pietonWasColliding.current = false
  wasOnSmallPothole.current = false
  wasOnBigPothole.current = false
  crashBackDistance.current = 0
  crashImpactForce.current = 0
  collisionTimer.current = 0
  carHitRef.current = null
  crashSequenceLock.current = false
  resetCollisionGuards()

  setIsColliding(false)
  setIsCrashing(false)
  setCollisionType(null)
  setShowCrashSprite(false)
  setCrashLock(false)

  accelerating.current = false

  const currentMaxSpeed = getLevelMaxPlayerSpeed(1)
  const nextMaxSpeed = getLevelMaxPlayerSpeed(1)

  const speedRatio =
    currentMaxSpeed > 0
      ? THREE.MathUtils.clamp(
          playerSpeed.current / currentMaxSpeed,
          0,
          1
        )
      : 0

  playerSpeed.current = speedRatio * nextMaxSpeed

  setDirection(0)
  setControlLoss(0)

  cameraTargetZ.current = cameraBaseZ

  isInvincible.current = true
  invincibleTimer.current = INVINCIBLE_DURATION

  setFinishedTurns(new Set())

  // 🔥 ACTIVE SPINNER
  // ==================================================
  // 🔥 ROUTE PROGRESS SUR TÉLÉPORT
  // ==================================================
  //
  // zStart est positif dans le système des niveaux.
  // Les intersections du parcours sont en z négatif.
  //
  // Exemple :
  // Commune : zStart ≈ 12758
  // routeStartZ doit être -12758
  //
  // Important :
  // La téléportation ne doit PAS ajouter automatiquement
  // la distance absolue Gouin → Commune.
  //
  // On conserve seulement la distance déjà réellement pédalée.
  // Puis le nouveau run commence à routeStartZ.

  const routeStartZ = -Math.abs(zStart)

  if (departOption === 1) {
    // Départ normal à Gouin : vraie nouvelle partie.
    traveledBeforeCurrentRunKmRef.current = 0
    runStartZRef.current = 0

    setRouteProgress(
      calculateRouteProgressKmFromZ({
        currentZ: 0,
        runStartZ: 0,
        intersections,
        traveledBeforeCurrentRunKm: 0
      })
    )
  } else {
    // Téléport à un niveau plus loin.
    // On garde la vraie distance déjà parcourue AVANT le téléport.
    traveledBeforeCurrentRunKmRef.current =
      routeProgressDistanceKmRef.current

    // La nouvelle portion commence à l’endroit téléporté.
    runStartZRef.current = routeStartZ

    setRouteProgress(
      calculateRouteProgressKmFromZ({
        currentZ: routeStartZ,
        runStartZ: routeStartZ,
        intersections,
        traveledBeforeCurrentRunKm:
          traveledBeforeCurrentRunKmRef.current
      })
    )
  }

  // ==================================================
  // 🔥 TELEPORT
  // ==================================================

  const startZ = -Math.abs(zStart)

if (departOption === 1) {
  traveledBeforeCurrentRunKmRef.current = 0
  routeProgressDistanceKmRef.current = 0
  runStartZRef.current = 0

  const nextProgress = calculateRouteProgressKmFromZ({
    currentZ: 0,
    runStartZ: 0,
    intersections,
    traveledBeforeCurrentRunKm: 0
  })

  routeProgressDistanceKmRef.current = nextProgress.distanceKm
  setRouteProgress(nextProgress)
} else {
  const alreadyTraveledKm = routeProgressDistanceKmRef.current || 0

  traveledBeforeCurrentRunKmRef.current = alreadyTraveledKm
  runStartZRef.current = startZ

  const nextProgress = calculateRouteProgressKmFromZ({
    currentZ: startZ,
    runStartZ: startZ,
    intersections,
    traveledBeforeCurrentRunKm: alreadyTraveledKm
  })

  routeProgressDistanceKmRef.current = nextProgress.distanceKm
  setRouteProgress(nextProgress)
}

  worldGroup.current.position.z = -zStart
  setTotalWidth(getRoadWidth(worldGroup.current.position.z))

  // 🔥 VERSION TELEPORT (déclenche loaders)
  lastTeleportTime.current = performance.now()
  setTeleportVersion((v) => v + 1)
  setTreeRefreshVersion((v) => v + 1)

  // 🔥 RESET DATA
  potholesRef.current = []
  conesRef.current = []
  bacsRef.current = []
}, [departOption])

  const playerControlsEnabled = controlsEnabled && gameState === "playing" && !isCrashing
  useTouchControls(accelerating, setDirection, () => { cyclistRef.current?.jump()}, playerControlsEnabled)
  useBikeKeyboardControls(playerControlsEnabled, accelerating, boostActive, setBraking, setDirection)

useEffect(() => {
  roadsideTreesRef.current?.pauseHeavyUpdates?.(700)
  setTreeRefreshVersion(v => v + 1)
}, [finishedTurns])

useEffect(() => {

  if (gameState === "playing") {

    // =========================
    // DÉBUT DU JEU
    // =========================

    spawnProtectionRef.current = 1.5
    collisionLock.current = false
    cyclistCollisionLock.current = false
    pietonCollisionLock.current = false
    pietonWasColliding.current = false
    collisionEventRef.current = null
    cyclistWasColliding.current = false
    collisionEventRef.current = null
    crashSequenceLock.current = false
    wasOnSmallPothole.current = false
    wasOnBigPothole.current = false
    resetCollisionGuards()
  

    // =========================
    // INVINCIBILITÉ TEMPORAIRE
    // =========================

    isInvincible.current = true
    invincibleTimer.current = INVINCIBLE_DURATION

  }

}, [gameState])

  // ==================================================
  // RESET GAME
  // ==================================================
useEffect(() => {

  resetWorldRef.current = resetWorld

}, [])

function getRoadWidth(worldZ) {

  let currentWidth = ROAD_WIDTH

  for (const section of roadSections) {

    let start = section.start

    const sectionWorldZ = -start

    if (worldZ >= sectionWorldZ) {
      currentWidth = section.width
    }
  }

  return currentWidth
}

function resetWorld() {

  // ==================================================
  // POSITION
  // ==================================================

  worldGroup.current.position.z = 0

  if (cyclistRef.current) {

    cyclistRef.current.position.x = 0
  }

  // ==================================================
  // RESET CRASH COMPLET
  // ==================================================

  collisionLock.current = false
  collisionEventRef.current = null
  waitingContinueTriggered.current = false

  cyclistCollisionLock.current = false
  pietonCollisionLock.current = false

  cyclistWasColliding.current = false
  pietonWasColliding.current = false

  wasOnSmallPothole.current = false
  wasOnBigPothole.current = false

  crashBackDistance.current = 0
  crashImpactForce.current = 0
  collisionTimer.current = 0

  crashSequenceLock.current = false
  carHitRef.current = null
  resetCollisionGuards()

  setIsColliding(false)
  setIsCrashing(false)
  setCollisionType(null)
  setShowCrashSprite(false)
  setCrashLock(false)

  // ==================================================
  // PLAYER
  // ==================================================

  accelerating.current = false
  playerSpeed.current = 0

  setDirection(0)
  setControlLoss(0)

  cameraTargetZ.current = cameraBaseZ

  // ==================================================
  // INVINCIBLE TEMP
  // ==================================================

  isInvincible.current = true
  invincibleTimer.current = INVINCIBLE_DURATION

  // ==================================================
  // 🔥 IMPORTANT :
  // FORCER TELEPORT STREAMER
  // ==================================================

  lastTeleportTime.current = performance.now()

  setTeleportVersion(v => v + 1)
  setTreeRefreshVersion(v => v + 1)

  // ==================================================
  // GAME
  // ==================================================

  setGameState("playing")
}
function checkRedLightViolation(worldZ) {

  const playerZ = -worldZ

  if (playerZ <= commune.z) {
    prevInIntersection.current = false
    return false
  }

  const api = trafficLightsRef.current
  if (!api) return false

  const lights = api.getStates?.()
  if (!lights) return false

  for (let i = 0; i < intersections.length; i++) {

    const inter = intersections[i]
    const dz = inter.z + worldZ
    const half = inter.width * 0.5 + 2
    const inZone = Math.abs(dz) < half

    // 🎯 détecter ENTRÉE dans l'intersection
    if (inZone && !prevInIntersection.current) {

      prevInIntersection.current = true

      const light = lights[i]?.state

      if (light === "red") {
        return true
      }
    }

    // reset quand on sort
    if (!inZone) {
      prevInIntersection.current = false
    }
  }

  return false
}


const levelThresholds = useMemo(() => {

  let sum = 0

  return LEVELS.map(level => {

    sum += LEVEL_EFFECTIVE_LENGTH[level] > 0 ? LEVEL_EFFECTIVE_LENGTH[level] : 999999

    return sum
  })

}, [])

//===================== TELEPORT ======================
useEffect(() => {

  const cyclist = cyclistRef.current
  if (!cyclist || !worldGroup.current) return

  worldPivotRef.current.rotation.y = 0
  const RIGHT_X = 0
  const LEFT_X  = -27

  // 🔥 attendre 1 frame
  requestAnimationFrame(() => {

    const currentLevel = departOption

if (currentLevel >= 14) {
      cyclist.position.x = LEFT_X
    } else if (currentLevel >= 12)
    {
      cyclist.position.x = 3
    } else{
      cyclist.position.x = RIGHT_X
    }

  })

}, [teleportVersion])


//======== FONCTIONS ==================================

function applyEnergyLoss(amount, hasCrashAnim = false) {

  setEnergy(e => {

    const newEnergy = Math.max(0, e - amount)

    if (newEnergy <= 0) {

      setLives(v => {

        const newLives = v - 1

        if (newLives <= 0) {

          if (hasCrashAnim) {

            setGameState("gameOverAnim")

          } else {

            setGameState("gameOver")
          }

        } else {

          // ✅ reset énergie si vie restante
          setEnergy(100)
        }

        return Math.max(0, newLives)
      })

      return 0
    }

    return newEnergy
  })
}

  function triggerCrash() {

  if (
    isInvincible.current ||
    spawnProtectionRef.current > 0 ||
    isInvincibleOption
  ) {
    collisionEventRef.current = null
    collisionLock.current = false
    cyclistCollisionLock.current = false
    pietonCollisionLock.current = false
    return false
  }

  if (isCrashingRef.current) return false

  if (collisionLock.current) return false

  if (!cyclistRef.current) return false

  collisionLock.current = true

  const MAX_TILT = Math.PI / 5   // ~36°

  flipData.current.tilt = -flipData.current.dirX * MAX_TILT

  const lateral = cyclistRef.current.getLateralVelocity()
  const forward = -1   // direction monde
  const LATERAL_DAMP = 0.25   // ← diminue la projection sur le côté
  const vec = new THREE.Vector2(lateral * LATERAL_DAMP, forward)

  if (vec.lengthSq() < 0.0001) vec.set(0, -1)
  else vec.normalize()

  flipData.current.t = 0
  flipData.current.startX = cyclistRef.current?.position?.x
  flipData.current.startZ = cyclistRef.current?.position?.z
  flipData.current.dirX = vec.x
  flipData.current.dirZ = vec.y

  const speedRatio = THREE.MathUtils.clamp(playerSpeed.current / MAX_PLAYER_SPEED,0,1)

  flipData.current.distance =  4.5 + 3.5 * Math.pow(speedRatio, 1.6)
  setShowCrashSprite(false)
  setCrashLock(true)
  setIsColliding(true)
  setIsCrashing(true)
  setCollisionType("obstacle")
  accelerating.current = false
  setDirection(0)
  return true
}

function checkPietonsCollision(group) {

  const list = group?.current?.children
  if (!list || list.length === 0) return null
  const playerPos = getPositionInFrame(cyclistRef.current, group.current, collisionPlayerPosition)
  if (!playerPos) return null

  const PLAYER_HALF_X = 0.45
  const PLAYER_HALF_Z = 0.9
  const PIETON_HALF_X = 0.35
  const PIETON_HALF_Z = 0.6

  for (let i = 0; i < list.length; i++) {

    const p = list[i]
    if (!p || p.visible === false) continue

    const dz = p.position.z - playerPos.z
    if (Math.abs(dz) > 2.2) continue

    const dx = p.position.x - playerPos.x

    if (
      Math.abs(dx) < PLAYER_HALF_X + PIETON_HALF_X &&
      Math.abs(dz) < PLAYER_HALF_Z + PIETON_HALF_Z
    ) {
      return dz
    }
  }

  return null
}
function checkPietonsCommune(group) {

  const list =
    group?.current?.actors

  if (!list || list.length === 0)
    return null
  const playerPos = getPositionInFrame(cyclistRef.current, group.current.group, collisionPlayerPosition)
  if (!playerPos) return null

  const PLAYER_HALF_X = 0.45
  const PLAYER_HALF_Z = 0.9

  const PIETON_HALF_X = 0.45
  const PIETON_HALF_Z = 0.7

  for (let i = 0; i < list.length; i++) {

    const p = list[i]

    if (!p || p.visible === false) continue

    // =====================================
    // POSITION RELATIVE CAMÉRA
    // =====================================

    const dz = p.z - playerPos.z

    // =====================================
    // HORS FENÊTRE
    // =====================================

    if (dz < -3 || dz > 3) {
      continue
    }

    const dx = p.x - playerPos.x

    // =====================================
    // COLLISION AABB
    // =====================================

    if (Math.abs(dx) < PLAYER_HALF_X + PIETON_HALF_X && Math.abs(dz) < PLAYER_HALF_Z + PIETON_HALF_Z) {

      return {
        pieton: p,
        dx,
        dz
      }
    }
  }

  return null
}
const potholeBox = new THREE.Box3().setFromBufferAttribute(
  POTHOLE_GEOMETRY.attributes.position
)

const potholeSize = new THREE.Vector3()

potholeBox.getSize(potholeSize)

function checkPotholesCollision(group) {

  if (!group?.current?.children?.[0]) {
    return { bigHit: false, smallHit: false }
  }

  const mesh = group.current.children[0]
  const playerPos = getPositionInFrame(cyclistRef.current, mesh, collisionPlayerPosition)
  if (!playerPos) return { bigHit: false, smallHit: false }

  const tempMatrix = new THREE.Matrix4()
  const tempPos = new THREE.Vector3()
  const tempQuat = new THREE.Quaternion()
  const tempScale = new THREE.Vector3()

  let bigHit = false
  let smallHit = false

  for (let i = 0; i < mesh.count; i++) {

    mesh.getMatrixAt(i, tempMatrix)

    tempMatrix.decompose(
      tempPos,
      tempQuat,
      tempScale
    )

    const x = tempPos.x
    const z = tempPos.z - playerPos.z

    if (z < -20 || z > 8) continue

    const dx = Math.abs(x - playerPos.x)

    const scale = tempScale.x

    // ⭐ vraies dimensions
    const hitX = (potholeSize.x * scale) * 0.5
    const hitZ = (potholeSize.y * scale) * 0.5

    const isBig = scale >= 1.5

    if (
      dx < hitX &&
      Math.abs(z) < hitZ
    ) {

      if (isBig) {
        bigHit = true
      } else {
        smallHit = true
      }

      if (bigHit && smallHit) break
    }
  }

  return { bigHit, smallHit }
}
function checkConeCollision(group) {

  const mesh = group?.current
  if (!mesh) return false

  const data = group.current?.__data || group.current?.parent?.__data

  if (!data) return false // ⚠️ à remplacer idéalement
  const playerPos = getPositionInFrame(cyclistRef.current, mesh, collisionPlayerPosition)
  if (!playerPos) return false

  const PLAYER_HALF_X = 0.45
  const PLAYER_HALF_Z = 0.9
  const CONE_HALF_X = 0.35
  const CONE_HALF_Z = 0.6

  const tempMatrix = new THREE.Matrix4()

  for (let i = 0; i < mesh.count; i++) {

    const c = data[i]
    if (!c) continue
    if (c.hit || c.locked) continue

    mesh.getMatrixAt(i, tempMatrix)

    const coneX = tempMatrix.elements[12]
    const coneZ = tempMatrix.elements[14] - playerPos.z

    if (coneZ < -20 || coneZ > 8) continue

    if (
      Math.abs(coneX - playerPos.x) < PLAYER_HALF_X + CONE_HALF_X &&
      Math.abs(coneZ) < PLAYER_HALF_Z + CONE_HALF_Z
    ) {

      const diff = playerPos.x - coneX

      c.hit = true
      c.dir = diff === 0 ? 1 : Math.sign(diff)
      c.axis = Math.random() < 0.85 ? "z" : "x"

      if (c.axis === "x") {
        c.dir = Math.random() < 0.8 ? 1 : -1
      }

      return true
    }
  }

  return false
}

function checkBacsCollision(group) {

  const list = group?.current?.children
  if (!list || list.length === 0) return false
  const playerPos = getPositionInFrame(cyclistRef.current, group.current.group, collisionPlayerPosition)
  if (!playerPos) return false

  const limitX = 0.45 + 0.4
  const limitZ = 0.9 + 0.6

  for (let i = 0; i < list.length; i++) {

    const bac = list[i]
    if (!bac) continue 

    if (bac.userData?.hit) continue

    const dx = bac.position.x - playerPos.x
    const dz = bac.position.z - playerPos.z

    if (dz < -6 || dz > 6) continue

    if (
      dx < limitX && dx > -limitX &&
      dz < limitZ && dz > -limitZ
    ) {

      bac.userData.hit = true

      const diff = playerPos.x - bac.position.x

      bac.userData.dir = diff === 0 ? 1 : (diff > 0 ? 1 : -1)
      bac.userData.axis = Math.random() < 0.85 ? "x" : "z"

      return true
    }
  }

  return false
}

function checkBollardCollision(group) {

  const list = group?.current?.bollards
  if (!list || list.length === 0) return false
  const playerPos = getPositionInFrame(cyclistRef.current, group.current.mesh, collisionPlayerPosition)
  if (!playerPos) return false

  const PLAYER_RADIUS = 0.45
  const BOLLARD_RADIUS = 0.075

  for (let i = 0; i < list.length; i++) {

    const obj = list[i]
    if (!obj) continue // 🔥 FIX

    const dz = obj.z - playerPos.z
    if (Math.abs(dz) > 6) continue

    const dx = obj.x - playerPos.x

    const distSq = dx * dx + dz * dz
    const minDist = PLAYER_RADIUS + BOLLARD_RADIUS

    if (distSq < minDist * minDist) {
      return true
    }
  }

  return false
}

function checkCyclistCollision(group) {

  const list = group?.current?.children

  if (!list || list.length === 0) return null
  const playerPos = getPositionInFrame(cyclistRef.current, group.current, collisionPlayerPosition)
  if (!playerPos) return null

  const PLAYER_HALF_X = 0.45
  const PLAYER_HALF_Z = 0.9
  const NPC_HALF_X = 0.5
  const NPC_HALF_Z = 1.0

  for (let i = 0; i < list.length; i++) {

    const npc = list[i]

    if (!npc || npc.visible === false) continue

    const dz = npc.position.z - playerPos.z

    if (Math.abs(dz) > 2.0) continue

    const dx = npc.position.x - playerPos.x

    if (
      Math.abs(dx) < PLAYER_HALF_X + NPC_HALF_X &&
      Math.abs(dz) < PLAYER_HALF_Z + NPC_HALF_Z
    ) {

      npc.getWorldPosition(collisionNpcWorldPosition)
      cyclistRef.current.pivot.getWorldPosition(collisionPlayerWorldPosition)
      return {
        npc, dz, speed: npc.userData?.speed || 0,
        dx: collisionNpcWorldPosition.x - collisionPlayerWorldPosition.x
      }
    }
  }

  return null
}

function checkCarHit(group) {

  const SIDE_PADDING = 0
  const FRONT_PADDING = 0
  const PLAYER_Z_OFFSET = 0

  const list = group?.current?.children

  if (!list?.length) {
    return null
  }
  const playerPos = getPositionInFrame(cyclistRef.current, group.current, collisionPlayerPosition)
  if (!playerPos) return null

  const PLAYER_HALF_X = 0.5
  const PLAYER_HALF_Z = 1.2

  for (let i = 0; i < list.length; i++) {

    const wrapper = list[i]

    if (!wrapper) continue

    const vehicle = wrapper.children?.[0]

    if (!vehicle) continue

    // =====================================
    // 🌍 WORLD SPACE
    // =====================================

    const dx = playerPos.x - wrapper.position.x

    // 🔥 OFFSET APPLIQUÉ EN MONDE
    const dz = (playerPos.z + PLAYER_Z_OFFSET) - wrapper.position.z

    // =====================================
    // 🚀 FAST BROAD PHASE
    // =====================================

    if (Math.abs(dx) > 8 || Math.abs(dz) > 12) {
      continue
    }

    // =====================================
    // 📦 SCALE RÉEL
    // =====================================

    const scaleX = 1
    const scaleZ = wrapper.scale?.z ?? 1.25

    // =====================================
    // 📦 SIZE RÉELLE
    // =====================================

    const halfW = (vehicle.userData?.halfWidth ?? 1.2) * scaleX
    const halfL = (vehicle.userData?.halfLength ?? 2.2) * scaleZ

    // =====================================
    // 🔄 LOCAL SPACE
    // =====================================

    const rotY = wrapper.rotation.y
    const cos = Math.cos(rotY)
    const sin = Math.sin(rotY)
    const localX = dx * cos + dz * sin
    const localZ = -dx * sin + dz * cos

    // =====================================
    // 💥 COLLISION
    // =====================================

    const hitX =
      Math.abs(localX) <=
      (
        halfW +
        PLAYER_HALF_X +
        SIDE_PADDING
      )

    const hitZ =
      Math.abs(localZ) <=
      (
        halfL +
        PLAYER_HALF_Z +
        FRONT_PADDING
      )

    if (hitX && hitZ) {

      return wrapper
    }
  }

  return null
}

function lowerBoundCollisionTreeByZ(sortedTrees, targetZ) {
  let left = 0
  let right = sortedTrees.length

  while (left < right) {
    const mid = (left + right) >> 1
    const midZ = Number(sortedTrees[mid]?.sourceZ) || 0

    /*
      Liste triée du z le plus proche du départ vers le plus loin :
      -5, -15, -30, -100...
    */
    if (midZ > targetZ) {
      left = mid + 1
    } else {
      right = mid
    }
  }

  return left
}

function getCollisionTreesNearZ(sortedTrees, cyclistZ, range = 80) {
  if (!sortedTrees?.length) return []

  const zMax = cyclistZ + range
  const zMin = cyclistZ - range

  const start = lowerBoundCollisionTreeByZ(sortedTrees, zMax)
  const result = []

  for (let i = start; i < sortedTrees.length; i++) {
    const treeEntry = sortedTrees[i]
    const sourceZ = Number(treeEntry?.sourceZ) || 0

    if (sourceZ < zMin) break

    result.push(treeEntry)
  }

  return result
}

function checkArbreCollision(roadsideTreesRef, cyclistX) {
  const api = roadsideTreesRef.current

  if (!api) return null

  const trees = api.collisionTreesRef.current

  if (!trees?.length) return null

  /*
    Hitbox cycliste.
    Z un peu plus généreux pour éviter de passer à travers
    quand le jeu avance vite.
  */
  const PLAYER_HALF_X = 0.28
  const PLAYER_HALF_Z = 1.05

  /*
    Hitbox arbre.
    X reste petit pour viser le tronc, pas le feuillage.
  */
  const TREE_HALF_X_SCALE = 0.22
  const TREE_HALF_Z_SCALE = 0.55

  const TREE_MIN_HALF_X = 0.08
  const TREE_MIN_HALF_Z = 0.16

  const TREE_MAX_HALF_X = 0.24
  const TREE_MAX_HALF_Z = 0.75

  /*
    Distance finale en Z.
    Plus large que 7 pour éviter les collisions ratées entre deux frames.
  */
  const FINAL_Z_SCAN_DISTANCE = 12

  const tempTreeWorldPos =
    checkArbreCollision.tempTreeWorldPos ||
    new THREE.Vector3()

  const tempCyclistWorldPos =
    checkArbreCollision.tempCyclistWorldPos ||
    new THREE.Vector3()

  checkArbreCollision.tempTreeWorldPos =
    tempTreeWorldPos

  checkArbreCollision.tempCyclistWorldPos =
    tempCyclistWorldPos

  const cyclistObject =
    cyclistRef.current?.pivot ||
    cyclistRef.current

  if (cyclistObject) {
    cyclistObject.updateWorldMatrix(true, false)
    tempCyclistWorldPos.setFromMatrixPosition(
      cyclistObject.matrixWorld
    )
  } else {
    tempCyclistWorldPos.set(cyclistX || 0, 0, 0)
  }

  const cyclistWorldX = tempCyclistWorldPos.x
  const cyclistWorldZ = tempCyclistWorldPos.z

  for (let i = 0; i < trees.length; i++) {
    const t = trees[i]
    const tree = t?.tree

    if (!tree) continue
    if (tree.visible === false) continue

    /*
      Très important :
      on recalcule la position monde actuelle.
      Ne pas se fier uniquement à t.worldX / t.worldZ,
      parce que le monde bouge à chaque frame.
    */
    tree.updateWorldMatrix(true, false)
    tempTreeWorldPos.setFromMatrixPosition(tree.matrixWorld)

    const dz = tempTreeWorldPos.z - cyclistWorldZ

    if (
      dz < -FINAL_Z_SCAN_DISTANCE ||
      dz > FINAL_Z_SCAN_DISTANCE
    ) {
      continue
    }

    const dx = tempTreeWorldPos.x - cyclistWorldX

    const treeHalfX = Math.min(
      TREE_MAX_HALF_X,
      Math.max(
        TREE_MIN_HALF_X,
        (t.halfX || 0) * TREE_HALF_X_SCALE
      )
    )

    const treeHalfZ = Math.min(
      TREE_MAX_HALF_Z,
      Math.max(
        TREE_MIN_HALF_Z,
        (t.halfZ || 0) * TREE_HALF_Z_SCALE
      )
    )

    const hitX =
      Math.abs(dx) < PLAYER_HALF_X + treeHalfX

    const hitZ =
      Math.abs(dz) < PLAYER_HALF_Z + treeHalfZ

    if (hitX && hitZ) {
      tree.userData.collisionWorldX = tempTreeWorldPos.x
      tree.userData.collisionWorldZ = tempTreeWorldPos.z
      tree.userData.collisionDx = dx
      tree.userData.collisionDz = dz
      tree.userData.collisionTreeHalfX = treeHalfX
      tree.userData.collisionTreeHalfZ = treeHalfZ

      return tree
    }
  }

  return null
}

function respawnWorldBackward(worldGroup, levelThresholds, maxBack = 20) {
  if (!worldGroup?.current) return

  const currentZ = worldGroup.current?.position?.z

  // 🔎 distance parcourue (positive)
  const progress = Math.abs(currentZ)

  // 🔎 début du niveau courant (en progress)
  let startProgress = 0

  for (let i = 0; i < levelThresholds.length; i++) {
    if (progress < levelThresholds[i]) {
      startProgress = i === 0 ? 0 : levelThresholds[i - 1]
      break
    }
  }

  // 🎯 tentative de recul
  let newProgress = progress - maxBack

  // 🚫 ne jamais dépasser le début du niveau courant
  if (newProgress < startProgress) newProgress = startProgress

  worldGroup.current.position.z = newProgress
}

function handleContinue() {

  // =====================================
  // 🔒 RESET VERROUS
  // =====================================

  collisionLock.current = false
  collisionEventRef.current = null
  waitingContinueTriggered.current = false
  cyclistCollisionLock.current = false
  pietonCollisionLock.current = false
  cyclistWasColliding.current = false
  pietonWasColliding.current = false
  wasOnSmallPothole.current = false
  wasOnBigPothole.current = false
  crashBackDistance.current = 0
  crashImpactForce.current = 0
  collisionTimer.current = 0
  carHitRef.current = null
  crashSequenceLock.current = false
  resetCollisionGuards()

  // =====================================
  // 🎬 RESET VISUEL
  // =====================================

  setIsColliding(false)
  setIsCrashing(false)
  setCollisionType(null)
  setShowCrashSprite(false)
  setCrashLock(false)

  // =====================================
  // 🚴 RESET JOUEUR
  // =====================================

  accelerating.current = false
  playerSpeed.current = 0

  // =====================================
  // 🌍 RESPAWN MONDE
  // =====================================

  respawnWorldBackward(worldGroup, levelThresholds, 20)

  // =====================================
  // 🚴 POSITION JOUEUR
  // =====================================

  const playerZ = -worldGroup?.current?.position?.z || 0

  const pivot = cyclistRef.current?.pivot
  const sprite = cyclistRef.current?.sprite

  if (pivot) {

    pivot.position.x = 0
    pivot.position.y = BASE_Y
    pivot.position.z = 0

    // ===================================
    // APRÈS LA COMMUNE
    // ===================================

    if (playerZ < commune.z) {

      pivot.position.x = -27

    } else if (playerZ < cherrier.z) {

      pivot.position.x = 3
    }
  }

  // =====================================
  // 🔥 RESET SPRITE COMPLET
  // =====================================

  if (sprite) {

    sprite.rotation.set(0, 0, 0)
    sprite.position.set(0, 0, 0)
    sprite.scale.set(1, 1, 1)
  }

  // =====================================
  // 🛡️ INVINCIBILITÉ
  // =====================================

  isInvincible.current = true
  invincibleTimer.current = INVINCIBLE_DURATION

  // =====================================
  // 🎮 RESET CONTRÔLES
  // =====================================

  setDirection(0)
  setControlLoss(0)

  // =====================================
  // 📷 CAMERA
  // =====================================

  cameraTargetZ.current = cameraBaseZ

  // =====================================
  // ▶️ REPRISE PROPRE
  // =====================================

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setGameState("playing")
    })
  })
}

function setCollisionEventSafe(newEvent) {

  // 🔥 GARDE-FOU GLOBAL
  if (gameState === "gameOver") return

  if (
    isInvincible.current ||
    spawnProtectionRef.current > 0 ||
    isInvincibleOption
  ) {
    collisionEventRef.current = null
    collisionLock.current = false
    cyclistCollisionLock.current = false
    pietonCollisionLock.current = false
    return
  }

  const current = collisionEventRef.current

  if (!current) {
    collisionEventRef.current = newEvent
    return
  }

  const priority = {
    obstacle: 2,
    cyclist_slow: 3,
    cyclist_fast: 4,
    pieton: 5,
    tree: 6,
    car: 7
  }

  const currentPriority = priority[current.type] || 0
  const newPriority = priority[newEvent.type] || 0

  if (newPriority > currentPriority) {
    collisionEventRef.current = newEvent
  }
}

function checkConeOverlap(group) {
    const mesh = group?.current
    if (!mesh) return false

    const data = group.current?.__data || group.current?.parent?.__data
    if (!data) return false
    const playerPos = getPositionInFrame(cyclistRef.current, mesh, collisionPlayerPosition)
    if (!playerPos) return false

    const PLAYER_HALF_X = 0.45
    const PLAYER_HALF_Z = 0.9
    const CONE_HALF_X = 0.35
    const CONE_HALF_Z = 0.6
    const tempMatrix = new THREE.Matrix4()

    for (let i = 0; i < mesh.count; i++) {
      const c = data[i]
      if (!c || c.hit || c.locked) continue

      mesh.getMatrixAt(i, tempMatrix)

      const coneX = tempMatrix.elements[12]
      const coneZ = tempMatrix.elements[14] - playerPos.z

      if (coneZ < -20 || coneZ > 8) continue

      if (
        Math.abs(coneX - playerPos.x) < PLAYER_HALF_X + CONE_HALF_X &&
        Math.abs(coneZ) < PLAYER_HALF_Z + CONE_HALF_Z
      ) {
        return true
      }
    }

    return false
  }

function checkBacsOverlap(group) {
    const list = group?.current?.children
    if (!list || list.length === 0) return false
    const playerPos = getPositionInFrame(cyclistRef.current, group.current.group, collisionPlayerPosition)
    if (!playerPos) return false

    const limitX = 0.45 + 0.4
    const limitZ = 0.9 + 0.6

    for (let i = 0; i < list.length; i++) {
      const bac = list[i]
      if (!bac || bac.userData?.hit) continue

      const dx = bac.position.x - playerPos.x
      const dz = bac.position.z - playerPos.z

      if (dz < -6 || dz > 6) continue

      if (
        dx < limitX && dx > -limitX &&
        dz < limitZ && dz > -limitZ
      ) {
        return true
      }
    }

    return false
  }

function getCurrentStartOverlaps(playerZ) {
    const cyclistX = cyclistRef.current?.position?.x ?? 0
    const { bigHit, smallHit } = checkPotholesCollision(potholesRef)
    const afterCremazie = playerZ <= CREMAZIE_Z
    const carHit =
      checkCarHit(trafficCarsRef) ||
      checkCarHit(staticCarsRef) ||
      checkCarHit(intersectionTrafficRef) ||
      (afterCremazie && checkCarHit(parkedStreetCarsRef))
    const pietonHit =
      checkPietonsCollision(pietonsRef) ??
      (playerZ < COMMUNE_Z
        ? checkPietonsCommune(pietonsCommuneRef)
        : null)

    return {
      obstacle:
        checkBollardCollision(bollardsRef) ||
        checkConeOverlap(conesRef) ||
        checkBacsOverlap(bacsRef),
      pothole: bigHit || smallHit,
      cyclist: checkCyclistCollision(cyclistsRef) !== null,
      pieton: pietonHit !== null,
      car: Boolean(carHit),
      tree: Boolean(checkArbreCollision(roadsideTreesRef, cyclistX))
    }
  }

function syncStartOverlapLocks(protectedState, playerZ) {
    const current = getCurrentStartOverlaps(playerZ)
    const locks = startOverlapLocksRef.current

    for (const key of Object.keys(locks)) {
      if (protectedState && current[key]) {
        locks[key] = true
      } else if (!current[key]) {
        locks[key] = false
      }
    }

    if (protectedState) {
      collisionEventRef.current = null
      collisionLock.current = false
      cyclistCollisionLock.current = false
      pietonCollisionLock.current = false
      cyclistWasColliding.current = current.cyclist
      pietonWasColliding.current = current.pieton
      wasOnSmallPothole.current = current.pothole
      wasOnBigPothole.current = current.pothole
    }
  }
  // 🚴 GAME LOOP
useFrame((state, dt) => {

  if (!worldGroup.current) return
  if (!cyclistRef.current) return

  const world = worldGroup.current
  const worldZ = world.position.z
  const playerZ = -world.position.z
  const cyclist = cyclistRef.current
  const cyclistX = cyclist?.position?.x
  const isFrozen = gameState === "waitingContinue"
  const rawDt = dt

  if (routeActorsRef.current) {
    routeActorsRef.current.position.x = getRouteOffsetX(playerZ)
  }

  if (gameState === "gameOver") return

  // ==================================================
// 🧊 IMMOBILISATION APRÈS CRASH
// ==================================================

if (gameState === "waitingContinue") {
  accelerating.current = false
  playerSpeed.current = 0
  boostFactor.current = 1
  crashImpactForce.current = 0

  if (world) {
    world.userData.worldSpeed = 0
  }
}

  // ==================================================
  // 🛡️ SPAWN PROTECTION
  // ==================================================

  if (spawnProtectionRef.current > 0) {

    spawnProtectionRef.current -= dt
    isInvincible.current = true

  } else if (!isInvincibleOption && invincibleTimer.current <= 0) {

    isInvincible.current = false
  }

// ==================================================
// ⏱️ DT STABLE + MOVE DT RÉEL
// ==================================================

const MIN_DT = 1 / 90
const MAX_PHYSICS_DT = 1 / 30
const moveDt = Math.min(rawDt, 1 / 15) // ⭐ vrai temps réel pour déplacement gameplay
 
dt = document.hidden ? MIN_DT : THREE.MathUtils.clamp(rawDt, MIN_DT, MAX_PHYSICS_DT) // ⭐ dt stable pour physique/caméra/collisions

// ==================================================
// 🔥 UI UPDATE FIXE
// ==================================================

tickRef.current += dt

if (tickRef.current > 0.033) {

  tickRef.current = 0
  forceUpdate(n => n + 1)
}

  const isCrashState = gameState === "crash" || gameState === "gameOverAnim"
  const crashLocked = isCrashing || gameState === "crash" || gameState === "gameOverAnim"
  
  // ==================================================
  // 🛡️ INVINCIBILITY TIMER
  // ==================================================

  if (isInvincibleOption) {

    // mode permanent
    isInvincible.current = true
    invincibleTimer.current = 0

  } else {

    // 🔥 si on sort du mode permanent
    // et qu'il n'y a pas d'invincibilité temporaire
    if (isInvincible.current && invincibleTimer.current <= 0) {
      isInvincible.current = false
    }

    // invincibilité temporaire
    if (invincibleTimer.current > 0) {

      isInvincible.current = true
      if (gameState !== "waitingContinue") {
        invincibleTimer.current -= dt
      }
      if (invincibleTimer.current <= 0) {

        invincibleTimer.current = 0
        isInvincible.current = false
      }
    }
  }
  const protectedState = isInvincible.current || spawnProtectionRef.current > 0
  syncStartOverlapLocks(protectedState, playerZ)
  const startOverlapLocks = startOverlapLocksRef.current
  //----------------------------------------------------

  // 🧊 HIT STOP : fige temporairement le jeu pendant quelques millisecondes
  // en interrompant la boucle de mise à jour pour créer un effet d'impact

  if (hitStopRef.current > 0) {
    hitStopRef.current -= dt
    return
  }

  //===============================================
  // 🛡️ SCORE
  //===============================================

  if (gameState === "playing") {

    const difficultyBonus = [0.8, 1.0, 1.4][difficulte - 1]
    const densityBonus = 0.7 + obstacleDensity
    const scoreMultiplier = difficultyBonus * densityBonus

    scoreAccumRef.current += playerSpeed.current * moveDt * scoreMultiplier

    if (scoreAccumRef.current >= 1) {

      const delta = Math.floor(scoreAccumRef.current)
      scoreAccumRef.current -= delta

      if (!protectedState) {
        setScore(prev => prev + delta)
      }
    }
  }
  // ==================================================
  // 🚴 CHANGER DE NIVEAU
  // ==================================================
 
  const progress = Math.abs(worldGroup.current?.position?.z)
  let newLevel = level

  for (let i = 0; i < levelThresholds.length; i++) {
    if (progress >= levelThresholds[i]) {
      newLevel = i + 2
    } else {
      break
    }
  }

  newLevel = Math.min(newLevel, levelThresholds.length + 1)

  if (newLevel > lastTriggeredLevel.current) {

    const now = performance.now()

    // ==================================================
    // 🔒 VERROU IMMÉDIAT
    // ==================================================

    lastTriggeredLevel.current = newLevel

    // ==================================================
    // 🚇 CHANGEMENT DE NIVEAU
    // ==================================================

    setLevel(newLevel)

    // ==================================================
    // 🚫 COOLDOWN TÉLÉPORT
    // ==================================================

    const TELEPORT_COOLDOWN = 1000

    if (now - lastTeleportTime.current < TELEPORT_COOLDOWN) {
      
      levelStartTime.current = now
      return
    }

    // ==================================================
    // ⏱️ BONUS TEMPS
    // ==================================================

    const timeSeconds =  (now - levelStartTime.current) / 1000
    const MAX_TIME = 60
    const speedFactor = Math.max(0, (MAX_TIME - timeSeconds) / MAX_TIME)
    const levelBonus = Math.floor(200 * newLevel * (0.5 + speedFactor))

    // ==================================================
    // 💰 SCORE
    // ==================================================

    if (!isInvincibleOption) {

      setScore(prev => prev + levelBonus)
    }

    // ==================================================
    // 🎬 ANIMATION LEVEL UP
    // ==================================================

    if (!levelAnimPlaying.current) {

      levelAnimPlaying.current = true

      setLevelAnimData({
        id: crypto.randomUUID(),
        level: newLevel,
        bonus: levelBonus
      })
    }

    // ==================================================
    // ⏱️ RESET TIMER NIVEAU
    // ==================================================

    levelStartTime.current = now
  }
  // ==================================================


  // ========= Vérifier si le cycliste passe sur la rouge =============

  if (!isFrozen) {

    const isRedViolation = checkRedLightViolation(worldZ)

    if (isRedViolation && !redLightPenaltyLock.current) {

      redLightPenaltyLock.current = true

      // ❌ perte de points
      if (!isInvincibleOption)
      setScore(prev => Math.max(0, prev - 500))

      // 💬 feedback visuel
      spawnText(textGroup, texts, cyclistX, -2, "-500", "red")
    }

    if (!isRedViolation) {
      redLightPenaltyLock.current = false
    }
  }

  // ==================================================
  // TEXTES
  // ==================================================

  if (textGroup.current) {

    const meshes = textGroup.current.children

    for (let i = texts.current.length - 1; i >= 0; i--) {

      const t = texts.current[i]
      const mesh = meshes[i]

      if (!mesh) continue

      t.life -= dt

      mesh.position.x = t.x
      mesh.position.z = t.z
      mesh.position.y = 2 + (1 - t.life) * 1.2
      mesh.material.opacity = t.life

      if (t.life <= 0) {
        texts.current.splice(i, 1)
        textGroup.current.remove(mesh)
      }
    }
  }

  // ==================================================
  // 🚴 VITESSE JOUEUR
  // ==================================================

  const ACCEL_SMOOTH = 2.4
  const DECEL_SMOOTH = 1.8
  const BRAKE_SMOOTH = 6.5

  if (!isFrozen) {

    if (isCrashing && (collisionType === "obstacle" || collisionType === "cyclist_fast")) {

      playerSpeed.current = THREE.MathUtils.lerp(playerSpeed.current, 0, 6 * dt)

   } else if (collisionType === "car" && isCrashState) {

      playerSpeed.current *= Math.pow(0.98, dt * 60)
      const TOTAL = 3 * 0.15

      if (collisionTimer.current > TOTAL) {

        // attendre une frame de plus pour laisser finir l'anim
        if (!waitingContinueTriggered.current) {

          waitingContinueTriggered.current = true

          setTimeout(() => {

            // 💀 si animation de mort en cours
            if (gameState === "gameOverAnim") {

              setGameState("gameOver")

            } else {

              setGameState("waitingContinue")
            }

          }, 100)
        }
      }
    } else {

    let smooth
    let targetSpeed

    if (accelerating.current) {

      targetSpeed = gameSpeed
      smooth = ACCEL_SMOOTH

    } else if (braking) {

      targetSpeed = 0
      smooth = BRAKE_SMOOTH

    } else {

      targetSpeed = 0
      smooth = DECEL_SMOOTH

    }

    // ⭐ ACCÉLÉRATION EXPONENTIELLE STABLE
    const accelDt = rawDt > (1 / 40) ? rawDt : dt
    const accelFactor = 1 - Math.exp(-smooth * accelDt * 3)

    playerSpeed.current = THREE.MathUtils.lerp(playerSpeed.current, targetSpeed, accelFactor)

    if (playerSpeed.current < 0.0005) {
      playerSpeed.current = 0
    }
  }
}

// ==================================================
// 🚀 SMOOTH DEBUG BOOST (SHIFT)
// ==================================================

// ⭐ réglages gameplay
const BOOST_UP_RATE = 1.8     // montée (plus petit = plus lent)
const BOOST_DOWN_RATE = 2.5   // descente
const targetBoost = boostActive.current ? BOOST_MAX : 1

boostFactor.current +=
  (targetBoost - boostFactor.current) *
  (boostActive.current ? BOOST_UP_RATE : BOOST_DOWN_RATE) *
  dt

// ==================================================
// 🌍 WORLD TRANSLATION
// ==================================================

if (isFrozen) {
  /*
    Après un crash, le cycliste doit rester immobile au sol.
    Aucun déplacement normal du monde.
  */
  accelerating.current = false
  playerSpeed.current = 0
  boostFactor.current = 1
  crashImpactForce.current = 0
  world.userData.worldSpeed = 0

} else if (
  (collisionType === "car" || collisionType === "tree") &&
  isCrashState
) {
  playerSpeed.current *= 0.96

  if (playerSpeed.current < 0.02) {
    playerSpeed.current = 0
  }

  if (!worldGroup.current) return

  collisionTimer.current += moveDt

  const IMPACT_DURATION =
    collisionType === "car" ? 0.16 : 0.08

  const TOTAL =
    collisionType === "tree"
      ? 3 * 0.08 + 0.24
      : 3 * 0.12 + 0.18

  // =====================================
  // 💥 IMPACT COURT SEULEMENT
  // =====================================

  if (collisionTimer.current <= IMPACT_DURATION) {
    const impactForce = crashImpactForce.current

    world.position.z += impactForce * moveDt

    if (collisionType === "car") {
      crashImpactForce.current *= 0.82
    }

    if (collisionType === "tree") {
      crashImpactForce.current *= 0.65
    }
  } else {
    /*
      Après l'impact initial, plus aucune poussée en z.
    */
    crashImpactForce.current = 0
  }

  world.userData.worldSpeed = 0

  // =====================================
  // FIN ANIMATION CRASH
  // =====================================

  if (collisionTimer.current > TOTAL) {
    crashImpactForce.current = 0
    playerSpeed.current = 0
    world.userData.worldSpeed = 0

    if (!waitingContinueTriggered.current) {
      waitingContinueTriggered.current = true

      setTimeout(() => {
        accelerating.current = false
        playerSpeed.current = 0
        boostFactor.current = 1
        crashImpactForce.current = 0
        world.userData.worldSpeed = 0

        setIsColliding(false)
        setIsCrashing(false)

        if (gameState === "gameOverAnim") {
          setGameState("gameOver")
        } else {
          setGameState("waitingContinue")
        }
      }, 120)
    }
  }

} else if (gameState === "playing") {
  // ⭐ VITESSE MONDE UNIQUE
  const worldSpeed =
    playerSpeed.current * boostFactor.current

  // ⭐ stockée pour les autres systèmes, trafic, etc.
  world.userData.worldSpeed = worldSpeed

  // ⭐ déplacement réel seulement en playing
  world.position.z += worldSpeed * moveDt

} else {
  /*
    Sécurité :
    si on n'est pas en playing ni en crash actif,
    le monde ne doit jamais avancer.
  */
  accelerating.current = false
  playerSpeed.current = 0
  boostFactor.current = 1
  crashImpactForce.current = 0
  world.userData.worldSpeed = 0
}

  // ==================================================
  // 🧱 OBSTACLES
  // ==================================================
  
  if (!isFrozen && !protectedState && !crashLocked) {

  // =====================================
  // 🧱 COLLISIONS OBSTACLES
  // =====================================

  const hitBac = checkBacsCollision(bacsRef)
  const hitBollard = checkBollardCollision(bollardsRef)
  const hitCone = checkConeCollision(conesRef)
  const hitArbre = checkArbreCollision(roadsideTreesRef, cyclistX)

  const { bigHit, smallHit } = checkPotholesCollision(potholesRef)
  const hitCyclist = checkCyclistCollision(cyclistsRef)
  const cyclistIsCollidingNow = hitCyclist !== null

  // =====================================
  // 🧱 FLAGS
  // =====================================

  const obstacleCollision =
    (hitBollard || hitCone || hitBac) &&
    !startOverlapLocks.obstacle &&
    !isColliding &&
    !isCrashing

  const arbreCollision =
    hitArbre &&
    !startOverlapLocks.tree &&
    !isColliding &&
    !isCrashing &&
    !crashLock &&
    !collisionLock.current &&
    !collisionEventRef.current

  // =====================================
  // 🧱 OBSTACLE
  // =====================================

  if (obstacleCollision && !protectedState) {

    setCollisionEventSafe({ type: "obstacle" })
  }

  // =====================================
  // 🌳 ARBRE
  // =====================================

  if (arbreCollision && !protectedState) {

    setCollisionEventSafe({ type: "tree" })
    cyclistHitDir.current =
      (hitArbre?.userData?.collisionWorldX ?? hitArbre?.position?.x) < cyclistX
        ? -1
        : 1
  }

  // =====================================
  // 🕳️ SMALL POTHOLE
  // =====================================

  if (smallHit && !wasOnSmallPothole.current && !protectedState && !startOverlapLocks.pothole) {

    camShakeTime.current = 0
    setControlLoss(0.9)
    setCamShake(1.2)
    applyEnergyLoss(3)
  }

  // =====================================
  // 🕳️ BIG POTHOLE
  // =====================================

  if (bigHit && !wasOnBigPothole.current && !cyclistWasColliding.current && !protectedState && !startOverlapLocks.pothole) {

    setCollisionEventSafe({ type: "obstacle" })
  }
  // =====================================
  // 🚴 COLLISION CYCLISTES IA
  // =====================================
  const speed = playerSpeed.current
  const MIN_FATAL_SPEED = 2.5

  // ==================================================
  // 🚴 CÔTÉ DU NPC
  // ==================================================

  let cyclistSide = 1

  if (hitCyclist?.npc) {
    cyclistSide = hitCyclist.dx < 0 ? -1 : 1
  }

  // ==================================================
  // 🚴 LE JOUEUR FONCE-T-IL VERS LE NPC ?
  // ==================================================

  const lateralVel = cyclistRef.current?.getLateralVelocity?.() || 0

  let playerInitiatedHit = false

  if (hitCyclist?.npc) {

    // distance latérale
    const dx = hitCyclist.dx

    // =====================================
    // le joueur va vers le NPC
    // =====================================

    const movingRightIntoNpc = lateralVel > 0.18 && dx > 0
    const movingLeftIntoNpc = lateralVel < -0.18 && dx < 0

    // =====================================
    // vitesse suffisante
    // =====================================

    const aggressiveSpeed = speed > gameSpeed * 0.35

    playerInitiatedHit = aggressiveSpeed && (movingRightIntoNpc || movingLeftIntoNpc)
  }

  // ==================================================
  // 🔓 RESET LOCK
  // ==================================================

  if (!cyclistIsCollidingNow) {
    cyclistCollisionLock.current = false
  }

  // ==================================================
  // 🚴 DÉBUT COLLISION
  // ==================================================

  const cyclistCollisionStarted =
    !protectedState &&
    !startOverlapLocks.cyclist &&
    cyclistIsCollidingNow &&
    !cyclistCollisionLock.current &&
    !collisionLock.current

  if (cyclistCollisionStarted) {

    cyclistCollisionLock.current = true

    // =====================================
    // 🚴 NPC RESPONSABLE
    // → jamais fatal
    // =====================================

    if (!playerInitiatedHit || speed < MIN_FATAL_SPEED) {

      setCollisionEventSafe({
        type: "cyclist_slow",
        side: cyclistSide
      })

    // =====================================
    // ☠️ JOUEUR RESPONSABLE
    // =====================================

    } else {

      const levelBonus = Math.min(0.25, level * 0.025)
      const speedFactor = THREE.MathUtils.clamp(speed / gameSpeed, 0, 1)
      const fatalProb = Math.min(0.95, (BASE_FATAL_PROB + levelBonus) * (0.4 + 0.6 * speedFactor))
      const isFatal = Math.random() < fatalProb

      cyclistHitSide.current = cyclistSide

      setCollisionEventSafe({ type: isFatal ? "cyclist_fast" : "cyclist_slow", side: cyclistSide })
    }

    setTimeout(() => { cyclistCollisionLock.current = false }, 300)
  }

  // =====================================
  // 💾 MÉMO
  // =====================================

  wasOnSmallPothole.current =  smallHit
  wasOnBigPothole.current = bigHit
}
// ==================================================
// 🧠 CONTROL LOSS DECAY (LINÉAIRE ET RAPIDE)
// ==================================================

  if (controlLoss > 0) {
    const DECAY_SPEED = 1.2   // 🔥 ajuste ici (plus grand = plus court)

    setControlLoss(v => {
      const next = v - DECAY_SPEED * dt
      return next <= 0 ? 0 : next
    })
  }

  /* ==================================================
  🚶 COLLISION PIÉTONS
  ================================================== */

  if (!isFrozen && !protectedState && !crashLocked) {
    let communeHit = null
    const playerZ = -worldGroup?.current?.position?.z || 0
    const pietonHit = checkPietonsCollision(pietonsRef)

    if (playerZ < COMMUNE_Z) {
      communeHit = checkPietonsCommune(pietonsCommuneRef)
    }
    const pietonIsCollidingNow = pietonHit !== null || communeHit !== null

    // ⭐ RESET LOCK quand plus en collision
    if (!pietonIsCollidingNow) {
      pietonCollisionLock.current = false
    }

    // ⭐ TRIGGER uniquement à l'entrée
    const collisionStarted =
      pietonIsCollidingNow &&
      !startOverlapLocks.pieton &&
      !pietonWasColliding.current &&
      !pietonCollisionLock.current &&
      !collisionLock.current &&
      !collisionEventRef.current

    if (!protectedState && collisionStarted) {

      pietonCollisionLock.current = true
      setCollisionEventSafe({ type: "pieton" })
    }

    // ⭐ mémoriser état
    pietonWasColliding.current = pietonIsCollidingNow
  }

// ==================================================
// 🚗 VEHICLE COLLISIONS
// ==================================================

if (!isFrozen && !protectedState && !startOverlapLocks.car && !isColliding && !isCrashing && !collisionLock.current && !collisionEventRef.current) {

  const afterCremazie = playerZ <= CREMAZIE_Z
  const carHit =
    checkCarHit(trafficCarsRef) ||
    checkCarHit(staticCarsRef) ||
    checkCarHit(intersectionTrafficRef) ||
    (afterCremazie && checkCarHit(parkedStreetCarsRef))

  // =====================================
  // 💥 COLLISION FOUND
  // =====================================

  if (carHit) {

    // 🔒 lock IMMÉDIAT
    setCollisionEventSafe({ type: "car", car: carHit })

    if (collisionEventRef.current?.type === "car") {
      collisionLock.current = true
    }
  }
}

  /* ============================================
   APPLY SINGLE COLLISION PER FRAME
  ============================================ */

  const collisionEvent = collisionEventRef.current

  if (collisionEvent && !isFrozen && !crashSequenceLock.current) {

    // ==================================================
    // 🛑 GAME OVER FINAL
    // ==================================================

    if (gameState === "gameOver") {
      collisionEventRef.current = null
      return
    }

    // ==================================================
    // 🛡️ INVINCIBLE
    // ==================================================

    if (
      protectedState ||
      isInvincible.current ||
      spawnProtectionRef.current > 0 ||
      isInvincibleOption
    ) {
      collisionEventRef.current = null
      collisionLock.current = false
      cyclistCollisionLock.current = false
      pietonCollisionLock.current = false
      return
    }

    crashSequenceLock.current = true

    // ==================================================
    // 🔥 HELPER COMMUN
    // ==================================================

    const startCrash = () => {
      accelerating.current = false
      camShakeTime.current = 0
      setIsColliding(true)
      setIsCrashing(true)
      setDirection(0)
      setGameState("crash")
    }

    // ==================================================
    // 🎯 COLLISIONS
    // ==================================================

    switch (collisionEvent.type) {

      // ==================================================
      // 🧱 OBSTACLE
      // ==================================================

      case "obstacle":

        setCollisionType("obstacle")
        if (triggerCrash()) {
          applyEnergyLoss(25, true)
        } else {
          crashSequenceLock.current = false
        }
        break

      // ==================================================
      // 🚴 CYCLISTE RAPIDE
      // ==================================================

      case "cyclist_fast":

        cameraTargetZ.current = cameraCrashZ
        collisionTimer.current = 0
        setCollisionType("cyclist_fast")
        startCrash()
        applyEnergyLoss(40, true)
        break

      // ==================================================
      // 🚴 CYCLISTE LENT
      // ==================================================

      case "cyclist_slow":
        if (gameState === "crash" || gameState === "gameOverAnim") {
            break  // 🔥 ne pas interrompre un vrai crash
          }

        camShakeTime.current = 0
        setCollisionType("cyclist_slow")
        setIsColliding(true)
        setControlLoss(2.2)
        setCamShake(2.8)
        applyEnergyLoss(8)
        setTimeout(() => 
        { 
          setIsColliding(false)     
          setControlLoss(0)
        }, 220)
        setTimeout(() => {
          crashSequenceLock.current = false
        }, 250)
        break

      // ==================================================
      // 🚶 PIÉTON
      // ==================================================

      case "pieton":

        camShakeTime.current = 0
        setCollisionType("pieton")
        setIsColliding(true)
        setControlLoss(1.5)
        setCamShake(1.0)
        applyEnergyLoss(15)
        setTimeout(() => { setIsColliding(false) }, 250)
        setTimeout(() => {
          crashSequenceLock.current = false
        }, 250)
        break

      // ==================================================
      // 🚗 VOITURE
      // ==================================================

      case "car": {

        const isTraffic = collisionEvent.car.userData?.isTraffic === true
        hitStopRef.current = 0.05
        collisionLock.current = true
        carHitRef.current = collisionEvent.car
        collisionTimer.current = 0
        crashImpactForce.current = isTraffic ? 20 : -10
        cameraTargetZ.current = cameraCrashZ + Math.abs(crashImpactForce.current) * 0.25
        setCollisionType("car")
        startCrash()
        applyEnergyLoss(isTraffic ? 80 : 40, true)
        setCamShake(0.5)
        break
      }

      // ==================================================
      // 🌳 ARBRE
      // ==================================================

      case "tree": {
  camShakeTime.current = 0
  collisionTimer.current = 0
  waitingContinueTriggered.current = false

  hitStopRef.current = 0.07
  collisionLock.current = true

  /*
    Garde l’impact monde court.
    Cyclist va gérer le recul visuel du cycliste lui-même.
  */
  crashImpactForce.current = -8

  cameraTargetZ.current = cameraCrashZ

  setCollisionType("tree")
  startCrash()

  applyEnergyLoss(50, true)

  setCamShake(0.4)
  setControlLoss(0)

  /*
    IMPORTANT :
    180 ms est trop court. Ça peut couper la collision
    pendant que Cyclist est encore sur les premiers frames.
  */
  setTimeout(() => {
    setIsColliding(false)
    collisionLock.current = false
    crashSequenceLock.current = false
  }, 560)

  break
}
      default:
        break
    }

    
    // ==================================================
    // 🧹 CLEAR EVENT
    // ==================================================

    collisionEventRef.current = null
  }


  // ==================================================
// 📳 CAMERA SHAKE
// ==================================================

if (camShake > 0) {

  camShakeTime.current += dt

  const intensity =
    camShake * Math.exp(-2 * camShakeTime.current)

  camera.position.x =
    (Math.random() - 0.5) * intensity

  camera.position.y =
    (Math.random() - 0.5) * intensity * 0.5

  if (intensity < 0.01) {

    camera.position.x = 0
    camera.position.y = 0

    setCamShake(0)
    camShakeTime.current = 0
  }
}
else {

  camera.position.x = 0
  camera.position.y = 0
}

// ==================================================
// 🎥 POSITION CAMÉRA (via le pivot, pas la caméra directe)
// ==================================================

const camPivot = cameraPivot.current
const cam = cameraRef.current

if (camPivot && cam && cyclist) {

  // ===================== X =========================

  cyclist.pivot.getWorldPosition(collisionPlayerWorldPosition)
  const realPlayerX = collisionPlayerWorldPosition.x
  followCyclistCameraX(camPivot, realPlayerX, dt)

  // ===================== Y =========================

  const targetCamY = collisionPlayerWorldPosition.y + (IS_MOBILE ? 3.4 : 3)

  camPivot.position.y = THREE.MathUtils.lerp(camPivot.position.y, targetCamY, 4 * dt)

  // ===================== Z =========================

  const targetCamZ = collisionPlayerWorldPosition.z + cameraTargetZ.current

  camPivot.position.z = THREE.MathUtils.lerp(camPivot.position.z, targetCamZ, 3 * dt)

  // ==================================================
  // 👁️ LOOK AT (ORIENTATION)
  // ==================================================

  const forwardX = realPlayerX
  const forwardZ = collisionPlayerWorldPosition.z - LOOK_FORWARD

  cam.lookAt(forwardX, LOOK_HEIGHT, forwardZ)
}

// ==================================================
// 🔥 DEPTH BUFFER FIX
// ==================================================

if (camera.near !== 0.8 || camera.far !== 450) {

  camera.near = 0.8
  camera.far = 450
  camera.updateProjectionMatrix()
}

// ==================================================
// 🔭 FOV
// ==================================================

if (camera.fov !== FOV) {

  camera.fov = FOV
  camera.updateProjectionMatrix()
}

// ==================================================
// SECTION COURANTE
// ==================================================
const currentSection = getRoadSectionAtZ(playerZ)

if (currentSection.width !== totalWidth) {
  setTotalWidth(currentSection.width)
}

})

// ==================================================
// SECTIONS
// ==================================================

const baseZ = getLevelStartZ(departOption)

// ==================================================
// REPÈRE DES ACTEURS DU PARCOURS
// ==================================================

const currentOffsetX = getRouteOffsetX(playerZ)
const roadObstaclesEnabled = shouldRenderRoadObstacles(playerZ)


return (
  <>
    <group ref={worldPivotRef}>
       {/* 🌍 WORLD GROUP */}
    <group ref={worldGroup}>

      {/* ========================= */}
      {/* 🛣 SOL */}
      {/* ========================= */}

      <GroundInstances key={groundVersion} sections={roadSections} x={-14} />

       {roadObstaclesEnabled && (
        <RandomPotholes
          ref={potholesRef}
          pisteX={pisteX}
          pisteW={pisteW}
          routeX={routeX}
          routeW={routeW}
          trottoirX={trottoirX}
          trottoirW={trottoirW}
          poteauxX={poteauxX}
          poteauxW={poteauxW}
          density={obstacleDensity}
          difficulte={difficulte}
        />
       )}

      <group ref={routeActorsRef} position={[currentOffsetX, 0, 0]}>

        <RandomBonuses
          pisteX={pisteX}
          pisteW={pisteW}
          worldRef={worldGroup}
          cyclistRef={cyclistRef}
          onCollectPoints={addScore}
          onCollectEnergy={addEnergy}
          count={Math.min(25, Math.floor(10 + level * 0.8))}
        />

        {/* ========================= */}
        {/* 🚧 OBSTACLES */}
        {/* ========================= */}

        <Pietons
          ref={pietonsRef}
          pisteX={pisteX}
          pisteW={pisteW}
          trottoirX={trottoirX}
          trottoirW={trottoirW}
        />

       {roadObstaclesEnabled && (
        <>
        <RandomCones
          ref={conesRef}
          pisteX={pisteX}
          pisteW={pisteW}
          trottoirX={trottoirX}
          trottoirW={trottoirW}
          poteauxX={poteauxX}
          poteauxW={poteauxW}
          density={obstacleDensity}
          difficulte={difficulte}
        />

         <StaticBikeLaneCars
          ref={staticCarsRef}
          pisteX={pisteX}
          pisteW={pisteW}
          density={obstacleDensity}
          difficulte={difficulte}
        />
        </>
       )}
        {playerZ > commune.z && (
          <>
          <Bollards
            ref={bollardsRef}
            texturePath={"/textures/poteau.jpg"}
            x={poteauxX-0.2}
          />
          <RandomBacs
            ref={bacsRef}
            trottoirX={trottoirX}
            trottoirW={trottoirW}
            pisteX={pisteX}
            pisteW={pisteW}
            density={obstacleDensity}
            difficulte={difficulte}
          />
          </>
        )}
         {playerZ <= mcGill.z && playerZ >= tournant.z && (
          <Bollards
            ref={communeBollardsRef}
            texturePath={"/textures/poteau2.jpg"}
            x={playerZ <= mill.z ? 6 : 7.8}
          />
         )}
        <ParkedStreetCars
          ref={parkedStreetCarsRef}
          routeX={routeX}
          routeW={routeW}
          count={50}
        />

        <RandomBikeTraffic
          ref={cyclistsRef}
          pisteX={pisteX}
          pisteW={pisteW}
          conesRef={conesRef}
          potholesRef={potholesRef}
          bollardsRef={bollardsRef}
          staticCarsRef={staticCarsRef}
          trafficCarsRef={trafficCarsRef}
          bacsRef={bacsRef}
          trafficLightsRef={trafficLightsRef}
          playerRef={cyclistRef}
          density={obstacleDensity}
        />

        <GLBActors
          actorsRef={cyclistsRef.current?.actorsRef}
        />

        <PietonsCommune
          ref={pietonsCommuneRef}
          leftX={10}
          rightX={16}
          active={
            playerZ < commune.z &&
            playerZ > youville.z + 20
          }
          limitZ={youville.z + 20}
        />

        <PietonsCommune
          ref={pietonsMcGillRef}
          leftX={-15}
          rightX={-5}
          active={playerZ < mcGill.z &&
            playerZ > mill.z}
          limitZ={mcGill.z - 500}
        />

      </group>

      {/* ========================= */}
      {/* 🚦 RESTE NON DÉCALÉ */}
      {/* ========================= */}

      <group position={[0, 0, baseZ]}>
        <Banderole
          leftX={trottoirLeftX+trottoirW/2}
          rightX={trottoirX}
          height={12}
          bannerHeight={3}
          poleRadius={0.25}
          z={-20}
          text={"Départ"}
          triggerZ={baseZ}
          worldGroup={worldGroup}
        />
      </group>

      <Banderole
        leftX={leftLimit-4}
        rightX={rightLimit+2}
        height={12}
        bannerHeight={3}
        poleRadius={0.25}
        z={ARRIVE}
        text={"Arrivée"}
        triggerZ={ARRIVE}
        worldGroup={worldGroup}
      />

      {
        -(worldGroup?.current?.position?.z || 0) < ARRIVE && (
          <Fireworks
            triggerZ={ARRIVE}
            worldGroup={worldGroup}
            camera={camera}
            cameraTargetZ={cameraTargetZ}
            onFinish={() => {
              setGameFinished(true)
            }}
            isInvincibleOption={isInvincibleOption}
            score={score}
            level={level}
            setGameState={setGameState}
          />
        )
      }

      <TrafficLights
        ref={trafficLightsRef}
        intersections={intersections}
        greenTime={10}
        yellowTime={2.5}
        redTime={8}
        scale={1.2}
      />

      <ParallaxSides
        xLeft={leftWallX - 16}
        xRight={rightWallX + 16}
        hiddenDuringTurnRef={activePivotTurnRef}
      />

      <LevelStreamer
        ref={roadsideTreesRef}
        xLeft={trottoirLeftX}
        xRight={trottoirX + trottoirW / 2}
        teleportVersion={teleportVersion}
        finishedTurns={finishedTurns}
        arbres={arbresSortedByZ}
      />

      <Intersections
        positions={intersections}
        roadLength={80}
        xLeft={trottoirLeftX + 1.5 }
        xRight={trottoirX + trottoirW / 2 -1.5}
        snappedTurnsRef={snappedTurnsRef}
      />

      <RandomTrafficCars
        ref={trafficCarsRef}
        routeX={routeX}
        routeW={routeW}
        density={0.3 + 0.2 * (1 - Math.exp(-level * 0.15))}
        trafficSpeed={12 + level * 1.4}
        intersections={intersections}
        trafficLightsRef={trafficLightsRef}
        bikeTrafficRef={cyclistsRef}
        cyclistRef={cyclistRef}
      />

      <IntersectionTraffic
        ref={intersectionTrafficRef}
        intersections={intersections}
        trafficLightsRef={trafficLightsRef}
        worldRef={worldGroup}
      />

    </group>

    </group>

    {/* ====================================== */}
    {/* 📷 CAMERA (hors pivot monde) */}
    {/* ====================================== */}
    <group ref={cameraPivot}>
      <PerspectiveCamera ref={cameraRef} makeDefault />
    </group>

    {/* ====================================== */}
    {/* 🚴 JOUEUR (NE TOURNE PAS) */}
    {/* ====================================== */}
    <Cyclist
      ref={cyclistRef}
      position={[0, 0, 0]}
      direction={direction}
      isColliding={isColliding}
      leftLimit={leftLimit}
      rightLimit={rightLimit}
      controlLoss={controlLoss}
      collisionType={collisionType}
      showCrashSprite={showCrashSprite}
      flipTilt={flipData.current.tilt}
      isInvincibleRef={isInvincible}
      hitCar={carHitRef.current}
      treeHitDir={cyclistHitDir.current}
      cyclistHitSide={cyclistHitSide.current}
    />

    {/* HUD */}
    <HudIntersection setLastIntersection={setLastIntersection} />
    <group ref={textGroup} />
    {/*DEBUG && <DebugWorldZ />*/}

  </>
)







}


function HudIntersection({ setLastIntersection }) {

  const { worldGroup } = useGame()

  useFrame(() => {

    if (!worldGroup?.current) return

    const worldZ = worldGroup?.current?.position?.z ?? 0

    let last = null

    for (let i = 0; i < intersections.length; i++) {

      const inter = intersections[i]

      // 🔥 POSITION RELATIVE AU JOUEUR
      const relativeZ = inter.z + worldZ

      // 🔥 si l'intersection est passée (derrière caméra)
      if (relativeZ > 0) {
        last = inter
      }
    }

    if (last) {
      setLastIntersection(last)
    } else {
      setLastIntersection({ offsetX: 0, id: "Gouin" })
    }

  })

  return null
}

function LevelLabel({
  animLevel,
  scoreBonusAnim,
  LEVEL_NAMES
}) {

  const [play, setPlay] = useState(false)

  useEffect(() => {

    setPlay(false)

    const r = requestAnimationFrame(() => {
      setPlay(true)
    })

    return () => cancelAnimationFrame(r)

  }, [animLevel])

  // ==================================================
  // LEVEL DATA
  // ==================================================

  const levelData =
    LEVEL_NAMES[animLevel - 1] || {}

  return (

    <div className={
      play
        ? "metro-level-label play"
        : "metro-level-label"
    }>

      {/* ==================================================
          🚇 METRO ICON
      ================================================== */}

      {levelData.metro && (

        <img
          src="/metro.png"
          alt="metro"
          draggable={false}
        />
      )}

      {/* ==================================================
          🏷️ NAME
      ================================================== */}

      <div className="metro-level-name">
        {levelData.name || ""}
      </div>

      {/* ==================================================
          💰 BONUS
      ================================================== */}

      <div className="metro-level-bonus">
        +{scoreBonusAnim || 0}
      </div>

    </div>
  )
}

function Spinner({ gameState }) {

    const isLoading = gameState === "loading"

    if (!isLoading) return null

    return (
      <div className="loading-overlay">
        <div className="spinner" />
        <div className="loading-text">
          Chargement...
        </div>
      </div>
    )
  }
function VisibilityRestore() {

  const { gl, scene } = useThree()

  useEffect(() => {

  const canvas = gl.domElement

  canvas.addEventListener(
    "webglcontextlost",
    (e) => {
      e.preventDefault()
      console.log("WEBGL LOST")
    }
  )

  canvas.addEventListener(
    "webglcontextrestored",
    () => {
      console.log("WEBGL RESTORED")
    }
  )

}, [gl])

  useEffect(() => {

    function restoreTextureQuality() {

      const maxAnisotropy =
        window.GAME_QUALITY?.maxAnisotropy ||
        gl.capabilities?.getMaxAnisotropy?.() ||
        1

      scene.traverse(obj => {

        const materials =
          Array.isArray(obj.material)
            ? obj.material
            : obj.material
              ? [obj.material]
              : []

        materials.forEach(material => {

          if (!material) return

          const maps = [
            material.map,
            material.alphaMap,
            material.emissiveMap,
            material.normalMap,
            material.roughnessMap,
            material.metalnessMap,
            material.aoMap
          ]

          maps.forEach(tex => {

            if (!tex?.isTexture) return

            const nearest =
              tex.magFilter === THREE.NearestFilter ||
              tex.minFilter === THREE.NearestFilter

            if (!nearest) {
              tex.anisotropy = maxAnisotropy
              tex.magFilter = THREE.LinearFilter
              tex.minFilter = THREE.LinearMipmapLinearFilter
              tex.generateMipmaps = true
            }

            tex.needsUpdate = true
          })

          material.needsUpdate = true
        })
      })
    }

    function handleVisibility() {

      if (document.hidden) return

      window.GAME_QUALITY.maxAnisotropy =
        gl.capabilities?.getMaxAnisotropy?.() ||
        window.GAME_QUALITY.maxAnisotropy ||
        1

      gl.setPixelRatio(
        window.GAME_QUALITY?.low
          ? 1
          : Math.min(window.devicePixelRatio, 1.5)
      )

      gl.setSize(
        window.innerWidth,
        window.innerHeight,
        false
      )

      restoreTextureQuality()

      console.log("Canvas restauré")
    }

    document.addEventListener(
      "visibilitychange",
      handleVisibility
    )

    return () =>
      document.removeEventListener(
        "visibilitychange",
        handleVisibility
      )

  }, [gl, scene])

  useEffect(() => {

  const canvas = gl.domElement

  function onLost(e) {
    e.preventDefault()
    console.log("WEBGL LOST")
  }

  function onRestored() {
    console.log("WEBGL RESTORED")
  }

  canvas.addEventListener(
    "webglcontextlost",
    onLost
  )

  canvas.addEventListener(
    "webglcontextrestored",
    onRestored
  )

  return () => {

    canvas.removeEventListener(
      "webglcontextlost",
      onLost
    )

    canvas.removeEventListener(
      "webglcontextrestored",
      onRestored
    )
  }

}, [gl])
  return null
}
// -----------------------------
// 🎮 MAIN COMPONENT
// -----------------------------
export default function JeuVelo() 
{
  const worldGroup = useRef() 
  const [level, setLevel] = useState(1) 
  const playerSpeed = useRef(0)
  const levelMaxSpeed = getLevelMaxPlayerSpeed(1)
  const [gameState, setGameState] = useState("loading")
  const [showIntro, setShowIntro] = useState(true)
  const runStartZRef = useRef(0)
  const traveledBeforeCurrentRunKmRef = useRef(0)
  const routeProgressDistanceKmRef = useRef(0)
  const [depart, setDepart] = useState(1)
  const [restartVersion, setRestartVersion] = useState(0)
  const [gameFinished, setGameFinished] = useState(false)
  const resetWorldRef = useRef(null)
  const [highscoreShown, setHighscoreShown] = useState(false)
  const [leaderboardResumeState, setLeaderboardResumeState] = useState("playing");
  const [levelAnimData, setLevelAnimData] = useState(null)
  const [continueHandler, setContinueHandler] = useState(null)
  const [lives, setLives] = useState(MAX_VIES)
  const [energy, setEnergy] = useState(100)
  const [difficulte, setDifficulte] = useState(1)
  const [isInvincibleOption, setIsInvincibleOption] = useState(false)
  const [score, setScore] = useState(0)
  const [showHighscore, setShowHighscore] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [finalLevel, setFinalLevel] = useState(0);
  const [latestScore, setLatestScore] = useState(null);
  const [sharePlayerName, setSharePlayerName] = useState("Cycliste anonyme");
  const [shareHighscoreRank, setShareHighscoreRank] = useState(null);
  const [lastIntersection, setLastIntersection] = useState(null)
const [routeProgress, setRouteProgress] = useState(() =>
  calculateRouteProgressKmFromZ({
    currentZ: 0,
    runStartZ: 0,
    intersections
  })
)

  const levelAnimPlaying = useRef(false)
  const gameOverTrackedRef = useRef(false)
  const routeCompletedTrackedRef = useRef(false)
  const isIOS =  /iPad|iPhone|iPod/i.test(navigator.userAgent)
  const LOW = useMemo(() => { return getInitialQuality() }, [])
  const handleHighscoreRankResolved = useCallback((rank) => {
    setShareHighscoreRank(rank)
  }, [])

useEffect(() => {

  if (!levelAnimData) return

  const t = setTimeout(() => {

    levelAnimPlaying.current = false
    setLevelAnimData(null)

  }, 1700)

  return () => clearTimeout(t)

}, [levelAnimData])


    // 🔒 lock scroll mobile
    useEffect(() => {

      const html = document.documentElement
      const body = document.body

      const prevHtml = {
        overflow: html.style.overflow,
        height: html.style.height
      }

      const prevBody = {
        overflow: body.style.overflow,
        height: body.style.height
      }

      html.style.overflow = "hidden"
      body.style.overflow = "hidden"
      html.style.height = "100%"
      body.style.height = "100%"

      return () => {
        html.style.overflow = prevHtml.overflow
        body.style.overflow = prevBody.overflow
        html.style.height = prevHtml.height
        body.style.height = prevBody.height
      }

    }, [])

function resetGame(){
  setGameFinished(false)
  setShowHighscore(false)
  setShowLeaderboard(false)
  setHighscoreShown(false)
  gameOverTrackedRef.current = false
  routeCompletedTrackedRef.current = false
  setLives(MAX_VIES)
  setEnergy(100)
  setScore(0)
  setLevel(1)
  setLatestScore(null)
  setSharePlayerName("Cycliste anonyme")
  setShareHighscoreRank(null)
  setDepart(1)
 traveledBeforeCurrentRunKmRef.current = 0
runStartZRef.current = 0

setRouteProgress(
  calculateRouteProgressKmFromZ({
    currentZ: 0,
    runStartZ: 0,
    intersections,
    traveledBeforeCurrentRunKm: 0
  })
)
  setRestartVersion(v => v + 1)

  resetWorldRef.current?.()

  setTimeout(()=>{
    document.querySelector("canvas")?.focus()
  },0)
}

function startGameFromIntro() {
  setShowIntro(false)

  // Important :
  // Si LevelStreamer est encore en train de charger,
  // ne pas forcer "playing".
  // Il va le faire lui-même quand il aura fini.
  if (gameState !== "loading") {
    setGameState("playing")
  }
}

useEffect(() => {

  console.log({

    userAgent:
      navigator.userAgent,

    isIOS:
      /iPad|iPhone|iPod/i.test(
        navigator.userAgent
      ),

    dpr:
      window.devicePixelRatio,

    cores:
      navigator.hardwareConcurrency,

    memory:
      navigator.deviceMemory,

    LOW,

    GAME_QUALITY:
      window.GAME_QUALITY
  })

}, [])

useEffect(() => {
  if (showIntro) return

  const id = window.setInterval(() => {
    const worldZ = worldGroup.current?.position?.z || 0
    const currentZ = -Math.abs(worldZ)

    const nextProgress = calculateRouteProgressKmFromZ({
      currentZ,
      runStartZ: runStartZRef.current,
      intersections,
      traveledBeforeCurrentRunKm:
        traveledBeforeCurrentRunKmRef.current
    })

    routeProgressDistanceKmRef.current = nextProgress.distanceKm
    setRouteProgress(nextProgress)
  }, 250)

  return () => window.clearInterval(id)
}, [showIntro])

//================ FIN DU JEU =========================
useEffect(() => {

  if (gameState === "gameOver" && !highscoreShown) {
    setFinalScore(score)
    setFinalLevel(level)
    setLatestScore(null)
    setSharePlayerName("Cycliste anonyme")
    setShareHighscoreRank(null)
    setShowHighscore(true)
    setHighscoreShown(true)

    if (!gameOverTrackedRef.current) {
      gameOverTrackedRef.current = true

      trackEvent("game_over", {
        game_name: REV_BERRI_ROUTE.name,
        score,
        distance_km: Number(routeProgress.distanceKm.toFixed(2)),
        route_completion_percent: Math.round(routeProgress.completionPercent),
        completed_route: routeProgress.completedRoute
      })
    }

    if (
      routeProgress.completedRoute &&
      !routeCompletedTrackedRef.current
    ) {
      routeCompletedTrackedRef.current = true

      trackEvent("route_completed", {
        game_name: REV_BERRI_ROUTE.name,
        distance_km: Number(routeProgress.distanceKm.toFixed(2)),
        score
      })
    }
  }

}, [gameState, highscoreShown, score, level, routeProgress])


const isGameReady = gameState !== "loading"
const showIntroLoadingOverlay = !showIntro && gameState === "loading"

return (
  <GameContext.Provider
    value={{
      worldGroup,
      level,
      setLevel,
      departOption: depart,
      restartVersion,
      playerSpeed,
      levelMaxSpeed,
      gameState,
      setGameState,
      runStartZRef,
      traveledBeforeCurrentRunKmRef,
      routeProgressDistanceKmRef,
      showIntro,
      controlsEnabled: gameState === "playing" && !showIntro && !showHighscore &&
        !showLeaderboard && !showOptions && !showHelp
    }}
  >
    <div className="velo-page">
    <Canvas
        onCreated={({ gl }) => { window.__GL__ = gl; window.GAME_QUALITY.maxAnisotropy = gl.capabilities.getMaxAnisotropy() }}
        shadows={false}
        tabIndex={0}
        dpr={LOW ? 1 : Math.min(window.devicePixelRatio, 1.5)}
        gl={{
          antialias: !LOW && !isIOS,
          stencil: false,
          powerPreference: LOW ? "default" : "high-performance",
          outputColorSpace: THREE.SRGBColorSpace,
          toneMapping: THREE.NoToneMapping,
        }}
        onPointerDown={(e) => e.target.focus()}
        onContextMenu={(e) => e.preventDefault() }
        frameloop="always"
        performance={{ min: 1 }}
        camera={{ position: [0, 4.6, 7] }}
        style={{
          position: "fixed",
          height: "100dvh",
          width: "100vw",
          display: "block",
          zIndex: 0,
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent"
        }}
      >
      <VisibilityRestore />
      <color attach="background" args={["skyblue"]} />
      <ambientLight intensity={3} />
      <hemisphereLight
        intensity={1.2}
        skyColor="#ffffff"
        groundColor="#aaaaaa"
      />
     
      <SkyBackground />
      <SunBillboard />
        <Scene
          gameState={gameState}
          setGameState={setGameState}
          lives={lives}
          setLives={setLives}
          level={level}
          departOption={depart}
          restartVersion={restartVersion}
          setLevel={setLevel}
          isInvincibleOption={isInvincibleOption}
          difficulte={difficulte}
          energy={energy}
          setEnergy={setEnergy}
          setContinueHandler={setContinueHandler}
          score={score}
          setScore={setScore}
          setLastIntersection={setLastIntersection}
          setLevelAnimData={setLevelAnimData}
          levelAnimPlaying = {levelAnimPlaying}
          setGameFinished={setGameFinished}
          resetWorldRef={resetWorldRef}
        />
    </Canvas>
    {showIntroLoadingOverlay && (
      <div className="velo-start-loading-overlay">
        <Spinner gameState="loading" />
        <p>Préparation du parcours…</p>
      </div>
    )}
    {!!levelAnimData && (
      <LevelLabel
        animLevel={levelAnimData.level}
        scoreBonusAnim={levelAnimData.bonus}
        LEVEL_NAMES={LEVEL_NAMES}
      />
    )}
{/* ========================= HUD ==================== */}
<div
  data-game-ui
  style={{
    position: "fixed",
    top: 0,
    left: 0,
    width: 271,
    height: 82,
    backgroundImage: "url('/textures/hud.png')",
    backgroundSize: "contain",     
    backgroundPosition: "left top",
    backgroundRepeat: "no-repeat",
    pointerEvents: "none",
    zIndex: 1000
  }}
>
  {/* ⏸ PAUSE */}
  <img
    src="/textures/pause.png"
    style={{
      position: "absolute",
      top: 5,
      right: 10,
      width: 28,
      height: 28,
      cursor: "pointer",
      pointerEvents: "auto",
    }}
    onClick={() => setGameState("waitingContinue")}
    alt="pause"
  />
  {/* PLAY */}
  {gameState === "waitingContinue" && (
    <img
      src="/textures/play.png"
      style={{
        position: "absolute",
        top: 5,
        right: 10,
        width: 28,
        height: 28,
        cursor: "pointer",
        pointerEvents: "auto",
      }}
      onClick={() => setGameState("playing")}
      alt="play"
    />
  )}
  <div
    style={{
      position: "relative",   
      width: "100%",
      height: "100%"
    }}
  >

    {/* ❤️ VIES */}
    <div
      style={{
        position: "absolute",
        top: -3,
        left: 30,
        fontSize: "10px",
        fontWeight: "bold",
        color: "red",
        textShadow: "0 0 6px black",
      }}
    >
      {"❤️".repeat(lives)}
    </div>  

    {/* 🔋 ÉNERGIE */}
    <div
      style={{
        position:"absolute",
        top:40,
        left:10,
        width:250,
        height:12,
        background:"#222",
        borderRadius:6,
        overflow:"hidden",
        boxShadow:"0 0 6px black"
      }}
    >
      <div
        style={{
          width:`${energy}%`,
          height:"100%",
          background:"linear-gradient(90deg,#00ff66,#00aa33)",
          transition:"width 0.2s"
        }}
      />
    </div>
    {/* 🗺 NIVEAU + INTERSECTION */}
    <div
      id="hud-level"
      style={{
        position: "absolute",
        top: 57,
        left: 20,
        fontSize: "10px",
        fontWeight: "bold",
        color: "lightGreen",
        textShadow: "0 0 6px black"
      }}
    >
      {level} - {lastIntersection?.id || "Gouin"}
    </div>

    {/* 🧮 SCORE */}
    <div
      style={{
        position:"absolute",
        top:-3,
        right:100,
        fontSize:12,
        fontWeight:"bold",
        color:"yellow",
        textShadow:"0 0 6px black"
      }}
    >
      {score}
    </div>

    <div className="velo-route-progress">
      <span>Distance : {formatKm(routeProgress.distanceKm)} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Parcours : {formatPercent(routeProgress.completionPercent)}</span>
    </div>
    
    {/* 🏆 LEADERBOARD */}
    <img
      alt="Highscores"
      src="/textures/highscores.png"
      style={{
        position: "absolute",
        top: 55,
        right: 60,
        width: 22,
        height: 22,
        cursor: "pointer",
        pointerEvents: "auto",
      }}
      onClick={() => {
        setLeaderboardResumeState("playing")
        setShowLeaderboard(true)
      }}
    />
    {/* ❓ CONFIGURATION */}
    <img
      alt="Configuration"
      src="/textures/configuration.png"
      style={{
        position: "absolute",
        top: 55,
        right: 35,
        width: 22,
        height: 22,
        cursor: "pointer",
        pointerEvents: "auto",
      }}
      onClick={() => setShowOptions(true)}
    />
    {/* ❓ AIDE */}
    <img
      alt="Aide"
      src="/textures/aide.png"
      style={{
        position: "absolute",
        top: 55,
        right: 10,
        width: 22,
        height: 22,
        cursor: "pointer",
        pointerEvents: "auto",
      }}
      onClick={() => setShowHelp(true)}
    />

  </div>
</div>

  {/* 🔘 CONTINUER */}
  {gameState === "waitingContinue" && !showLeaderboard && (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        background: "rgba(0,0,0,0.35)",
      }}
    >
      <button
        onClick={() => continueHandler?.()}
        style={{
          fontSize: "34px",
          padding: "18px 42px",
          borderRadius: "14px",
          border: "none",
          background: "#FFFFFF",
          transform: "translateY(-100px)"
        }}
      >
        CONTINUER
      </button>
    </div>
  )}

  {/* 💀 GAME OVER */}
  {gameState === "gameOver" && !showHighscore && false && (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.6)",
        color: "white",
        fontSize: "48px",
        fontWeight: "bold",
        textShadow: "0 0 10px black",
        pointerEvents: "auto"
      }}
    >
      PARTIE TERMINÉE

      <button
        onClick={resetGame}
        style={{
          marginTop: 40,
          fontSize: 28,
          padding: "14px 36px",
          borderRadius: 10,
          border: "none",
          color: "black",
          background: "white",
          cursor: "pointer"
        }}
      >
        Recommencer
      </button>
    </div>
  )}

  {/* ⚙️ OPTIONS MODAL */}
  {gameState === "gameOver" && !showHighscore && !showLeaderboard && (
    <VeloShareScore
      score={finalScore}
      routeProgress={routeProgress}
      level={finalLevel}
      lastIntersection={lastIntersection}
      playerName={sharePlayerName}
      highscoreRank={shareHighscoreRank}
      onReplay={resetGame}
    />
  )}

  <OptionsModal
    isOpen={showOptions}
    initialDepart={depart}
    initialDifficulte={difficulte}
    initialInvincible={isInvincibleOption}
    onOpen={() => {
      setGameState("waitingContinue")
    }}
    onClose={() => {
      setShowOptions(false)
      setGameState("playing")
    }}
    onSave={(options) => {
      setDepart(options.depart)
      setDifficulte(options.difficulte)
      setIsInvincibleOption(options.invincible)
      setShowOptions(false);
    }}
  />

  {/* 🏆 HIGHSCORE */}
  <HighscoreModal
    isOpen={showHighscore}
    score={finalScore}
    level={finalLevel}
    onClose={() => {
      setShowHighscore(false)
      setHighscoreShown(true)
    }}
    onSuccess={(newScore) => {
      setLatestScore(newScore)
      setSharePlayerName(newScore?.nom || "Cycliste anonyme")
      setShareHighscoreRank(null)
      setShowHighscore(false)
      setLeaderboardResumeState("gameOver")
      setShowLeaderboard(true)
    }}
  />
  {/* 📊 LEADERBOARD */}
  <Leaderboard
    isOpen={showLeaderboard}
    onOpen={() => setGameState("waitingContinue")}
    onClose={() => {
      setShowLeaderboard(false)
      setGameState(leaderboardResumeState)
    }}
    latestScore={latestScore}
    onRankResolved={handleHighscoreRankResolved}
  />
  {/* ❓ AIDE */}
  {showHelp && (
    <AideModal
      onOpen={() => setGameState("waitingContinue")}
      onClose={() => {
        setShowHelp(false)
        setGameState("playing")}}
    />
    
)}
    {showIntro && (
      <div className="velo-intro-overlay">
        <VeloIntro
          onStart={startGameFromIntro}
          isGameReady={isGameReady}
        />
      </div>
    )}
    </div>
  </GameContext.Provider>
)
}
